/**
 * Vlag-configurator: upload of kies een vlag, verplaats en schaal het ontwerp.
 * Eén gedeeld beeld — bij dubbelzijdig staan beide kanten om de mast heen.
 * Stuurt positie + één voorbeeldafbeelding mee als line item properties.
 */

const STANDAARDMAAT = { w: 150, h: 100 };

/** Bouwt het tekenvlak op de verhouding van het gekozen vlagformaat. */
function bouwLayout(verhouding, dubbel, focus, compact) {
  // Focusstand bij een dubbelzijdige vlag: een zijde groot in beeld om in te
  // werken (zie #zetFocus). De zijde wordt gelijkmatig vergroot vanuit de maat
  // in de gewone weergave (600 breed), zodat de verhouding van het vlagvlak
  // exact gelijk blijft. Alles op de vlag staat in procenten van dat vlak: een
  // element op 50% blijft op 50%, en het drukbestand verandert niet.
  if (dubbel && (focus === 0 || focus === 1)) {
    const fh0 = Math.round(600 / verhouding);
    const k = 1.55;
    const fw = 600 * k;
    const fh = fh0 * k;
    const rand = 16;
    const mastBreed = 12;
    const tussen = 10;
    const view = { w: rand + fw + tussen + mastBreed + rand, h: rand + fh + rand };
    // De linkerzijde hangt links van de mast, de rechterzijde rechts ervan.
    const mastRechts = focus === 0;
    const vlagX = mastRechts ? rand : rand + mastBreed + tussen;
    const mastX = mastRechts ? rand + fw + tussen : rand;
    const inBeeld = { x: vlagX, y: rand, w: fw, h: fh };
    // De andere zijde blijft bestaan met dezelfde maat, maar buiten beeld. Zo
    // blijven alle berekeningen voor die zijde geldig (scherpte, drukbestand,
    // plaatsing); alleen tekenen en aanraken slaan hem over.
    const buitenBeeld = { x: view.w + 200, y: rand, w: fw, h: fh };
    return {
      view,
      mast: { x: mastX, y: 6, w: mastBreed, h: view.h - 12 },
      foot: null,
      flags: focus === 0 ? [inBeeld, buitenBeeld] : [buitenBeeld, inBeeld],
      ticks: [rand + fh * 0.175, rand + fh * 0.5, rand + fh * 0.825],
      focus,
    };
  }
  if (dubbel) {
    const fw = 600;
    const fh = Math.round(fw / verhouding);
    const view = { w: 1320, h: fh + 100 };
    return {
      view,
      mast: { x: 653, y: 14, w: 14, h: view.h - 28 },
      foot: { x: 643, y: view.h - 20, w: 34, h: 11 },
      flags: [
        { x: 25, y: 40, w: fw, h: fh },
        { x: 695, y: 40, w: fw, h: fh },
      ],
      ticks: [40 + fh * 0.175, 40 + fh * 0.5, 40 + fh * 0.825],
    };
  }
  // Ontwerptool op een telefoon (Jos, 11 okt 2026): zo min mogelijk lege rand,
  // zodat de vlag groter in beeld staat. De stok met voetje blijft zichtbaar en
  // rechts blijft een smalle marge. Alles op de vlag staat in procenten van het
  // vlagvlak, dus het ontwerp en het drukbestand veranderen hier niet door.
  if (compact) {
    const fw = 900;
    const fh = Math.round(fw / verhouding);
    const links = 34, boven = 14, rechts = 18, onder = 24;
    const view = { w: links + fw + rechts, h: boven + fh + onder };
    return {
      view,
      mast: { x: 22, y: 4, w: 12, h: view.h - 14 },
      foot: { x: 14, y: view.h - 16, w: 28, h: 10 },
      flags: [{ x: links, y: boven, w: fw, h: fh }],
      ticks: [boven + fh * 0.167, boven + fh * 0.5, boven + fh * 0.833],
    };
  }
  const fw = 900;
  const fh = Math.round(fw / verhouding);
  const view = { w: 1000, h: fh + 60 };
  return {
    view,
    mast: { x: 58, y: 20, w: 12, h: view.h - 30 },
    foot: { x: 50, y: view.h - 16, w: 28, h: 10 },
    flags: [{ x: 70, y: 30, w: fw, h: fh }],
    ticks: [30 + fh * 0.167, 30 + fh * 0.5, 30 + fh * 0.833],
  };
}

/**
 * Beachflags zijn geen rechthoek: het doek is gebogen en zit met een sok om de
 * mast. De preview knipt het ontwerp daarom uit in de echte vlagvorm, zodat de
 * klant ziet wat er wegvalt aan de bovenrand.
 */
function beachflagLayout() {
  const view = { w: 760, h: 1180 };
  return {
    view,
    mast: { x: 150, y: 30, w: 9, h: view.h - 60 },
    foot: { x: 96, y: view.h - 34, w: 118, h: 9 },
    flags: [{ x: 159, y: 50, w: 340, h: 1010 }],
    ticks: [],
    beach: true,
  };
}

/**
 * Beachflag van Probo: de verhouding komt uit de werktekening, dus het doek op
 * het scherm heeft precies de vorm die gesneden wordt. Bewust geen afronding
 * op de breedte: het drukbestand neemt de verhouding van dit vak over.
 */
function proboBeachLayout(v) {
  const view = { w: 760, h: 1180 };
  const h = 1010;
  const w = h * (v.doek.b / v.doek.h);
  const onder = 50 + h;
  // De stok staat alleen onder de vlag. Langs de vlag zelf zie je hem niet: daar
  // zit de band (of bij de Square de tunnel) eromheen, en bij een gebogen
  // bovenrand buigt de stok mee. Een rechte stok naast de vlag verwarde klanten
  // (Jos, 18 sep 2026).
  return {
    view,
    mast: { x: 153, y: onder - 10, w: 9, h: view.h - 34 - onder + 10 },
    foot: { x: 96, y: view.h - 34, w: 118, h: 9 },
    flags: [{ x: 159, y: 50, w, h }],
    ticks: [],
    beach: true,
    probo: true,
  };
}

/**
 * De Probo-vorm bij een vormsleutel, of null. Beachflags ("straight-80x315")
 * staan in probo-beachflag-vormen.js, de boot-, golf- en kioskvlag
 * ("kiosk-68x136-links") in probo-vlag-vormen.js.
 */
function proboVorm(vorm) {
  if (!vorm) return null;
  const beach = window.PROBO_BEACH_VORMEN;
  const vlag = window.PROBO_VLAG_VORMEN;
  return (beach && beach[vorm]) || (vlag && vlag[vorm]) || null;
}

/**
 * Een pad uit de werktekening (in cm) als punten in het vak r. Een vak met
 * spiegel (de achterkant van een dubbelzijdige kioskvlag) krijgt de vorm in
 * spiegelbeeld: zo zie je die kant van de vlag staan.
 */
function proboPunten(pad, v, r) {
  const sx = r.w / v.doek.b;
  const sy = r.h / v.doek.h;
  const px = r.spiegel ? (x) => r.x + r.w - x * sx : (x) => r.x + x * sx;
  const uit = [];
  let x0 = 0;
  let y0 = 0;
  for (const s of pad) {
    if (s[0] === 'M' || s[0] === 'L') {
      x0 = s[1];
      y0 = s[2];
      uit.push([px(x0), r.y + y0 * sy]);
    } else if (s[0] === 'C') {
      const [x1, y1, x2, y2, x3, y3] = s.slice(1);
      for (let i = 1; i <= 12; i++) {
        const t = i / 12;
        const u = 1 - t;
        const x = u * u * u * x0 + 3 * u * u * t * x1 + 3 * u * t * t * x2 + t * t * t * x3;
        const y = u * u * u * y0 + 3 * u * u * t * y1 + 3 * u * t * t * y2 + t * t * t * y3;
        uit.push([px(x), r.y + y * sy]);
      }
      x0 = x3;
      y0 = y3;
    }
  }
  return uit;
}

/**
 * Bootvlag, golfvlag en kioskvlag van Probo. Het doek heeft de vorm uit de
 * werktekening en de stok staat waar hij echt zit: rechtop links (boot, golf)
 * of schuin door de tunnel (kiosk).
 *
 * De dubbelzijdige kioskvlag heeft twee zijden naast elkaar. De linker staat in
 * spiegelbeeld: zo zie je de vlag van de andere kant. Dat is geen versiering.
 * In de werktekening van Probo loopt langs de stok een vouwlijn - het doek gaat
 * om de stok heen - en wat je om een vouw vouwt, ligt gespiegeld. Een concurrent
 * laat de klant daarom ook twee gespiegelde vakken vullen. En zonder spiegelen
 * klopt het gewoon niet: je schrijft van links naar rechts, dus zou de eerste
 * letter op de ene zijde bij de stok staan en op de andere zijde er juist
 * vandaan (Jos, 21 september 2026). In de focusstand (zie bouwLayout) staat één
 * zijde groot in beeld en de andere erbuiten.
 *
 * Het drukbestand hoeft hier niets voor te doen: de lagen staan al in de
 * percentages van deze zijde, dus het bestand is precies wat de klant op deze
 * zijde ziet - leesbaar, met de stok aan de kant waar hij op die helft zit.
 */
function proboVlagLayout(v, dubbel, focus) {
  const rand = 40;
  const verhouding = v.doek.b / v.doek.h;
  const staand = !!(v.mast && v.mast.staand);
  const passend = (maxB, maxH) => {
    let w = maxB;
    let h = w / verhouding;
    if (h > maxH) {
      h = maxH;
      w = h * verhouding;
    }
    return { w, h };
  };
  const schuin = (r) => {
    if (!v.mast || !v.mast.lijn) return null;
    const [x1, y1, x2, y2] = v.mast.lijn;
    const sx = r.w / v.doek.b;
    const sy = r.h / v.doek.h;
    const px = (x) => (r.spiegel ? r.x + r.w - x * sx : r.x + x * sx);
    return { x1: px(x1), y1: r.y + y1 * sy, x2: px(x2), y2: r.y + y2 * sy, w: 10 };
  };

  if (dubbel && (focus === 0 || focus === 1)) {
    const maat = passend(900, 1300);
    const view = { w: Math.max(760, maat.w + rand * 2), h: maat.h + rand * 2 };
    const inBeeld = { x: (view.w - maat.w) / 2, y: rand, w: maat.w, h: maat.h, spiegel: focus === 0 };
    const buitenBeeld = { x: view.w + 200, y: rand, w: maat.w, h: maat.h, spiegel: focus !== 0 };
    const flags = focus === 0 ? [inBeeld, buitenBeeld] : [buitenBeeld, inBeeld];
    return { view, mast: null, foot: null, flags, ticks: [], mastLijnen: [schuin(inBeeld)].filter(Boolean), focus, probo: true };
  }
  if (dubbel) {
    const tussen = 60;
    const maat = passend(600, 900);
    const view = { w: rand * 2 + maat.w * 2 + tussen, h: maat.h + rand * 2 };
    const flags = [
      { x: rand, y: rand, w: maat.w, h: maat.h, spiegel: true },
      { x: rand + maat.w + tussen, y: rand, w: maat.w, h: maat.h },
    ];
    return { view, mast: null, foot: null, flags, ticks: [], mastLijnen: flags.map(schuin).filter(Boolean), probo: true };
  }
  const stokRuimte = staand ? 30 : 0;
  const maat = passend(900, 1000);
  const view = { w: Math.max(760, maat.w + stokRuimte + rand * 2), h: maat.h + rand * 2 };
  const vlag = { x: (view.w - maat.w - stokRuimte) / 2 + stokRuimte, y: rand, w: maat.w, h: maat.h };
  return {
    view,
    mast: staand ? { x: vlag.x - 12, y: rand - 24, w: 10, h: maat.h + 48 } : null,
    foot: null,
    flags: [vlag],
    ticks: [],
    mastLijnen: [schuin(vlag)].filter(Boolean),
    probo: true,
  };
}

/**
 * Wimpel (Tim, 25 okt 2026): een smalle staande vlag die met band, koord en lus aan de korte
 * bovenkant aan de mast hangt. Het doek past in een vaste hoogte: met de gewone indeling (900 breed)
 * wordt het tekenvlak bij 1 op 8 ruim elf miljoen beeldpunten, meer dan een iPhone wil tekenen.
 * Boven het doek de band met het koord naar de mast, zodat de klant ziet wat boven is.
 */
function wimpelLayout(verhouding) {
  const h = 1000;
  const w = Math.round(h * verhouding);
  const zij = 40, boven = 56, onder = 24;
  const view = { w: w + zij * 2, h: boven + h + onder };
  const vlag = { x: zij, y: boven, w, h };
  const bandY = boven - 6;
  return {
    view,
    mast: null,
    foot: null,
    flags: [vlag],
    ticks: [],
    mastLijnen: [
      { x1: vlag.x, y1: bandY, x2: vlag.x + w, y2: bandY, w: 10 },
      { x1: vlag.x, y1: bandY, x2: vlag.x - 16, y2: 14, w: 3 },
      { x1: vlag.x + w, y1: bandY, x2: vlag.x + w + 24, y2: 8, w: 3 },
    ],
  };
}

/**
 * Een spandoek hangt niet aan een mast maar wordt met ringen opgespannen.
 * De preview toont daarom het doek met zoom en zeilringen langs de rand,
 * zodat de klant ziet hoe dicht zijn ontwerp bij de ringen komt.
 */
function spandoekLayout(verhouding) {
  const fw = 900;
  const fh = Math.max(140, Math.round(fw / verhouding));
  const marge = 46;
  const view = { w: fw + marge * 2, h: fh + marge * 2 };
  return {
    view,
    mast: null,
    foot: null,
    flags: [{ x: marge, y: marge, w: fw, h: fh }],
    ticks: [],
    spandoek: true,
  };
}

/** Punten van de vlagvorm binnen het opgegeven vak. */
function vlagPad(vorm, r) {
  const pv = proboVorm(vorm);
  if (pv) return proboPunten(pv.snij, pv, r);
  const { x, y, w, h } = r;
  const bz = (p0, p1, p2, p3, n = 48) => {
    const uit = [];
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const u = 1 - t;
      uit.push([
        u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0],
        u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1],
      ]);
    }
    return uit;
  };

  if (vorm === 'recht') {
    return [
      [x, y],
      [x + w, y],
      [x + w, y + h],
      [x, y + h],
    ];
  }
  if (vorm === 'druppel') {
    return [[x, y + h], [x, y]].concat(
      bz([x, y], [x + w * 1.45, y + h * 0.1], [x + w * 1.3, y + h * 0.7], [x + w * 0.3, y + h])
    );
  }
  // veervorm: rechte voorrand, brede ronde schouder, rechte achterrand
  const schouder = h * 0.3;
  return [[x, y + h], [x, y + schouder * 0.1]]
    .concat(bz([x, y + schouder * 0.1], [x + w * 0.12, y], [x + w * 0.72, y + schouder * 0.1], [x + w, y + schouder]))
    .concat([[x + w, y + h]]);
}

/* Uitkomsten van veiligVak() blijven bewaard: per vorm hoeft die som maar één
   keer gemaakt te worden. */
const veiligVakGeheugen = new Map();

/**
 * Het grootste rechthoekige vlak dat binnen een vlagvorm past, in procenten van
 * het doek.
 *
 * Sjablonen zijn gemaakt voor een rechthoekige vlag. Op een beachflag, kiosk-,
 * boot- of golfvlag zou tekst daardoor in de punt belanden of onder de band
 * verdwijnen (Jos, 21 september 2026). Daarom zoeken we hier het vlak waar
 * alles zeker binnen valt, en daarin wordt het sjabloon gezet.
 *
 * Werkwijze: de snijlijn in horizontale lijnen verdelen, per lijn kijken waar
 * je binnen het doek bent, en dan de rechthoek nemen met de grootste
 * oppervlakte. Daarna nog inperken tot de veilige marge uit de werktekening,
 * zodat tekst ook niet net op de rand of onder de band komt.
 */
function veiligVak(vorm) {
  if (veiligVakGeheugen.has(vorm)) return veiligVakGeheugen.get(vorm);
  const heel = { x: 0, y: 0, w: 100, h: 100 };
  const v = proboVorm(vorm);
  if (!v) return heel;

  const doek = { x: 0, y: 0, w: v.doek.b, h: v.doek.h };
  const punten = proboPunten(v.snij, v, doek);
  if (punten.length < 3) return heel;

  // Waar ligt de rand op hoogte y? Alle snijpunten met de omtrek.
  const snijpunten = (y) => {
    const uit = [];
    for (let i = 0, j = punten.length - 1; i < punten.length; j = i++) {
      const a = punten[i];
      const b = punten[j];
      if ((a[1] > y) === (b[1] > y)) continue;
      uit.push(a[0] + ((y - a[1]) / (b[1] - a[1])) * (b[0] - a[0]));
    }
    return uit.sort((p, q) => p - q);
  };

  const N = 48;
  const lijnen = [];
  for (let i = 0; i <= N; i++) {
    const y = doek.h * (i / N);
    const s = snijpunten(y);
    // Het breedste stuk op deze hoogte; bij een gat in de vorm is dat het doek.
    let breedste = null;
    for (let k = 0; k + 1 < s.length; k += 2) {
      if (!breedste || s[k + 1] - s[k] > breedste[1] - breedste[0]) breedste = [s[k], s[k + 1]];
    }
    lijnen.push({ y, links: breedste ? breedste[0] : null, rechts: breedste ? breedste[1] : null });
  }

  let beste = null;
  for (let i = 0; i < lijnen.length; i++) {
    if (lijnen[i].links === null) continue;
    let links = lijnen[i].links;
    let rechts = lijnen[i].rechts;
    for (let j = i + 1; j < lijnen.length; j++) {
      if (lijnen[j].links === null) break;
      links = Math.max(links, lijnen[j].links);
      rechts = Math.min(rechts, lijnen[j].rechts);
      const b = rechts - links;
      const h = lijnen[j].y - lijnen[i].y;
      if (b <= 0 || h <= 0) break;
      // Grootste oppervlak, maar een extreem smalle koker telt minder mee: op
      // een waveflag koos de som anders een strook van 36 bij 330 cm, en daar
      // past geen enkel sjabloon leesbaar in.
      const punt = b * h * Math.min(1, (b / h) / 0.3);
      if (!beste || punt > beste.punt) beste = { x: links, y: lijnen[i].y, w: b, h: h, punt: punt };
    }
  }
  if (!beste) return heel;

  // Binnen de veilige marge blijven: de stippellijn uit de werktekening.
  const margePunten = [];
  for (const pad of v.marge || []) {
    for (const p of proboPunten(pad, v, doek)) margePunten.push(p);
  }
  if (margePunten.length > 1) {
    const mx0 = Math.min.apply(null, margePunten.map((p) => p[0]));
    const mx1 = Math.max.apply(null, margePunten.map((p) => p[0]));
    const my0 = Math.min.apply(null, margePunten.map((p) => p[1]));
    const my1 = Math.max.apply(null, margePunten.map((p) => p[1]));
    const x0 = Math.max(beste.x, mx0);
    const x1 = Math.min(beste.x + beste.w, mx1);
    const y0 = Math.max(beste.y, my0);
    const y1 = Math.min(beste.y + beste.h, my1);
    if (x1 - x0 > 1 && y1 - y0 > 1) beste = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  }

  const vak = {
    x: (beste.x / doek.w) * 100,
    y: (beste.y / doek.h) * 100,
    w: (beste.w / doek.w) * 100,
    h: (beste.h / doek.h) * 100,
  };
  veiligVakGeheugen.set(vorm, vak);
  return vak;
}

/**
 * Lettertypen voor tekst op de vlag. Bewust kort en bewust alleen fonts die op
 * vrijwel elke computer en telefoon staan: de tekst wordt in de browser van de
 * klant in het drukbestand gebakken, dus een lettertype dat hij niet heeft,
 * wordt stilletjes vervangen — en dan drukken we iets anders dan hij zag.
 */
// Kleur van het kader om het element dat je versleept. Paars, want dat komt
// nergens anders op de vlag of in de winkel voor: rood zou verward worden met
// de huisstijl en met de rode hulplijnen bij het uitlijnen.
const SELECTIE_KLEUR = '#8b5cf6';

const LETTERTYPEN = [
  // Op elk apparaat aanwezig
  { naam: 'Arial', css: 'Arial, Helvetica, sans-serif', soort: 'standaard' },
  { naam: 'Impact', css: 'Impact, "Arial Black", sans-serif', soort: 'standaard' },
  { naam: 'Times', css: '"Times New Roman", Times, serif', soort: 'standaard' },
  { naam: 'Georgia', css: 'Georgia, "Times New Roman", serif', soort: 'standaard' },
  { naam: 'Courier', css: '"Courier New", Courier, monospace', soort: 'standaard' },
  { naam: 'Verdana', css: 'Verdana, Geneva, sans-serif', soort: 'standaard' },
  // Meegeladen via Google Fonts. Voor het drukbestand wordt gewacht tot ze
  // binnen zijn — anders bakt de browser er stilletjes Arial in en krijgt de
  // klant een andere vlag dan hij zag.
  //
  // Gekozen op wat op een vlag werkt: zwaar genoeg om van afstand te lezen, of
  // sierlijk genoeg voor een bruiloft. Een dun lettertype verdwijnt op vier
  // meter afstand, hoe mooi het van dichtbij ook is.
  //
  // De eerste reeks wordt altijd vet (700) getekend, zoals sinds het begin; dat
  // blijft zo, anders verandert een ontwerp dat al in een winkelmand ligt. De
  // tweede reeks (13 okt 2026) heeft een eigen gewicht: precies het gewicht dat
  // geladen wordt, anders maakt de browser er een nep-vette versie van.
  { naam: 'Anton', css: 'Anton, Impact, sans-serif', web: true, soort: 'strak' },
  { naam: 'Bebas Neue', css: '"Bebas Neue", Impact, sans-serif', web: true, soort: 'strak' },
  { naam: 'Oswald', css: 'Oswald, "Arial Narrow", sans-serif', web: true, soort: 'strak' },
  { naam: 'Montserrat', css: 'Montserrat, Arial, sans-serif', web: true, soort: 'strak' },
  { naam: 'Archivo Black', css: '"Archivo Black", Impact, sans-serif', web: true, soort: 'strak' },
  { naam: 'Staatliches', css: 'Staatliches, Impact, sans-serif', web: true, soort: 'strak' },
  { naam: 'Teko', css: 'Teko, "Arial Narrow", sans-serif', web: true, soort: 'strak' },
  { naam: 'Poppins', css: 'Poppins, Arial, sans-serif', web: true, soort: 'strak', gewicht: 800 },
  { naam: 'Quicksand', css: 'Quicksand, Verdana, sans-serif', web: true, soort: 'strak', gewicht: 700 },
  { naam: 'Comfortaa', css: 'Comfortaa, Verdana, sans-serif', web: true, soort: 'strak', gewicht: 700 },
  { naam: 'Baloo 2', css: '"Baloo 2", Verdana, sans-serif', web: true, soort: 'strak', gewicht: 800 },
  { naam: 'Nunito', css: 'Nunito, Verdana, sans-serif', web: true, soort: 'strak', gewicht: 900 },
  { naam: 'Righteous', css: 'Righteous, Impact, sans-serif', web: true, soort: 'dik' },
  { naam: 'Fredoka', css: 'Fredoka, Verdana, sans-serif', web: true, soort: 'dik' },
  { naam: 'Bungee', css: 'Bungee, Impact, sans-serif', web: true, soort: 'dik', gewicht: 400 },
  { naam: 'Lilita One', css: '"Lilita One", Impact, sans-serif', web: true, soort: 'dik', gewicht: 400 },
  { naam: 'Titan One', css: '"Titan One", Impact, sans-serif', web: true, soort: 'dik', gewicht: 400 },
  { naam: 'Luckiest Guy', css: '"Luckiest Guy", Impact, sans-serif', web: true, soort: 'dik', gewicht: 400 },
  { naam: 'Bowlby One SC', css: '"Bowlby One SC", Impact, sans-serif', web: true, soort: 'dik', gewicht: 400 },
  { naam: 'Chewy', css: 'Chewy, Impact, sans-serif', web: true, soort: 'dik', gewicht: 400 },
  { naam: 'Bangers', css: 'Bangers, Impact, sans-serif', web: true, soort: 'dik', gewicht: 400 },
  { naam: 'Lobster', css: 'Lobster, Georgia, cursive', web: true, soort: 'retro' },
  { naam: 'Alfa Slab One', css: '"Alfa Slab One", Georgia, serif', web: true, soort: 'retro' },
  { naam: 'Abril Fatface', css: '"Abril Fatface", Georgia, serif', web: true, soort: 'retro' },
  { naam: 'Shrikhand', css: 'Shrikhand, Georgia, cursive', web: true, soort: 'retro', gewicht: 400 },
  { naam: 'Cherry Bomb One', css: '"Cherry Bomb One", Verdana, sans-serif', web: true, soort: 'retro', gewicht: 400 },
  { naam: 'Bagel Fat One', css: '"Bagel Fat One", Impact, sans-serif', web: true, soort: 'retro', gewicht: 400 },
  { naam: 'Rammetto One', css: '"Rammetto One", Impact, sans-serif', web: true, soort: 'retro', gewicht: 400 },
  { naam: 'Playfair Display', css: '"Playfair Display", Georgia, serif', web: true, soort: 'chique' },
  { naam: 'Cinzel', css: 'Cinzel, Georgia, serif', web: true, soort: 'chique' },
  { naam: 'DM Serif Display', css: '"DM Serif Display", Georgia, serif', web: true, soort: 'chique', gewicht: 400 },
  { naam: 'Yeseva One', css: '"Yeseva One", Georgia, serif', web: true, soort: 'chique', gewicht: 400 },
  { naam: 'Cormorant Garamond', css: '"Cormorant Garamond", Georgia, serif', web: true, soort: 'chique', gewicht: 700 },
  { naam: 'Gloock', css: 'Gloock, Georgia, serif', web: true, soort: 'chique', gewicht: 400 },
  { naam: 'Pacifico', css: 'Pacifico, Georgia, cursive', web: true, soort: 'schrift' },
  { naam: 'Great Vibes', css: '"Great Vibes", Georgia, cursive', web: true, soort: 'schrift' },
  { naam: 'Dancing Script', css: '"Dancing Script", Georgia, cursive', web: true, soort: 'schrift', gewicht: 700 },
  { naam: 'Allura', css: 'Allura, Georgia, cursive', web: true, soort: 'schrift', gewicht: 400 },
  { naam: 'Parisienne', css: 'Parisienne, Georgia, cursive', web: true, soort: 'schrift', gewicht: 400 },
  { naam: 'Alex Brush', css: '"Alex Brush", Georgia, cursive', web: true, soort: 'schrift', gewicht: 400 },
  { naam: 'Satisfy', css: 'Satisfy, Georgia, cursive', web: true, soort: 'schrift', gewicht: 400 },
  { naam: 'Yellowtail', css: 'Yellowtail, Georgia, cursive', web: true, soort: 'schrift', gewicht: 400 },
  { naam: 'Caveat', css: 'Caveat, Georgia, cursive', web: true, soort: 'hand' },
  { naam: 'Permanent Marker', css: '"Permanent Marker", Georgia, cursive', web: true, soort: 'hand' },
  { naam: 'Amatic SC', css: '"Amatic SC", "Arial Narrow", sans-serif', web: true, soort: 'hand', gewicht: 700 },
  { naam: 'Patrick Hand', css: '"Patrick Hand", Verdana, sans-serif', web: true, soort: 'hand', gewicht: 400 },
  { naam: 'Gochi Hand', css: '"Gochi Hand", Verdana, sans-serif', web: true, soort: 'hand', gewicht: 400 },
  { naam: 'Kalam', css: 'Kalam, Georgia, cursive', web: true, soort: 'hand', gewicht: 700 },
  { naam: 'Caveat Brush', css: '"Caveat Brush", Georgia, cursive', web: true, soort: 'hand', gewicht: 400 },
  { naam: 'Mountains of Christmas', css: '"Mountains of Christmas", Georgia, cursive', web: true, soort: 'speciaal', gewicht: 700 },
  { naam: 'Creepster', css: 'Creepster, Impact, sans-serif', web: true, soort: 'speciaal', gewicht: 400 },
  { naam: 'Rubik Bubbles', css: '"Rubik Bubbles", Impact, sans-serif', web: true, soort: 'speciaal', gewicht: 400 },
  { naam: 'Press Start 2P', css: '"Press Start 2P", "Courier New", monospace', web: true, soort: 'speciaal', gewicht: 400 },
];

/** Kopjes in de keuzelijst met lettertypen. */
const LETTER_SOORTEN = {
  standaard: { nl: 'Standaard', en: 'Standard', de: 'Standard', fr: 'Standard', es: 'Estándar', it: 'Standard' },
  strak: { nl: 'Strak', en: 'Clean', de: 'Klar', fr: 'Épuré', es: 'Limpio', it: 'Pulito' },
  dik: { nl: 'Dik en vrolijk', en: 'Bold and fun', de: 'Fett und fröhlich', fr: 'Gras et joyeux', es: 'Grueso y alegre', it: 'Spesso e allegro' },
  retro: { nl: 'Retro', en: 'Retro', de: 'Retro', fr: 'Rétro', es: 'Retro', it: 'Retrò' },
  chique: { nl: 'Chique', en: 'Elegant', de: 'Elegant', fr: 'Chic', es: 'Elegante', it: 'Elegante' },
  schrift: { nl: 'Sierlijk schrift', en: 'Script', de: 'Schreibschrift', fr: 'Calligraphie', es: 'Caligrafía', it: 'Corsivo' },
  hand: { nl: 'Handgeschreven', en: 'Handwritten', de: 'Handschrift', fr: 'Manuscrit', es: 'Manuscrita', it: 'Scritto a mano' },
  speciaal: { nl: 'Speciaal', en: 'Special', de: 'Besonders', fr: 'Spécial', es: 'Especial', it: 'Speciale' },
};

/** Het gewicht waarmee een lettertype getekend wordt (zie hierboven). */
function letterGewicht(css) {
  const lt = LETTERTYPEN.find((x) => x.css === css);
  return (lt && lt.gewicht) || 700;
}
function letterFont(css, px) {
  return letterGewicht(css) + ' ' + px + 'px ' + css;
}

/**
 * De tweede reeks lettertypen staat in een eigen stylesheet die de pagina niet
 * ophoudt (snippet: #flagcfg-letters-extra). Voor het drukbestand moet die wel
 * binnen zijn, anders kent de browser het lettertype nog niet en bakt hij er
 * stilletjes een ander in.
 */
function letterbladKlaar() {
  const link = document.getElementById('flagcfg-letters-extra');
  if (!link || link.sheet) return Promise.resolve();
  return new Promise((ok) => {
    link.addEventListener('load', ok, { once: true });
    link.addEventListener('error', ok, { once: true });
    setTimeout(ok, 6000);
  });
}

/**
 * Tekststijl (Jos, 13 okt 2026: "Uitlijnen, vervagen, etc."). Eén plek die tekst
 * meet en tekent, voor het doek, het drukbestand en de voorbeeldjes van de
 * sjablonen: zo kan wat de klant ziet nooit verschillen van wat er gedrukt wordt.
 *
 * Eigenschappen op een tekstlaag, allemaal optioneel (zonder = zoals altijd):
 *   spatie       letterafstand in honderdsten van de letterhoogte (-10..60)
 *   boog         kromming -100..100: 100 = halve cirkel met de bolle kant boven,
 *                negatief = bolle kant onder
 *   rand         rand om de letters, dikte in honderdsten van de hoogte (0..20)
 *   randKleur
 *   effect       '', 'schaduw', 'hard', 'gloed', 'echo', 'hol' of 'vlak'
 *   effectKleur
 *   dekking      10..100 procent
 * Allemaal getallen of teksten, dus #laagGegevens bewaart ze vanzelf.
 */
function tekstOpmaak(ctx, l, px) {
  ctx.font = letterFont(l.font, px);
  const tekst = l.tekst || '';
  const spatie = ((Number(l.spatie) || 0) / 100) * px;
  const boog = Math.max(-100, Math.min(100, Number(l.boog) || 0));
  if (!spatie && !boog) return { tekst, px, breed: ctx.measureText(tekst).width, hoog: px, tekens: null, boog: 0 };
  // Per letter, maar gemeten als stukje van de hele tekst: zo blijft de
  // onderlinge afstand (kerning) van het lettertype behouden.
  const tekens = [];
  let voor = 0;
  let x = 0;
  let stuk = '';
  Array.from(tekst).forEach((t) => {
    stuk += t;
    const na = ctx.measureText(stuk).width;
    const w = na - voor;
    tekens.push({ t, mid: x + w / 2 });
    x += w + spatie;
    voor = na;
  });
  const breed = Math.max(0, x - spatie);
  tekens.forEach((c) => { c.mid -= breed / 2; });
  if (!boog || breed <= 0) return { tekst, px, breed, hoog: px, tekens, boog: 0 };
  const hoek = (Math.abs(boog) / 100) * Math.PI;
  const R = breed / hoek;
  const pijl = R * (1 - Math.cos(hoek / 2));
  const koorde = hoek >= Math.PI ? 2 * R : 2 * R * Math.sin(hoek / 2);
  return { tekst, px, breed: koorde, hoog: px + pijl, tekens, boog, R, pijl };
}

function kleurMetAlfa(hex, a) {
  const h = /^#([0-9a-f]{6})$/i.exec(hex || '');
  if (!h) return 'rgba(0,0,0,' + a + ')';
  const n = parseInt(h[1], 16);
  return 'rgba(' + (n >> 16) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + a + ')';
}

function rondeRechthoek(ctx, x, y, b, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + b, y, x + b, y + h, r);
  ctx.arcTo(x + b, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + b, y, r);
  ctx.closePath();
}

/** Tekent een tekstlaag rond het nulpunt (ctx staat al verschoven en gedraaid). */
function tekenTekst(ctx, l, px, kleur) {
  const m = tekstOpmaak(ctx, l, px);
  if (!m.tekst) return m;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const elk = (fn) => {
    if (!m.tekens) { fn(m.tekst, 0, 0); return; }
    m.tekens.forEach((c) => {
      if (!m.boog) { fn(c.t, c.mid, 0); return; }
      const phi = c.mid / m.R;
      ctx.save();
      if (m.boog > 0) {
        ctx.translate(m.R * Math.sin(phi), m.R * (1 - Math.cos(phi)) - m.pijl / 2);
        ctx.rotate(phi);
      } else {
        ctx.translate(m.R * Math.sin(phi), -m.R * (1 - Math.cos(phi)) + m.pijl / 2);
        ctx.rotate(-phi);
      }
      fn(c.t, 0, 0);
      ctx.restore();
    });
  };
  const vul = (k) => { ctx.fillStyle = k; elk((t, x, y) => ctx.fillText(t, x, y)); };
  const omlijn = (k, w) => {
    ctx.strokeStyle = k;
    ctx.lineWidth = w;
    ctx.lineJoin = 'round';
    ctx.miterLimit = 2;
    elk((t, x, y) => ctx.strokeText(t, x, y));
  };
  const eff = l.effect || '';
  const ek = l.effectKleur || '#000000';
  const randW = l.rand ? ((Number(l.rand) || 0) / 100) * px * 2 : 0;
  const randK = l.randKleur || '#ffffff';
  const alfa = ctx.globalAlpha;
  ctx.globalAlpha = alfa * (l.dekking == null || l.dekking === '' ? 1 : Math.max(0.05, Math.min(1, Number(l.dekking) / 100)));

  if (eff === 'vlak') {
    const pad = px * 0.32;
    const b = m.breed + pad * 2;
    const h = m.hoog + px * 0.34;
    ctx.fillStyle = ek;
    rondeRechthoek(ctx, -b / 2, -h / 2, b, h, Math.min(h / 2, px * 0.28));
    ctx.fill();
  } else if (eff === 'hard') {
    ctx.save();
    ctx.translate(px * 0.07, px * 0.07);
    if (randW) omlijn(ek, randW);
    vul(ek);
    ctx.restore();
  } else if (eff === 'echo') {
    [[0.18, 0.3], [0.09, 0.6]].forEach(([d, a]) => {
      ctx.save();
      ctx.globalAlpha *= a;
      ctx.translate(px * d, px * d);
      vul(ek);
      ctx.restore();
    });
  } else if (eff === 'hol') {
    // Holle letters: alleen de omtrek, in de kleur van de tekst.
    omlijn(kleur, Math.max(px * 0.04, randW));
    ctx.globalAlpha = alfa;
    return m;
  } else if (eff === 'schaduw' || eff === 'gloed') {
    // Schaduw en gloed werken in beeldpunten van het doek, niet in de maat van
    // de tekening. Daarom omrekenen met de schaal van het doek: dan is de
    // schaduw op het drukbestand even groot als op het scherm.
    const t = ctx.getTransform ? ctx.getTransform() : null;
    const schaal = t ? Math.hypot(t.a, t.b) || 1 : 1;
    ctx.save();
    ctx.shadowColor = eff === 'schaduw' ? kleurMetAlfa(ek, 0.55) : ek;
    ctx.shadowBlur = px * (eff === 'schaduw' ? 0.14 : 0.3) * schaal;
    ctx.shadowOffsetX = eff === 'schaduw' ? px * 0.06 * schaal : 0;
    ctx.shadowOffsetY = ctx.shadowOffsetX;
    for (let i = 0; i < (eff === 'gloed' ? 2 : 1); i++) {
      if (randW) omlijn(randK, randW);
      else vul(kleur);
    }
    ctx.restore();
  }
  if (randW) omlijn(randK, randW);
  vul(kleur);
  ctx.globalAlpha = alfa;
  return m;
}

/** Teksten van het stijlpaneel, in de taal van de winkel. */
function stijlTeksten() {
  const T = {
    nl: { stijl: 'Stijl', uitlijnen: 'Uitlijnen', links: 'Links', midden: 'Midden', rechts: 'Rechts', boven: 'Boven', onder: 'Onder',
      afstand: 'Afstand', boog: 'Boog', rand: 'Rand', effect: 'Effect', dekking: 'Zichtbaar', wissen: 'Stijl wissen',
      geen: 'Geen', schaduw: 'Schaduw', hard: 'Harde schaduw', gloed: 'Gloed', echo: 'Echo', hol: 'Holle letters', vlak: 'Vlak erachter' },
    en: { stijl: 'Style', uitlijnen: 'Align', links: 'Left', midden: 'Centre', rechts: 'Right', boven: 'Top', onder: 'Bottom',
      afstand: 'Spacing', boog: 'Curve', rand: 'Outline', effect: 'Effect', dekking: 'Opacity', wissen: 'Clear style',
      geen: 'None', schaduw: 'Shadow', hard: 'Hard shadow', gloed: 'Glow', echo: 'Echo', hol: 'Hollow', vlak: 'Background' },
    de: { stijl: 'Stil', uitlijnen: 'Ausrichten', links: 'Links', midden: 'Mitte', rechts: 'Rechts', boven: 'Oben', onder: 'Unten',
      afstand: 'Abstand', boog: 'Bogen', rand: 'Kontur', effect: 'Effekt', dekking: 'Deckkraft', wissen: 'Stil löschen',
      geen: 'Keiner', schaduw: 'Schatten', hard: 'Harter Schatten', gloed: 'Leuchten', echo: 'Echo', hol: 'Hohl', vlak: 'Hintergrund' },
    fr: { stijl: 'Style', uitlijnen: 'Aligner', links: 'Gauche', midden: 'Centre', rechts: 'Droite', boven: 'Haut', onder: 'Bas',
      afstand: 'Espacement', boog: 'Courbe', rand: 'Contour', effect: 'Effet', dekking: 'Opacité', wissen: 'Effacer le style',
      geen: 'Aucun', schaduw: 'Ombre', hard: 'Ombre nette', gloed: 'Lueur', echo: 'Écho', hol: 'Creux', vlak: 'Fond' },
    es: { stijl: 'Estilo', uitlijnen: 'Alinear', links: 'Izquierda', midden: 'Centro', rechts: 'Derecha', boven: 'Arriba', onder: 'Abajo',
      afstand: 'Espaciado', boog: 'Curva', rand: 'Contorno', effect: 'Efecto', dekking: 'Opacidad', wissen: 'Borrar estilo',
      geen: 'Ninguno', schaduw: 'Sombra', hard: 'Sombra dura', gloed: 'Brillo', echo: 'Eco', hol: 'Hueco', vlak: 'Fondo' },
    it: { stijl: 'Stile', uitlijnen: 'Allinea', links: 'Sinistra', midden: 'Centro', rechts: 'Destra', boven: 'Alto', onder: 'Basso',
      afstand: 'Spaziatura', boog: 'Curva', rand: 'Contorno', effect: 'Effetto', dekking: 'Opacità', wissen: 'Cancella stile',
      geen: 'Nessuno', schaduw: 'Ombra', hard: 'Ombra netta', gloed: 'Bagliore', echo: 'Eco', hol: 'Vuoto', vlak: 'Sfondo' },
  };
  const taal = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
  return T[taal] || T.nl;
}

/** Opmaak van het stijlpaneel. Hier en niet in de snippet: die bundel ververst niet altijd tegelijk met dit script. */
function stijlCss() {
  if (document.getElementById('flagcfg-stijl-css')) return;
  const st = document.createElement('style');
  st.id = 'flagcfg-stijl-css';
  st.textContent =
    '.flagcfg-stijl{order:6;flex:1 0 100%;display:grid;gap:.45rem;padding:.6rem .7rem;margin-top:.15rem;border:1px solid var(--color-border,#ddd);border-radius:8px;background:rgb(0 0 0/.025);font-size:.75rem}' +
    '.flagcfg-stijl__rij{display:flex;align-items:center;gap:.5rem;flex-wrap:wrap}' +
    '.flagcfg-stijl__label{flex:0 0 4.6rem;opacity:.8}' +
    '.flagcfg-stijl__rij input[type=range]{flex:1 1 8rem;min-width:6rem;min-height:30px}' +
    '.flagcfg-stijl__groep{display:inline-flex;border:1px solid var(--color-border,#ccc);border-radius:6px;overflow:hidden}' +
    '.flagcfg-stijl__knop{width:34px;height:32px;display:grid;place-items:center;padding:0;border:0;background:none;color:inherit;cursor:pointer}' +
    '.flagcfg-stijl__knop+.flagcfg-stijl__knop{border-left:1px solid var(--color-border,#ccc)}' +
    '.flagcfg-stijl__knop:hover{background:rgb(0 0 0/.06)}' +
    '.flagcfg-stijl__knop svg{width:18px;height:18px;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round}' +
    '.flagcfg-stijl__kleur{flex:none;width:40px;height:32px;padding:0;border:1px solid var(--color-border,#ccc);border-radius:6px;background:none;cursor:pointer}' +
    '.flagcfg-stijl select{flex:1 1 8rem;min-height:32px;padding:.2rem .4rem;font:inherit;font-size:.8rem;color:inherit;background:none;border:1px solid var(--color-border,#ccc);border-radius:6px}' +
    '.flagcfg-stijl__wis{justify-self:start;background:none;border:0;padding:.2rem 0;font:inherit;font-size:.75rem;text-decoration:underline;color:inherit;opacity:.6;cursor:pointer}';
  document.head.appendChild(st);
}

/**
 * Vormen voor de ontwerptool. Elke vorm wordt getekend in een vak van 1 bij 1
 * rond het middelpunt (dus van -0,5 tot 0,5), zodat dezelfde functie zowel het
 * knopje van 40 beeldpunten als de vlag van drie meter kan tekenen — één som,
 * geen tweede versie die uit de pas gaat lopen.
 *
 * Bewust getekend en niet als plaatje ingeladen: zo blijven ze op elk formaat
 * scherp, ook in het drukbestand, en er komt geen bestand bij het thema.
 */
const FLAGCFG_VERSIE = "2026-11-03 09:00";

const DPR = 1.6;

/**
 * Getekende elementen (Jos, 6 okt 2026): linten, emblemen, ballonnen, bloemen en
 * zo verder, zelf getekend zodat er geen licentie aan vastzit. Ze staan in de
 * bestanden flag-kleur-el-*.js en komen als laag `type: 'vorm'` op de vlag, met
 * de tekening als tekst in `l.tekening`: "breed,hoog|rol:pad|rol:pad".
 *
 * Een tekst en geen lijst, omdat #laagGegevens alleen tekst, getallen en ja/nee
 * bewaart. Een lijst zou wegvallen bij kopiëren naar de andere kant en bij het
 * terugzetten uit de winkelmand.
 *
 * Rollen: k hoofdkleur, d donker, z heel donker, l licht, m heel licht, a en b
 * buurkleuren — die volgen alle uit de ene kleur die de klant kiest. Verder w
 * (wit) of een vaste kleur #rrggbb. Erachter optioneel een cijfer (doorzichtig-
 * heid, 5 = 50%) en een e (even-odd, voor ringen en kaders). Rol c knipt alles
 * wat volgt op dat pad, tot rol x.
 *
 * Sinds 13 oktober 2026 (tweede reeks, fijne lijnen): een rol kan ook een lijn
 * zijn, met s en de dikte erachter ("ks7" = lijn van 7 in de hoofdkleur, ronde
 * uiteinden), en een kleurverloop g1..g9. De verlopen staan in de kop:
 * "b,h;g1=#a>#b>#c@L0_0_100_0" (lineair x1_y1_x2_y2) of "@R50_50_40" (rond).
 */
const ELEMENT_ROL = /^(#[0-9a-fA-F]{6}|[kdzlmwab]|g\d)(\d)?(e)?(?:s(\d+(?:\.\d+)?))?$/;
const ELEMENT_VERLOOP = /^(#[0-9a-fA-F]{6}(?:>#[0-9a-fA-F]{6})+)@([LR])(-?\d+(?:_-?\d+){2,3})$/;
const elementTekeningen = new Map();

/**
 * Welk vlaggetje (1..N, de volgorde op de band) hoort op welke plek van het drukvel? Uit de werktekeningen
 * van Probo (15 okt 2026), waar de nummers in staan:
 *  - wimpels: de vlaggetjes met de punt omlaag (draai 0) van links naar rechts 1, 2, 3 ...; daarna die met
 *    de punt omhoog (draai 180) van rechts naar links. Achterzijde: eerst de punt-omhoog van rechts naar
 *    links, dan de punt-omlaag van links naar rechts.
 *  - rechthoekig: bovenste rij van links naar rechts, dan de onderste rij. Achterzijde: eerst de onderste,
 *    dan de bovenste rij.
 * Geeft per plek (in de volgorde van vel.plekken) het nummer terug.
 */
function vlagNummers(vorm, vel, achter) {
  const p = vel.plekken.map((q, i) => ({ i, x: q.x, y: q.y, draai: q.draai || 0 }));
  const opX = (a, b) => a.x - b.x;
  let volgorde;
  if (String(vorm).indexOf('wimpel') >= 0) {
    const omlaag = p.filter((q) => !q.draai).sort(opX);
    const omhoog = p.filter((q) => q.draai).sort((a, b) => b.x - a.x);
    volgorde = achter ? omhoog.concat(omlaag) : omlaag.concat(omhoog);
  } else {
    const minY = Math.min(...p.map((q) => q.y));
    const boven = p.filter((q) => q.y < minY + 3).sort(opX);
    const onder = p.filter((q) => q.y >= minY + 3).sort(opX);
    volgorde = achter ? onder.concat(boven) : boven.concat(onder);
  }
  const nr = [];
  volgorde.forEach((q, k) => { nr[q.i] = k + 1; });
  return nr;
}

/** Teksten van het vlaggetjes-paneel. */
function vlagTeksten() {
  const T = {
    nl: { aan: 'Elk vlaggetje anders: een naam of foto’s', naam: 'Naam over de vlaggetjes, 1 letter per vlaggetje', fotos: 'Foto’s kiezen', uitleg: ['Naam: elke letter komt op een eigen vlaggetje.','Tekst, symbolen, emoji en foto’s (via Uploaden) komen op het gekozen vlaggetje (paars omrand), of op alle vlaggetjes als je dat kiest.','Zo kun je elk vlaggetje een eigen lettertype, symbool of foto geven.'], van: 'van', nr: 'Vlaggetje', dubbel: 'Dubbelzijdig: de achterkant maken wij erbij. Van beide kanten zie je dezelfde slinger en lees je de naam goed.', lettertype: 'Lettertype van de naam', kleur: 'Kleur van de naam', komtOp: 'Wat je toevoegt komt op:', dit: 'Dit vlaggetje', alle: 'Alle vlaggetjes', naarAlle: 'Op alle vlaggetjes', alleenHier: 'Alleen op vlaggetje' },
    en: { aan: 'Every flag different: a name or photos', naam: 'Name across the flags, 1 letter per flag', fotos: 'Choose photos', uitleg: ['Name: each letter goes on its own flag.','Text, symbols, emoji and photos (via Upload) go on the chosen flag (purple outline), or on all flags if you choose that.','So every flag can have its own font, symbol or photo.'], van: 'of', nr: 'Flag', dubbel: 'Double-sided: we make the back for you. From both sides you see the same bunting and can read the name.', lettertype: 'Font of the name', kleur: 'Colour of the name', komtOp: 'What you add goes on:', dit: 'This flag', alle: 'All flags', naarAlle: 'On all flags', alleenHier: 'Only on flag' },
    de: { aan: 'Jedes Fähnchen anders: ein Name oder Fotos', naam: 'Name über die Fähnchen, 1 Buchstabe pro Fähnchen', fotos: 'Fotos wählen', uitleg: ['Name: jeder Buchstabe kommt auf ein eigenes Fähnchen.','Text, Symbole, Emoji und Fotos (über Hochladen) kommen auf das gewählte Fähnchen (lila umrandet) oder auf alle, wenn du das wählst.','So bekommt jedes Fähnchen seine eigene Schrift, sein Symbol oder Foto.'], van: 'von', nr: 'Fähnchen', dubbel: 'Doppelseitig: die Rückseite machen wir dazu. Von beiden Seiten siehst du dieselbe Kette und kannst den Namen lesen.', lettertype: 'Schrift des Namens', kleur: 'Farbe des Namens', komtOp: 'Was du hinzufügst, kommt auf:', dit: 'Dieses Fähnchen', alle: 'Alle Fähnchen', naarAlle: 'Auf alle Fähnchen', alleenHier: 'Nur auf Fähnchen' },
    fr: { aan: 'Chaque fanion différent : un prénom ou des photos', naam: 'Prénom sur les fanions, 1 lettre par fanion', fotos: 'Choisir des photos', uitleg: ['Prénom : chaque lettre va sur son propre fanion.','Texte, symboles, emoji et photos (via Importer) vont sur le fanion choisi (cadre violet), ou sur tous si vous le choisissez.','Chaque fanion peut ainsi avoir sa police, son symbole ou sa photo.'], van: 'sur', nr: 'Fanion', dubbel: 'Recto verso : nous créons le verso. Des deux côtés, vous voyez la même guirlande et le prénom se lit correctement.', lettertype: 'Police du prénom', kleur: 'Couleur du prénom', komtOp: 'Ce que vous ajoutez va sur :', dit: 'Ce fanion', alle: 'Tous les fanions', naarAlle: 'Sur tous les fanions', alleenHier: 'Seulement sur le fanion' },
    es: { aan: 'Cada banderín distinto: un nombre o fotos', naam: 'Nombre en los banderines, 1 letra por banderín', fotos: 'Elegir fotos', uitleg: ['Nombre: cada letra va en su propio banderín.','Texto, símbolos, emoji y fotos (con Subir) van en el banderín elegido (borde morado), o en todos si lo eliges.','Así cada banderín puede tener su fuente, símbolo o foto.'], van: 'de', nr: 'Banderín', dubbel: 'Doble cara: nosotros hacemos el reverso. Desde ambos lados ves la misma guirnalda y el nombre se lee bien.', lettertype: 'Fuente del nombre', kleur: 'Color del nombre', komtOp: 'Lo que añades va en:', dit: 'Este banderín', alle: 'Todos los banderines', naarAlle: 'En todos los banderines', alleenHier: 'Solo en el banderín' },
    it: { aan: 'Ogni bandierina diversa: un nome o foto', naam: 'Nome sulle bandierine, 1 lettera per bandierina', fotos: 'Scegli le foto', uitleg: ['Nome: ogni lettera va su una bandierina.','Testo, simboli, emoji e foto (con Carica) vanno sulla bandierina scelta (bordo viola), o su tutte se lo scegli.','Così ogni bandierina può avere il suo font, simbolo o foto.'], van: 'di', nr: 'Bandierina', dubbel: 'Fronte e retro: il retro lo facciamo noi. Da entrambi i lati vedi la stessa ghirlanda e il nome si legge bene.', lettertype: 'Font del nome', kleur: 'Colore del nome', komtOp: 'Quello che aggiungi va su:', dit: 'Questa bandierina', alle: 'Tutte le bandierine', naarAlle: 'Su tutte le bandierine', alleenHier: 'Solo sulla bandierina' },
  };
  const taal = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
  return T[taal] || T.nl;
}

function vlagCss() {
  if (document.getElementById('flagcfg-vlaggetjes-css')) return;
  const st = document.createElement('style');
  st.id = 'flagcfg-vlaggetjes-css';
  st.textContent =
    '.flagcfg-vlaggetjes{min-width:0;max-width:100%;overflow:hidden;box-sizing:border-box;display:grid;grid-template-columns:minmax(0,1fr);gap:.55rem;margin:0 0 .8rem;padding:.7rem .8rem;border:1px solid var(--color-border,#ddd);border-radius:10px;font-size:.85rem}' +
    '.flagcfg-vlaggetjes label.flagcfg-vlaggetjes__aan{display:flex;gap:.5rem;align-items:center;font-weight:600;cursor:pointer}' +
    '.flagcfg-vlaggetjes>*{min-width:0}' +
    '.flagcfg-vlaggetjes__strook{display:flex;gap:6px;overflow-x:auto;overscroll-behavior-x:contain;-webkit-overflow-scrolling:touch;touch-action:pan-x pan-y;padding:2px 2px 6px}' +
    '.flagcfg-vlaggetjes__strook button{flex:none;display:grid;justify-items:center;gap:2px;padding:3px;border:2px solid transparent;border-radius:6px;background:none;cursor:pointer;font:inherit;font-size:11px;color:inherit}' +
    '.flagcfg-vlaggetjes__strook button[aria-pressed=true]{border-color:#8b5cf6}' +
    '.flagcfg-vlaggetjes__strook canvas{width:38px;height:auto;display:block}' +
    '.flagcfg-vlaggetjes__rij{display:flex;gap:.5rem;flex-wrap:wrap;align-items:center}' +
    '.flagcfg-vlaggetjes__rij input[type=text]{flex:1 1 10rem;min-width:0;width:100%;padding:.55rem .6rem;font-size:16px;border:1px solid var(--color-border,#ccc);border-radius:6px;background:none;color:inherit;text-transform:none}' +
    '.flagcfg-vlaggetjes__fotos{padding:.5rem .8rem;border:1px solid var(--color-border,#ccc);border-radius:999px;background:none;font:inherit;font-size:.8rem;color:inherit;cursor:pointer}' +
    '.flagcfg-vlaggetjes__uitleg{margin:0;padding-left:1.1rem;font-size:.75rem;opacity:.8;display:grid;gap:.15rem}' +
    '.flagcfg-vlaggetjes__kop{font-size:.75rem;opacity:.75}' +
    '.flagcfg-vlaggetjes__font{flex:1 1 9rem;min-width:0;min-height:44px;padding:.4rem;font:inherit;font-size:.85rem;color:inherit;background:none;border:1px solid var(--color-border,#ccc);border-radius:6px}' +
    '.flagcfg-vlaggetjes__kleur{flex:none;width:48px;height:44px;padding:0;border:1px solid var(--color-border,#ccc);border-radius:6px;background:none;cursor:pointer}' +
    '.flagcfg-vlaggetjes__titel{margin:.2rem 0 .5rem}' +
    '.flagcfg-vlaggetjes__waar{display:flex;flex-wrap:wrap;align-items:center;gap:.4rem .6rem;font-size:.8rem}' +
    '.flagcfg-vlaggetjes__keuze{display:inline-flex;border:1px solid var(--color-border,#ccc);border-radius:999px;overflow:hidden}' +
    '.flagcfg-vlaggetjes__keuze button{padding:.4rem .8rem;border:0;background:none;font:inherit;font-size:.8rem;color:inherit;cursor:pointer}' +
    '.flagcfg-vlaggetjes__keuze button[aria-pressed=true]{background:#8b5cf6;color:#fff}' +
    '.flagcfg-vlaggetjes__rand{position:relative;display:flex;align-items:center;gap:4px;min-width:0;max-width:100%}' +
    '.flagcfg-vlaggetjes__rand .flagcfg-vlaggetjes__strook{flex:1;min-width:0;scroll-behavior:smooth}' +
    '.flagcfg-vlaggetjes__pijl{flex:none;width:34px;height:34px;display:grid;place-items:center;padding:0;border:1px solid var(--color-border,#ccc);border-radius:50%;background:#fff;color:#1f2430;cursor:pointer;box-shadow:0 1px 4px rgb(0 0 0/.15)}' +
    '.flagcfg-vlaggetjes__pijl[hidden]{display:none}' +
    '.flagcfg-vlaggetjes__pijl svg{width:18px;height:18px;fill:none;stroke:currentColor;stroke-width:2.4;stroke-linecap:round;stroke-linejoin:round}';
  document.head.appendChild(st);
}

function leesElement(tekening) {
  let t = elementTekeningen.get(tekening);
  if (t) return t;
  const stukken = String(tekening).split('|');
  const kop = stukken[0].split(';');
  const maat = kop[0].split(',').map(Number);
  const verlopen = {};
  for (let i = 1; i < kop.length; i++) {
    const is = kop[i].indexOf('=');
    const v = ELEMENT_VERLOOP.exec(kop[i].slice(is + 1));
    if (is > 0 && v) verlopen[kop[i].slice(0, is)] = { stops: v[1].split('>'), rond: v[2] === 'R', xy: v[3].split('_').map(Number) };
  }
  const delen = [];
  let kleurbaar = false;
  for (let i = 1; i < stukken.length; i++) {
    const dp = stukken[i].indexOf(':');
    const rol = stukken[i].slice(0, dp);
    const d = stukken[i].slice(dp + 1);
    if (rol === 'x') { delen.push({ knip: -1 }); continue; }
    if (rol === 'c') { delen.push({ knip: 1, d }); continue; }
    const m = ELEMENT_ROL.exec(rol);
    if (!m) continue;
    if (/^[kdzlmab]$/.test(m[1])) kleurbaar = true;
    delen.push({ kleur: m[1], o: m[2] ? Number(m[2]) / 10 : 1, e: !!m[3], s: m[4] ? Number(m[4]) : 0, d });
  }
  t = { w: maat[0] || 1, h: maat[1] || 1, delen, kleurbaar, verlopen };
  if (elementTekeningen.size > 400) elementTekeningen.clear();
  elementTekeningen.set(tekening, t);
  return t;
}

/** De kleurfamilie bij één gekozen kleur. Dezelfde som als in scratchpad/elementen/maak.js. */
const elementKleurFamilies = new Map();
function elementKleuren(k) {
  const hex = /^#[0-9a-fA-F]{6}$/.test(k || '') ? k.toLowerCase() : '#000000';
  let fam = elementKleurFamilies.get(hex);
  if (fam) return fam;
  const rgb = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const naarHex = (c) => '#' + c.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
  const meng = (o, t) => naarHex(rgb.map((v, i) => v + (o[i] - v) * t));
  const draai = (graden) => {
    const [r, g, b] = rgb.map((v) => v / 255);
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let tint = 0, s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      tint = (max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4) * 60;
    }
    tint = (((tint + graden) % 360) + 360) % 360;
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
    const kanaal = (t) => { t = (t + 1) % 1; return t < 1 / 6 ? p + (q - p) * 6 * t : t < 1 / 2 ? q : t < 2 / 3 ? p + (q - p) * (2 / 3 - t) * 6 : p; };
    const hh = tint / 360;
    return naarHex([kanaal(hh + 1 / 3), kanaal(hh), kanaal(hh - 1 / 3)].map((v) => v * 255));
  };
  fam = { k: hex, d: meng([0, 0, 0], 0.3), z: meng([0, 0, 0], 0.5), l: meng([255, 255, 255], 0.45), m: meng([255, 255, 255], 0.72), w: '#ffffff', a: draai(55), b: draai(-55) };
  elementKleurFamilies.set(hex, fam);
  return fam;
}

/** Tekent een element rond het nulpunt, in een vak van breed x hoog. */
function tekenElementPaden(ctx, t, kleur, breed, hoog) {
  const K = elementKleuren(kleur);
  ctx.save();
  ctx.scale(breed / t.w, hoog / t.h);
  ctx.translate(-t.w / 2, -t.h / 2);
  let diepte = 0;
  for (const p of t.delen) {
    if (p.knip === 1) {
      ctx.save();
      diepte += 1;
      ctx.clip(p.pad || (p.pad = new Path2D(p.d)));
      continue;
    }
    if (p.knip === -1) {
      if (diepte) { ctx.restore(); diepte -= 1; }
      continue;
    }
    ctx.globalAlpha = p.o;
    const v = p.kleur[0] === 'g' && t.verlopen[p.kleur];
    let stijl = p.kleur[0] === '#' ? p.kleur : K[p.kleur];
    if (v) {
      // Het verloop ligt in de maten van de tekening; de schaal staat al op ctx.
      stijl = v.rond ? ctx.createRadialGradient(v.xy[0], v.xy[1], 0, v.xy[0], v.xy[1], v.xy[2])
        : ctx.createLinearGradient(v.xy[0], v.xy[1], v.xy[2], v.xy[3]);
      v.stops.forEach((c, i) => stijl.addColorStop(i / (v.stops.length - 1), c));
    }
    const pad = p.pad || (p.pad = new Path2D(p.d));
    if (p.s) {
      ctx.strokeStyle = stijl || '#000000';
      ctx.lineWidth = p.s;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke(pad);
    } else {
      ctx.fillStyle = stijl || '#000000';
      ctx.fill(pad, p.e ? 'evenodd' : 'nonzero');
    }
  }
  while (diepte > 0) { ctx.restore(); diepte -= 1; }
  ctx.restore();
}

/** Hetzelfde element als SVG-tekst, voor de knopjes in de kiezer en de lagenlijst. */
function elementSvg(tekening, kleur) {
  const t = leesElement(tekening);
  const K = elementKleuren(kleur);
  let uit = '';
  let defs = '';
  let n = 0;
  Object.keys(t.verlopen).forEach((g) => {
    const v = t.verlopen[g];
    const stops = v.stops.map((c, i) => '<stop offset="' + i / (v.stops.length - 1) + '" stop-color="' + c + '"/>').join('');
    defs += v.rond
      ? '<radialGradient id="' + g + '" gradientUnits="userSpaceOnUse" cx="' + v.xy[0] + '" cy="' + v.xy[1] + '" r="' + v.xy[2] + '">' + stops + '</radialGradient>'
      : '<linearGradient id="' + g + '" gradientUnits="userSpaceOnUse" x1="' + v.xy[0] + '" y1="' + v.xy[1] + '" x2="' + v.xy[2] + '" y2="' + v.xy[3] + '">' + stops + '</linearGradient>';
  });
  t.delen.forEach((p) => {
    if (p.knip === 1) { n += 1; uit += '<clipPath id="k' + n + '"><path d="' + p.d + '"/></clipPath><g clip-path="url(#k' + n + ')">'; return; }
    if (p.knip === -1) { uit += '</g>'; return; }
    const kleur = p.kleur[0] === 'g' ? 'url(#' + p.kleur + ')' : p.kleur[0] === '#' ? p.kleur : K[p.kleur];
    uit += p.s
      ? '<path fill="none" stroke="' + kleur + '" stroke-width="' + p.s + '" stroke-linecap="round" stroke-linejoin="round"' + (p.o < 1 ? ' stroke-opacity="' + p.o + '"' : '') + ' d="' + p.d + '"/>'
      : '<path fill="' + kleur + '"' + (p.o < 1 ? ' fill-opacity="' + p.o + '"' : '') + (p.e ? ' fill-rule="evenodd"' : '') + ' d="' + p.d + '"/>';
  });
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + t.w + ' ' + t.h + '">' + (defs ? '<defs>' + defs + '</defs>' : '') + uit + '</svg>';
}

/**
 * Als plaatje en niet als SVG in de pagina: een categorie van dertig elementen
 * met elk tientallen paden zou anders duizenden knooppunten in de pagina zetten,
 * en dat merk je op een telefoon bij het scrollen door de kiezer.
 */
function elementBeeld(tekening, kleur) {
  const img = new Image();
  img.alt = '';
  img.decoding = 'async';
  img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(elementSvg(tekening, kleur));
  return img;
}

/* ---------- Achtergrond verwijderen -----------------------------------------
 * Jos (10–11 okt 2026): een achtergrondverwijderaar zoals die van Canva. Gratis,
 * zonder betaalde dienst, en zonder dat de ontwerptool gaat haperen.
 *
 * - Al het rekenwerk gebeurt in een eigen worker (achtergrondWerker), nooit op
 *   de hoofdthread. De klant kan intussen gewoon slepen, zoomen en scrollen.
 * - Logo's en tekeningen op een effen achtergrond: vullen vanaf de rand op
 *   kleur. Dat geeft scherpe randen, en witte letters ín een logo blijven staan.
 * - Foto's (persoon, huisdier, product): het AI-model U²-Netp (Apache 2.0,
 *   4,6 MB, staat als asset in het thema) via ONNX Runtime Web (MIT, jsDelivr).
 *   Het draait in de browser zelf: geen kosten per foto, en de foto gaat nergens
 *   heen. Grotere modellen (BiRefNet, 115–224 MB) zijn te zwaar voor een telefoon;
 *   RMBG mag niet commercieel en bij IS-Net is de licentie niet eenduidig.
 * - Randverbetering: een guided filter legt de rand van het masker op de randen
 *   in de foto, en een snelle voorgrondschatting (blur-fusion, Forte & Pitié
 *   2021) haalt de achtergrondkleur uit de randpixels, zodat er geen wit of
 *   groen randje overblijft.
 * - Niet-destructief: het origineel blijft bewaard en het masker staat apart. De
 *   uitsnede is origineel × masker. "Uitsnede verbeteren" schildert alleen in het
 *   masker (herstel- en verwijderpenseel); elke streek is een paar punten, geen
 *   kopie van het beeld, dus ongedaan maken kost niets.
 *
 * Gemeten op een laptop met 4 kernen en 4 GB, wat in de buurt komt van een
 * gewone telefoon: het model 2,8–3,0 s in WASM op één thread, de randverbetering
 * ±0,7 s. Op de hoofdthread kwam er geen taak langer dan 50 ms bij.
 */
const BG_ORT = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.22.0/dist/';
const BG_INVOER_MAAT = 1280; // maat die naar de worker gaat: logo's werken hierop, foto's op 1024

/**
 * De worker. Deze functie wordt als tekst in een eigen worker gestart (zie
 * bgWerker), dus hij mag niets van buiten gebruiken. Berichten:
 *   { id, w, h, px: RGBA, cfg: { ort, model, gpu } }
 * Antwoord: { id, methode: 'effen'|'ai', w, h, masker: RGBA, kleur: RGBA, ms }
 * of { id, reden: 'al'|'geen'|'fout' }.
 */
function achtergrondWerker() {
  let ort = null;
  let sessie = null;
  let laden = null;
  let rij = Promise.resolve();

  function laadModel(cfg) {
    if (sessie) return Promise.resolve(sessie);
    if (!laden) {
      laden = (async () => {
        ort = await import(cfg.ort + (cfg.gpu ? 'ort.webgpu.min.mjs' : 'ort.wasm.min.mjs'));
        ort.env.wasm.wasmPaths = cfg.ort;
        // Meer threads kan alleen met cross-origin isolation, en die heeft Shopify niet.
        ort.env.wasm.numThreads = 1;
        const r = await fetch(cfg.model);
        if (!r.ok) throw new Error('model ' + r.status);
        const buf = new Uint8Array(await r.arrayBuffer());
        try {
          sessie = await ort.InferenceSession.create(buf, { executionProviders: cfg.gpu ? ['webgpu', 'wasm'] : ['wasm'] });
        } catch (err) {
          if (!cfg.gpu) throw err;
          sessie = await ort.InferenceSession.create(buf, { executionProviders: ['wasm'] });
        }
        return sessie;
      })().catch((err) => {
        laden = null;
        throw err;
      });
    }
    return laden;
  }

  /** Naar een andere maat: verkleinen met gemiddelden (geen kartels), vergroten bilineair. */
  function maatRGBA(px, w, h, nw, nh) {
    const uit = new Uint8ClampedArray(nw * nh * 4);
    if (nw <= w && nh <= h) {
      for (let y = 0; y < nh; y++) {
        const y0 = Math.floor((y * h) / nh), y1 = Math.max(y0 + 1, Math.floor(((y + 1) * h) / nh));
        for (let x = 0; x < nw; x++) {
          const x0 = Math.floor((x * w) / nw), x1 = Math.max(x0 + 1, Math.floor(((x + 1) * w) / nw));
          let r = 0, g = 0, b = 0, a = 0;
          for (let yy = y0; yy < y1; yy++) {
            for (let xx = x0; xx < x1; xx++) {
              const i = (yy * w + xx) * 4;
              r += px[i];
              g += px[i + 1];
              b += px[i + 2];
              a += px[i + 3];
            }
          }
          const n = (y1 - y0) * (x1 - x0), o = (y * nw + x) * 4;
          uit[o] = r / n;
          uit[o + 1] = g / n;
          uit[o + 2] = b / n;
          uit[o + 3] = a / n;
        }
      }
      return uit;
    }
    for (let y = 0; y < nh; y++) {
      const sy = Math.min(h - 1, Math.max(0, ((y + 0.5) * h) / nh - 0.5));
      const y0 = Math.floor(sy), y1 = Math.min(h - 1, y0 + 1), fy = sy - y0;
      for (let x = 0; x < nw; x++) {
        const sx = Math.min(w - 1, Math.max(0, ((x + 0.5) * w) / nw - 0.5));
        const x0 = Math.floor(sx), x1 = Math.min(w - 1, x0 + 1), fx = sx - x0;
        for (let c = 0; c < 4; c++) {
          const a = px[(y0 * w + x0) * 4 + c], b = px[(y0 * w + x1) * 4 + c];
          const d = px[(y1 * w + x0) * 4 + c], e = px[(y1 * w + x1) * 4 + c];
          uit[(y * nw + x) * 4 + c] = (a * (1 - fx) + b * fx) * (1 - fy) + (d * (1 - fx) + e * fx) * fy;
        }
      }
    }
    return uit;
  }

  /** Een masker (0..1) bilineair vergroten. */
  function maatMasker(m, mw, mh, w, h) {
    const uit = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
      const sy = Math.min(mh - 1, Math.max(0, ((y + 0.5) * mh) / h - 0.5));
      const y0 = Math.floor(sy), y1 = Math.min(mh - 1, y0 + 1), fy = sy - y0;
      for (let x = 0; x < w; x++) {
        const sx = Math.min(mw - 1, Math.max(0, ((x + 0.5) * mw) / w - 0.5));
        const x0 = Math.floor(sx), x1 = Math.min(mw - 1, x0 + 1), fx = sx - x0;
        uit[y * w + x] = (m[y0 * mw + x0] * (1 - fx) + m[y0 * mw + x1] * fx) * (1 - fy) +
          (m[y1 * mw + x0] * (1 - fx) + m[y1 * mw + x1] * fx) * fy;
      }
    }
    return uit;
  }

  /** Kastfilter (gemiddelde over een vierkant van 2r+1) via een integraalbeeld; I wordt hergebruikt. */
  function kast(src, w, h, r, uit, I) {
    const W = w + 1;
    for (let x = 0; x <= w; x++) I[x] = 0;
    for (let y = 0; y < h; y++) {
      let rijsom = 0;
      I[(y + 1) * W] = 0;
      for (let x = 0; x < w; x++) {
        rijsom += src[y * w + x];
        I[(y + 1) * W + x + 1] = I[y * W + x + 1] + rijsom;
      }
    }
    for (let y = 0; y < h; y++) {
      const y0 = Math.max(0, y - r), y1 = Math.min(h - 1, y + r);
      for (let x = 0; x < w; x++) {
        const x0 = Math.max(0, x - r), x1 = Math.min(w - 1, x + r);
        const s = I[(y1 + 1) * W + x1 + 1] - I[y0 * W + x1 + 1] - I[(y1 + 1) * W + x0] + I[y0 * W + x0];
        uit[y * w + x] = s / ((y1 - y0 + 1) * (x1 - x0 + 1));
      }
    }
    return uit;
  }

  /** Guided filter (He e.a.) met de grijswaarde als gids: legt de maskerrand op de randen in de foto. */
  function geleid(I, p, w, h, r, eps, buf) {
    const n = w * h;
    const t1 = new Float32Array(n), t2 = new Float32Array(n);
    const mI = kast(I, w, h, r, new Float32Array(n), buf);
    const mp = kast(p, w, h, r, new Float32Array(n), buf);
    for (let i = 0; i < n; i++) t1[i] = I[i] * p[i];
    const mIp = kast(t1, w, h, r, new Float32Array(n), buf);
    for (let i = 0; i < n; i++) t1[i] = I[i] * I[i];
    const mII = kast(t1, w, h, r, t2, buf);
    // a en b hergebruiken de geheugenruimte van mIp en mII.
    for (let i = 0; i < n; i++) {
      const a = (mIp[i] - mI[i] * mp[i]) / (mII[i] - mI[i] * mI[i] + eps);
      mIp[i] = a;
      mII[i] = mp[i] - a * mI[i];
    }
    const ma = kast(mIp, w, h, r, mI, buf);
    const mb = kast(mII, w, h, r, mp, buf);
    const q = t1;
    for (let i = 0; i < n; i++) q[i] = Math.max(0, Math.min(1, ma[i] * I[i] + mb[i]));
    return q;
  }

  /**
   * Voorgrondkleur in de randpixels (blur-fusion, Forte & Pitié 2021): een
   * randpixel is een mengsel van voorgrond en achtergrond; dit schat de
   * voorgrondkleur, zodat er geen achtergrondkleur in de rand achterblijft.
   * Geeft een RGBA-laag die alleen in de rand (0 < alfa < 1) iets bevat.
   */
  function voorgrond(px, al, w, h) {
    const n = w * h;
    const buf = new Float64Array((w + 1) * (h + 1));
    const rgb = [new Float32Array(n), new Float32Array(n), new Float32Array(n)];
    for (let i = 0; i < n; i++) for (let c = 0; c < 3; c++) rgb[c][i] = px[i * 4 + c] / 255;
    const inv = new Float32Array(n);
    for (let i = 0; i < n; i++) inv[i] = 1 - al[i];
    const t = new Float32Array(n), bFA = new Float32Array(n), bB1A = new Float32Array(n);
    const schat = (F, B, r) => {
      const bA = kast(al, w, h, r, new Float32Array(n), buf);
      const nF = [], nB = [];
      for (let c = 0; c < 3; c++) {
        for (let i = 0; i < n; i++) t[i] = F[c][i] * al[i];
        kast(t, w, h, r, bFA, buf);
        for (let i = 0; i < n; i++) t[i] = B[c][i] * inv[i];
        kast(t, w, h, r, bB1A, buf);
        const f = new Float32Array(n), b = new Float32Array(n);
        for (let i = 0; i < n; i++) {
          const fF = bFA[i] / (bA[i] + 1e-5), fB = bB1A[i] / (1 - bA[i] + 1e-5);
          b[i] = fB;
          f[i] = Math.max(0, Math.min(1, fF + al[i] * (rgb[c][i] - al[i] * fF - (1 - al[i]) * fB)));
        }
        nF.push(f);
        nB.push(b);
      }
      return [nF, nB];
    };
    const [F1, B1] = schat(rgb, rgb, Math.max(8, Math.round(Math.max(w, h) / 12)));
    const F = schat(F1, B1, 3)[0];
    const uit = new Uint8ClampedArray(n * 4);
    for (let i = 0; i < n; i++) {
      if (al[i] <= 0.004 || al[i] >= 0.996) continue;
      uit[i * 4] = F[0][i] * 255;
      uit[i * 4 + 1] = F[1][i] * 255;
      uit[i * 4 + 2] = F[2][i] * 255;
      uit[i * 4 + 3] = 255;
    }
    return uit;
  }

  /** De buitenste rij en kolom. */
  function randpunten(w, h) {
    const rand = [];
    for (let x = 0; x < w; x++) rand.push(x, (h - 1) * w + x);
    for (let y = 1; y < h - 1; y++) rand.push(y * w, y * w + w - 1);
    return rand;
  }

  /**
   * Is dit een logo of tekening (vlakke kleuren, weinig tinten) en geen foto?
   * Alleen dan is weghalen op kleur beter dan de AI. Bij een foto tegen een witte
   * muur zou weghalen op kleur ook een wit overhemd aanvreten.
   */
  function isTekening(px, w, h) {
    // De rand moet vrijwel één kleur zijn. Een productfoto op een grijs verloop
    // heeft ook weinig tinten, maar daar laat weghalen op kleur stukken staan.
    const rand = randpunten(w, h);
    const randBak = new Map();
    const bakVan = (i) => ((px[i * 4] >> 4) << 8) | ((px[i * 4 + 1] >> 4) << 4) | (px[i * 4 + 2] >> 4);
    for (const i of rand) if (px[i * 4 + 3] >= 16) randBak.set(bakVan(i), (randBak.get(bakVan(i)) || 0) + 1);
    let hoofd = -1, meest = 0;
    randBak.forEach((aantal, b) => {
      if (aantal > meest) {
        meest = aantal;
        hoofd = b;
      }
    });
    if (hoofd < 0) return false;
    let rr = 0, rg = 0, rb = 0, rn = 0;
    for (const i of rand) {
      if (px[i * 4 + 3] >= 16 && bakVan(i) === hoofd) {
        rr += px[i * 4];
        rg += px[i * 4 + 1];
        rb += px[i * 4 + 2];
        rn++;
      }
    }
    rr /= rn;
    rg /= rn;
    rb /= rn;
    let zelfde = 0;
    for (const i of rand) {
      if (Math.max(Math.abs(px[i * 4] - rr), Math.abs(px[i * 4 + 1] - rg), Math.abs(px[i * 4 + 2] - rb)) <= 10) zelfde++;
    }
    if (zelfde / rand.length < 0.9) return false;

    const stap = Math.max(1, Math.floor(Math.max(w, h) / 240));
    const bakjes = new Map();
    let n = 0, vlak = 0;
    for (let y = 0; y < h - 1; y += stap) {
      for (let x = 0; x < w - 1; x += stap) {
        const i = (y * w + x) * 4;
        if (px[i + 3] < 16) continue;
        n++;
        const j = i + 4, k = i + w * 4;
        const d = Math.max(Math.abs(px[i] - px[j]), Math.abs(px[i + 1] - px[j + 1]), Math.abs(px[i + 2] - px[j + 2]),
          Math.abs(px[i] - px[k]), Math.abs(px[i + 1] - px[k + 1]), Math.abs(px[i + 2] - px[k + 2]));
        if (d <= 6) vlak++;
        const b = ((px[i] >> 4) << 8) | ((px[i + 1] >> 4) << 4) | (px[i + 2] >> 4);
        bakjes.set(b, (bakjes.get(b) || 0) + 1);
      }
    }
    if (!n) return false;
    const tellingen = [...bakjes.values()].sort((a, b) => b - a);
    let som = 0, k = 0;
    while (k < tellingen.length && som < 0.95 * n) som += tellingen[k++];
    return vlak / n >= 0.8 && k <= 48;
  }

  /**
   * Weghalen op kleur: de kleur die aan de rand het meest voorkomt, en alles in
   * die kleur dat met de rand verbonden is. Wat binnen het ontwerp ligt blijft
   * staan, ook als het dezelfde kleur heeft. Geeft het masker (0..1) of null.
   */
  function effen(px, w, h) {
    const n = w * h, rand = randpunten(w, h);
    const bakjes = new Uint32Array(4096);
    const bak = (i) => ((px[i * 4] >> 4) << 8) | ((px[i * 4 + 1] >> 4) << 4) | (px[i * 4 + 2] >> 4);
    for (const i of rand) if (px[i * 4 + 3] >= 16) bakjes[bak(i)]++;
    let beste = 0;
    for (let b = 1; b < 4096; b++) if (bakjes[b] > bakjes[beste]) beste = b;
    let sr = 0, sg = 0, sb = 0, sn = 0;
    for (const i of rand) {
      if (px[i * 4 + 3] >= 16 && bak(i) === beste) {
        sr += px[i * 4];
        sg += px[i * 4 + 1];
        sb += px[i * 4 + 2];
        sn++;
      }
    }
    if (!sn) return null;
    const br = sr / sn, bg = sg / sn, bb = sb / sn;
    // Afstand tot de achtergrondkleur, gewogen naar hoe het oog verschil ziet.
    const afstand = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      if (px[i * 4 + 3] < 16) continue;
      const dr = px[i * 4] - br, dg = px[i * 4 + 1] - bg, db = px[i * 4 + 2] - bb;
      afstand[i] = Math.min(255, Math.sqrt((2 * dr * dr + 4 * dg * dg + 3 * db * db) / 9));
    }
    const HARD = 26, ZACHT = 64;
    let gelijk = 0;
    for (const i of rand) if (afstand[i] < HARD + 14) gelijk++;
    if (gelijk / rand.length < 0.55) return null;
    // Vullen vanaf de rand, regel voor regel.
    const weg = new Uint8Array(n);
    const vul = (start) => {
      const stapel = [start];
      while (stapel.length) {
        const p = stapel.pop();
        if (weg[p] || afstand[p] >= HARD) continue;
        const y = (p / w) | 0;
        let x0 = p - y * w;
        while (x0 > 0 && !weg[y * w + x0 - 1] && afstand[y * w + x0 - 1] < HARD) x0--;
        let boven = false, onder = false;
        for (let x = x0; x < w; x++) {
          const q = y * w + x;
          if (weg[q] || afstand[q] >= HARD) break;
          weg[q] = 1;
          if (y > 0) {
            const ok = !weg[q - w] && afstand[q - w] < HARD;
            if (ok && !boven) stapel.push(q - w);
            boven = ok;
          }
          if (y < h - 1) {
            const ok = !weg[q + w] && afstand[q + w] < HARD;
            if (ok && !onder) stapel.push(q + w);
            onder = ok;
          }
        }
      }
    };
    for (const i of rand) if (!weg[i] && afstand[i] < HARD) vul(i);
    // Twee ringen rond de weggehaalde achtergrond worden half doorzichtig.
    const ring = new Uint8Array(n);
    for (let r = 1; r <= 2; r++) {
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = y * w + x;
          if (weg[i] || ring[i] || afstand[i] >= ZACHT) continue;
          let buur = false;
          for (let dy = -1; dy <= 1 && !buur; dy++) {
            const yy = y + dy;
            if (yy < 0 || yy >= h) continue;
            for (let dx = -1; dx <= 1; dx++) {
              const xx = x + dx;
              if (xx < 0 || xx >= w) continue;
              const j = yy * w + xx;
              if (weg[j] || (r === 2 && ring[j] === 1)) {
                buur = true;
                break;
              }
            }
          }
          if (buur) ring[i] = r;
        }
      }
    }
    const al = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      if (weg[i]) continue;
      if (!ring[i]) {
        al[i] = 1;
        continue;
      }
      const a = Math.min(1, Math.max(0, (afstand[i] - HARD) / (ZACHT - HARD)));
      al[i] = a <= 0.02 ? 0 : a;
    }
    return al;
  }

  /** Het AI-masker: model op 320 x 320, daarna vergroot en op de foto gelegd. */
  async function ai(cfg, px, w, h) {
    const s = await laadModel(cfg);
    const M = 320, n = M * M;
    const k = maatRGBA(px, w, h, M, M);
    // Zoals rembg: eerst delen door de hoogste waarde, dan de ImageNet-normalisatie.
    let max = 0;
    for (let i = 0; i < n; i++) for (let c = 0; c < 3; c++) max = Math.max(max, k[i * 4 + c]);
    max = max / 255 || 1;
    const gem = [0.485, 0.456, 0.406], sd = [0.229, 0.224, 0.225];
    const chw = new Float32Array(3 * n);
    for (let i = 0; i < n; i++) for (let c = 0; c < 3; c++) chw[c * n + i] = (k[i * 4 + c] / 255 / max - gem[c]) / sd[c];
    const uit = await s.run({ [s.inputNames[0]]: new ort.Tensor('float32', chw, [1, 3, M, M]) });
    const ruw = uit[s.outputNames[0]].data;
    let mn = Infinity, mx = -Infinity;
    for (let i = 0; i < n; i++) {
      if (ruw[i] < mn) mn = ruw[i];
      if (ruw[i] > mx) mx = ruw[i];
    }
    const m = new Float32Array(n);
    for (let i = 0; i < n; i++) m[i] = (ruw[i] - mn) / (mx - mn || 1);
    Object.values(uit).forEach((x) => x.dispose && x.dispose());
    // Geen duidelijk onderwerp: het model twijfelt overal even hard.
    if (mx - mn < 0.15) return null;
    const p = maatMasker(m, M, M, w, h);
    const grijs = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) grijs[i] = (0.299 * px[i * 4] + 0.587 * px[i * 4 + 1] + 0.114 * px[i * 4 + 2]) / 255;
    const r = Math.max(2, Math.round(Math.max(w, h) / 160));
    const q = geleid(grijs, p, w, h, r, 1e-3, new Float64Array((w + 1) * (h + 1)));
    // Kleine restwaarden weg en de kern helemaal dicht.
    for (let i = 0; i < q.length; i++) q[i] = Math.max(0, Math.min(1, (q[i] - 0.08) / 0.84));
    return q;
  }

  async function knip(b) {
    const { w, h, px, cfg } = b;
    const rand = randpunten(w, h);
    let doorzichtig = 0;
    for (const i of rand) if (px[i * 4 + 3] < 16) doorzichtig++;
    if (doorzichtig / rand.length > 0.5) return { reden: 'al' };

    let al = null, methode = '', ww = w, hh = h, bron = px;
    if (isTekening(px, w, h)) {
      al = effen(px, w, h);
      if (al) methode = 'effen';
    }
    if (!al) {
      const s = Math.min(1, BG_AI_MAAT / Math.max(w, h));
      if (s < 1) {
        ww = Math.max(1, Math.round(w * s));
        hh = Math.max(1, Math.round(h * s));
        bron = maatRGBA(px, w, h, ww, hh);
      }
      // Doorzichtige delen als wit: hun kleur is willekeurig (vaak zwart).
      for (let i = 0; i < ww * hh; i++) {
        const a = bron[i * 4 + 3] / 255;
        if (a < 1) for (let c = 0; c < 3; c++) bron[i * 4 + c] = bron[i * 4 + c] * a + 255 * (1 - a);
      }
      al = await ai(cfg, bron, ww, hh);
      if (!al) return { reden: 'geen' };
      methode = 'ai';
    }
    // Bijna niets over, of juist alles: dan is er geen onderwerp om vrij te zetten.
    let dicht = 0;
    for (let i = 0; i < al.length; i++) if (al[i] > 0.5) dicht++;
    if (dicht < al.length * 0.005 || dicht > al.length * 0.99) return { reden: 'geen' };

    const kleur = voorgrond(bron, al, ww, hh);
    const masker = new Uint8ClampedArray(ww * hh * 4);
    for (let i = 0; i < al.length; i++) masker[i * 4 + 3] = al[i] * 255 + 0.5;
    return { methode, w: ww, h: hh, masker, kleur };
  }

  // De worker ziet de buitenwereld niet; daarom staat de werkmaat hier.
  const BG_AI_MAAT = 1024;

  self.onmessage = (e) => {
    const b = e.data;
    // Eén opdracht tegelijk; het model kan niet twee keer tegelijk draaien.
    rij = rij.then(async () => {
      try {
        const t0 = performance.now();
        const uit = await knip(b);
        uit.id = b.id;
        uit.ms = Math.round(performance.now() - t0);
        self.postMessage(uit, uit.masker ? [uit.masker.buffer, uit.kleur.buffer] : []);
      } catch (err) {
        self.postMessage({ id: b.id, reden: 'fout', fout: String((err && err.message) || err) });
      }
    });
  };
}

const bgWerk = { werker: null, teller: 0, wacht: new Map(), klok: 0 };

/** Start de worker (eenmalig); het model laadt pas bij de eerste opdracht. */
function bgWerker() {
  clearTimeout(bgWerk.klok);
  if (bgWerk.werker) return bgWerk.werker;
  const url = URL.createObjectURL(new Blob(['(' + achtergrondWerker.toString() + ')()'], { type: 'text/javascript' }));
  const w = new Worker(url, { type: 'module' });
  w.onmessage = (e) => {
    const klaar = bgWerk.wacht.get(e.data.id);
    if (klaar) {
      bgWerk.wacht.delete(e.data.id);
      klaar(e.data);
    }
    bgSlaap();
  };
  w.onerror = () => bgStop({ reden: 'fout' });
  bgWerk.werker = w;
  return w;
}

function bgStop(antwoord) {
  bgWerk.wacht.forEach((klaar) => klaar(antwoord));
  bgWerk.wacht.clear();
  if (bgWerk.werker) bgWerk.werker.terminate();
  bgWerk.werker = null;
}

/** Drie minuten niets te doen: worker met model en werkgeheugen opruimen. */
function bgSlaap() {
  clearTimeout(bgWerk.klok);
  if (bgWerk.wacht.size) return;
  bgWerk.klok = setTimeout(() => {
    if (!bgWerk.wacht.size) bgStop(null);
  }, 180000);
}

function bgVraag(bericht, overdracht) {
  return new Promise((klaar) => {
    const id = ++bgWerk.teller;
    bgWerk.wacht.set(id, klaar);
    try {
      bgWerker().postMessage(Object.assign({ id: id }, bericht), overdracht || []);
    } catch (err) {
      bgWerk.wacht.delete(id);
      klaar({ reden: 'fout' });
    }
  });
}

function bgDoek(w, h) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

function bgVrij(c) {
  if (c && c.getContext) {
    c.width = 0;
    c.height = 0;
  }
}

/** De pixels van een beeld op werkmaat (kort werk: tekenen en uitlezen). */
function bgInvoer(bron, maxZijde) {
  const bw = bron.naturalWidth || bron.width, bh = bron.naturalHeight || bron.height;
  const s = Math.min(1, maxZijde / Math.max(bw, bh));
  const w = Math.max(1, Math.round(bw * s)), h = Math.max(1, Math.round(bh * s));
  const c = bgDoek(w, h);
  const k = c.getContext('2d', { willReadFrequently: true });
  k.imageSmoothingQuality = 'high';
  k.drawImage(bron, 0, 0, w, h);
  const px = k.getImageData(0, 0, w, h).data;
  bgVrij(c);
  return { w: w, h: h, px: px };
}

function bgUitPixels(w, h, data) {
  const c = bgDoek(w, h);
  c.getContext('2d').putImageData(new ImageData(data, w, h), 0, 0);
  return c;
}

/**
 * De uitsnede op volle maat: origineel, daarover de voorgrondkleur in de rand,
 * en dan alleen houden wat het masker zegt. Alles met drawImage (snel, op de
 * GPU). Net als vroeger maximaal 8 miljoen beeldpunten: een iPhone kan niet veel
 * meer canvasgeheugen aan.
 */
function bgSamenstellen(orig, u) {
  const bw = orig.naturalWidth || orig.width, bh = orig.naturalHeight || orig.height;
  const s = Math.min(1, Math.sqrt(8e6 / (bw * bh)));
  const w = Math.max(1, Math.round(bw * s)), h = Math.max(1, Math.round(bh * s));
  const c = bgDoek(w, h);
  const k = c.getContext('2d');
  k.imageSmoothingEnabled = true;
  k.imageSmoothingQuality = 'high';
  k.drawImage(orig, 0, 0, w, h);
  k.drawImage(u.kleur, 0, 0, w, h);
  k.globalCompositeOperation = 'destination-in';
  k.drawImage(u.masker, 0, 0, w, h);
  k.globalCompositeOperation = 'source-over';
  c.naturalWidth = w;
  c.naturalHeight = h;
  return c;
}

/** Eigen werkkopieën van masker en randkleur, zodat de basis van de AI onaangetast blijft. */
function bgWerkkopie(u) {
  if (u.masker === u.basis) {
    u.masker = bgDoek(u.w, u.h);
    u.masker.getContext('2d').drawImage(u.basis, 0, 0);
  }
  if (u.kleur === u.basisKleur) {
    u.kleur = bgDoek(u.w, u.h);
    u.kleur.getContext('2d').drawImage(u.basisKleur, 0, 0);
  }
}

/**
 * Een penseelstreek in het masker. Herstellen: masker dicht en de randkleur weg
 * (dan komen de echte pixels van het origineel terug). Verwijderen: masker open.
 * `vanaf` is het punt waar verder getekend wordt (0 = de hele streek).
 */
function bgStreek(u, st, vanaf) {
  const p = st.p;
  const lagen = st.m === 'h' ? [[u.masker, 'source-over'], [u.kleur, 'destination-out']] : [[u.masker, 'destination-out']];
  lagen.forEach(([doek, op]) => {
    const k = doek.getContext('2d');
    k.save();
    k.globalCompositeOperation = op;
    k.fillStyle = '#000';
    k.strokeStyle = '#000';
    k.lineWidth = st.r * 2;
    k.lineCap = 'round';
    k.lineJoin = 'round';
    k.beginPath();
    if (p.length <= 2) {
      k.arc(p[0], p[1], st.r, 0, Math.PI * 2);
      k.fill();
    } else {
      const start = Math.max(0, vanaf - 2);
      k.moveTo(p[start], p[start + 1]);
      for (let i = start + 2; i < p.length; i += 2) k.lineTo(p[i], p[i + 1]);
      k.stroke();
    }
    k.restore();
  });
}

/** Masker opnieuw opbouwen: de basis van de AI plus alle streken (na ongedaan maken). */
function bgHerbouw(u) {
  if (!u.streken.length) {
    if (u.masker !== u.basis) bgVrij(u.masker);
    if (u.kleur !== u.basisKleur) bgVrij(u.kleur);
    u.masker = u.basis;
    u.kleur = u.basisKleur;
    return;
  }
  bgWerkkopie(u);
  [[u.masker, u.basis], [u.kleur, u.basisKleur]].forEach(([doek, basis]) => {
    const k = doek.getContext('2d');
    k.globalCompositeOperation = 'copy';
    k.drawImage(basis, 0, 0);
    k.globalCompositeOperation = 'source-over';
  });
  u.streken.forEach((st) => bgStreek(u, st, 0));
}

/** Masker en randkleur als PNG, voor het bewaren van het ontwerp (één keer per uitsnede). */
function bgBlobs(u) {
  if (!u.blobs) {
    const naarBlob = (c) => new Promise((klaar) => {
      try {
        c.toBlob((b) => klaar(b), 'image/png');
      } catch (err) {
        klaar(null);
      }
    });
    u.blobs = Promise.all([naarBlob(u.basis), naarBlob(u.basisKleur)]);
  }
  return u.blobs;
}

/** Een bewaarde uitsnede terugzetten, zonder de AI opnieuw te laten rekenen. */
async function bgUitOpslag(z) {
  if (!z || !z.basis || !z.kleur) return null;
  try {
    const [a, b] = await Promise.all([createImageBitmap(z.basis), createImageBitmap(z.kleur)]);
    const u = { methode: z.methode || 'ai', w: a.width, h: a.height, streken: Array.isArray(z.streken) ? z.streken : [], terug: [], versie: 1 };
    u.basis = bgDoek(u.w, u.h);
    u.basis.getContext('2d').drawImage(a, 0, 0);
    u.basisKleur = bgDoek(u.w, u.h);
    u.basisKleur.getContext('2d').drawImage(b, 0, 0, u.w, u.h);
    a.close && a.close();
    b.close && b.close();
    u.masker = u.basis;
    u.kleur = u.basisKleur;
    u.blobs = Promise.resolve([z.basis, z.kleur]);
    bgHerbouw(u);
    return u;
  } catch (err) {
    return null;
  }
}

/** Korte sleutel voor de vingerafdruk: verandert zodra het masker verandert. */
function bgSleutel(u) {
  if (!u) return 0;
  const t = JSON.stringify(u.streken);
  let h = 2166136261;
  for (let i = 0; i < t.length; i++) h = Math.imul(h ^ t.charCodeAt(i), 16777619) >>> 0;
  return u.methode + u.streken.length + ':' + h.toString(36);
}

function bgVrijUitsnede(u) {
  if (!u) return;
  [u.masker, u.kleur, u.basis, u.basisKleur].forEach(bgVrij);
}

/** Toverstaf en draaiend rondje voor de knop "Achtergrond verwijderen". */
const BG_STAF = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 20 15 9"/><path d="M13 7l4 4"/>' +
  '<path d="M18 2v3M16.5 3.5h3M21 8v2M20 9h2M9 3v2M8 4h2"/></svg>';
const BG_DRAAIER = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><circle cx="12" cy="12" r="9" fill="none" ' +
  'stroke="currentColor" stroke-width="3" stroke-opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" ' +
  'stroke-width="3" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" ' +
  'dur=".8s" repeatCount="indefinite"/></path></svg>';

/** Bijsnijd-ikoon voor de knop "Bijsnijden". */
const BG_BIJSNIJD = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 2v14a2 2 0 0 0 2 2h14"/><path d="M18 22V8a2 2 0 0 0-2-2H2"/></svg>';

/** Eigen kopie van een uitsnede (als de andere zijde dezelfde deelt en deze wordt bijgewerkt). */
function bgKopie(u) {
  const kopie = (c) => {
    const d = bgDoek(u.w, u.h);
    d.getContext('2d').drawImage(c, 0, 0);
    return d;
  };
  const n = { methode: u.methode, w: u.w, h: u.h, basis: kopie(u.basis), basisKleur: kopie(u.basisKleur), streken: u.streken.slice(), terug: [], blobs: u.blobs };
  n.masker = n.basis;
  n.kleur = n.basisKleur;
  bgHerbouw(n);
  return n;
}

/* ---------- Venster "Uitsnede verbeteren" ------------------------------------
 * Herstel- en verwijderpenseel op het masker. Eén vinger of de muis schildert,
 * twee vingers zoomen en schuiven (een streek die net begon wordt dan
 * teruggedraaid, zodat knijpen geen vlek achterlaat). Het scherm toont het
 * voorbeeld op werkmaat; per streek wordt alleen het stukje rond de penseel
 * opnieuw samengesteld.
 */
const UITSNEDE_STIJL = `
.ojv-uitsnede{position:fixed;inset:0;z-index:2147483647;display:flex;flex-direction:column;background:#1d1f24;color:#fff;font-family:inherit;touch-action:manipulation;-webkit-user-select:none;user-select:none}
.ojv-uitsnede *{box-sizing:border-box}
.ojv-uitsnede button{font:inherit;color:inherit;cursor:pointer}
.ojv-uitsnede__kop{display:flex;align-items:center;gap:.75rem;padding:.6rem max(.75rem,env(safe-area-inset-left)) .6rem .9rem;padding-top:max(.6rem,env(safe-area-inset-top))}
.ojv-uitsnede__titel{flex:1;font-weight:700;font-size:.95rem}
.ojv-uitsnede__klaar{background:#fff;color:#111!important;border:0;border-radius:999px;padding:.5rem 1.1rem;font-weight:700;font-size:.9rem}
.ojv-uitsnede__vlak{position:relative;flex:1;min-height:0;overflow:hidden;touch-action:none;background-color:#fff;background-image:linear-gradient(45deg,#d9d9d9 25%,transparent 25%,transparent 75%,#d9d9d9 75%),linear-gradient(45deg,#d9d9d9 25%,transparent 25%,transparent 75%,#d9d9d9 75%);background-size:20px 20px;background-position:0 0,10px 10px}
.ojv-uitsnede__vlak canvas{position:absolute;inset:0;width:100%;height:100%;display:block;touch-action:none;cursor:none}
.ojv-uitsnede__uitleg{position:absolute;left:50%;bottom:.6rem;transform:translateX(-50%);max-width:calc(100% - 1.5rem);background:rgba(17,17,17,.78);color:#fff;font-size:.75rem;line-height:1.35;padding:.4rem .7rem;border-radius:8px;text-align:center;pointer-events:none;transition:opacity .4s}
.ojv-uitsnede__balk{display:grid;gap:.55rem;padding:.7rem .9rem;padding-bottom:max(.7rem,env(safe-area-inset-bottom));background:#1d1f24}
.ojv-uitsnede__modus{display:grid;grid-template-columns:1fr 1fr;gap:.4rem}
.ojv-uitsnede__modus button{display:flex;align-items:center;justify-content:center;gap:.4rem;min-height:42px;border:1px solid rgba(255,255,255,.25);background:transparent;border-radius:10px;font-size:.85rem}
.ojv-uitsnede__modus button[aria-pressed=true]{background:#fff;color:#111}
.ojv-uitsnede__rij{display:flex;align-items:center;gap:.5rem;min-width:0}
.ojv-uitsnede__maat{display:flex;align-items:center;gap:.5rem;flex:1;min-width:0;font-size:.78rem;opacity:.9}
.ojv-uitsnede__maat input{flex:1;min-width:0;accent-color:#fff}
.ojv-uitsnede__ikoon{display:grid;place-items:center;width:42px;height:42px;flex:none;border:1px solid rgba(255,255,255,.25);background:transparent;border-radius:10px}
.ojv-uitsnede__ikoon:disabled{opacity:.35;cursor:default}
.ojv-uitsnede__ikoon[aria-pressed=true]{background:#fff;color:#111}
.ojv-uitsnede__links{display:flex;flex-wrap:wrap;justify-content:space-between;gap:.3rem 1rem}
.ojv-uitsnede__links button{background:none;border:0;padding:.2rem 0;font-size:.78rem;text-decoration:underline;opacity:.8}
.ojv-uitsnede__links button:disabled{opacity:.35;text-decoration:none;cursor:default}
@media (min-width:750px){.ojv-uitsnede__balk{grid-template-columns:minmax(0,22rem) minmax(0,1fr);align-items:center;column-gap:1.25rem}.ojv-uitsnede__links{grid-column:1/-1}}
`;

const UITSNEDE_IKONEN = {
  herstel: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 21c3 0 5-1.5 5-4a2.5 2.5 0 1 0-5 0"/><path d="m8 17 9.5-9.5a2.1 2.1 0 0 0-3-3L5 14"/><path d="M19 15v6M16 18h6"/></svg>',
  wis: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m7 21-4.3-4.3a1 1 0 0 1 0-1.4l9.6-9.6a1 1 0 0 1 1.4 0l5.6 5.6a1 1 0 0 1 0 1.4L13 19"/><path d="M22 21H7"/><path d="m5 11 9 9"/></svg>',
  terug: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 14 4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11"/></svg>',
  vooruit: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 14 5-5-5-5"/><path d="M20 9H9.5a5.5 5.5 0 0 0 0 11H13"/></svg>',
  vergelijk: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg>',
};

class UitsnedeVenster {
  /**
   * opties: { orig, u, t (teksten), bijKlaar(gewijzigd), bijOrigineel() }
   */
  constructor(opties) {
    this.o = opties;
    this.u = opties.u;
    this.t = opties.t;
    this.modus = 'w';
    this.straal = 22;
    this.zoom = 1;
    this.tx = 0;
    this.ty = 0;
    this.wijzers = new Map();
    this.streek = null;
    this.knijp = null;
    this.vergelijk = false;
    this.cursor = null;
    this.beginSleutel = bgSleutel(this.u);
    this.bouw();
  }

  bouw() {
    if (!document.getElementById('ojv-uitsnede-stijl')) {
      const st = document.createElement('style');
      st.id = 'ojv-uitsnede-stijl';
      st.textContent = UITSNEDE_STIJL;
      document.head.appendChild(st);
    }
    const t = this.t;
    const el = document.createElement('div');
    el.className = 'ojv-uitsnede';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', t.verbeter);
    el.innerHTML =
      '<div class="ojv-uitsnede__kop"><span class="ojv-uitsnede__titel"></span><button type="button" class="ojv-uitsnede__klaar"></button></div>' +
      '<div class="ojv-uitsnede__vlak"><canvas></canvas><p class="ojv-uitsnede__uitleg"></p></div>' +
      '<div class="ojv-uitsnede__balk">' +
      '<div class="ojv-uitsnede__modus" role="group"><button type="button" data-modus="h">' + UITSNEDE_IKONEN.herstel + '<span></span></button>' +
      '<button type="button" data-modus="w">' + UITSNEDE_IKONEN.wis + '<span></span></button></div>' +
      '<div class="ojv-uitsnede__rij"><label class="ojv-uitsnede__maat"><span></span><input type="range" min="4" max="90" step="1"></label>' +
      '<button type="button" class="ojv-uitsnede__ikoon" data-act="terug">' + UITSNEDE_IKONEN.terug + '</button>' +
      '<button type="button" class="ojv-uitsnede__ikoon" data-act="vooruit">' + UITSNEDE_IKONEN.vooruit + '</button>' +
      '<button type="button" class="ojv-uitsnede__ikoon" data-act="vergelijk">' + UITSNEDE_IKONEN.vergelijk + '</button></div>' +
      '<div class="ojv-uitsnede__links"><button type="button" data-act="wisalles"></button><button type="button" data-act="origineel"></button></div>' +
      '</div>';
    const $ = (s) => el.querySelector(s);
    $('.ojv-uitsnede__titel').textContent = t.verbeter;
    $('.ojv-uitsnede__klaar').textContent = t.klaar;
    $('.ojv-uitsnede__uitleg').textContent = t.uitleg;
    $('[data-modus="h"] span').textContent = t.herstel;
    $('[data-modus="w"] span').textContent = t.wis;
    $('.ojv-uitsnede__maat span').textContent = t.maat;
    $('[data-act="wisalles"]').textContent = t.wisAlles;
    $('[data-act="origineel"]').textContent = t.origineel;
    [['terug', t.terug], ['vooruit', t.vooruit], ['vergelijk', t.vergelijk]].forEach(([a, tekst]) => {
      $('[data-act="' + a + '"]').setAttribute('aria-label', tekst);
      $('[data-act="' + a + '"]').title = tekst;
    });
    this.el = el;
    this.vlak = $('.ojv-uitsnede__vlak');
    this.doek = $('canvas');
    this.ctx = this.doek.getContext('2d');
    this.uitleg = $('.ojv-uitsnede__uitleg');
    this.maat = $('.ojv-uitsnede__maat input');
    this.maat.value = String(this.straal);
    this.knopTerug = $('[data-act="terug"]');
    this.knopVooruit = $('[data-act="vooruit"]');
    this.knopWisAlles = $('[data-act="wisalles"]');

    // Voorbeeld op werkmaat: origineel, randkleur, masker.
    const u = this.u;
    this.origKlein = bgDoek(u.w, u.h);
    const ok = this.origKlein.getContext('2d');
    ok.imageSmoothingQuality = 'high';
    ok.drawImage(this.o.orig, 0, 0, u.w, u.h);
    this.voorbeeld = bgDoek(u.w, u.h);
    this.vk = this.voorbeeld.getContext('2d');
    this.ververs(0, 0, u.w, u.h);

    el.querySelectorAll('[data-modus]').forEach((k) => k.addEventListener('click', () => this.zetModus(k.dataset.modus)));
    this.maat.addEventListener('input', () => {
      this.straal = Number(this.maat.value);
      this.tekenLater();
    });
    $('.ojv-uitsnede__klaar').addEventListener('click', () => this.sluit(true));
    this.knopTerug.addEventListener('click', () => this.terug());
    this.knopVooruit.addEventListener('click', () => this.vooruit());
    this.knopWisAlles.addEventListener('click', () => this.wisAlles());
    $('[data-act="origineel"]').addEventListener('click', () => {
      this.sluit(false);
      this.o.bijOrigineel();
    });
    const vergelijk = $('[data-act="vergelijk"]');
    const aan = (e) => {
      e.preventDefault();
      this.vergelijk = true;
      vergelijk.setAttribute('aria-pressed', 'true');
      this.tekenLater();
    };
    const uit = () => {
      if (!this.vergelijk) return;
      this.vergelijk = false;
      vergelijk.setAttribute('aria-pressed', 'false');
      this.tekenLater();
    };
    vergelijk.addEventListener('pointerdown', aan);
    ['pointerup', 'pointerleave', 'pointercancel'].forEach((n) => vergelijk.addEventListener(n, uit));
    vergelijk.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Enter') aan(e);
    });
    vergelijk.addEventListener('keyup', uit);

    this.doek.addEventListener('pointerdown', (e) => this.neer(e));
    this.doek.addEventListener('pointermove', (e) => this.beweeg(e));
    ['pointerup', 'pointercancel'].forEach((n) => this.doek.addEventListener(n, (e) => this.op(e)));
    this.doek.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'mouse' && !this.wijzers.size) {
        this.cursor = null;
        this.tekenLater();
      }
    });
    this.doek.addEventListener('wheel', (e) => this.wiel(e), { passive: false });
    this.toets = (e) => {
      if (e.key === 'Escape') this.sluit(true);
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) this.vooruit();
        else this.terug();
      }
    };
    document.addEventListener('keydown', this.toets);

    this.oudOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.appendChild(el);
    this.zetModus(this.modus);
    this.knoppen();
    this.waarnemer = new ResizeObserver(() => this.maatVlak());
    this.waarnemer.observe(this.vlak);
    this.maatVlak(true);
    this.uitlegKlok = setTimeout(() => {
      this.uitleg.style.opacity = '0';
    }, 6000);
    $('.ojv-uitsnede__klaar').focus({ preventScroll: true });
  }

  zetModus(m) {
    this.modus = m;
    this.el.querySelectorAll('[data-modus]').forEach((k) => k.setAttribute('aria-pressed', k.dataset.modus === m ? 'true' : 'false'));
    this.tekenLater();
  }

  knoppen() {
    this.knopTerug.disabled = !this.u.streken.length;
    this.knopVooruit.disabled = !this.u.terug.length;
    this.knopWisAlles.disabled = !this.u.streken.length;
  }

  maatVlak(passen) {
    const r = this.vlak.getBoundingClientRect();
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.vw = Math.max(1, r.width);
    this.vh = Math.max(1, r.height);
    this.doek.width = Math.round(this.vw * this.dpr);
    this.doek.height = Math.round(this.vh * this.dpr);
    if (passen || !this.pasZoom) {
      this.pasZoom = Math.min(this.vw / this.u.w, this.vh / this.u.h) * 0.92;
      this.zoom = this.pasZoom;
      this.tx = (this.vw - this.u.w * this.zoom) / 2;
      this.ty = (this.vh - this.u.h * this.zoom) / 2;
    } else {
      this.pasZoom = Math.min(this.vw / this.u.w, this.vh / this.u.h) * 0.92;
      this.begrens();
    }
    this.teken();
  }

  /** Niet verder zoomen dan nodig, en het beeld niet uit beeld laten schuiven. */
  begrens() {
    this.zoom = Math.min(this.pasZoom * 10, Math.max(this.pasZoom * 0.6, this.zoom));
    const bw = this.u.w * this.zoom, bh = this.u.h * this.zoom, m = 40;
    this.tx = Math.min(this.vw - m, Math.max(m - bw, this.tx));
    this.ty = Math.min(this.vh - m, Math.max(m - bh, this.ty));
  }

  /** Alleen het stukje rond de penseel opnieuw samenstellen (bron-rechthoek, dus snel). */
  ververs(x, y, w, h) {
    const u = this.u;
    x = Math.max(0, Math.floor(x));
    y = Math.max(0, Math.floor(y));
    w = Math.min(u.w - x, Math.ceil(w));
    h = Math.min(u.h - y, Math.ceil(h));
    if (w <= 0 || h <= 0) return;
    const k = this.vk;
    // Alleen binnen dit stukje tekenen. 'destination-in' (en 'copy') maken
    // anders alles búiten het getekende stukje leeg, en dan leek het
    // verwijderpenseel de hele uitsnede weg te halen (Jos, 11 okt 2026).
    k.save();
    k.beginPath();
    k.rect(x, y, w, h);
    k.clip();
    k.clearRect(x, y, w, h);
    k.drawImage(this.origKlein, x, y, w, h, x, y, w, h);
    k.drawImage(u.kleur, x, y, w, h, x, y, w, h);
    k.globalCompositeOperation = 'destination-in';
    k.drawImage(u.masker, x, y, w, h, x, y, w, h);
    k.restore();
  }

  tekenLater() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.teken();
    });
  }

  teken() {
    if (!this.el) return;
    const k = this.ctx, d = this.dpr;
    k.setTransform(1, 0, 0, 1, 0, 0);
    k.clearRect(0, 0, this.doek.width, this.doek.height);
    k.setTransform(d * this.zoom, 0, 0, d * this.zoom, d * this.tx, d * this.ty);
    k.imageSmoothingEnabled = true;
    if (this.vergelijk) {
      k.drawImage(this.origKlein, 0, 0);
    } else {
      // Met het herstelpenseel zie je vaag wat er weg is, zodat je weet waar je kunt herstellen.
      if (this.modus === 'h') {
        k.globalAlpha = 0.28;
        k.drawImage(this.origKlein, 0, 0);
        k.globalAlpha = 1;
      }
      k.drawImage(this.voorbeeld, 0, 0);
    }
    if (this.cursor && !this.vergelijk) {
      k.setTransform(d, 0, 0, d, 0, 0);
      k.beginPath();
      k.arc(this.cursor.x, this.cursor.y, this.straal, 0, Math.PI * 2);
      k.lineWidth = 3;
      k.strokeStyle = 'rgba(0,0,0,.55)';
      k.stroke();
      k.lineWidth = 1.5;
      k.strokeStyle = this.modus === 'h' ? '#7ee2a8' : '#ffffff';
      k.stroke();
    }
  }

  punt(e) {
    const r = this.doek.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  naarBeeld(p) {
    return { x: (p.x - this.tx) / this.zoom, y: (p.y - this.ty) / this.zoom };
  }

  neer(e) {
    if (e.pointerType === 'mouse' && e.button !== 0 && e.button !== 1) return;
    e.preventDefault();
    try {
      this.doek.setPointerCapture(e.pointerId);
    } catch (err) {
      /* niet erg */
    }
    const p = this.punt(e);
    this.wijzers.set(e.pointerId, p);
    this.uitleg.style.opacity = '0';
    if (this.wijzers.size === 1 && e.button === 1) {
      this.schuif = { p: p, tx: this.tx, ty: this.ty };
      return;
    }
    if (this.wijzers.size === 1) {
      this.cursor = e.pointerType === 'mouse' ? p : null;
      this.begin(p);
    } else if (this.wijzers.size === 2) {
      // Tweede vinger: geen streek maar knijpen. Een streek van een paar
      // punten (net begonnen) draaien we terug.
      this.eindStreek(this.streek && this.streek.p.length <= 8 && performance.now() - this.streek.t < 250);
      const [a, b] = [...this.wijzers.values()];
      this.knijp = {
        d: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        mx: (a.x + b.x) / 2,
        my: (a.y + b.y) / 2,
        zoom: this.zoom,
        tx: this.tx,
        ty: this.ty,
      };
      this.cursor = null;
      this.tekenLater();
    }
  }

  beweeg(e) {
    const p = this.punt(e);
    if (e.pointerType === 'mouse') this.cursor = p;
    if (!this.wijzers.has(e.pointerId)) {
      this.tekenLater();
      return;
    }
    this.wijzers.set(e.pointerId, p);
    if (this.schuif) {
      this.tx = this.schuif.tx + p.x - this.schuif.p.x;
      this.ty = this.schuif.ty + p.y - this.schuif.p.y;
      this.begrens();
      this.tekenLater();
      return;
    }
    if (this.knijp && this.wijzers.size >= 2) {
      const [a, b] = [...this.wijzers.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      const k = this.knijp;
      const z = Math.min(this.pasZoom * 10, Math.max(this.pasZoom * 0.6, k.zoom * (d / k.d)));
      // Het punt onder het midden van de vingers blijft onder de vingers.
      this.tx = mx - ((k.mx - k.tx) / k.zoom) * z;
      this.ty = my - ((k.my - k.ty) / k.zoom) * z;
      this.zoom = z;
      this.begrens();
      this.tekenLater();
      return;
    }
    if (this.streek) {
      const reeks = e.getCoalescedEvents ? e.getCoalescedEvents() : [];
      (reeks.length ? reeks : [e]).forEach((ev) => this.voegToe(this.punt(ev)));
      if (e.pointerType !== 'mouse') this.cursor = p;
    }
    this.tekenLater();
  }

  op(e) {
    this.wijzers.delete(e.pointerId);
    if (this.schuif) this.schuif = null;
    if (this.streek && !this.wijzers.size) this.eindStreek(false);
    if (this.wijzers.size < 2) this.knijp = null;
    if (e.pointerType !== 'mouse') this.cursor = null;
    this.tekenLater();
  }

  wiel(e) {
    e.preventDefault();
    const p = this.punt(e);
    if (e.ctrlKey || e.metaKey) {
      // Knijpen op een touchpad komt binnen als ctrl + scrollen.
      const z = Math.min(this.pasZoom * 10, Math.max(this.pasZoom * 0.6, this.zoom * Math.exp(-e.deltaY * 0.01)));
      this.tx = p.x - ((p.x - this.tx) / this.zoom) * z;
      this.ty = p.y - ((p.y - this.ty) / this.zoom) * z;
      this.zoom = z;
    } else if (e.shiftKey || Math.abs(e.deltaX) > 0 || this.zoom > this.pasZoom * 1.05) {
      this.tx -= e.deltaX || (e.shiftKey ? e.deltaY : 0);
      this.ty -= e.shiftKey ? 0 : e.deltaY;
    } else {
      // Muiswiel zonder iets erbij: zoomen, zoals in de meeste fotobewerkers.
      const z = Math.min(this.pasZoom * 10, Math.max(this.pasZoom * 0.6, this.zoom * Math.exp(-e.deltaY * 0.0015)));
      this.tx = p.x - ((p.x - this.tx) / this.zoom) * z;
      this.ty = p.y - ((p.y - this.ty) / this.zoom) * z;
      this.zoom = z;
    }
    this.cursor = p;
    this.begrens();
    this.tekenLater();
  }

  begin(p) {
    const u = this.u;
    bgWerkkopie(u);
    const b = this.naarBeeld(p);
    // Straal in beeldpunten van het masker: de cirkel op het scherm is wat je raakt.
    const st = { m: this.modus, r: Math.max(0.5, Math.round((this.straal / this.zoom) * 10) / 10), p: [Math.round(b.x * 2) / 2, Math.round(b.y * 2) / 2] };
    this.streek = { st: st, p: st.p, t: performance.now() };
    bgStreek(u, st, 0);
    this.ververs(b.x - st.r - 2, b.y - st.r - 2, st.r * 2 + 4, st.r * 2 + 4);
  }

  voegToe(p) {
    const s = this.streek;
    if (!s) return;
    const b = this.naarBeeld(p);
    const x = Math.round(b.x * 2) / 2, y = Math.round(b.y * 2) / 2;
    const n = s.p.length;
    const vx = s.p[n - 2], vy = s.p[n - 1];
    if (Math.abs(x - vx) + Math.abs(y - vy) < 0.75) return;
    s.p.push(x, y);
    bgStreek(this.u, s.st, n);
    const r = s.st.r + 2;
    this.ververs(Math.min(x, vx) - r, Math.min(y, vy) - r, Math.abs(x - vx) + r * 2, Math.abs(y - vy) + r * 2);
  }

  /** Streek afronden; `weg`: toch niet bewaren (begin van een knijpgebaar). */
  eindStreek(weg) {
    const s = this.streek;
    if (!s) return;
    this.streek = null;
    if (weg) {
      bgHerbouw(this.u);
      this.ververs(0, 0, this.u.w, this.u.h);
      return;
    }
    this.u.streken.push(s.st);
    this.u.terug = [];
    this.knoppen();
  }

  terug() {
    const st = this.u.streken.pop();
    if (!st) return;
    this.u.terug.push(st);
    bgHerbouw(this.u);
    this.ververs(0, 0, this.u.w, this.u.h);
    this.knoppen();
    this.tekenLater();
  }

  vooruit() {
    const st = this.u.terug.pop();
    if (!st) return;
    bgWerkkopie(this.u);
    this.u.streken.push(st);
    bgStreek(this.u, st, 0);
    this.ververs(0, 0, this.u.w, this.u.h);
    this.knoppen();
    this.tekenLater();
  }

  wisAlles() {
    if (!this.u.streken.length) return;
    while (this.u.streken.length) this.u.terug.push(this.u.streken.pop());
    bgHerbouw(this.u);
    this.ververs(0, 0, this.u.w, this.u.h);
    this.knoppen();
    this.tekenLater();
  }

  sluit(bewaar) {
    if (!this.el) return;
    if (this.streek) this.eindStreek(false);
    clearTimeout(this.uitlegKlok);
    if (this.frame) cancelAnimationFrame(this.frame);
    this.waarnemer.disconnect();
    document.removeEventListener('keydown', this.toets);
    document.documentElement.style.overflow = this.oudOverflow;
    this.el.remove();
    this.el = null;
    bgVrij(this.origKlein);
    bgVrij(this.voorbeeld);
    bgVrij(this.doek);
    if (bewaar) this.o.bijKlaar(bgSleutel(this.u) !== this.beginSleutel);
  }
}

/** Vaste nummers voor beelden en bestanden, voor de sleutel van een stap in "Ongedaan maken". */
const objectNummers = new WeakMap();
let objectTeller = 0;
function objectNummer(o) {
  let n = objectNummers.get(o);
  if (!n) {
    n = ++objectTeller;
    objectNummers.set(o, n);
  }
  return n;
}

/* ---------- Venster "Bijsnijden" ---------------------------------------------
 * Jos (11 okt 2026): een toegevoegde afbeelding bijsnijden. Zelfde venster en
 * opmaak als "Uitsnede verbeteren", zodat het één geheel blijft. Niet-destructief:
 * de afbeelding blijft heel, de laag onthoudt alleen welk stuk zichtbaar is
 * (snX, snY, snB, snH als deel van de breedte en hoogte).
 */
const BIJSNIJD_STIJL = `
.ojv-uitsnede__verhoudingen{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:.4rem}
.ojv-uitsnede__verhoudingen button{min-height:42px;border:1px solid rgba(255,255,255,.25);background:transparent;border-radius:10px;font-size:.82rem;padding:0 .3rem}
.ojv-uitsnede__verhoudingen button[aria-pressed=true]{background:#fff;color:#111}
.ojv-uitsnede__vlak--bijsnijden canvas{cursor:default}
`;

class BijsnijdVenster {
  /** opties: { beeld, snede: {x, y, b, h} (delen van 0 tot 1) of null, t (teksten), bijKlaar(snede of null) } */
  constructor(opties) {
    this.o = opties;
    const b = opties.beeld;
    this.iw = b instanceof HTMLImageElement ? b.naturalWidth : b.width;
    this.ih = b instanceof HTMLImageElement ? b.naturalHeight : b.height;
    const s = opties.snede;
    this.r = s
      ? { x: s.x * this.iw, y: s.y * this.ih, w: s.b * this.iw, h: s.h * this.ih }
      : { x: 0, y: 0, w: this.iw, h: this.ih };
    this.verhouding = 0;
    this.greep = null;
    this.bouw();
  }

  bouw() {
    [['ojv-uitsnede-stijl', UITSNEDE_STIJL], ['ojv-bijsnijd-stijl', BIJSNIJD_STIJL]].forEach(([id, css]) => {
      if (document.getElementById(id)) return;
      const st = document.createElement('style');
      st.id = id;
      st.textContent = css;
      document.head.appendChild(st);
    });
    const t = this.o.t;
    const el = document.createElement('div');
    el.className = 'ojv-uitsnede';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', t.bijsnijden);
    el.innerHTML =
      '<div class="ojv-uitsnede__kop"><span class="ojv-uitsnede__titel"></span><button type="button" class="ojv-uitsnede__klaar"></button></div>' +
      '<div class="ojv-uitsnede__vlak ojv-uitsnede__vlak--bijsnijden"><canvas></canvas><p class="ojv-uitsnede__uitleg"></p></div>' +
      '<div class="ojv-uitsnede__balk">' +
      '<div class="ojv-uitsnede__verhoudingen" role="group">' +
      '<button type="button" data-v="0"></button><button type="button" data-v="1"></button>' +
      '<button type="button" data-v="1.5"></button><button type="button" data-v="0.6667"></button></div>' +
      '<div class="ojv-uitsnede__links"><button type="button" data-act="heel"></button></div>' +
      '</div>';
    const $ = (s) => el.querySelector(s);
    $('.ojv-uitsnede__titel').textContent = t.bijsnijden;
    $('.ojv-uitsnede__klaar').textContent = t.klaar;
    $('.ojv-uitsnede__uitleg').textContent = t.bsUitleg;
    [['0', t.bsVrij], ['1', t.bsVierkant], ['1.5', t.bsLiggend], ['0.6667', t.bsStaand]].forEach(([v, tekst]) => {
      $('[data-v="' + v + '"]').textContent = tekst;
    });
    $('[data-act="heel"]').textContent = t.bsHeel;
    this.el = el;
    this.vlak = $('.ojv-uitsnede__vlak');
    this.doek = $('canvas');
    this.ctx = this.doek.getContext('2d');
    this.uitleg = $('.ojv-uitsnede__uitleg');

    el.querySelectorAll('[data-v]').forEach((k) => k.addEventListener('click', () => this.zetVerhouding(Number(k.dataset.v))));
    $('[data-act="heel"]').addEventListener('click', () => {
      this.r = { x: 0, y: 0, w: this.iw, h: this.ih };
      this.zetVerhouding(0);
    });
    $('.ojv-uitsnede__klaar').addEventListener('click', () => this.sluit());
    this.doek.addEventListener('pointerdown', (e) => this.neer(e));
    this.doek.addEventListener('pointermove', (e) => this.beweeg(e));
    ['pointerup', 'pointercancel'].forEach((n) => this.doek.addEventListener(n, () => this.op()));
    this.toets = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter') this.sluit();
    };
    document.addEventListener('keydown', this.toets);

    this.oudOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.appendChild(el);
    this.knoppen();
    this.waarnemer = new ResizeObserver(() => this.maatVlak());
    this.waarnemer.observe(this.vlak);
    this.maatVlak();
    this.uitlegKlok = setTimeout(() => {
      this.uitleg.style.opacity = '0';
    }, 6000);
    $('.ojv-uitsnede__klaar').focus({ preventScroll: true });
  }

  knoppen() {
    this.el.querySelectorAll('[data-v]').forEach((k) =>
      k.setAttribute('aria-pressed', Math.abs(Number(k.dataset.v) - this.verhouding) < 0.01 ? 'true' : 'false'));
  }

  /** Vaste verhouding (0 = vrij): het grootste kader met die verhouding rond het huidige midden. */
  zetVerhouding(v) {
    this.verhouding = v;
    if (v) {
      const mx = this.r.x + this.r.w / 2, my = this.r.y + this.r.h / 2;
      let w = Math.min(this.iw, this.ih * v), h = w / v;
      if (h > this.ih) {
        h = this.ih;
        w = h * v;
      }
      // Niet groter dan nodig rond het midden van wat er nu gekozen is.
      const past = Math.min(1, Math.max(this.r.w / w, this.r.h / h));
      w *= past;
      h *= past;
      this.r = {
        x: Math.min(this.iw - w, Math.max(0, mx - w / 2)),
        y: Math.min(this.ih - h, Math.max(0, my - h / 2)),
        w: w,
        h: h,
      };
    }
    this.knoppen();
    this.teken();
  }

  maatVlak() {
    const r = this.vlak.getBoundingClientRect();
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.vw = Math.max(1, r.width);
    this.vh = Math.max(1, r.height);
    this.doek.width = Math.round(this.vw * this.dpr);
    this.doek.height = Math.round(this.vh * this.dpr);
    const rand = 28;
    this.s = Math.max(0.001, Math.min((this.vw - rand * 2) / this.iw, (this.vh - rand * 2) / this.ih));
    this.ox = (this.vw - this.iw * this.s) / 2;
    this.oy = (this.vh - this.ih * this.s) / 2;
    this.teken();
  }

  /** Het kader op het scherm. */
  kader() {
    return { x: this.ox + this.r.x * this.s, y: this.oy + this.r.y * this.s, w: this.r.w * this.s, h: this.r.h * this.s };
  }

  teken() {
    if (!this.el) return;
    const k = this.ctx, d = this.dpr;
    k.setTransform(1, 0, 0, 1, 0, 0);
    k.clearRect(0, 0, this.doek.width, this.doek.height);
    k.setTransform(d, 0, 0, d, 0, 0);
    k.imageSmoothingQuality = 'high';
    k.drawImage(this.o.beeld, this.ox, this.oy, this.iw * this.s, this.ih * this.s);
    const c = this.kader();
    // Wat wegvalt donker, wat blijft helder.
    k.beginPath();
    k.rect(0, 0, this.vw, this.vh);
    k.rect(c.x, c.y, c.w, c.h);
    k.fillStyle = 'rgba(20, 22, 26, 0.62)';
    k.fill('evenodd');
    // Derdeslijnen, zoals in de meeste fotobewerkers.
    k.strokeStyle = 'rgba(255,255,255,.45)';
    k.lineWidth = 1;
    k.beginPath();
    for (let i = 1; i < 3; i++) {
      k.moveTo(c.x + (c.w * i) / 3, c.y);
      k.lineTo(c.x + (c.w * i) / 3, c.y + c.h);
      k.moveTo(c.x, c.y + (c.h * i) / 3);
      k.lineTo(c.x + c.w, c.y + (c.h * i) / 3);
    }
    k.stroke();
    k.strokeStyle = '#fff';
    k.lineWidth = 2;
    k.strokeRect(c.x, c.y, c.w, c.h);
    // Hoekgrepen (L-vorm) en, zonder vaste verhouding, streepjes midden op de randen.
    const L = Math.min(22, c.w / 3, c.h / 3);
    k.lineWidth = 4;
    k.lineCap = 'round';
    k.beginPath();
    [[c.x, c.y, 1, 1], [c.x + c.w, c.y, -1, 1], [c.x, c.y + c.h, 1, -1], [c.x + c.w, c.y + c.h, -1, -1]].forEach(([x, y, sx, sy]) => {
      k.moveTo(x + sx * L, y);
      k.lineTo(x, y);
      k.lineTo(x, y + sy * L);
    });
    if (!this.verhouding) {
      const m = Math.min(12, c.w / 4, c.h / 4);
      k.moveTo(c.x + c.w / 2 - m, c.y);
      k.lineTo(c.x + c.w / 2 + m, c.y);
      k.moveTo(c.x + c.w / 2 - m, c.y + c.h);
      k.lineTo(c.x + c.w / 2 + m, c.y + c.h);
      k.moveTo(c.x, c.y + c.h / 2 - m);
      k.lineTo(c.x, c.y + c.h / 2 + m);
      k.moveTo(c.x + c.w, c.y + c.h / 2 - m);
      k.lineTo(c.x + c.w, c.y + c.h / 2 + m);
    }
    k.stroke();
    k.lineCap = 'butt';
  }

  punt(e) {
    const r = this.doek.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  /** Welke greep ligt onder dit punt: een hoek, een rand, of het kader zelf. */
  greepOp(p) {
    const c = this.kader(), g = 24;
    const bijX = (x) => Math.abs(p.x - x) <= g, bijY = (y) => Math.abs(p.y - y) <= g;
    const binnenX = p.x > c.x - g && p.x < c.x + c.w + g, binnenY = p.y > c.y - g && p.y < c.y + c.h + g;
    const links = bijX(c.x), rechts = bijX(c.x + c.w), boven = bijY(c.y), onder = bijY(c.y + c.h);
    if (links && boven) return 'nw';
    if (rechts && boven) return 'ne';
    if (links && onder) return 'sw';
    if (rechts && onder) return 'se';
    if (!this.verhouding) {
      if (boven && binnenX) return 'n';
      if (onder && binnenX) return 's';
      if (links && binnenY) return 'w';
      if (rechts && binnenY) return 'e';
    }
    if (p.x > c.x && p.x < c.x + c.w && p.y > c.y && p.y < c.y + c.h) return 'schuif';
    return null;
  }

  neer(e) {
    if (this.greep) return;
    const p = this.punt(e);
    const g = this.greepOp(p);
    if (!g) return;
    e.preventDefault();
    try {
      this.doek.setPointerCapture(e.pointerId);
    } catch (err) {
      /* niet erg */
    }
    this.uitleg.style.opacity = '0';
    this.greep = { g: g, id: e.pointerId, p: p, r: Object.assign({}, this.r) };
  }

  beweeg(e) {
    const p = this.punt(e);
    if (!this.greep) {
      if (e.pointerType === 'mouse') {
        const g = this.greepOp(p);
        const cursor = { nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize', n: 'ns-resize', s: 'ns-resize', w: 'ew-resize', e: 'ew-resize', schuif: 'move' }[g] || 'default';
        if (this.doek.style.cursor !== cursor) this.doek.style.cursor = cursor;
      }
      return;
    }
    if (e.pointerId !== this.greep.id) return;
    const { g, r: o } = this.greep;
    const dx = (p.x - this.greep.p.x) / this.s, dy = (p.y - this.greep.p.y) / this.s;
    const min = Math.max(16, Math.min(this.iw, this.ih) * 0.04);
    if (g === 'schuif') {
      this.r = { x: Math.min(this.iw - o.w, Math.max(0, o.x + dx)), y: Math.min(this.ih - o.h, Math.max(0, o.y + dy)), w: o.w, h: o.h };
    } else {
      let x0 = o.x, y0 = o.y, x1 = o.x + o.w, y1 = o.y + o.h;
      if (g.includes('w')) x0 = Math.min(x1 - min, Math.max(0, o.x + dx));
      if (g.includes('e')) x1 = Math.max(x0 + min, Math.min(this.iw, o.x + o.w + dx));
      if (g.includes('n')) y0 = Math.min(y1 - min, Math.max(0, o.y + dy));
      if (g.includes('s')) y1 = Math.max(y0 + min, Math.min(this.ih, o.y + o.h + dy));
      if (this.verhouding && g.length === 2) {
        // Vaste verhouding: de breedte bepaalt, de hoogte volgt; de tegenoverliggende hoek blijft staan.
        const v = this.verhouding;
        let w = x1 - x0, h = w / v;
        const ruimteH = g.includes('n') ? o.y + o.h : this.ih - o.y;
        if (h > ruimteH) {
          h = ruimteH;
          w = h * v;
        }
        if (g.includes('w')) x0 = x1 - w;
        else x1 = x0 + w;
        if (g.includes('n')) y0 = y1 - h;
        else y1 = y0 + h;
      }
      this.r = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
    }
    if (!this.frame) {
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.teken();
      });
    }
  }

  op() {
    this.greep = null;
  }

  sluit() {
    if (!this.el) return;
    clearTimeout(this.uitlegKlok);
    if (this.frame) cancelAnimationFrame(this.frame);
    this.waarnemer.disconnect();
    document.removeEventListener('keydown', this.toets);
    document.documentElement.style.overflow = this.oudOverflow;
    this.el.remove();
    this.el = null;
    bgVrij(this.doek);
    const r = this.r;
    const heel = r.x < 1 && r.y < 1 && r.w > this.iw - 1 && r.h > this.ih - 1;
    this.o.bijKlaar(heel ? null : { x: r.x / this.iw, y: r.y / this.ih, b: r.w / this.iw, h: r.h / this.ih });
  }
}

class FlagConfigurator extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';

    this.noPreviewText = this.dataset.tNofile || '';
    this.pdfBezigText = this.dataset.tPdf || '';
    this.flagBase = this.dataset.flagBase || '';
    this.uploadUrl = this.dataset.uploadUrl || '';
    this.chosenText = this.dataset.tChosen || '';

    const panels = [...this.querySelectorAll('.flagcfg__side')];
    this.dubbel = panels.length > 1;

    try {
      this.maten = JSON.parse(this.dataset.variantSizes || '{}');
    } catch (err) {
      this.maten = {};
    }
    try {
      this.vormen = JSON.parse(this.dataset.variantVormen || '{}');
    } catch (err) {
      this.vormen = {};
    }
    this.isBeach = Object.keys(this.vormen).length > 0;
    // Kleur van de elastische band per variant (Probo-beachflag): wit of zwart.
    try {
      this.banden = JSON.parse(this.dataset.variantBanden || '{}');
    } catch (err) {
      this.banden = {};
    }
    // Vorm per variant bij de boot-, golf- en kioskvlag van Probo
    // ("kiosk-68x136-links"). Alleen als de vorm ook echt geladen is; anders
    // gedraagt het product zich als een gewone rechthoekige vlag.
    try {
      this.proboVormen = JSON.parse(this.dataset.variantProboVormen || '{}');
    } catch (err) {
      this.proboVormen = {};
    }
    this.proboVlag = false;
    if (!this.isBeach) {
      const vorm = this.proboVormen[String(this.dataset.variantId)];
      const v = proboVorm(vorm);
      if (v) {
        this.proboVlag = true;
        this.vorm = vorm;
        this.probo = v;
      }
    }

    this.isSpandoek = this.dataset.soort === 'spandoek';
    this.isWimpel = this.hasAttribute('data-wimpel');
    // Keuzerijen op de productpagina (zie #bindRijen).
    this.rijen = this.hasAttribute('data-rijen');
    // Afloop en veilige marge uit de productvelden, in centimeters. Staan
    // standaard op 0: dan verandert er niets aan drukbestand of doek.
    this.afloopCm = Math.max(0, Number(this.dataset.afloopMm) || 0) / 10;
    this.margeCm = Math.max(0, Number(this.dataset.margeMm) || 0) / 10;

    if (this.isBeach) {
      this.vorm = this.vormen[String(this.dataset.variantId)] || 'veer';
      this.band = this.banden[String(this.dataset.variantId)] || 'wit';
      this.probo = proboVorm(this.vorm);
      if (this.probo) {
        // Probo: de snijmaat uit de werktekening. Daarmee kan er ook een
        // exact drukbestand gemaakt worden (zie #maakDrukbestanden).
        this.flagCm = { w: this.probo.doek.b, h: this.probo.doek.h };
        // De afloop staat in de werktekening zelf en verschilt per vorm
        // (Square 1 cm, Straight 0,5 cm, Wave soms maar enkele millimeters).
        // Die telt zwaarder dan het productveld.
        if (this.probo.afloop > 0) this.afloopCm = this.probo.afloop;
        this.layout = proboBeachLayout(this.probo);
      } else {
        // Representatieve printbreedte van een beachflag; alleen gebruikt om de
        // scherpte in ppi te schatten, niet als maatvoering naar de klant.
        this.flagCm = { w: 72, h: 260 };
        this.layout = beachflagLayout();
      }
    } else if (this.proboVlag) {
      // Snijmaat en afloop uit de werktekening, net als bij de Probo-beachflag.
      this.flagCm = { w: this.probo.doek.b, h: this.probo.doek.h };
      // Baniervlag: de band met haken in de gekozen kleur (wit of zwart).
      this.band = this.banden[String(this.dataset.variantId)] || 'wit';
      if (this.probo.afloop > 0) this.afloopCm = this.probo.afloop;
      this.layout = proboVlagLayout(this.probo, this.dubbel);
    } else if (this.isSpandoek) {
      this.flagCm = this.#maatVan(this.dataset.variantId) || { w: 300, h: 100 };
      this.layout = spandoekLayout(this.flagCm.w / this.flagCm.h);
    } else {
      this.flagCm = this.#maatVan(this.dataset.variantId) || STANDAARDMAAT;
      this.layout = this.isWimpel
        ? wimpelLayout(this.flagCm.w / this.flagCm.h)
        : bouwLayout(this.flagCm.w / this.flagCm.h, this.dubbel);
    }

    this.canvas = this.querySelector('[data-canvas]');
    this.ctx = this.canvas.getContext('2d');
    this.emptyEl = this.querySelector('[data-empty]');
    this.previewInput = this.querySelector('[data-preview]');

    this.#zetCanvas();

    // op aanraakschermen de hint aanpassen (knijpen kan daar)
    if (this.dataset.tTouch && matchMedia('(pointer: coarse)').matches) {
      this.querySelectorAll('[data-hint]').forEach((el) => {
        el.innerHTML = this.dataset.tTouch;
      });
    }

    this.#initNames();
    this.sides = panels.map((el, i) => this.#initSide(el, i));
    this.#bindGestures();
    this.#bindKlikOpDoek();
    this.#bindBusyState();
    this.#bindScherpteRem();
    this.#bindVariant();
    this.#bindFormulier();
    this.#bindVolScherm();
    this.#bindPreview();
    this.#bindRuimte();
    this.#bindFocus();
    this.#bindHerstelDoek();
    this.#markeerFavoriet();
    this.#bindAfwerking();
    this.#bindRijen();
    this.#verzorgDoekkeuze();
    this.#render();
    this.#disableForeignProperties();
    this.#bindOntwerpOpslag();
    this.#ontgrendelBestelknop();
  }

  /**
   * De bestelknop komt uitgeschakeld uit de server (snippets/add-to-cart-button
   * .liquid) zodra een product de ontwerptool heeft, met daaronder een regel
   * die uitlegt waarom.
   *
   * Dat is het vangnet voor een browser waarin dit script niet draait. De
   * controle "is er wel een ontwerp?" staat in #bindOntwerpOpslag en is dus
   * javascript; zonder javascript verstuurt de browser het formulier gewoon
   * zelf en komt er een vlag in de winkelwagen met alle ontwerpvelden leeg.
   * Nagemeten op 22 september 2026 met een kale POST naar /cart/add.
   *
   * Draaien we wél, dan mag de knop meteen aan: vanaf hier bewaakt
   * #bindOntwerpOpslag het ontwerp en houdt #zetBestelknop het uploaden en de
   * scherpterem bij. Alleen knoppen met het kenmerk worden aangeraakt, zodat
   * een uitverkocht product uit blijft staan.
   */
  #ontgrendelBestelknop() {
    const form = this.closest('form');
    if (!form) return;
    form.querySelectorAll('button[data-ontwerp-vereist]').forEach((knop) => {
      knop.removeAttribute('data-ontwerp-vereist');
      knop.disabled = false;
    });
    this.#zetBestelknop();
  }

  /**
   * 150 x 100 cm is de gangbare gevelmaat en veruit de meest gekozen. Hem
   * voorselecteren kan niet zonder zichtbare sprong: Shopify rendert altijd de
   * eerste variant, en de variantvolgorde is bij Shopify gekoppeld aan de
   * volgorde van de maatlijst — je kunt dus niet én oplopend tonen én een
   * andere maat als standaard hebben. Daarom wijzen we hem alleen aan: de knop
   * krijgt een rand en het label "meest gekozen".
   */
  /**
   * Wisselt de tekening naast de afwerkingskeuze mee met wat er gekozen is.
   *
   * De vier tekeningen staan allemaal in de pagina en er is er steeds één
   * zichtbaar. Dat is eenvoudiger dan ze inladen, en het werkt ook als het
   * script om wat voor reden dan ook niet draait: dan staat de eerste er.
   */
  #bindAfwerking() {
    const keuze = this.querySelector('[data-finishkeuze]');
    if (!keuze) return;
    const beeld = this.querySelector('[data-finishbeeld]');
    const iconen = beeld ? [...beeld.querySelectorAll('[data-finishicoon]')] : [];
    // Als keuzerij (Jos, 15 sep 2026): vier tegels met de tekening, en de
    // gekozen naam in de kop van de rij. De keuzelijst blijft het veld dat
    // meegaat naar de bestelling; de tegels zetten alleen zijn waarde.
    const tegels = [...this.querySelectorAll('[data-afwerkingoptie]')];
    const naam = this.querySelector('[data-afwerkingwaarde]');
    const toon = () => {
      const i = keuze.selectedIndex < 0 ? 0 : keuze.selectedIndex;
      iconen.forEach((el, n) => { el.hidden = n !== i; });
      tegels.forEach((el, n) => { el.setAttribute('aria-pressed', n === i ? 'true' : 'false'); });
      const optie = keuze.options[i];
      const tekst = optie ? optie.textContent.trim() : '';
      if (naam && naam.textContent !== tekst) naam.textContent = tekst;
      // Wat de klant op de afrekenpagina ziet: de keuze in zijn eigen taal (zie de snippet).
      const zicht = this.querySelector('[data-finishzicht]');
      if (zicht && tekst) zicht.value = tekst;
    };
    tegels.forEach((el) => {
      el.addEventListener('click', () => {
        const n = Number(el.dataset.afwerkingoptie);
        if (keuze.selectedIndex !== n) {
          keuze.selectedIndex = n;
          keuze.dispatchEvent(new Event('change', { bubbles: true }));
        }
        // Gekozen, dus de rij weer dicht, net als een keuzelijst.
        this.#klapRij(this.querySelector('[data-afwerkingrij]'), false);
      });
    });
    keuze.addEventListener('change', toon);
    toon();
  }

  /**
   * Keuzerijen (Jos, 15 sep 2026). "Jouw ontwerp" en "Afwerking" staan elk op
   * één regel en klappen open met een tik. Het ontwerp begint dicht; de kop
   * laat zien hoe ver de klant is (#toonOntwerpStatus). Bestellen zonder
   * ontwerp zet de rij open (#vraagOmOntwerp).
   *
   * Op het volledige scherm doen de rijen niets: daar zet de opmaak ze op
   * display: contents en ziet de ontwerptool eruit als voorheen.
   */
  #bindRijen() {
    if (!this.rijen) return;
    this.ontwerpRij = this.querySelector('[data-ontwerprij]');
    this.ontwerpStatus = this.querySelector('[data-ontwerpstatus]');

    [this.ontwerpRij, this.querySelector('[data-afwerkingrij]')].forEach((rij) => {
      const knop = rij && rij.querySelector('.flagcfg__rijkop');
      if (!knop) return;
      knop.addEventListener('click', () => this.#klapRij(rij, rij.classList.contains('is-dicht')));
    });

    // Formaat en doek (variant-main-picker.liquid): de hele regel is een
    // onzichtbare keuzelijst met de gekozen waarde ernaast. Het thema bouwt de
    // kiezer na een wissel opnieuw op met de juiste waarde, maar dat wacht op
    // een netwerkverzoek. Tot die tijd hier alvast de nieuwe waarde tonen.
    document.addEventListener('change', (e) => {
      const lijst = e.target;
      if (!(lijst instanceof HTMLSelectElement) || !lijst.closest('.variant-picker--rijen')) return;
      const optie = lijst.closest('.variant-option');
      const waarde = optie && optie.querySelector('.variant-option__waarde');
      if (waarde && waarde.textContent !== lijst.value) waarde.textContent = lijst.value;
    });

    this.#toonOntwerpStatus();
  }

  /** Klapt een keuzerij open of dicht. */
  #klapRij(rij, open) {
    if (!rij || rij.classList.contains('is-dicht') !== open) return;
    rij.classList.toggle('is-dicht', !open);
    const knop = rij.querySelector('.flagcfg__rijkop');
    if (knop) knop.setAttribute('aria-expanded', String(open));
    // Het doek was onzichtbaar. Opnieuw tekenen, voor het geval Safari het
    // beeld intussen heeft weggegooid (zie "Lege vlag op de iPhone").
    if (open && rij === this.ontwerpRij) this.#render(true);
  }

  /** Staat er op deze zijde iets om te drukken? Ook een bestand zonder voorbeeld telt. */
  #zijdeHeeftOntwerp(s) {
    return !!(s.img || this.#heeftLagen(s) || (s.fileInput && s.fileInput.files && s.fileInput.files.length));
  }

  /** De kop van de rij "Jouw ontwerp": nog niets, één zijde, of klaar. */
  #toonOntwerpStatus() {
    const chip = this.ontwerpStatus;
    if (!chip || !this.sides) return;
    const d = this.dataset;
    const klaar = this.sides.map((s) => this.#zijdeHeeftOntwerp(s));
    let tekst = d.tStatusLeeg || '';
    let stand = 'leeg';
    if (klaar.every(Boolean)) {
      tekst = (klaar.length > 1 ? d.tStatusBeide : d.tStatusKlaar) || '';
      stand = 'klaar';
    } else if (klaar.some(Boolean)) {
      const namen = [d.tStatusLinks || '', d.tStatusRechts || ''];
      const i = klaar[0] ? 0 : 1;
      tekst = namen[i] + ' · ' + namen[1 - i].toLowerCase() + ' ' + (d.tStatusZijdeLeeg || '');
      stand = 'half';
    }
    // Alleen schrijven wat verandert: dit loopt mee met elke tekenbeurt.
    if (chip.textContent !== tekst) chip.textContent = tekst;
    // 'Tekst of naam erbij?' alleen voor wie van een vlagpagina komt en die vlag
    // al op het doek heeft; anders altijd 'Nog geen ontwerp?' (Jos, 11 okt 2026).
    this.#zetOpenerTekst(stand !== 'leeg' && !!this.vanVlagpagina);
    if (chip.dataset.stand !== stand) {
      chip.dataset.stand = stand;
      chip.classList.toggle('is-leeg', stand === 'leeg');
      chip.classList.toggle('is-klaar', stand !== 'leeg');
    }
  }

  /**
   * De donkere knop naar de ontwerptool (Jos, 7 okt 2026). Zonder ontwerp zegt
   * hij "Nog geen ontwerp? Ontwerp je eigen vlag". Heeft de klant al een vlag
   * of bestand, bijvoorbeeld via een vlagpagina, dan wil die er juist iets bij
   * zetten: dan zegt hij "Tekst of naam erbij? Voeg tekst toe". Schrijft alleen
   * als de stand omslaat.
   */
  #zetOpenerTekst(heeft) {
    if (this.openerHeeft === heeft) return;
    this.openerHeeft = heeft;
    const d = this.dataset;
    if (!d.tCtaHeeftTitel) return;
    const delen = [
      ['.flagcfg__tekstopen-eyebrow', d.tCtaHeeftEyebrow],
      ['.flagcfg__tekstopen-titel', d.tCtaHeeftTitel],
      ['.flagcfg__tekstopen-uitleg', d.tCtaHeeftUitleg],
    ];
    this.querySelectorAll('[data-tekstopen]').forEach((knop) => {
      delen.forEach(([sel, erbij]) => {
        const el = knop.querySelector(sel);
        if (!el) return;
        if (el.dataset.nieuw === undefined) el.dataset.nieuw = el.textContent;
        const tekst = heeft ? erbij || el.dataset.nieuw : el.dataset.nieuw;
        if (el.textContent !== tekst) el.textContent = tekst;
      });
      knop.dataset.modus = heeft ? 'erbij' : 'nieuw';
    });
  }

  /**
   * In de ontwerptool even de knop Tekst laten oplichten: daar zet je een naam
   * of tekst op de vlag. Voor wie via "Voeg tekst toe" binnenkomt.
   */
  #wijsTekstKnop() {
    setTimeout(() => {
      const side = this.sides.find((s) => s.tekstKnop && s.tekstKnop.offsetParent);
      const knop = side && side.tekstKnop;
      if (!knop) return;
      const rustig = matchMedia('(prefers-reduced-motion: reduce)').matches;
      knop.scrollIntoView({ block: 'nearest', behavior: rustig ? 'auto' : 'smooth' });
      if (!knop.animate) return;
      knop.animate(
        [{ boxShadow: '0 0 0 0 rgba(164, 35, 37, 0.65)' }, { boxShadow: '0 0 0 12px rgba(164, 35, 37, 0)' }],
        { duration: 900, iterations: rustig ? 1 : 3, easing: 'ease-out' }
      );
    }, 400);
  }

  /** Bestellen zonder ontwerp tegenhouden? Alleen met keuzerijen, en niet bij het bijwerken van de winkelmand. */
  #moetOntwerpVragen() {
    return !!(this.rijen && this.ontwerpRij && !this.bewerk && this.sides &&
      !this.sides.some((s) => this.#zijdeHeeftOntwerp(s)));
  }

  /** Rij open, in beeld, en even laten oplichten. */
  #vraagOmOntwerp() {
    const rij = this.ontwerpRij;
    if (!rij) return;
    this.#klapRij(rij, true);
    rij.classList.add('is-nodig');
    clearTimeout(this.nodigKlok);
    this.nodigKlok = setTimeout(() => rij.classList.remove('is-nodig'), 2600);
    const zacht = !matchMedia('(prefers-reduced-motion: reduce)').matches;
    rij.scrollIntoView({ behavior: zacht ? 'smooth' : 'auto', block: 'start' });
  }

  #markeerFavoriet() {
    if (this.isBeach || this.isSpandoek || this.proboVlag) return;
    const tekst = this.dataset.tFavoriet;
    if (!tekst) return;

    const past = (waarde) => /150\s*[x×]\s*100/.test(waarde || '');

    const markeer = () => {
      // Knoppen: een tweede regel in de knop zelf.
      const knop = [...document.querySelectorAll('fieldset.variant-option input[type="radio"]')]
        .find((i) => past(i.value));
      if (knop) {
        const label = knop.closest('label');
        if (!label || label.classList.contains('flagcfg-favoriet')) return true;
        label.classList.add('flagcfg-favoriet');
        const vlag = document.createElement('span');
        vlag.className = 'flagcfg-favoriet__label';
        vlag.textContent = tekst;
        label.appendChild(vlag);
        return true;
      }

      // Keuzelijst: er is geen ruimte voor een tweede regel, dus komt het
      // achter de maat te staan — "150 × 100 cm — meest gekozen".
      const optie = [...document.querySelectorAll('.variant-option select option')]
        .find((o) => past(o.value));
      if (optie) {
        if (optie.dataset.favoriet === '1') return true;
        optie.dataset.favoriet = '1';
        optie.textContent = optie.textContent.trim() + ' — ' + tekst;
        return true;
      }

      return false;
    };

    // Het thema bouwt de variantkiezer bij elke wissel opnieuw op, dus blijven
    // we kijken; markeer() doet niets meer zodra de markering er al staat.
    const kijken = (doel) => {
      this.favorietWacht = new MutationObserver(() => markeer());
      this.favorietWacht.observe(doel, { childList: true, subtree: true });
    };
    // Niet alleen fieldset: als keuzelijst staat de variantkiezer in een div.
    const veld = () => document.querySelector('.variant-option');
    if (markeer() && veld()) {
      kijken(veld().parentElement);
      return;
    }
    let pogingen = 0;
    const klok = setInterval(() => {
      if (markeer()) {
        clearInterval(klok);
        if (veld()) kijken(veld().parentElement);
      } else if (++pogingen > 20) {
        clearInterval(klok);
      }
    }, 200);
  }

  /**
   * De doekkeuze (standaard 115 g of brandvertragend B1) is een bijzaak naast
   * het formaat, maar het thema geeft elke variantoptie hetzelfde gewicht: twee
   * knoppen over de volle breedte. En de uitleg erover stond onderaan de
   * configurator, honderden pixels lager dan de keuze zelf.
   *
   * Hier wordt allebei rechtgezet: het fieldset krijgt een klasse waarmee de
   * opties compacte pillen worden, en het ingeklapte uitlegblokje verhuist naar
   * datzelfde fieldset. Lukt het niet, dan blijft de uitleg verborgen — dat is
   * beter dan een losse alinea ver van waar hij over gaat.
   */
  #verzorgDoekkeuze() {
    const rauw = this.dataset.doekIndex;
    // let op: Number('') is 0, en dat zou de formaatkeuze treffen
    if (rauw === undefined || rauw === '') return;
    const index = Number(rauw);
    if (!Number.isInteger(index)) return;

    // Het blokje losmaken en zelf vasthouden. Het thema bouwt de variantkiezer
    // bij elke variantwissel opnieuw op; stond het blokje dan nog in het oude
    // fieldset, dan verdween het met dat fieldset mee uit de pagina.
    const hint = this.querySelector('[data-doekhint]');
    if (hint) {
      hint.remove();
      hint.hidden = false;
      this.doekHint = hint;
    }

    const inrichten = () => {
      const veld = document.querySelectorAll('fieldset.variant-option')[index];
      if (!veld) return false;
      veld.classList.add('flagcfg-doek');
      if (this.doekHint && this.doekHint.parentElement !== veld) {
        veld.appendChild(this.doekHint);
      }
      return true;
    };

    // Na een herbouw opnieuw aankleden. De waarnemer legt geen lus aan: zodra
    // klasse en blokje op hun plek staan doet inrichten() niets meer.
    const kijken = (doel) => {
      this.doekWacht = new MutationObserver(() => inrichten());
      this.doekWacht.observe(doel, { childList: true, subtree: true });
    };

    if (inrichten()) {
      kijken(document.querySelectorAll('fieldset.variant-option')[index].parentElement);
      return;
    }
    // de variantkiezer kan later renderen dan de configurator
    let pogingen = 0;
    const klok = setInterval(() => {
      if (inrichten()) {
        clearInterval(klok);
        kijken(document.querySelectorAll('fieldset.variant-option')[index].parentElement);
      } else if (++pogingen > 20) {
        clearInterval(klok);
      }
    }, 200);
  }

  // ---------- gekozen formaat ----------

  /** Leest "150 × 100 cm" uit een varianttitel, in welke taal dan ook. */
  #leesMaat(tekst) {
    const m = /(\d{2,4})\s*[x×]\s*(\d{2,4})\s*cm/i.exec(tekst || '');
    return m ? { w: Number(m[1]), h: Number(m[2]) } : null;
  }

  #maatVan(variantId) {
    return this.#leesMaat(this.maten && this.maten[String(variantId)]);
  }

  #zetCanvas() {
    const { view } = this.layout;
    this.canvas.width = view.w * DPR;
    this.canvas.height = view.h * DPR;
    this.ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
  }

  /** Het tekenvlak bij de huidige keuze: soort, formaat, vorm en focusstand. */
  #nieuweLayout() {
    const verhouding = this.flagCm.w / this.flagCm.h;
    if (this.isSpandoek) return spandoekLayout(verhouding);
    if (this.probo && this.isBeach) return proboBeachLayout(this.probo);
    if (this.probo) return proboVlagLayout(this.probo, this.dubbel, this.focus);
    if (this.isBeach) return beachflagLayout();
    if (this.isWimpel) return wimpelLayout(verhouding);
    return bouwLayout(verhouding, this.dubbel, this.focus, this.compact);
  }

  /**
   * De compacte indeling van het tekenvlak: alleen in de ontwerptool, op een
   * smal scherm, bij een gewone enkelzijdige vlag (de andere soorten hebben hun
   * eigen indeling). `stil`: niet zelf tekenen, de aanroeper doet dat.
   */
  #controleerCompact(aan, stil) {
    const compact = !!aan && !this.dubbel && !this.probo && !this.isBeach && !this.isSpandoek && !this.isWimpel &&
      window.matchMedia('(max-width: 749px)').matches;
    if (compact === !!this.compact) return;
    this.compact = compact;
    this.classList.toggle('is-compact', compact);
    this.layout = this.#nieuweLayout();
    this.sides.forEach((side, i) => {
      side.rect = this.layout.flags[i];
    });
    if (!stil) {
      this.#zetCanvas();
      this.#render();
    }
  }

  /** Ander formaat gekozen: tekenvlak herbouwen, ontwerp blijft staan. */
  #zetMaat(cm) {
    this.flagCm = cm;
    this.layout = this.#nieuweLayout();
    this.#zetCanvas();
    this.sides.forEach((side, i) => {
      side.rect = this.layout.flags[i];
      // Een andere maat kan een andere vorm doek zijn (300 x 100 in plaats van
      // 150 x 100): dan horen er andere sjablonen bij.
      if (side.sjabloonKlaar && window.FLAGCFG_SJABLONEN) this.#bouwSjablonen(side);
    });
    this.#render();
  }

  /** Volgt de maatkeuze van de variantkiezer boven het formulier. */
  #bindVariant() {
    const bron = this.closest('.shopify-section') || document;
    bron.addEventListener('variant:update', (e) => {
      const id = e.detail && e.detail.resource && e.detail.resource.id;
      if (!id) return;

      if (this.isBeach || this.proboVlag) {
        this.#volgBeachVariant(id);
        return;
      }

      const cm = this.#maatVan(id);
      if (!cm || (cm.w === this.flagCm.w && cm.h === this.flagCm.h)) return;
      this.#zetMaat(cm);
    });
  }

  /**
   * Vangnet onder #bindVariant: houdt de preview gelijk aan het verborgen
   * variantveld van het formulier.
   *
   * Aanleiding is bestelling #1009: daar stond in de bestelling 120 x 90 cm
   * terwijl de plaatsing en het controlebeeld op 300 x 200 cm waren berekend.
   * De klant betaalde dus voor een andere maat dan waarop zijn ontwerp was
   * ingedeeld. Het `variant:update`-signaal bereikt de configurator blijkbaar
   * niet in elke situatie — een tweede kiezer buiten de sectie, een lade, een
   * herstelde pagina. Wat er ook gebeurt, `input[name="id"]` in het formulier
   * is wat er daadwerkelijk besteld wordt, dus daar kijken we naar.
   *
   * Een waarnemer op het attribuut vangt de meeste thema's; sommige zetten
   * alleen de eigenschap, en daarvoor is de trage controle erachter bedoeld.
   */
  #bindFormulier() {
    const formulier = this.closest('form[action*="/cart/add"]') || this.closest('form');
    const veld = formulier && formulier.querySelector('input[name="id"]');
    if (!veld) return;

    this.variantVeld = veld;
    let laatste = veld.value;

    const gelijktrekken = () => {
      if (veld.value === laatste) return;
      laatste = veld.value;

      if (this.isBeach || this.proboVlag) {
        this.#volgBeachVariant(veld.value);
        return;
      }

      const cm = this.#maatVan(veld.value);
      if (!cm || (cm.w === this.flagCm.w && cm.h === this.flagCm.h)) return;
      this.#zetMaat(cm);
    };

    new MutationObserver(gelijktrekken).observe(veld, { attributes: true, attributeFilter: ['value'] });
    formulier.addEventListener('change', () => setTimeout(gelijktrekken, 0));
    this.maatWacht = setInterval(gelijktrekken, 500);
  }

  disconnectedCallback() {
    clearInterval(this.maatWacht);
    if (this.escHandler) document.removeEventListener('keydown', this.escHandler);
    if (this.klaarZwevend) { this.klaarZwevend.remove(); this.klaarZwevend = null; }
    document.body.style.overflow = '';
    this.#sluitPreview();
    document.body.classList.remove('flagcfg-vol-open');
    this.#stapelVrij(false);
    if (this.doekWacht) this.doekWacht.disconnect();
    if (this.favorietWacht) this.favorietWacht.disconnect();
  }

  /** Landnamen in de taal van de bezoeker (valt terug op Engels) */
  #initNames() {
    try {
      this.names = new Intl.DisplayNames([this.dataset.locale || 'nl'], { type: 'region' });
    } catch (err) {
      this.names = null;
    }
  }

  /**
   * Volgorde van de kopjes in de databank. Thema's staan vooraan: wie een
   * landenvlag zoekt typt de naam, maar wie niet weet dat er themavlaggen
   * zijn kan die alleen vinden door ze te zien staan.
   *
   * Shopify vertelt ons uit welk land de klant bestelt. Koopt iemand vanuit
   * Belgie, dan komen de Belgische vlaggen voor de Nederlandse.
   *
   * Buiten Nederland (1 nov 2026, Tim: de landensites moeten als een lokale winkel
   * voelen) komen de Nederlandse provincies en gemeenten achteraan: een Fransman
   * zag anders als tweede kopje "Provinces néerlandaises". Nederland zelf blijft
   * precies zoals het was.
   */
  #groepvolgorde() {
    const land = (this.dataset.koperland || '').toUpperCase();
    if (land === 'NL' || land === '') return ['thema', 'provincie', 'gemeente', 'belgie', 'land'];
    if (land === 'BE') return ['thema', 'belgie', 'land', 'provincie', 'gemeente'];
    return ['thema', 'land', 'belgie', 'provincie', 'gemeente'];
  }

  #countryName(code, fallback) {
    if (this.names && /^[a-z]{2}$/i.test(code)) {
      try {
        const n = this.names.of(code.toUpperCase());
        if (n && n.toUpperCase() !== code.toUpperCase()) return n;
      } catch (err) {
        /* val terug op Engels */
      }
    }
    return fallback;
  }

  async #flags() {
    if (this.flagCache) return this.flagCache;
    try {
      const res = await fetch(this.dataset.flagList);
      const raw = await res.json();
      const taal = (this.dataset.locale || 'nl').slice(0, 2);
      this.flagCache = raw
        .map((f) => ({
          code: f.c,
          // Landnamen vertaalt de browser zelf; themavlaggen zijn geen land,
          // dus die staan vertaald in het bestand onder "t".
          label: (f.t && f.t[taal]) || this.#countryName(f.c, f.n),
          groep: f.g || 'land',
          zoek: f.z || '',
        }))
        .sort((a, b) => {
          const v = this.#groepvolgorde();
          const g = v.indexOf(a.groep) - v.indexOf(b.groep);
          if (g) return g;
          // De eigen landvlag bovenaan de landen (niet in Nederland, daar blijft alles zoals het was).
          const eigen = (this.dataset.koperland || '').toLowerCase();
          if (a.groep === 'land' && eigen && eigen !== 'nl') {
            const ea = a.code === eigen, eb = b.code === eigen;
            if (ea !== eb) return ea ? -1 : 1;
          }
          // Themavlaggen van één land (code "nl-prins", "nl-geus") horen bij dat land:
          // buiten Nederland achteraan bij de thema's, zodat een Fransman niet met
          // de Prinsenvlag begint (Tim, 1 nov 2026). Themavlaggen van het eigen land
          // komen juist vooraan, mochten die er ooit komen.
          if (a.groep === 'thema' && eigen && eigen !== 'nl') {
            const rang = (f) => {
              const m = /^([a-z]{2})-/.exec(f.code);
              if (!m) return 1;
              return m[1] === eigen ? 0 : 2;
            };
            const r = rang(a) - rang(b);
            if (r) return r;
          }
          return a.label.localeCompare(b.label, this.dataset.locale || 'nl');
        });
    } catch (err) {
      this.flagCache = [];
    }
    return this.flagCache;
  }

  #initSide(root, index) {
    const side = {
      root,
      index,
      rect: this.layout.flags[index],
      fileInput: root.querySelector('[data-file]'),
      posInput: root.querySelector('[data-pos]'),
      controls: root.querySelector('[data-controls]'),
      zoomInput: root.querySelector('[data-zoom]'),
      bgInput: root.querySelector('[data-bg]'),
      nameEl: root.querySelector('[data-filename]'),
      noteEl: root.querySelector('[data-note]'),
      libToggle: root.querySelector('[data-libtoggle]'),
      libPanel: root.querySelector('[data-libpanel]'),
      libSearch: root.querySelector('[data-libsearch]'),
      libToggleWb: root.querySelector('[data-libtoggle-wb]'),
      libBlok: root.querySelector('.flagcfg__library'),
      werkbalk: root.querySelector('[data-werkbalk]'),
      libGrid: root.querySelector('[data-libgrid]'),
      libEmpty: root.querySelector('[data-libempty]'),
      libValue: root.querySelector('[data-libvalue]'),
      qualityEl: root.querySelector('[data-quality]'),
      drukInput: root.querySelector('[data-druk]'),
      img: null,
      vector: false,
      ppi: 0,
      fileName: '',
      cx: 50,
      cy: 50,
      zoom: 1,
      stretch: false,
      bg: '#ffffff',
      objectUrl: null,
      // Tekstlagen. Elke laag: { id, tekst, font, kleur, grootte, hoek, cx, cy }
      // grootte is een percentage van de vlaghoogte, cx/cy percentages van het
      // doek — net als bij het ontwerp zelf, zodat alles meeschaalt met de maat.
      lagen: [],
      tekstLijst: root.querySelector('[data-tekstlijst]'),
      tekstKnop: root.querySelector('[data-tekstadd]'),
    };

    side.fileInput.addEventListener('change', () => this.#onFile(side));
    side.zoomInput.addEventListener('input', () => {
      side.zoom = Number(side.zoomInput.value) / 100;
      side.stretch = false;
      this.#render();
    });
    // Twee kleurkiezers voor dezelfde achtergrond: een bij de uploadknoppen
    // en een in de ontwerptool. Ze houden elkaar bij, anders staat de ene op
    // rood terwijl de vlag blauw is.
    side.bgInput2 = side.root.querySelector('[data-bg2]');
    const zetBg = (waarde, bron) => {
      side.bg = waarde;
      [side.bgInput, side.bgInput2].forEach((el) => {
        if (el && el !== bron) el.value = waarde;
      });
      // Een nieuwe achtergrond kan de tekst onleesbaar maken. Lagen die nog
      // op de standaardkleur staan schuiven mee; wie zelf een kleur koos
      // houdt die.
      side.lagen.forEach((l) => {
        if (l.type === 'kleur' || !l.kleur) return;
        if (l.kleur === '#000000' || l.kleur === '#ffffff') l.kleur = this.#leesbareKleur(side);
      });
      this.#tekenLagenLijst(side);
      this.#render();
    };
    // De wisknop alleen tonen als er iets te wissen valt. Als methode, want
    // een sjabloon zet de achtergrond ook — en die kwam hier niet langs, zodat
    // de knop uitbleef terwijl er wel degelijk een kleur stond.
    side.bgWis = side.root.querySelector('[data-bgwis]');
    const toonWis = () => this.#toonBgWis(side);

    side.bgInput.addEventListener('input', () => { zetBg(side.bgInput.value, side.bgInput); toonWis(); });
    if (side.bgInput2) {
      side.bgInput2.addEventListener('input', () => { zetBg(side.bgInput2.value, side.bgInput2); toonWis(); });
    }
    if (side.bgWis) {
      side.bgWis.addEventListener('click', () => {
        zetBg('#ffffff', null);
        toonWis();
      });
    }
    toonWis();

    // Alles wissen. Een bevestiging ertussen, want dit haalt in een tik weg
    // waar iemand tien minuten aan gewerkt heeft en er is geen stap terug.
    side.wisAlles = side.root.querySelector('[data-wisalles]');
    if (side.wisAlles) {
      side.wisAlles.addEventListener('click', () => {
        const vraag = this.dataset.tWisVraag || 'Alles wissen?';
        if (!window.confirm(vraag)) return;
        this.#wisAlles(side);
      });
    }
    // Kopieer naar de andere zijde, alleen bij dubbelzijdig (#vraagKopieer).
    side.kopieer = side.root.querySelector('[data-kopieer]');
    if (side.kopieer) {
      side.kopieer.addEventListener('click', () => this.#vraagKopieer(side));
    }
    this.#toonWisAlles(side);

    root.querySelectorAll('[data-act]').forEach((btn) => {
      btn.addEventListener('click', () => this.#action(side, btn.dataset.act));
    });
    // Achtergrond verwijderen, naast Vlag vullen en Midden (de gewone weergave).
    side.bgWegKnop = root.querySelector('[data-bgweg]');
    if (side.bgWegKnop) {
      side.bgWegKnop.addEventListener('click', () => (side.uitsnede ? this.#openUitsnede(side, null) : this.#verwijderAchtergrond(side, null)));
    }

    this.#bindLibrary(side);
    this.#bindTekst(side);
    return side;
  }

  // ---------- lagen: tekst en vormen ----------

  /**
   * De ontwerptool draait op de gewone vlaggen en sinds 8 september 2026 ook op
   * het spandoek. Daar wordt namelijk wél een drukbestand gemaakt op de echte
   * maat, dus wat de klant ziet is wat er gedrukt wordt.
   *
   * Bij beachflags blijft hij uit, en dat is geen kwestie van tijd: het doek is
   * gebogen en de printmaat in deze code (72 x 260 cm) is een schatting, geen
   * template van de fabriek. Er wordt daarom bewust geen drukbestand gemaakt.
   * Een ontwerp tonen dat zo niet gedrukt wordt is erger dan het niet
   * aanbieden. Zet dit niet aan zonder echte fabrieksmaten.
   */
  get tekstToegestaan() {
    // Uitzondering: beachflags van Probo. Daarvan staan de echte snijlijn en
    // snijmaat in probo-beachflag-vormen.js, dus daar kan het wel.
    return !this.isBeach || !!this.probo;
  }

  #bindTekst(side) {
    // Op producten zonder ontwerptool verdwijnt het hele blok, niet alleen de
    // knop — anders staat er een uitklapper die niets doet.
    if (!this.tekstToegestaan) {
      const blok = side.root.querySelector('.flagcfg__tekstblok');
      if (blok) blok.hidden = true;
      return;
    }
    if (side.tekstKnop) side.tekstKnop.addEventListener('click', () => this.#voegTekstToe(side));

    const eigen = side.root.querySelector('[data-eigenfile]');
    if (eigen) {
      eigen.addEventListener('change', () => {
        const bestand = eigen.files && eigen.files[0];
        if (bestand) this.#voegEigenToe(side, bestand);
        // leegmaken, anders kun je hetzelfde bestand niet twee keer kiezen
        eigen.value = '';
      });
    }


    const opener = side.root.querySelector('[data-tekstopen]');
    const paneel = side.root.querySelector('[data-tekstpaneel]');

    // Bij een dubbelzijdige vlag heeft alleen de laatste kant een openknop —
    // de ontwerptool bewerkt beide kanten tegelijk. De andere kant heeft dus
    // wel een paneel maar geen knop, en dat is geen reden om hieronder te
    // stoppen: de symbolenknop van die kant moet gewoon werken.
    if (opener && paneel) {
      opener.addEventListener('click', () => {
        paneel.hidden = false;
        opener.setAttribute('aria-expanded', 'true');
        opener.classList.add('is-open');
        // Bewust geen tekstlaag vooraf. Wie de tool opent hoort een lege vlag
        // te zien en zelf te kiezen wat erop komt; een "Jouw tekst" die er al
        // staat moet eerst weer weg voordat iemand kan beginnen.
        this.#laadSymbolen(side);
        this.#laadSjablonen(side);
        this.#volScherm(true);
        if (opener.dataset.modus === 'erbij') this.#wijsTekstKnop();
      });
    }

    this.#bindSymboolKnop(side);
  }

  /** De knop Symbolen in de werkbalk vouwt de bibliotheek open en dicht. */
  #bindSymboolKnop(side) {
    const knop = side.root.querySelector('[data-symboolopen]');
    const blok = side.root.querySelector('[data-symboolblok]');
    if (!knop || !blok) return;
    // Het blok begint dicht; de kiezer wordt pas gevuld als de bibliotheek
    // binnen is, en die komt bij het openen van de ontwerptool.
    knop.addEventListener('click', () => {
      const open = blok.hidden;
      blok.hidden = !open;
      knop.setAttribute('aria-expanded', String(open));
      // Maar één paneel tegelijk: gaat Symbolen open, dan gaan de vlaggen dicht.
      // Anders stonden beide knoppen zwart en twee lijsten onder elkaar.
      if (open && side.libPanel && !side.libPanel.hidden) {
        side.libPanel.hidden = true;
        this.#libAria(side, false);
      }
    });
  }

  /**
   * Zet de configurator over het hele scherm, of weer terug.
   *
   * Ontwerpen op een gewone productpagina betekent voortdurend heen en weer
   * schuiven: een knop onderin indrukken en dan naar boven om te kijken wat
   * er gebeurde. In dit scherm blijft de vlag bovenaan staan.
   *
   * Bewust geen aparte pagina: dan zouden ontwerp, maat en afwerking mee
   * moeten reizen en weer terug naar de winkelwagen. Dit blijft hetzelfde
   * formulier, dus er kan niets zoekraken.
   */
  /**
   * Zet --flagcfg-balkhoogte op de werkelijke hoogte van de balk bovenin, plus
   * een paar pixels lucht. De vlag hangt daaronder vast tijdens het scrollen.
   *
   * Waarom gemeten en niet vastgezet: de balkhoogte hangt af van het
   * lettertype, de taal en de lengte van de titel. Een vast getal klopt dan
   * precies zolang niemand er iets aan verandert.
   */
  #meetBalk() {
    const balk = this.querySelector('[data-volbalk]');
    if (!balk) return;
    // De keuze links/rechts/beide staat vlak onder de titel, naast de zwevende
    // Klaar-knop. Houd rechts zoveel vrij dat hij niet onder die knop schuift.
    // Gemeten, want "Klaar" is in elke taal anders breed.
    const keuze = balk.querySelector('[data-zijdekeuze]');
    if (keuze && !keuze.hidden && this.klaarZwevend) {
      const k = this.klaarZwevend.getBoundingClientRect();
      if (k.width) {
        const b = balk.getBoundingClientRect();
        const inhoudRechts = b.right - (parseFloat(getComputedStyle(balk).paddingRight) || 0);
        keuze.style.marginInlineEnd = Math.max(0, Math.ceil(inhoudRechts - k.left + 8)) + 'px';
      }
    }
    const h = balk.getBoundingClientRect().height;
    if (!h) return;
    this.style.setProperty('--flagcfg-balkhoogte', Math.round(h + 8) + 'px');
  }

  /**
   * Bepaalt of een element een eigen stapelcontext maakt. Dat is de reden dat
   * een z-index van 9999 soms toch verliest van een z-index van 8: die twee
   * getallen worden alleen met elkaar vergeleken als ze in dezelfde context
   * zitten. Zit de tool in een eigen context, dan telt alleen de plek van die
   * context zelf mee — hoe hoog het getal daarbinnen ook staat.
   */
  #maaktStapel(el) {
    const s = getComputedStyle(el);
    // sticky en fixed maken er altijd een, ook zonder z-index. Precies dit
    // deed de productkolom, en dat was de oorzaak.
    if (s.position === 'sticky' || s.position === 'fixed') return true;
    if (s.zIndex !== 'auto' && s.position !== 'static') return true;
    if (s.transform !== 'none' || s.filter !== 'none' || s.perspective !== 'none') return true;
    if (s.opacity !== '1' || s.isolation === 'isolate' || s.mixBlendMode !== 'normal') return true;
    if (s.willChange !== 'auto') return true;
    if (s.contain && /paint|layout|strict|content/.test(s.contain)) return true;
    if (s.containerType && s.containerType !== 'normal') return true;
    if (s.viewTransitionName && s.viewTransitionName !== 'none') return true;
    return false;
  }

  /**
   * Zorgt dat het volledige scherm ook echt bovenop ligt.
   *
   * De ontwerptool staat in de productkolom, en die kolom is `position: sticky`
   * zodat hij meeloopt bij het scrollen. Een sticky element maakt altijd een
   * eigen stapelcontext. Alles daarbinnen stapelt daardoor als een geheel, en
   * de menubalk bovenaan de winkel (z-index 8) kwam zo over het ontwerpscherm
   * heen te liggen. Zichtbaar zodra de menubalk vastgeplakt stond — vandaar dat
   * het de ene keer wel gebeurde en de andere keer niet.
   *
   * De voorouders die zo'n context maken krijgen zolang de tool openstaat zelf
   * een hoge z-index. Dat verschuift niets aan de opmaak en gaat bij het
   * sluiten exact terug naar wat er stond. Bewust niet het element verhuizen
   * naar de body: het staat in het bestelformulier, en daar mag niets tussen
   * de vlag en de bestelling in komen.
   */
  #stapelVrij(aan) {
    if (aan) {
      if (this.stapelHerstel) return;
      this.stapelHerstel = [];
      let p = this.parentElement;
      while (p && p !== document.body) {
        if (this.#maaktStapel(p)) {
          this.stapelHerstel.push({ el: p, z: p.style.zIndex, pos: p.style.position });
          // Zonder positie doet z-index niets; relatief zonder verschuiving
          // verandert de opmaak niet.
          if (getComputedStyle(p).position === 'static') p.style.position = 'relative';
          p.style.zIndex = '9999';
        }
        p = p.parentElement;
      }
    } else if (this.stapelHerstel) {
      this.stapelHerstel.forEach(({ el, z, pos }) => {
        el.style.zIndex = z;
        el.style.position = pos;
      });
      this.stapelHerstel = null;
    }
  }

  /**
   * Tekent een vlagzijde op een eigen doek, op elk gewenst formaat.
   *
   * Dezelfde som als het drukbestand, alleen kleiner: achtergrondkleur, de
   * geuploade afbeelding op zijn plek, en daarbovenop de lagen. Bewust een
   * aparte methode en niet meeliften op #maakDrukbestand — dat pad gaat naar
   * de drukker en daar hoort geen schermfunctie tussen te komen.
   */
  #previewDoek(side, breed, nr) {
    const r = side.rect;
    const hoog = Math.max(1, Math.round((breed * r.h) / r.w));
    const doek = document.createElement('canvas');
    doek.width = breed;
    doek.height = hoog;
    const d = doek.getContext('2d');
    d.imageSmoothingQuality = 'high';

    // De vergroting laat de vlag zien zoals hij geleverd wordt, dus uitgeknipt
    // in zijn eigen vorm (Jos, 20 september 2026: "wanneer je het vergroot wil
    // ik de vlagvorm zien die je koopt"). Eerder was dit altijd een rechthoek,
    // ook bij een kiosk-, boot- of beachflag. Het vak spiegelt mee, zodat de
    // achterkant van een dubbelzijdige kioskvlag klopt.
    const vak = { x: 0, y: 0, w: breed, h: hoog, spiegel: r.spiegel };
    const vorm = () => {
      d.beginPath();
      if (this.isBeach || this.proboVlag) {
        const p = vlagPad(this.vorm, vak);
        d.moveTo(p[0][0], p[0][1]);
        for (let i = 1; i < p.length; i++) d.lineTo(p[i][0], p[i][1]);
        d.closePath();
      } else {
        d.rect(0, 0, breed, hoog);
      }
    };

    d.save();
    try {
      vorm();
      d.clip();
      d.fillStyle = side.bg || '#ffffff';
      d.fillRect(0, 0, breed, hoog);
      if (side.img) {
        this.#clamp(side);
        const g = this.#geometry(side);
        const schaal = breed / r.w;
        d.drawImage(side.img, (g.x - r.x) * schaal, (g.y - r.y) * schaal, g.w * schaal, g.h * schaal);
      }
      this.#tekenLagen(d, side, vak, nr === undefined ? null : nr);
    } finally {
      d.restore();
    }
    return doek;
  }

  /**
   * Vlaggenlijn: de hele slinger in één beeld, alle vlaggetjes aan een koord (Jos, 18 okt 2026: "kan je
   * geen preview maken waarop je de gehele slinger ziet?"). Op een smal scherm in een paar rijen.
   */
  #slingerDoek(side, breed) {
    const N = this.#aantalVlaggetjes();
    const apart = this.#vlagApart(side);
    const max = window.innerWidth < 600 ? 5 : 8;
    const rijen = Math.ceil(N / max);
    const perRij = Math.ceil(N / rijen);
    const r = side.rect;
    const tussen = Math.round(breed * 0.012);
    const marge = Math.round(breed * 0.03);
    const pw = Math.floor((breed - 2 * marge - (perRij - 1) * tussen) / perRij);
    const ph = Math.round((pw * r.h) / r.w);
    const doorhang = Math.round(ph * 0.12);
    const rijHoog = ph + doorhang + Math.round(ph * 0.25);
    const doek = document.createElement('canvas');
    doek.width = breed;
    doek.height = Math.round(rijen * rijHoog + marge);
    const d = doek.getContext('2d');
    for (let rij = 0; rij < rijen; rij++) {
      const van = rij * perRij;
      const aantal = Math.min(perRij, N - van);
      const top = marge + rij * rijHoog;
      const totaal = aantal * pw + (aantal - 1) * tussen;
      const x0 = (breed - totaal) / 2;
      const yKoord = (x) => top + doorhang * Math.sin(Math.PI * Math.min(1, Math.max(0, (x - x0 + pw * 0.3) / (totaal + pw * 0.6))));
      d.strokeStyle = 'rgba(255,255,255,.75)';
      d.lineWidth = Math.max(2, breed / 700);
      d.beginPath();
      for (let x = x0 - pw * 0.3; x <= x0 + totaal + pw * 0.3; x += 6) { const y = yKoord(x); if (x === x0 - pw * 0.3) d.moveTo(x, y); else d.lineTo(x, y); }
      d.stroke();
      for (let k = 0; k < aantal; k++) {
        const x = x0 + k * (pw + tussen);
        const v = this.#previewDoek(side, pw, apart ? van + k + 1 : null);
        const y = yKoord(x + pw / 2);
        d.drawImage(v, x, y - ph * 0.04);
        v.width = 0;
        v.height = 0;
      }
    }
    return doek;
  }

  /** Valt er iets te bekijken? Een leeg wit vlak vergroten heeft geen zin. */
  #ietsTeZien() {
    return this.sides.some((s) => s.img || this.#heeftLagen(s));
  }

  #toonPreviewKnop() {
    const knop = this.querySelector('[data-previewknop]');
    if (!knop) return;
    // Beachflag heeft geen rechthoekig doek en geen ontwerptool; daar zou de
    // vergroting een andere vorm tonen dan wat er gedrukt wordt.
    const verberg = (this.isBeach && !this.probo) || !this.#ietsTeZien();
    if (knop.hidden !== verberg) knop.hidden = verberg;
    const ruimte = this.querySelector('[data-ruimteknop]');
    if (ruimte) {
      const weg = !this.#kanRuimte() || !this.#ietsTeZien();
      if (ruimte.hidden !== weg) ruimte.hidden = weg;
    }
  }

  /**
   * Toont de vlag zo groot als het scherm toelaat.
   *
   * Het venster hangt rechtstreeks aan de pagina met eigen stijlen, net als de
   * Klaar-knop. Reden: de opmaak zit in een gebundeld themabestand dat niet
   * altijd meteen ververst, en een vergroting die half opgemaakt over het
   * scherm hangt is erger dan geen vergroting. Zo hangt het nergens van af.
   */
  #openPreview() {
    if (this.previewVenster) return;
    if (!this.#ietsTeZien()) return;

    const breedScherm = window.innerWidth > window.innerHeight && this.sides.length > 1;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    // Ruimte per zijde: bij twee zijden naast elkaar krijgt elke zijde de helft.
    const perZijde = breedScherm ? window.innerWidth / 2 : window.innerWidth;
    const breed = Math.max(900, Math.min(2400, Math.round(perZijde * dpr)));

    const venster = document.createElement('div');
    venster.setAttribute('data-flagcfg-preview', '');
    venster.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:2147483647',
      'background:rgb(18 19 21 / .93)', 'display:flex', 'flex-direction:column',
      'align-items:center', 'justify-content:center', 'gap:14px',
      'padding:56px 16px 24px', 'box-sizing:border-box', 'overflow:auto',
    ].join(';');

    const rij = document.createElement('div');
    rij.style.cssText = [
      'display:flex', 'gap:18px',
      'align-items:center', 'justify-content:center',
      'flex-direction:' + (breedScherm ? 'row' : 'column'),
      'width:100%', 'height:100%',
    ].join(';');

    const dubbel = this.sides.length > 1;
    const slinger = this.probo && this.probo.vel;
    if (slinger) {
      const doek = this.#slingerDoek(this.sides[0], Math.max(900, Math.min(2400, Math.round(window.innerWidth * dpr))));
      doek.style.cssText = 'display:block;max-width:100%;max-height:82vh;width:auto;height:auto;filter:drop-shadow(0 8px 24px rgb(0 0 0 / .55))';
      rij.appendChild(doek);
    }
    (slinger ? [] : this.sides).forEach((side) => {
      const vak = document.createElement('div');
      // Naast elkaar krijgt elke kant hoogstens de helft, anders wipt de tweede
      // vlag van het scherm af.
      vak.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:7px;min-width:0;flex:0 1 auto;max-width:' +
        (breedScherm ? 'calc(50% - 9px)' : '100%');

      if (dubbel) {
        const titel = side.root.querySelector('.flagcfg__sidetitle');
        if (titel && titel.textContent.trim()) {
          const t = document.createElement('span');
          t.textContent = titel.textContent.trim();
          t.style.cssText = 'color:rgb(255 255 255 / .72);font:600 11px/1 system-ui,sans-serif;letter-spacing:.09em;text-transform:uppercase';
          vak.appendChild(t);
        }
      }

      const doek = this.#previewDoek(side, breed);
      // De hoogte per zijde beperken zodat twee kanten onder elkaar allebei
      // in beeld passen, ook op een telefoon.
      const hoogteRuimte = dubbel && !breedScherm ? '36vh' : '74vh';
      // Geen witte achtergrond achter het doek: bij een uitgeknipte vorm (kiosk,
      // boot, golf, beachflag) zou je anders alsnog een witte rechthoek zien.
      doek.style.cssText = [
        'display:block', 'max-width:100%', 'max-height:' + hoogteRuimte,
        'width:auto', 'height:auto', 'border-radius:3px',
        'filter:drop-shadow(0 8px 24px rgb(0 0 0 / .55))', 'background:transparent',
      ].join(';');
      vak.appendChild(doek);
      rij.appendChild(vak);
    });

    const sluit = document.createElement('button');
    sluit.type = 'button';
    sluit.textContent = this.dataset.tPreviewSluit || 'Sluiten';
    sluit.style.cssText = [
      'position:fixed', 'top:12px', 'right:12px', 'z-index:1',
      'min-height:44px', 'padding:10px 22px', 'border:none', 'border-radius:999px',
      'font:600 15px/1 system-ui, sans-serif', 'cursor:pointer',
      'background:#fff', 'color:#1c1c1c', 'box-shadow:0 2px 10px rgb(0 0 0 / .3)',
    ].join(';');
    sluit.addEventListener('click', () => this.#sluitPreview());

    venster.append(rij, sluit);
    // Klikken naast de vlag sluit ook; op de vlag zelf niet, anders sluit het
    // venster zodra iemand het beeld aanraakt om te zoomen.
    venster.addEventListener('click', (e) => {
      if (e.target === venster || e.target === rij) this.#sluitPreview();
    });

    // De zwevende Klaar-knop staat op dezelfde hoogte als de sluitknop en zou
    // er doorheen prikken.
    if (this.klaarZwevend) this.klaarZwevend.style.display = 'none';

    this.previewScrollTerug = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.appendChild(venster);
    this.previewVenster = venster;
    sluit.focus();
  }

  #sluitPreview() {
    if (!this.previewVenster) return;
    // De vergroting is tot 2400 px breed per zijde. Vrijgeven, anders blijft dat
    // geheugen op een iPhone nog een tijd bezet.
    this.previewVenster.querySelectorAll('canvas').forEach((c) => {
      c.width = 0;
      c.height = 0;
    });
    this.previewVenster.remove();
    this.previewVenster = null;
    document.body.style.overflow = this.previewScrollTerug || '';
    if (this.klaarZwevend) this.klaarZwevend.style.display = '';
  }

  #bindPreview() {
    const knop = this.querySelector('[data-previewknop]');
    if (!knop) return;
    knop.addEventListener('click', () => this.#openPreview());
  }

  /**
   * Vlag in 3D (30 okt 2026, Tim): een wapperende vlag met het ontwerp op beide kanten, om rond te
   * draaien. Alleen bij de gewone rechthoekige dubbelzijdige vlag: geen spiegelzijde (kiosk), geen
   * vlaggenlijn, geen beachflag. De 3D-code (assets/flag-3d.js + three.js) laadt pas na een tik.
   */
  #kanRuimte() {
    return this.#kanFocussen() && !this.isBeach && !(this.probo && this.probo.vel) &&
      !this.sides.some((s) => s.rect && s.rect.spiegel) && !!this.dataset.srcRuimte;
  }

  #bindRuimte() {
    const knop = this.querySelector('[data-ruimteknop]');
    if (!knop) return;
    knop.addEventListener('click', () => this.#openRuimte());
  }

  async #openRuimte() {
    if (this.vlagRuimte || this.ruimteLaden || !this.#kanRuimte() || !this.#ietsTeZien()) return;
    const knop = this.querySelector('[data-ruimteknop]');
    this.ruimteLaden = true;
    if (knop) knop.setAttribute('aria-busy', 'true');
    // Voorkant = de zijde rechts van de mast: van daaruit gezien staat de mast links, zoals in 3D.
    const titel = (s) => {
      const t = s.root && s.root.querySelector('.flagcfg__sidetitle');
      return (t && t.textContent.trim()) || '';
    };
    let voor = null;
    let achter = null;
    try {
      voor = this.#previewDoek(this.sides[1], 1536);
      achter = this.#previewDoek(this.sides[0], 1536);
      const afwerking = this.querySelector('[data-finishkeuze]');
      const { openVlag3d } = await import(this.dataset.srcRuimte);
      if (this.klaarZwevend) this.klaarZwevend.style.display = 'none';
      this.vlagRuimte = await openVlag3d({
        three: this.dataset.srcThree,
        voor,
        achter,
        maat: { w: this.flagCm.w, h: this.flagCm.h },
        afwerking: afwerking && !afwerking.disabled ? afwerking.value : '',
        teksten: {
          voor: titel(this.sides[1]) || '1',
          achter: titel(this.sides[0]) || '2',
          sluit: this.dataset.tPreviewSluit || 'Sluiten',
          hint: this.dataset.tRuimteHint || '',
          titel: this.dataset.tRuimte || '3D',
        },
        gesloten: () => {
          this.vlagRuimte = null;
          [voor, achter].forEach((c) => { if (c) { c.width = 0; c.height = 0; } });
          if (this.klaarZwevend) this.klaarZwevend.style.display = '';
          if (knop) knop.focus({ preventScroll: true });
        },
      });
    } catch (err) {
      // 3D lukt niet (geen WebGL, laden mislukt): dan de gewone vergroting, nooit niets.
      this.vlagRuimte = null;
      [voor, achter].forEach((c) => { if (c) { c.width = 0; c.height = 0; } });
      if (this.klaarZwevend) this.klaarZwevend.style.display = '';
      this.#openPreview();
    } finally {
      this.ruimteLaden = false;
      if (knop) knop.removeAttribute('aria-busy');
    }
  }

  /** Kan deze vlag in de focusstand? Alleen een gewone dubbelzijdige vlag. */
  #kanFocussen() {
    return !!(this.dubbel && !this.isSpandoek && !this.isBeach && this.sides && this.sides.length === 2);
  }

  /**
   * Zet de focusstand: een zijde groot in beeld (0 = links, 1 = rechts), of
   * beide zijden (null).
   *
   * Het ontwerp zelf verandert niet. Alles op de vlag staat in procenten van het
   * vlagvlak; alleen de indeling van het canvas wordt anders. Slepen, knijpen,
   * uitlijnen, hulplijnen en de schuifregelaars rekenen in die procenten en
   * werken dus op elk formaat hetzelfde. Een aanraking wordt omgerekend met de
   * werkelijke maat van het canvas op het scherm (naarVlak in #bindGestures),
   * dus ook dat schaalt vanzelf mee.
   */
  #zetFocus(focus) {
    if (!this.#kanFocussen() || !this.classList.contains('is-vol')) return;
    const nieuw = focus === 0 || focus === 1 ? focus : null;
    if (this.focus === nieuw) return;
    this.focus = nieuw;
    this.#zetMaat(this.flagCm);
    this.#toonFocus();
    // De bediening van de gekozen zijde van boven laten beginnen.
    this.scrollTop = 0;
  }

  /** Knoppen en panelen gelijk laten lopen met de focusstand. */
  #toonFocus() {
    const focus = this.#kanFocussen() && this.classList.contains('is-vol') ? this.focus : null;
    const inFocus = focus === 0 || focus === 1;
    this.classList.toggle('is-focus', inFocus);
    this.querySelectorAll('[data-focusknop]').forEach((knop) => {
      const waarde = knop.getAttribute('data-focusknop');
      const actief = waarde === 'beide' ? !inFocus : inFocus && Number(waarde) === focus;
      knop.setAttribute('aria-pressed', actief ? 'true' : 'false');
    });
    // Alleen de bediening van de zijde die in beeld is. Anders zet je een tekst
    // op een zijde die je op dat moment niet ziet. Rechtstreeks op het element,
    // net als de rest van het volledige scherm (zie #volScherm).
    if (this.sides) {
      this.sides.forEach((s, i) => {
        s.root.style.display = inFocus && i !== focus ? 'none' : '';
      });
    }
    // De vlag onder de balk kan een andere hoogte krijgen.
    this.#meetBalk();
  }

  #bindFocus() {
    this.querySelectorAll('[data-focusknop]').forEach((knop) => {
      knop.addEventListener('click', () => {
        const waarde = knop.getAttribute('data-focusknop');
        this.#zetFocus(waarde === 'beide' ? null : Number(waarde));
      });
    });
  }

  #volScherm(aan) {
    if (this.classList.contains('is-vol') === aan) return;
    this.classList.toggle('is-vol', aan);
    // Wie na de ontwerptool op Klaar tikt, hoort zijn ontwerp terug te zien en
    // geen dichte rij.
    if (aan && this.ontwerpRij) this.#klapRij(this.ontwerpRij, true);

    // Merkteken voor de rest van de winkel: zolang dit erop staat is het
    // ontwerpscherm open. De WhatsApp-knop verstopt zich daarop.
    document.body.classList.toggle('flagcfg-vol-open', aan);
    this.#stapelVrij(aan);

    this.sides.forEach((side) => this.#libVerhuis(side, aan));

    // De opmaak staat in een {% stylesheet %}-blok, en dat bundelt Shopify in
    // een themabestand dat niet altijd meteen ververst. Dan draait de bezoeker
    // met nieuwe code en oude opmaak, komt het volledige scherm niet op en
    // staat hij vast tussen de ontwerpknoppen zonder Klaar-knop. Wat het
    // scherm echt moet doen, zetten we daarom rechtstreeks op het element.
    const stijl = this.style;
    if (aan) {
      stijl.position = 'fixed';
      stijl.inset = '0';
      stijl.zIndex = '9999';
      stijl.margin = '0';
      stijl.overflowY = 'auto';
      stijl.background = 'var(--color-background, #fff)';
    } else {
      ['position', 'inset', 'zIndex', 'margin', 'overflowY', 'background'].forEach((n) => {
        stijl[n] = '';
      });
    }

    // Zelfde reden: de panelen mogen buiten dit scherm niet blijven staan.
    this.sides.forEach((s) => {
      const paneel = s.root.querySelector('[data-tekstpaneel]');
      if (paneel) paneel.style.display = aan ? '' : 'none';
    });

    this.#zwevendeKlaar(aan);
    this.#doekAanwijzer();

    // Ook de balk met de Klaar-knop krijgt zijn stand rechtstreeks mee, zodat
    // hij er staat ook als de gebundelde opmaak nog niet ververst is.
    const balk = this.querySelector('[data-volbalk]');
    if (balk) {
      balk.hidden = !aan;
      if (aan) {
        balk.style.display = 'flex';
        balk.style.position = 'sticky';
        balk.style.top = '0';
        balk.style.zIndex = '3';
        balk.style.alignItems = 'center';
        balk.style.justifyContent = 'space-between';
        balk.style.background = 'var(--color-background, #fff)';
        // Met de keuze links/rechts/beide erin minder lucht boven, onder en
        // tussen titel en keuze. Op een telefoon nam de balk anders bijna een
        // vijfde van het scherm in (Jos, 11 sep).
        const metKeuze = this.#kanFocussen();
        balk.style.padding = metKeuze ? '0.5rem 1rem' : '0.7rem 1rem';
        balk.style.rowGap = metKeuze ? '0.3rem' : '';
        balk.style.flexWrap = 'wrap';
      } else {
        balk.removeAttribute('style');
      }
      // De keuze links/rechts/beide staat in deze balk, zodat hij in beeld
      // blijft terwijl de klant door de bediening schuift.
      const keuze = balk.querySelector('[data-zijdekeuze]');
      if (keuze) keuze.hidden = !(aan && this.#kanFocussen());
    }

    // De vlag plakt vast onder deze balk. Meet hoe hoog hij werkelijk is in
    // plaats van een vast getal aan te houden: bij een langere titel of een
    // ander lettertype schoof de vlag anders onder de balk en werd de
    // bovenkant afgesneden.
    if (aan) {
      this.#meetBalk();
      // Draaien van de telefoon of het venster verslepen verandert de hoogte.
      this.balkMeter = () => {
        this.#meetBalk();
        // Telefoon gedraaid of venster smaller/breder: compacte indeling aan of uit.
        this.#controleerCompact(true, false);
      };
      window.addEventListener('resize', this.balkMeter);
      window.addEventListener('orientationchange', this.balkMeter);
    } else {
      if (this.balkMeter) {
        window.removeEventListener('resize', this.balkMeter);
        window.removeEventListener('orientationchange', this.balkMeter);
        this.balkMeter = null;
      }
      this.style.removeProperty('--flagcfg-balkhoogte');
    }

    if (aan) {
      // Bij een dubbelzijdige vlag horen beide kanten open te staan. De knop
      // om een kant te openen is in dit scherm verborgen, dus wie hier binnen
      // komt via de voorkant zou de achterkant nooit meer kunnen bereiken.
      this.sides.forEach((s) => {
        const paneel = s.root.querySelector('[data-tekstpaneel]');
        const opener = s.root.querySelector('[data-tekstopen]');
        if (paneel) paneel.hidden = false;
        if (opener) {
          opener.setAttribute('aria-expanded', 'true');
          opener.classList.add('is-open');
        }
        this.#laadSymbolen(s);
        this.#laadSjablonen(s);
      });
    }

    if (!aan) {
      // Buiten de ontwerptool geen gekozen element, dus geen kader over de vlag.
      this.sides.forEach((s) => { s.gekozenLaag = null; });
      // Terug naar de hoofdpagina: de bediening van de ontwerptool hoort daar
      // niet thuis. Het ontwerp zelf blijft staan — dat zit in de lagen, niet
      // in deze panelen — dus de klant ziet zijn vlag gewoon.
      this.sides.forEach((s) => {
        const paneel = s.root.querySelector('[data-tekstpaneel]');
        const opener = s.root.querySelector('[data-tekstopen]');
        const symBlok = s.root.querySelector('[data-symboolblok]');
        const symKnop = s.root.querySelector('[data-symboolopen]');
        if (paneel) paneel.hidden = true;
        if (opener) {
          opener.setAttribute('aria-expanded', 'false');
          opener.classList.remove('is-open');
        }
        if (symBlok) symBlok.hidden = true;
        if (symKnop) symKnop.setAttribute('aria-expanded', 'false');
      });
    }

    // Achtergrond niet mee laten schuiven; op iOS scrollt de pagina anders
    // door onder het venster.
    if (aan) {
      this.scrollTerug = window.scrollY;
      document.body.style.overflow = 'hidden';
      // Twee keer terug naar boven: meteen, en nog eens na een beeldopbouw.
      // De eerste keer staat de nieuwe hoogte soms nog niet vast, en dan
      // begint het scherm halverwege — met de balk buiten beeld.
      this.scrollTop = 0;
      requestAnimationFrame(() => {
        this.scrollTop = 0;
      });
    } else {
      document.body.style.overflow = '';
      if (typeof this.scrollTerug === 'number') window.scrollTo(0, this.scrollTerug);
    }

    // Een dubbelzijdige vlag begint in het ontwerpscherm op de linkerzijde,
    // groot in beeld. Bij het sluiten terug naar beide zijden: buiten dit
    // scherm hoort de vlag er helemaal te staan.
    if (this.#kanFocussen()) {
      this.focus = aan ? 0 : null;
      this.layout = this.#nieuweLayout();
      this.sides.forEach((side, i) => {
        side.rect = this.layout.flags[i];
      });
    }
    this.#controleerCompact(aan, true);
    this.#toonFocus();

    // Het doek is nu anders groot; opnieuw tekenen zodat het scherp blijft.
    this.#zetCanvas();
    this.#render();
  }

  /**
   * Zwevende Klaar-knop, rechtstreeks aan de pagina gehangen en niet aan de
   * configurator. Die knop is de enige uitweg uit het ontwerpscherm; hij mag
   * dus nergens van afhangen — niet van de gebundelde opmaak, niet van waar
   * de bezoeker gescrold heeft, en niet van de plek van het element in de
   * pagina. Alles staat rechtstreeks op de knop.
   */
  #zwevendeKlaar(aan) {
    if (aan) {
      if (this.klaarZwevend) return;
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = this.dataset.tVolKlaar || 'Klaar';
      // Versie in de zweefhulp, zodat bij twijfel te zien is of de nieuwste
      // code draait. Verdwijnt zodra dit uitgetest is.
      b.title = 'Ontwerptool ' + FLAGCFG_VERSIE;
      b.setAttribute('data-flagcfg-klaar', '');
      b.style.cssText = [
        'position:fixed', 'top:12px', 'right:12px', 'z-index:2147483647',
        'min-height:44px', 'padding:10px 22px', 'border:none', 'border-radius:999px',
        'font:600 15px/1 system-ui, sans-serif', 'cursor:pointer',
        'background:#1c1c1c', 'color:#fff', 'box-shadow:0 2px 10px rgb(0 0 0 / .25)',
      ].join(';');
      b.addEventListener('click', () => this.#volScherm(false));
      document.body.appendChild(b);
      this.klaarZwevend = b;
    } else if (this.klaarZwevend) {
      this.klaarZwevend.remove();
      this.klaarZwevend = null;
    }
  }

  #bindVolScherm() {
    this.querySelectorAll('[data-volsluit]').forEach((b) =>
      b.addEventListener('click', () => this.#volScherm(false))
    );
    // Ontsnappen met Escape hoort te werken; dat verwacht iedereen.
    this.escHandler = (e) => {
      if (e.key !== 'Escape') return;
      // Staat de vergroting open, dan sluit Escape eerst die - anders klapt het
      // hele ontwerpscherm dicht terwijl de klant alleen het beeld wegklikte.
      if (this.previewVenster) { this.#sluitPreview(); return; }
      // Idem voor "Uitsnede verbeteren": dat venster sluit zichzelf; de ontwerptool blijft open.
      if (this.uitsnedeVenster) return;
      if (this.classList.contains('is-vol')) this.#volScherm(false);
    };
    document.addEventListener('keydown', this.escHandler);
    this.#bindStappen();
  }

  /** Knoppen en sneltoetsen voor ongedaan maken, opnieuw en dupliceren. */
  #bindStappen() {
    this.querySelectorAll('[data-stap]').forEach((b) =>
      b.addEventListener('click', () => (b.dataset.stap === 'terug' ? this.#ongedaan() : this.#opnieuw())));
    this.stapToets = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || this.uitsnedeVenster) return;
      const el = e.target;
      // In een tekstveld doet ctrl+z wat de browser daar doet: tekst terugzetten.
      if (el && (el.isContentEditable || /^(TEXTAREA|SELECT)$/.test(el.tagName) ||
          (el.tagName === 'INPUT' && !/^(range|color|checkbox|radio|button)$/.test(el.type)))) return;
      const k = (e.key || '').toLowerCase();
      if (k === 'z' && !e.shiftKey) {
        if (!this.geschiedenis || this.stapPlek <= 0) return;
        e.preventDefault();
        this.#ongedaan();
      } else if ((k === 'z' && e.shiftKey) || k === 'y') {
        if (!this.geschiedenis || this.stapPlek >= this.geschiedenis.length - 1) return;
        e.preventDefault();
        this.#opnieuw();
      } else if (k === 'd' && this.classList.contains('is-vol')) {
        const side = this.sides.find((z) => z.gekozenLaag && z.lagen.includes(z.gekozenLaag));
        if (!side) return;
        e.preventDefault();
        this.#dupliceerLaag(side, side.gekozenLaag, true);
      }
    };
    document.addEventListener('keydown', this.stapToets);
  }

  /**
   * Haalt de symbolenbibliotheek pas op als iemand de ontwerptool opent.
   * Het bestand is een paar honderd kilobyte; dat wil je niet meesturen met
   * elke productpagina, waar de meeste bezoekers gewoon een bestand uploaden.
   */
  #laadSymbolen(side) {
    if (window.FLAGCFG_KLEUR_INDEX) {
      this.#bouwSymboolKiezer(side);
      return;
    }
    // Alleen het overzicht van categorieen; dat is twee kilobyte. De symbolen
    // zelf komen pas als de klant een categorie kiest.
    const url = this.dataset.kleurIndexUrl;
    if (!url) return;

    if (!FlagConfigurator.symbolenBelofte) {
      FlagConfigurator.symbolenBelofte = new Promise((klaar, mislukt) => {
        const s = document.createElement('script');
        s.src = url;
        s.onload = klaar;
        s.onerror = mislukt;
        document.head.appendChild(s);
      });
    }
    FlagConfigurator.symbolenBelofte
      .then(() => this.#bouwSymboolKiezer(side))
      .catch(() => {
        // Lukt het laden niet, dan blijft de rest gewoon werken. Beter een
        // tool zonder symbolen dan een kapotte tool.
        const knop = side.root.querySelector('[data-symboolopen]');
        if (knop) knop.hidden = true;
      });
  }

  /**
   * Zwart of wit, afhankelijk van waar de laag op komt te staan. Zonder dit
   * krijgt de eerste laag een vaste kleur, en dat is op de helft van de
   * vlaggen onzichtbaar — wit op een wit doek lijkt op een kapotte knop.
   */
  #leesbareKleur(side) {
    const hex = (side.bg || '#ffffff').replace('#', '');
    if (hex.length !== 6) return '#000000';
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    // waargenomen helderheid: groen telt zwaarder dan blauw
    const licht = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return licht > 0.55 ? '#000000' : '#ffffff';
  }

  /**
   * Staat er iets op deze zijde dat gedrukt moet worden? Een lege tekstlaag
   * telt niet mee, een vorm altijd wel — die heeft geen inhoud nodig.
   */
  #heeftLagen(side) {
    return !!(side.lagen && side.lagen.some((l) => l.type !== 'tekst' || l.tekst));
  }

  /** Nieuwe lagen zakken iets naar beneden, zodat ze niet op elkaar landen. */
  #vrijePlek(side) {
    const n = side.lagen.length;
    const basis = { cx: 50, cy: n ? Math.min(85, 50 + n * 12) : 50 };
    // Bij een uitgeknipte vorm hoort een nieuw element in het zichtbare deel te
    // beginnen: op een kiosk- of bootvlag viel het midden van het doek anders
    // buiten de vlag, en dan zie je niet wat je net toevoegde (Jos, 21 sep).
    const vak = this.#sjabloonVak(side);
    if (!vak) return basis;
    return {
      cx: vak.x + (basis.cx / 100) * vak.w,
      cy: vak.y + (basis.cy / 100) * vak.h,
    };
  }

  /**
   * Startmaat voor een nieuw element. Maten zijn een percentage van de
   * vlaghoogte; op een smalle beachflag zou 14% van 4,30 meter een letter van
   * 60 cm geven. Daarom rekenen we terug naar het zichtbare vlak.
   */
  #vrijeMaat(side, basis) {
    const vak = this.#sjabloonVak(side);
    return vak ? Math.max(3, Math.round(basis * (vak.h / 100))) : basis;
  }

  #nieuwId() {
    return 'l' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  }

  #voegTekstToe(side) {
    const plek = this.#vrijePlek(side);
    const l = {
      id: this.#nieuwId(),
      type: 'tekst',
      tekst: this.dataset.tTekstVoorbeeld || 'Jouw tekst',
      font: 'Arial, Helvetica, sans-serif',
      kleur: this.#leesbareKleur(side),
      grootte: this.#vrijeMaat(side, 14),
      hoek: 0,
      cx: plek.cx,
      cy: plek.cy,
    };
    this.#opVlaggetje(side, l);
    side.lagen.unshift(l);
    // Past de tekst niet in de breedte van het zichtbare vlak, dan meteen
    // kleiner maken in plaats van hem half buiten de vlag te laten beginnen.
    this.#pasTekstInBreedte(side);
    this.#tekenLagenLijst(side);
    // Meteen typen op de vlag, met de voorbeeldtekst geselecteerd: wat de klant
    // typt vervangt "Jouw tekst". Dit gebeurt binnen de klik op de knop Tekst,
    // anders opent een iPhone het toetsenbord niet.
    if (this.classList.contains('is-vol')) side.gekozenLaag = l;
    this.#render();
    if (this.classList.contains('is-vol')) this.#typOpVlag(side, l, true);
  }

  #verwijderLaag(side, id) {
    side.lagen.forEach((l) => { if (l.id === id && l.tekening) this.#elementKopieWeg(l); });
    const weg = side.lagen.find((l) => l.id === id);
    side.lagen = side.lagen.filter((l) => l.id !== id);
    // Een uitsnede (masker en samengesteld beeld) meteen vrijgeven; een
    // resultaat dat nog onderweg is, wordt daarna genegeerd.
    if (weg && (weg.uitsnede || weg.bgBezig)) this.#vergeetAchtergrond(weg, 'plaatje');
    this.#tekenLagenLijst(side);
    this.#render();
  }

  /** Rij knopjes met een voorbeeldje van elke vorm, getekend met dezelfde som. */
  /**
   * Bouwt de symbolenkiezer: een keuzelijst met categorieen en een raster met
   * de symbolen van de gekozen categorie. De symbolen van een categorie
   * worden pas opgehaald zodra iemand die categorie kiest.
   */
  #bouwSymboolKiezer(side) {
    const blok = side.root.querySelector('[data-symboolblok]');
    const keuze = side.root.querySelector('[data-symboolcat]');
    const raster = side.root.querySelector('[data-symboolraster]');
    if (!blok || !keuze || !raster) return;
    if (side.symboolKlaar) return;
    side.symboolKlaar = true;

    const index = window.FLAGCFG_KLEUR_INDEX;
    const knop = side.root.querySelector('[data-symboolopen]');
    if (!index || !index.length) {
      // Geen bibliotheek: dan ook geen knop die niets doet.
      if (knop) knop.hidden = true;
      return;
    }

    const taal = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
    keuze.textContent = '';
    // Twee groepen in de keuzelijst: de getekende elementen bovenaan, de emoji
    // eronder. Zonder kopje staan "Feest en verjaardag" en dergelijke er twee keer.
    const groepNamen = {
      el: { nl: 'Elementen', en: 'Elements', de: 'Elemente', fr: 'Éléments', es: 'Elementos', it: 'Elementi' },
      // Tweede reeks (13 okt 2026): eigen kopjes per tekenstijl.
      fl: { nl: 'Fijne lijnen', en: 'Fine line', de: 'Feine Linien', fr: 'Traits fins', es: 'Línea fina', it: 'Linea sottile' },
      dd: { nl: 'Doodles', en: 'Doodles', de: 'Doodles', fr: 'Doodles', es: 'Doodles', it: 'Doodle' },
      emoji: { nl: 'Emoji', en: 'Emoji', de: 'Emoji', fr: 'Emoji', es: 'Emoji', it: 'Emoji' },
    };
    let groepEl = null;
    let groep = null;
    index.forEach((c, i) => {
      const g = c.groep || 'emoji';
      if (g !== groep) {
        groep = g;
        groepEl = document.createElement('optgroup');
        groepEl.label = (groepNamen[g] && (groepNamen[g][taal] || groepNamen[g].nl)) || g;
        keuze.appendChild(groepEl);
      }
      const o = document.createElement('option');
      o.value = String(i);
      o.textContent = c.namen[taal] || c.namen.nl;
      groepEl.appendChild(o);
    });
    keuze.value = '0';

    const knopVoor = (sym) => {
      const knop = document.createElement('button');
      knop.type = 'button';
      knop.className = 'flagcfg__vormknop flagcfg__vormknop--kleur';
      const titel = (sym.namen && (sym.namen[taal] || sym.namen.nl)) || sym.t;
      knop.title = titel;
      knop.setAttribute('aria-label', titel);
      if (sym.tekening) {
        knop.appendChild(elementBeeld(sym.tekening, sym.kleur));
      } else {
        knop.innerHTML = sym.s;
      }
      const svg = knop.querySelector('svg');
      if (svg) {
        svg.setAttribute('width', '100%');
        svg.setAttribute('height', '100%');
      }
      knop.addEventListener('click', () => {
        if (sym.vorm) this.#voegVormToe(side, sym);
        else this.#voegKleurToe(side, sym);
      });
      return knop;
    };

    const vul = () => {
      const cat = index[Number(keuze.value) || 0];
      const klaar = window.FLAGCFG_KLEUR && window.FLAGCFG_KLEUR[cat.id];
      if (!klaar) {
        raster.textContent = '';
        const p = document.createElement('p');
        p.className = 'flagcfg__symboolladen';
        p.textContent = this.dataset.tSymboolLaden || 'Even geduld…';
        raster.appendChild(p);
        this.#laadKleurCategorie(cat, () => {
          // alleen bijwerken als de klant nog naar deze categorie kijkt
          const nu = index[Number(keuze.value) || 0];
          if (nu && nu.id === cat.id) vul();
        });
        return;
      }
      raster.textContent = '';
      window.FLAGCFG_KLEUR[cat.id].forEach((sym) => raster.appendChild(knopVoor(sym)));
    };

    keuze.addEventListener('change', vul);
    vul();
  }

  /** Haalt de symbolen van een kleurencategorie op; elke categorie apart. */
  #laadKleurCategorie(cat, klaar) {
    window.FLAGCFG_KLEUR = window.FLAGCFG_KLEUR || {};
    if (window.FLAGCFG_KLEUR[cat.id]) { klaar(); return; }

    FlagConfigurator.kleurBeloften = FlagConfigurator.kleurBeloften || {};
    if (!FlagConfigurator.kleurBeloften[cat.id]) {
      let urls = {};
      try {
        urls = JSON.parse(this.dataset.kleurUrls || '{}');
      } catch (err) {
        urls = {};
      }
      const url = urls[cat.id];
      if (!url) return;
      FlagConfigurator.kleurBeloften[cat.id] = new Promise((ok, fout) => {
        const s = document.createElement('script');
        s.src = url;
        s.onload = ok;
        s.onerror = fout;
        document.head.appendChild(s);
      });
    }
    FlagConfigurator.kleurBeloften[cat.id].then(klaar).catch(() => klaar());
  }

  /**
   * Kleurensymbolen hebben kleurverlopen en tientallen paden; die kun je niet
   * als pad natekenen. Ze worden daarom als afbeelding geladen en getekend.
   * Dat werkt ook op drukformaat: de browser rekent de vector uit op de maat
   * waarop hij getekend wordt, dus hij blijft scherp.
   *
   * Laden gaat niet meteen, dus na afloop opnieuw tekenen. Zonder dat blijft
   * het symbool onzichtbaar tot de klant toevallig iets anders aanraakt.
   */
  #laadPlaatje(l) {
    if (l.plaatje || l.plaatjeBezig || l.plaatjeStuk) return;
    l.plaatjeBezig = true;
    const img = new Image();
    img.onload = () => {
      l.plaatje = img;
      l.plaatjeBezig = false;
      this.#render();
    };
    img.onerror = () => {
      l.plaatjeBezig = false;
      l.plaatjeStuk = true;
    };
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(l.svg);
  }

  /**
   * Voegt een eigen afbeelding toe als element: verslepen en schalen zoals
   * een symbool, en je kunt er meerdere op een vlag zetten.
   *
   * Anders dan het uploadveld op de productpagina, dat er precies een neemt
   * en die over de hele vlag legt. Dat blijft zoals het was — een landenvlag
   * wil je nu eenmaal gewoon vullend.
   */
  #voegEigenToe(side, bestand, nrVlaggetje) {
    // Vlaggenlijn met elk vlaggetje anders: een foto komt op één vlaggetje (het gekozen, of het
    // opgegeven nummer) en vult dat, achter de tekst (Jos, 18 okt 2026: "die komt overal te staan").
    const apartNr = nrVlaggetje || (this.#vlagApart(side) && !side.vlagAlles ? this.vlagNr || 1 : 0);
    const plek = this.#vrijePlek(side);
    const l = {
      id: this.#nieuwId(),
      type: 'eigen',
      naam: bestand.naam || bestand.name,
      grootte: this.#vrijeMaat(side, 30),
      hoek: 0,
      cx: plek.cx,
      cy: plek.cy,
      verhouding: 1,
    };

    // Het beeld staat als blob in de browser; dat is genoeg voor de preview
    // en voor het drukbestand, want dat wordt hier getekend.
    const url = URL.createObjectURL(bestand);
    l.objectUrl = url;
    // Het bestand zelf blijft erbij, zodat het ontwerp later vanuit de
    // winkelmand opnieuw geopend kan worden (#momentopname). Geen eigenschap
    // voor het kenmerk: het beeld zelf komt al uit l.plaatje.
    l.bronBestand = bestand;
    // Een SVG is een tekening en blijft op elk formaat scherp. Zonder deze
    // markering zou de scherptemeting zijn intrinsieke maat (vaak 128 px)
    // aanzien voor de echte resolutie en onterecht waarschuwen.
    l.vector = /svg/i.test(bestand.type || '') || /\.svg$/i.test(l.naam || '');
    const img = new Image();
    img.onload = () => {
      l.plaatje = img;
      l.verhouding = img.naturalWidth / Math.max(img.naturalHeight, 1);
      if (l.vulVlaggetje && side.rect) {
        // Foto op een vlaggetje: het hele vlaggetje vullen, zonder te vervormen.
        delete l.vulVlaggetje;
        const rr = side.rect;
        l.grootte = Math.round(Math.max(100, (rr.w / rr.h) / l.verhouding * 100));
        l.cx = 50;
        l.cy = 50;
      }
      this.#tekenLagenLijst(side);
      this.#render();
    };
    img.onerror = () => {
      l.plaatjeStuk = true;
      URL.revokeObjectURL(url);
      this.#verwijderLaag(side, l.id);
    };
    img.src = url;

    if (apartNr) {
      side.lagen.filter((x) => x.vlaggetje === apartNr && x.type === 'eigen').forEach((x) => this.#verwijderLaag(side, x.id));
      l.vlaggetje = apartNr;
      l.vulVlaggetje = true;
      side.lagen.push(l);
    } else {
      side.lagen.unshift(l);
    }
    // Het bestand moet ook naar de drukker: die wil het origineel, niet
    // alleen ons drukbestand. Uploaden gebeurt op de achtergrond.
    this.#uploadEigen(side, l, bestand);
    this.#tekenLagenLijst(side);
    this.#render();
  }

  /** Voegt een landvlag uit de databank toe als element. */
  #voegVlagToe(side, flag) {
    const plek = this.#vrijePlek(side);
    const l = {
      id: this.#nieuwId(),
      type: 'eigen',
      naam: flag.label,
      vlagCode: flag.code,
      grootte: this.#vrijeMaat(side, 30),
      hoek: 0,
      cx: plek.cx,
      cy: plek.cy,
      verhouding: 1.5,
      // De databank bestaat uit tekeningen; die zijn altijd scherp.
      vector: true,
    };

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      l.plaatje = img;
      l.verhouding = img.naturalWidth / Math.max(img.naturalHeight, 1);
      this.#tekenLagenLijst(side);
      this.#render();
    };
    img.onerror = () => {
      l.plaatjeStuk = true;
      this.#verwijderLaag(side, l.id);
    };
    img.src = this.flagBase + 'flag-' + flag.code + '.svg';

    this.#opVlaggetje(side, l);
    side.lagen.unshift(l);
    // paneel sluiten, anders staat de landenlijst over je ontwerp heen
    if (side.libPanel) {
      side.libPanel.hidden = true;
      this.#libAria(side, false);
    }
    this.#tekenLagenLijst(side);
    this.#render();
  }

  /** Stuurt een element-afbeelding naar het uploadpunt en bewaart het adres. */
  async #uploadEigen(side, l, bestand) {
    if (!this.uploadUrl) return;
    try {
      const body = new FormData();
      body.append('file', bestand, bestand.name);
      const res = await fetch(this.uploadUrl, { method: 'POST', body });
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.url) {
        l.bronUrl = data.url;
        this.#output();
      }
    } catch (err) {
      /* Lukt het niet, dan staat het beeld nog wel in het drukbestand. */
    }
  }

  /* ---------- sjablonen ---------- */

  /**
   * Haalt de sjablonen op en bouwt de balk. Het bestand is een paar kilobyte en
   * wordt pas opgehaald zodra iemand de ontwerptool opent.
   */
  #laadSjablonen(side) {
    if (window.FLAGCFG_SJABLONEN) { this.#bouwSjablonen(side); return; }
    const url = this.dataset.sjabloonUrl;
    if (!url) return;

    if (!FlagConfigurator.sjabloonBelofte) {
      FlagConfigurator.sjabloonBelofte = new Promise((klaar, mislukt) => {
        const s = document.createElement('script');
        s.src = url;
        s.onload = klaar;
        s.onerror = mislukt;
        document.head.appendChild(s);
      });
    }
    // De vormen komen er meteen bij. Dat bestand is vier kilobyte, en zonder de
    // vormenlijst kan een vorm uit een sjabloon zijn naam en icoontje niet
    // opzoeken — dan staat er een naamloze regel in de lijst.
    const vormcat = (window.FLAGCFG_KLEUR_INDEX || []).find((c) => c.id === 'vormen');
    if (vormcat) this.#laadKleurCategorie(vormcat, () => this.#tekenLagenLijst(side));

    FlagConfigurator.sjabloonBelofte
      .then(() => this.#bouwSjablonen(side))
      .catch(() => {
        // Lukt het niet, dan blijft de tool gewoon werken zonder sjablonen.
        const balk = side.root.querySelector('[data-sjabloonbalk]');
        if (balk) balk.hidden = true;
      });
  }

  /**
   * Bouwt de sjabloonbalk. Die verschijnt alleen zolang de vlag nog leeg is:
   * een sjabloon is een startpunt, en wie al bezig is heeft er niets meer aan.
   * Zo komt er ook geen vierde knop bij in de werkbalk.
   */
  #bouwSjablonen(side) {
    const balk = side.root.querySelector('[data-sjabloonbalk]');
    if (!balk) return;

    // Alleen sjablonen die bij deze doekvorm passen. Een vlag is ongeveer
    // 1,5 : 1, een spandoek 2 : 1 tot 4 : 1. Een vlagsjabloon op een spandoek
    // zet alles als een postzegel in het midden met lege zijkanten; dat ziet
    // eruit als een fout. Zonder 'voor' hoort een sjabloon bij de vlaggen.
    //
    // 21 september 2026: er zijn twee soorten bijgekomen. 'smal' is voor de
    // beachflags (1 : 3 tot 1 : 5), 'punt' voor de kioskvlag, die naar boven
    // toe smaller wordt. De boot- en golfvlag lijken genoeg op een gewone vlag
    // om dezelfde sjablonen te gebruiken. Waar ze terechtkomen regelt
    // veiligVak(): dat houdt alles binnen de vorm en buiten de band.
    //
    // 5 oktober 2026: bij een doek (soort spandoek in de tool) bepaalt de
    // verhouding welke set past, want daar vallen nu ook staande en vierkante
    // producten onder: baniervlag, handdoek, backdrop. Zie #sjabloonSoort.
    const dezeVorm = this.#sjabloonSoort();
    const alle = window.FLAGCFG_SJABLONEN || [];
    // 6 oktober 2026: de sjablonen met getekende elementen (id 'el-...') vooraan.
    // sort() is stabiel, dus binnen beide groepen blijft de volgorde van het bestand.
    const lijst = alle.filter((s) => (s.voor || 'vlag') === dezeVorm || s.voor === 'beide')
      .sort((a, b) => (String(b.id).indexOf('el-') === 0) - (String(a.id).indexOf('el-') === 0));
    if (!lijst.length) { balk.hidden = true; return; }
    balk.hidden = false;
    if (side.sjabloonKlaar === dezeVorm) { this.#toonSjablonen(side); return; }
    side.sjabloonKlaar = dezeVorm;

    const taal = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
    const rij = balk.querySelector('[data-sjabloonrij]');
    rij.textContent = '';

    // Eerst "Geen": een leeg doek. Zonder deze knop moet iemand die per ongeluk
    // een sjabloon koos elk element apart weghalen voordat hij zelf kan
    // beginnen.
    const leeg = document.createElement('button');
    leeg.type = 'button';
    leeg.className = 'flagcfg__sjabloon flagcfg__sjabloon--leeg';
    const leegNaam = this.dataset.tSjabloonGeen || 'Geen';
    leeg.setAttribute('aria-label', leegNaam);
    const leegStaand = this.#staandeVorm();
    const leegDoek = document.createElement('canvas');
    this.#maatVoorbeeld(leegDoek, leegStaand);
    const lb = leegDoek.width;
    const lh = leegDoek.height;
    const lc = leegDoek.getContext('2d');
    lc.fillStyle = '#ffffff';
    lc.fillRect(0, 0, lb, lh);
    lc.strokeStyle = 'rgba(0,0,0,0.14)';
    lc.lineWidth = 1;
    lc.strokeRect(0.5, 0.5, lb - 1, lh - 1);
    // schuine streep, zoals bij "geen kleur"
    lc.strokeStyle = 'rgba(0,0,0,0.22)';
    lc.beginPath();
    lc.moveTo(lb * 0.17, lh * 0.78);
    lc.lineTo(lb * 0.83, lh * 0.22);
    lc.stroke();
    const leegLabel = document.createElement('span');
    leegLabel.textContent = leegNaam;
    leeg.append(leegDoek, leegLabel);
    leeg.addEventListener('click', () => this.#leegDeVlag(side));
    rij.appendChild(leeg);

    const teTekenen = [];
    lijst.forEach((sj) => {
      const knop = document.createElement('button');
      knop.type = 'button';
      knop.className = 'flagcfg__sjabloon';
      const naam = sj.namen[taal] || sj.namen.nl;
      knop.setAttribute('aria-label', naam);

      const doek = document.createElement('canvas');
      // Het voorbeeldje krijgt de verhouding van het echte doek, anders belooft
      // een vierkant plaatje iets anders dan de klant straks ziet. Bij een
      // staande vorm (beachflag) draait het voorbeeld dus mee.
      this.#maatVoorbeeld(doek, this.#staandeVorm());
      teTekenen.push([doek, sj]);

      const label = document.createElement('span');
      label.textContent = naam;

      knop.append(doek, label);
      knop.addEventListener('click', () => this.#pasSjabloonToe(side, sj));
      rij.appendChild(knop);
    });

    // De voorbeeldjes in porties tekenen, steeds hooguit 8 ms per beeldje. Met
    // ruim zestig sjablonen vol getekende elementen zou alles in één keer
    // tekenen het openen van de ontwerptool op een telefoon laten haperen.
    const portie = () => {
      const start = performance.now();
      while (teTekenen.length && performance.now() - start < 8) {
        const [doek, sj] = teTekenen.shift();
        try { this.#tekenSjabloonVoorbeeld(doek, sj); } catch (err) { /* één kapot voorbeeld mag de rest niet tegenhouden */ }
      }
      if (teTekenen.length) requestAnimationFrame(portie);
    };
    portie();

    this.#bindSjabloonKlap(side);
    this.#toonSjablonen(side);
  }

  /**
   * Verkleint tekstlagen van een pas geplaatst sjabloon tot ze op het doek
   * passen.
   *
   * De grootte van tekst is een percentage van de HOOGTE, maar of hij past
   * hangt af van de BREEDTE en van hoeveel letters er staan. Een sjabloon dat
   * mooi op een vlag van 1,5 : 1 staat, loopt op een spandoek van 4 : 1 aan
   * beide kanten van het doek af — "NU 50% KORTING" werd "50% KORT".
   *
   * Eén vaste maat per sjabloon kan dus niet kloppen: dezelfde tekst moet op
   * 200 x 100 en op 400 x 100 passen. Daarom hier meten en zo nodig
   * verkleinen. Alleen verkleinen — een sjabloon mag nooit groter worden dan
   * de ontwerper bedoelde.
   */
  #pasTekstInBreedte(side) {
    const r = side.rect;
    if (!r || !r.w || !r.h || !this.ctx) return;
    const MARGE = 0.9;                     // 90% van de breedte; de rest is lucht
    // Bij een uitgeknipte vorm telt niet de hele breedte maar alleen het
    // veilige vlak: op een beachflag is dat een smalle kolom.
    const vak = this.#sjabloonVak(side);
    const ruimte = (vak ? r.w * (vak.w / 100) : r.w) * MARGE;

    side.lagen.forEach((l) => {
      if (l.type !== 'tekst' || !l.tekst || l.verdeel) return;
      let px = (l.grootte / 100) * r.h;
      this.ctx.save();
      const breed = tekstOpmaak(this.ctx, l, px).breed;
      this.ctx.restore();
      // Een tekst uit een sjabloon kan een eigen grens hebben: de ruimte op het
      // lint of naast de taart. Een vertaling is vaak langer dan het Nederlands.
      const eigen = l.maxBreed ? Math.min(ruimte, r.w * (l.maxBreed / 100)) : ruimte;
      if (breed <= eigen) return;

      const factor = eigen / breed;
      const nieuw = Math.max(2, Math.floor(l.grootte * factor));
      l.grootte = nieuw;
    });
  }

  /**
   * Het veilige vlak voor sjablonen, of null bij een gewone rechthoekige vlag.
   * Zie veiligVak() bovenaan dit bestand.
   */
  #sjabloonVak(side) {
    if (!(this.probo || this.proboVlag) || !this.vorm) return null;
    const vak = veiligVak(this.vorm);
    if (!vak || (vak.w >= 99 && vak.h >= 99)) return null;
    // De achterkant van een dubbelzijdige kioskvlag staat in spiegelbeeld, dus
    // ligt het veilige vlak daar aan de andere kant van het doek.
    if (side && side.rect && side.rect.spiegel) {
      return { x: 100 - vak.x - vak.w, y: vak.y, w: vak.w, h: vak.h };
    }
    return vak;
  }

  /** Toont de wisknop zodra de achtergrond iets anders is dan wit. */
  #toonBgWis(side) {
    if (side.bgWis) {
      side.bgWis.hidden = (side.bg || '#ffffff').toLowerCase() === '#ffffff';
    }
  }

  /**
   * Haalt alles van de vlag en zet de achtergrond terug op wit.
   *
   * Het geuploade ontwerp blijft staan: dat heeft de klant zelf aangeleverd en
   * hoort niet weg te vallen doordat hij op "Geen" tikt.
   */
  #leegDeVlag(side) {
    side.lagen.forEach((l) => { if (l.tekening) this.#elementKopieWeg(l); });
    side.lagen = [];
    side.bg = '#ffffff';
    const veld = side.root.querySelector('[data-bg2]');
    if (veld) veld.value = '#ffffff';
    this.#toonBgWis(side);
    this.#tekenLagenLijst(side);
    this.#render();
  }

  /**
   * Toont de knop "alles wissen" alleen als er ook echt iets op de vlag staat.
   * Op een lege vlag zou het een knop zijn die niets doet, en dat is precies
   * het soort knop dat de tool onoverzichtelijk maakt.
   */
  #toonWisAlles(side) {
    const ietsErop = this.#heeftIetsErop(side);
    if (side.wisAlles) side.wisAlles.hidden = !ietsErop;
    // Kopieer naar de andere zijde verschijnt op hetzelfde moment.
    if (side.kopieer && side.kopieer.hidden === ietsErop) side.kopieer.hidden = !ietsErop;
  }

  /** Elementen, een vullende afbeelding of een andere achtergrond dan wit. */
  #heeftIetsErop(side) {
    return (
      (side.lagen && side.lagen.length > 0) ||
      !!side.img ||
      (side.bg || '#ffffff').toLowerCase() !== '#ffffff'
    );
  }

  /**
   * Maakt deze kant helemaal leeg en zet hem terug in de begintoestand.
   *
   * Anders dan #leegDeVlag gaat het geuploade ontwerp hier wel weg: de knop
   * heet "alles wissen", en dan is het gek als juist het grootste ding op de
   * vlag blijft staan. #wisOntwerp zet meteen ook alle velden terug die naar
   * de bestelling gaan, zodat er geen drukbestand van een gewiste vlag
   * achterblijft.
   */
  #wisAlles(side) {
    side.lagen.forEach((l) => { if (l.tekening) this.#elementKopieWeg(l); });
    side.lagen = [];
    side.sleepLaag = null;
    side.hulplijnen = null;
    this.#wisOntwerp(side);
    if (side.bgInput2) side.bgInput2.value = '#ffffff';
    this.#toonBgWis(side);
    this.#tekenLagenLijst(side);
    this.#render();
  }

  /**
   * Kopieer naar de andere zijde, na een bevestiging (Jos, 15 sep 2026). Staat
   * er op de andere zijde al iets, dan zegt de vraag er ook bij dat dat
   * vervangen wordt.
   */
  #vraagKopieer(bron) {
    if (!this.sides || this.sides.length !== 2) return;
    const doel = this.sides[1 - bron.index];
    if (!doel || !this.#heeftIetsErop(bron)) return;
    const knop = bron.kopieer;
    let vraag = (knop && knop.dataset.vraag) || 'Kopiëren naar de andere zijde?';
    if (this.#heeftIetsErop(doel) && knop && knop.dataset.vervang) vraag += '\n\n' + knop.dataset.vervang;
    if (!window.confirm(vraag)) return;

    // Eerst laten zien dat het gelukt is, dan pas kopieren. Het kopieren zelf
    // duurt op een telefoon een paar seconden: de andere zijde wordt opnieuw
    // opgebouwd en er komen twee drukbestanden van 8000 px. Deden we het meteen,
    // dan bevroor het scherm en leek de knop kapot (Jos, 21 september 2026).
    this.#meldGekopieerd(knop);
    setTimeout(() => this.#kopieerZijde(bron, doel), 60);
  }

  /**
   * Zet kort "Gekopieerd" op de knop. Bewust op de knop zelf en niet als
   * melding erboven: er komt geen element bij en je kijkt toch al naar je vinger.
   */
  #meldGekopieerd(knop) {
    if (!knop) return;
    const label = knop.querySelector('span');
    if (!label) return;
    if (knop.dataset.terug === undefined) knop.dataset.terug = label.textContent;
    label.textContent = knop.dataset.gelukt || 'Gekopieerd';
    knop.classList.add('is-gelukt');
    clearTimeout(knop.geluktTimer);
    knop.geluktTimer = setTimeout(() => {
      label.textContent = knop.dataset.terug;
      knop.classList.remove('is-gelukt');
    }, 1800);
  }

  /**
   * Zet het ontwerp van de ene zijde ook op de andere: de achtergrondkleur, de
   * vullende afbeelding met zijn plaats en grootte, en alle elementen. Alles
   * staat in procenten van het vlagvlak en beide zijden zijn even groot, dus
   * het komt precies op dezelfde plek.
   *
   * De andere zijde wordt eerst gewist met #wisOntwerp. Zo worden ook de velden
   * voor de bestelling (plaatsing, drukbestand) opnieuw opgebouwd, en krijgt de
   * drukker geen oud bestand.
   *
   * Beelden worden gedeeld, niet opnieuw ingelezen: een grote foto twee keer in
   * het geheugen is precies wat een iPhone niet trekt (zie "Lege vlag op de
   * iPhone"). #beeldGedeeld zorgt dat de ene zijde het beeld van de andere dan
   * niet vrijgeeft.
   */
  #kopieerZijde(bron, doel) {
    doel.lagen = [];
    doel.sleepLaag = null;
    doel.hulplijnen = null;
    doel.gekozenLaag = null;
    this.#wisOntwerp(doel);

    // Bij de dubbelzijdige kioskvlag staat de ene zijde in spiegelbeeld: de
    // stok zit daar aan de andere kant van het doek. Zetten we het ontwerp dan
    // één op één over, dan staat de eerste letter op de ene zijde bij de stok
    // en op de andere zijde er juist vandaan. Daarom spiegelen we de plaats:
    // de tekst blijft gewoon leesbaar, maar staat weer even ver van de stok.
    const spiegelen = !!(bron.rect && doel.rect) && !!bron.rect.spiegel !== !!doel.rect.spiegel;

    doel.bg = bron.bg || '#ffffff';
    [doel.bgInput, doel.bgInput2].forEach((el) => {
      if (el) el.value = doel.bg;
    });

    if (bron.img) {
      doel.img = bron.img;
      // Achtergrond verwijderd? Dan het origineel en het masker mee, zodat "Uitsnede verbeteren" en "Origineel herstellen" ook hier werken.
      doel.zonderAchtergrond = !!bron.zonderAchtergrond;
      doel.origBeeld = bron.origBeeld || null;
      doel.uitsnede = bron.uitsnede || null;
      doel.vector = !!bron.vector;
      doel.vlagCode = bron.vlagCode || null;
      doel.bronBestand = bron.bronBestand || null;
      doel.fileName = bron.fileName || '';
      doel.zoom = typeof bron.zoom === 'number' ? bron.zoom : 1;
      doel.cx = typeof bron.cx === 'number' ? (spiegelen ? 100 - bron.cx : bron.cx) : 50;
      doel.cy = typeof bron.cy === 'number' ? bron.cy : 50;
      doel.stretch = !!bron.stretch;
      if (doel.zoomInput) doel.zoomInput.value = String(Math.round(doel.zoom * 100));
      doel.nameEl.textContent = bron.nameEl.textContent;
      doel.libValue.value = bron.libValue.value;
      doel.libValue.disabled = bron.libValue.disabled;
      doel.controls.hidden = false;
      doel.noteEl.hidden = true;
      if (doel.vlagCode && doel.libGrid) {
        const knop = doel.libGrid.querySelector('[data-code="' + doel.vlagCode + '"]');
        if (knop) knop.classList.add('is-chosen');
      }
      // Een eigen bestand moet ook voor deze zijde mee naar de drukker. Staat
      // het al bij de uploadserver, dan gaat dezelfde link mee.
      if (!doel.vlagCode && doel.bronBestand) {
        if (bron.vooraf) {
          doel.vooraf = bron.vooraf;
          this.#schakelBestandsveld(doel);
        } else {
          try {
            const dt = new DataTransfer();
            dt.items.add(doel.bronBestand);
            doel.fileInput.files = dt.files;
          } catch (err) {
            /* oudere browser: dan is de upload hieronder de weg */
          }
          this.#alvastUploaden(doel, doel.bronBestand);
        }
      }
    }

    // Elk element als eigen kopie met een nieuwe id, zodat verschuiven op de ene
    // zijde de andere niet meeneemt. Het plaatje zelf wordt gedeeld.
    doel.lagen = (bron.lagen || []).map((l) => {
      const kopie = Object.assign({}, this.#laagGegevens(l), { id: this.#nieuwId() });
      if (spiegelen) {
        if (typeof kopie.cx === 'number') kopie.cx = 100 - kopie.cx;
        if (kopie.hoek) kopie.hoek = -kopie.hoek;
      }
      if (l.plaatje) kopie.plaatje = l.plaatje;
      if (l.origBeeld) kopie.origBeeld = l.origBeeld;
      if (l.uitsnede) kopie.uitsnede = l.uitsnede;
      if (l.plaatjeStuk) kopie.plaatjeStuk = true;
      if (l.bronBestand) kopie.bronBestand = l.bronBestand;
      if (!kopie.plaatje && kopie.type === 'kleur' && kopie.svg) this.#laadPlaatje(kopie);
      return kopie;
    });

    this.#toonBgWis(doel);
    this.#tekenLagenLijst(doel);
    this.#toonWisAlles(bron);
    this.#render();

    // In de focusstand meteen naar de zijde waar het ontwerp naartoe ging, zodat
    // de klant ziet dat het gelukt is.
    if (this.focus === bron.index) this.#zetFocus(doel.index);
  }

  /** Gebruikt de andere zijde hetzelfde beeld? Dan mag deze het niet vrijgeven. */
  #beeldGedeeld(side) {
    return !!(side.img && this.sides && this.sides.some((z) => z !== side && z.img === side.img));
  }

  /**
   * Klapt de sjabloonbalk open of dicht, en onthoudt dat.
   *
   * Bij een dubbelzijdige vlag staan er twee panelen onder elkaar, en dan nemen
   * twee volle sjabloonbalken te veel ruimte in — zeker op een telefoon. Daarom
   * begint hij daar dichtgeklapt; bij een enkelzijdige vlag gewoon open.
   */
  #bindSjabloonKlap(side) {
    const balk = side.root.querySelector('[data-sjabloonbalk]');
    if (!balk) return;
    const knop = balk.querySelector('[data-sjabloonklap]');
    const rij = balk.querySelector('[data-sjabloonrij]');
    if (!knop || !rij) return;
    // Bij een andere maat wordt de rij opnieuw gevuld; de klapknop maar één
    // keer koppelen, anders klapt hij bij één tik twee keer.
    if (knop.dataset.gekoppeld) return;
    knop.dataset.gekoppeld = '1';

    let open;
    try {
      const bewaard = localStorage.getItem(FlagConfigurator.SJABLOON_SLEUTEL);
      open = bewaard === null ? this.sides.length < 2 : bewaard === 'open';
    } catch (e) {
      open = this.sides.length < 2;
    }

    const zet = (nu) => {
      rij.hidden = !nu;
      knop.setAttribute('aria-expanded', nu ? 'true' : 'false');
      knop.classList.toggle('is-open', nu);
    };
    zet(open);

    knop.addEventListener('click', () => {
      open = !open;
      try {
        localStorage.setItem(FlagConfigurator.SJABLOON_SLEUTEL, open ? 'open' : 'dicht');
      } catch (e) { /* niet kunnen bewaren mag de knop niet breken */ }
      // beide kanten gelijk laten lopen
      this.sides.forEach((s) => {
        const b = s.root.querySelector('[data-sjabloonbalk]');
        if (!b) return;
        const k = b.querySelector('[data-sjabloonklap]');
        const r = b.querySelector('[data-sjabloonrij]');
        if (r) r.hidden = !open;
        if (k) {
          k.setAttribute('aria-expanded', open ? 'true' : 'false');
          k.classList.toggle('is-open', open);
        }
      });
    });
  }

  /** Sleutel waaronder de open of dichte stand van de sjabloonbalk staat. */
  static get SJABLOON_SLEUTEL() {
    return 'flagcfg-sjabloonbalk';
  }

  /**
   * Laat de sjabloonbalk staan zolang de ontwerptool open is.
   *
   * Eerder verdween hij zodra je iets koos, maar dan kun je geen tweede
   * sjabloon meer bekijken — en juist het vergelijken is waar zo'n balk voor
   * dient. Hij blijft dus staan; een volgende keuze vervangt de vorige.
   */
  #toonSjablonen(side) {
    const balk = side.root.querySelector('[data-sjabloonbalk]');
    if (balk) balk.hidden = !window.FLAGCFG_SJABLONEN;
  }

  /**
   * Tekent een voorbeeld met dezelfde code als de vlag zelf, zodat wat de klant
   * in de keuzebalk ziet altijd klopt met wat hij krijgt. Een met de hand
   * nagemaakt plaatje zou na elke wijziging in het sjabloon gaan afwijken.
   */
  #tekenSjabloonVoorbeeld(doek, sj) {
    const ctx = doek.getContext('2d');
    const b = doek.width;
    const h = doek.height;

    // Bij een uitgeknipte vorm het voorbeeld in die vorm tonen, en het sjabloon
    // in hetzelfde veilige vlak zetten als op de echte vlag. Zo belooft het
    // plaatje niets anders dan wat er na het aantikken staat.
    // Vlaggenlijnsjablonen (18 okt 2026) vullen het hele vlaggetje; de letters passen zichzelf in.
    const vakVorm = this.#sjabloonVak();
    const vak = sj.voor === 'vlaggetje' ? null : vakVorm;
    const vorm = () => {
      ctx.beginPath();
      if (vakVorm) {
        const p = vlagPad(this.vorm, { x: 0, y: 0, w: b, h: h });
        ctx.moveTo(p[0][0], p[0][1]);
        for (let i = 1; i < p.length; i++) ctx.lineTo(p[i][0], p[i][1]);
        ctx.closePath();
      } else {
        ctx.rect(0, 0, b, h);
      }
    };

    ctx.save();
    vorm();
    ctx.clip();
    ctx.fillStyle = sj.bg || '#ffffff';
    ctx.fillRect(0, 0, b, h);

    const vlakVerhouding = this.#doekVerhouding() * (vak ? vak.w / Math.max(1, vak.h) : 1);
    [...this.#sjabloonLagen(sj)].reverse().forEach((l) => {
      const cx = vak ? vak.x + (l.cx / 100) * vak.w : l.cx;
      const cy = vak ? vak.y + (l.cy / 100) * vak.h : l.cy;
      const maat = vak ? l.grootte * (vak.h / 100) : l.grootte;
      const ruimte = l.type === 'tekst' ? this.#sjabloonRuimte(sj, l, vlakVerhouding) : 0;
      l = Object.assign({}, l, { cx: cx, cy: cy, grootte: maat, ruimte });
      const x = (l.cx / 100) * b;
      const y = (l.cy / 100) * h;
      ctx.save();
      ctx.translate(x, y);
      if (l.hoek) ctx.rotate((l.hoek * Math.PI) / 180);
      if (l.type === 'vorm') {
        const hoog = (l.grootte / 100) * h;
        this.#tekenVorm(ctx, l, hoog * (l.verhouding || 1), hoog);
      } else if (l.type === 'kleur') {
        // In het voorbeeld het emoji-teken zelf tekenen. De echte vlag gebruikt
        // straks de nette Noto-tekening, maar daarvoor zou hier een bestand van
        // honderden kilobytes geladen moeten worden — voor een plaatje van
        // 120 bij 80 is dat de moeite niet waard.
        const px = (l.grootte / 100) * h;
        ctx.font = px + 'px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(l.teken, 0, 0);
      } else {
        // Dezelfde inperking als bij het echte doek (#pasTekstInBreedte):
        // anders belooft het voorbeeldje grote letters die na het aanklikken
        // kleiner blijken.
        let px = (l.grootte / 100) * h;
        const tl = Object.assign({}, l, { tekst: this.#sjabloonTekst(l) || '' });
        const breed = tekstOpmaak(ctx, tl, px).breed;
        const ruimte = (vak ? b * (vak.w / 100) : b) * (l.ruimte || 90) / 100;
        if (breed > ruimte) px = px * (ruimte / breed);
        tekenTekst(ctx, tl, px, l.kleur);
      }
      ctx.restore();
    });

    ctx.restore();

    ctx.strokeStyle = 'rgba(0,0,0,0.14)';
    ctx.lineWidth = 1;
    if (vakVorm) {
      vorm();
      ctx.stroke();
    } else {
      ctx.strokeRect(0.5, 0.5, b - 1, h - 1);
    }
  }

  /** Is het doek staand (hoger dan breed)? Dan draait ook het voorbeeldje mee. */
  #staandeVorm() {
    return this.#doekVerhouding() < 0.8;
  }

  /**
   * Breedte gedeeld door hoogte van het doek: bij een Probo-vorm uit de
   * werktekening, anders uit de gekozen maat. Een oude beachflag zonder
   * werktekening is staand.
   */
  #doekVerhouding() {
    const v = proboVorm(this.vorm);
    if (v) return v.doek.b / v.doek.h;
    if (this.isBeach) return 0.3;
    const cm = this.flagCm || STANDAARDMAAT;
    return cm.w / Math.max(cm.h, 1);
  }

  /**
   * Welke set sjablonen bij dit doek hoort. Beachflag en kioskvlag hebben een
   * eigen set voor hun vorm. Voor de rest beslist de verhouding: een tuinposter
   * van 150 x 100 krijgt de vlagsjablonen, dezelfde tuinposter in 300 x 100 de
   * spandoeksjablonen, een backdrop van 200 x 200 de vierkante.
   */
  #sjabloonSoort() {
    if (this.isBeach) return 'smal';
    if (String(this.vorm || '').indexOf('kiosk') === 0) return 'punt';
    // Vlaggenlijn (18 okt 2026): alleen de eigen sjablonen (Jos: "oude sjablonen mogen weg").
    if (this.probo && this.probo.vel) return 'vlaggetje';
    // Een staand vlaggetje krijgt de staande set.
    if (!this.isSpandoek) return this.#doekVerhouding() < 0.8 ? 'staand' : 'vlag';
    const r = this.#doekVerhouding();
    if (r >= 1.8) return 'spandoek';
    if (r >= 1.25) return 'vlag';
    if (r >= 0.8) return 'vierkant';
    if (r >= 0.4) return 'staand';
    return 'smal';
  }

  /**
   * Geeft een voorbeeldje van een sjabloon de verhouding van het echte doek, in
   * een vak van hoogstens 84 x 84 punten. Het tekenvlak is anderhalf keer zo
   * groot, voor scherpte op een telefoon.
   */
  #maatVoorbeeld(doek, staand) {
    const r = Math.min(Math.max(this.#doekVerhouding(), 0.25), 4);
    const b = staand ? Math.max(28, Math.round(84 * r)) : 84;
    const h = staand ? 84 : Math.max(24, Math.round(84 / r));
    doek.width = Math.round(b * 1.5);
    doek.height = Math.round(h * 1.5);
    doek.className = 'flagcfg__sjabloonbeeld' + (staand ? ' flagcfg__sjabloonbeeld--staand' : '');
    doek.style.width = b + 'px';
    doek.style.height = h + 'px';
  }

  /**
   * De lagen van een sjabloon met de teksten bovenop. In een paar oudere
   * sjablonen (Verjaardag, Actie, Sportclub streep, Korting op de beachflag)
   * stond de cirkel of balk vóór de tekst in de lijst, en de eerste laag ligt
   * bovenop: de tekst viel er dan onder weg (gezien 6 okt 2026, ook live).
   * sort() is stabiel, dus verder blijft de volgorde zoals hij was.
   */
  #sjabloonLagen(sj) {
    let lagen = sj.lagen;
    // Vlaggenlijnsjablonen zijn getekend voor de wimpel (letters hoog, waar de punt nog breed is). Op een
    // rechthoekig vlaggetje komen de letters in het midden en groter, en een versiering eronder
    // (Jos, 19 okt 2026: "de letters staan te hoog bij rechthoekige vlaggetjes").
    if (sj.voor === 'vlaggetje' && String(this.vorm || '').indexOf('rechthoek') >= 0) {
      lagen = lagen.map((l) => {
        if (l.type === 'tekst' && l.verdeel) return Object.assign({}, l, { cy: 48, grootte: Math.round(l.grootte * 1.6) });
        if (l.type === 'vorm' && l.grootte < 100 && l.cy > 50) return Object.assign({}, l, { cy: 80, grootte: Math.round(l.grootte * 0.9) });
        return l;
      });
    }
    return [...lagen].sort((a, b) => (b.type === 'tekst') - (a.type === 'tekst'));
  }

  /**
   * De voorbeeldtekst van een sjabloon in de taal van de bezoeker (Jos, 6 okt
   * 2026). De vertalingen staan in FLAGCFG_SJABLOON_TEKSTEN, onderaan
   * assets/flag-sjablonen.js, met de Nederlandse tekst als sleutel. Een naam of
   * een getal staat er niet in en blijft gewoon staan.
   */
  #sjabloonTekst(l) {
    const taal = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
    if (taal === 'nl') return l.tekst;
    const t = (window.FLAGCFG_SJABLOON_TEKSTEN || {})[l.tekst];
    return (t && t[taal]) || l.tekst;
  }

  /**
   * Hoe breed een tekst uit een sjabloon mag worden, in procenten van het vlak.
   * Een vertaling is vaak langer: "BON ANNIVERSAIRE" in plaats van "ABRAHAM".
   * Staat de tekst op iets (een lint, een zegel, in een krans), dan blijft hij
   * daarbinnen; staat er links of rechts een element, dan blijft hij ervan af.
   * Slingers, confetti en golven tellen niet: daar mag tekst overheen.
   * A is breedte gedeeld door hoogte van het vlak.
   */
  #sjabloonRuimte(sj, t, A) {
    const achtergrond = /^el-(confetti|sterretjes|hartjes|fonkelingen|serpentines|vuurwerk|golven|golf|hoekgolf|hoekboog|zonnestralen|halftoon|dambord|kwart-cirkel|halve-cirkel|vlaggenlijn.*|pompomslinger|ballonrand|bloemenrand|wolk|wolken|fl-p-.*|fl-confetti|fl-slinger|fl-hartjes|fl-sterren|fl-hoekbloemen|dd-confetti|dd-slinger|dd-vuurwerk)$/;
    // Tweede reeks (13 okt 2026): patronen, confetti en slingers liggen eronder; tekst mag erover.
    const binnenFactor = {
      'el-lint': 0.6, 'el-lint-gebogen': 0.55, 'el-banner': 0.8, 'el-banner-stiksel': 0.75, 'el-zegel': 0.72,
      'el-ster-zegel': 0.66, 'el-krans': 0.6, 'el-lauwerkrans': 0.58, 'el-hartkrans': 0.45, 'el-rozet': 0.5,
      'el-label': 0.55, 'el-ticket': 0.6, 'el-spreekballon': 0.85, 'el-denkwolk': 0.7,
      'el-fl-krans': 0.62, 'el-fl-bladerkader': 0.72, 'el-dd-tekstwolk': 0.8, 'el-dd-hart': 0.55,
      balk: 0.92, streep: 0.92, cirkel: 0.74, ring: 0.68, kader: 0.86, vierkant: 0.86, ster: 0.42, hart: 0.6, ruit: 0.5, vlag: 0.7,
    };
    const half = t.grootte / 2;
    let links = 5;
    let rechts = 95;
    let binnen = Infinity;
    sj.lagen.forEach((l) => {
      if (l === t || l.type === 'tekst') return;
      if (l.type === 'vorm' && achtergrond.test(l.vorm || '')) return;
      const bw = (l.grootte * (l.type === 'kleur' ? 1 : (l.verhouding || 1))) / Math.max(0.1, A);
      const x0 = l.cx - bw / 2;
      const x1 = l.cx + bw / 2;
      const y0 = l.cy - l.grootte / 2;
      const y1 = l.cy + l.grootte / 2;
      if (y1 < t.cy - half * 0.8 || y0 > t.cy + half * 0.8) return;
      if (t.cx > x0 && t.cx < x1 && t.cy > y0 && t.cy < y1) {
        binnen = Math.min(binnen, bw * (binnenFactor[l.vorm] || (l.type === 'kleur' ? 0.6 : 0.8)));
      } else if (x1 <= t.cx) {
        links = Math.max(links, x1 + 1);
      } else if (x0 >= t.cx) {
        rechts = Math.min(rechts, x0 - 1);
      }
    });
    return Math.max(15, Math.min(90, 2 * Math.min(t.cx - links, rechts - t.cx), binnen));
  }

  /**
   * Zet een sjabloon op de vlag. Alles blijft daarna gewoon aan te passen —
   * het is een beginpunt, geen eindresultaat.
   */
  #pasSjabloonToe(side, sj) {
    // Een sjabloon mag ook emoji bevatten. Die staan per categorie in een apart
    // bestand dat pas wordt opgehaald als het nodig is, dus eerst ophalen en
    // daarna pas plaatsen — anders staat er een gat waar het symbool hoort.
    const nodig = [...new Set(
      sj.lagen.filter((l) => l.type === 'kleur' && l.cat).map((l) => l.cat)
    )];

    // Bij een uitgeknipte vorm past het sjabloon zich aan het veilige vlak aan,
    // zodat niets in de punt of onder de band terechtkomt. Niet bij de vlaggenlijnsjablonen: die vullen
    // het hele vlaggetje en passen de letters zelf in (#pasLetter).
    const vak = sj.voor === 'vlaggetje' ? null : this.#sjabloonVak(side);

    const zetNeer = () => {
      side.lagen.forEach((l) => { if (l.tekening) this.#elementKopieWeg(l); });
      const vlakVerhouding = this.#doekVerhouding() * (vak ? vak.w / Math.max(1, vak.h) : 1);
      side.lagen = this.#sjabloonLagen(sj).map((l) => {
        const laag = Object.assign({}, l, { id: this.#nieuwId() });
        if (l.type === 'tekst') {
          laag.tekst = this.#sjabloonTekst(l);
          const ruimte = this.#sjabloonRuimte(sj, l, vlakVerhouding);
          if (ruimte < 90) laag.maxBreed = ruimte * (vak ? vak.w / 100 : 1);
        }
        if (vak) {
          laag.cx = vak.x + (l.cx / 100) * vak.w;
          laag.cy = vak.y + (l.cy / 100) * vak.h;
          laag.grootte = Math.max(2, l.grootte * (vak.h / 100));
        }
        if (l.type === 'kleur') {
          const pot = (window.FLAGCFG_KLEUR || {})[l.cat] || [];
          const sym = pot.find((x) => x.t === l.teken);
          if (!sym) return null;              // symbool niet gevonden: overslaan
          laag.svg = sym.s;
          delete laag.cat;
        }
        if (l.type === 'vorm' && String(l.vorm || '').indexOf('el-') === 0) {
          // Getekend element: de tekening staat één keer in het sjablonenbestand,
          // niet in elk sjabloon opnieuw.
          const el = (window.FLAGCFG_SJABLOON_TEKENINGEN || {})[l.vorm];
          if (!el) return null;
          laag.tekening = el.t;
          laag.verhouding = el.v;
          laag.teken = el.n;
          const taal = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
          laag.elementNaam = (el.namen && (el.namen[taal] || el.namen.nl)) || el.n;
          if (!laag.kleur) laag.kleur = '#000000';
        }
        return laag;
      }).filter(Boolean);

      if (sj.bg) {
        side.bg = sj.bg;
        const veld = side.root.querySelector('[data-bg2]');
        if (veld) veld.value = sj.bg;
      }
      this.#toonBgWis(side);
      this.#pasTekstInBreedte(side);
      side.lagen.forEach((l) => { if (l.type === 'kleur') this.#laadPlaatje(l); });
      this.#toonSjablonen(side);
      this.#tekenLagenLijst(side);
      this.#render();
    };

    if (!nodig.length) { zetNeer(); return; }

    const index = window.FLAGCFG_KLEUR_INDEX || [];
    let open = nodig.length;
    nodig.forEach((id) => {
      const cat = index.find((c) => c.id === id);
      if (!cat) { open -= 1; if (!open) zetNeer(); return; }
      this.#laadKleurCategorie(cat, () => {
        open -= 1;
        if (!open) zetNeer();
      });
    });
  }

  /**
   * Zet een vorm op de vlag.
   *
   * Krijgt een startkleur die op het doek te zien is: op een witte vlag zou een
   * witte vorm onzichtbaar zijn, en dan lijkt het alsof de knop niets deed.
   */
  #voegVormToe(side, sym) {
    const verhouding = sym.verhouding || 1;
    // Een breed element (lint, sierlijn) begint kleiner, anders steekt het
    // meteen buiten de vlag. Kaders en randen hebben hun eigen maat en plek.
    const basis = sym.maat || (sym.tekening ? Math.round(Math.max(10, Math.min(32, 110 / Math.max(1, verhouding)))) : 25);
    const grootte = this.#vrijeMaat(side, basis);
    const plek = this.#elementPlek(side, sym, grootte, verhouding) || this.#vrijePlek(side);
    const l = {
      id: this.#nieuwId(),
      type: 'vorm',
      vorm: sym.vorm,
      teken: sym.t,
      namen: sym.namen,
      verhouding,
      kleur: sym.tekening ? this.#elementStartKleur(side, sym) : this.#leesbareKleur(side),
      grootte,
      hoek: 0,
      cx: plek.cx,
      cy: plek.cy,
    };
    if (sym.tekening) {
      l.tekening = sym.tekening;
      const taal = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
      l.elementNaam = (sym.namen && (sym.namen[taal] || sym.namen.nl)) || sym.t;
    }
    this.#opVlaggetje(side, l);
    side.lagen.unshift(l);
    this.#tekenLagenLijst(side);
    this.#render();
  }

  /**
   * Waar een element begint dat bij een rand hoort: golven onderaan, een
   * vlaggenlijn bovenaan, een hoekgolf linksboven, een kader in het midden.
   * Bij een uitgeknipte vorm (kiosk, beachflag) niet: daar bepaalt het veilige
   * vlak de plek.
   */
  #elementPlek(side, sym, grootte, verhouding) {
    if (!sym.plek || this.#sjabloonVak(side)) return null;
    const breedte = (grootte * verhouding) / Math.max(0.2, this.#doekVerhouding());
    if (sym.plek === 'onder') return { cx: 50, cy: 100 - grootte / 2 };
    if (sym.plek === 'boven') return { cx: 50, cy: grootte / 2 };
    if (sym.plek === 'linksboven') return { cx: Math.min(50, breedte / 2), cy: grootte / 2 };
    if (sym.plek === 'midden') return { cx: 50, cy: 50 };
    return null;
  }

  /**
   * Startkleur van een kleurbaar element. Lijkt die te veel op de achtergrond
   * (een rood lint op een rode vlag), dan zwart of wit, zodat de klant meteen
   * ziet dat er iets is bijgekomen.
   */
  #elementStartKleur(side, sym) {
    if (!sym.kleur) return '#000000';
    const rgb = (h) => [1, 3, 5].map((i) => parseInt(String(h).slice(i, i + 2), 16) || 0);
    const a = rgb(sym.kleur), b = rgb(side.bg || '#ffffff');
    const afstand = Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
    return afstand < 90 ? this.#leesbareKleur(side) : sym.kleur;
  }

  #voegKleurToe(side, sym) {
    const plek = this.#vrijePlek(side);
    const l = {
      id: this.#nieuwId(),
      type: 'kleur',
      teken: sym.t,
      svg: sym.s,
      grootte: this.#vrijeMaat(side, 25),
      hoek: 0,
      cx: plek.cx,
      cy: plek.cy,
    };
    this.#opVlaggetje(side, l);
    side.lagen.unshift(l);
    this.#laadPlaatje(l);
    this.#tekenLagenLijst(side);
    this.#render();
  }

  /**
   * Slepen aan de greep verandert de volgorde. Bewust met pointer-events en
   * niet met de sleepfunctie van de browser: die werkt op een telefoon niet.
   *
   * Tijdens het slepen wordt de rij zelf in de lijst verplaatst en de laag in
   * de gegevens meeverschoven; de lijst wordt pas na afloop opnieuw opgebouwd.
   * Halverwege opnieuw opbouwen zou de rij onder je vinger vandaan halen.
   */
  #bindSleepVolgorde(side, rij, greep, laag) {
    const lijst = side.tekstLijst;
    if (!lijst) return;

    // De greep is maar een paar millimeter groot. Wie de rij ergens anders
    // vastpakt verwacht ook dat hij meekomt, dus de hele rij luistert mee —
    // behalve op de bedieningsonderdelen, want daar wil je schuiven en typen.
    rij.addEventListener('pointerdown', (e) => {
      const opBediening = e.target.closest('input, select, textarea, a') ||
                          (e.target.closest('button') && !e.target.closest('.flagcfg-tekst__greep'));
      if (opBediening) return;

      const startY = e.clientY;
      let bezig = false;

      try {
        rij.setPointerCapture(e.pointerId);
      } catch (err) {
        /* Vastpakken mag mislukken; slepen moet dan gewoon doorgaan. */
      }

      const verplaats = (y) => {
        const anderen = [...lijst.children].filter((r) => r !== rij);
        let voor = null;
        for (const r of anderen) {
          const b = r.getBoundingClientRect();
          if (y < b.top + b.height / 2) { voor = r; break; }
        }
        if (voor) lijst.insertBefore(rij, voor);
        else lijst.appendChild(rij);
      };

      const beweeg = (ev) => {
        // Pas slepen na een paar pixels. Zonder die drempel telt elke klik
        // als een minisleep en verspringt de lijst bij het aantikken.
        if (!bezig && Math.abs(ev.clientY - startY) < 5) return;
        if (!bezig) {
          bezig = true;
          rij.classList.add('is-sleep');
        }
        ev.preventDefault();
        verplaats(ev.clientY);
      };

      const stop = () => {
        rij.removeEventListener('pointermove', beweeg);
        rij.removeEventListener('pointerup', stop);
        rij.removeEventListener('pointercancel', stop);
        if (!bezig) return;
        rij.classList.remove('is-sleep');

        // volgorde in de gegevens gelijktrekken met wat er op het scherm staat
        const volgorde = [...lijst.children].map((r) => r.dataset.id);
        side.lagen.sort((a, b) => volgorde.indexOf(a.id) - volgorde.indexOf(b.id));
        this.#tekenLagenLijst(side);
        this.#render();
      };

      rij.addEventListener('pointermove', beweeg);
      rij.addEventListener('pointerup', stop);
      rij.addEventListener('pointercancel', stop);
    });
  }

  /* ---------- Ongedaan maken en opnieuw ----------------------------------------
   * Jos (11 okt 2026): "stel je verplaatst iets waardoor je heel je vlag verpest,
   * dan kan je hem weer terugspoelen". Na elke wijziging (en pas als de klant
   * even stilzit, dus niet tijdens het slepen) wordt de stand vastgelegd. De
   * stand bevat alleen getallen, teksten en verwijzingen naar dezelfde beelden:
   * geen kopieën van afbeeldingen, dus 40 stappen kosten vrijwel geen geheugen.
   */
  #staat() {
    return this.sides.map((s) => ({
      bg: s.bg || '#ffffff',
      zoom: s.zoom,
      cx: s.cx,
      cy: s.cy,
      stretch: !!s.stretch,
      img: s.img || null,
      vector: !!s.vector,
      vlagCode: s.vlagCode || null,
      bronBestand: s.bronBestand || null,
      fileName: s.fileName || '',
      vooraf: s.vooraf || null,
      objectUrl: s.objectUrl || null,
      origBeeld: s.origBeeld || null,
      uitsnede: s.uitsnede || null,
      zonderAchtergrond: !!s.zonderAchtergrond,
      naam: s.nameEl ? s.nameEl.textContent : '',
      lib: s.libValue ? s.libValue.value : '',
      libUit: s.libValue ? s.libValue.disabled : true,
      lagen: (s.lagen || []).map((l) => {
        const c = Object.assign({}, l);
        ['stipEl', 'stipNaNaam', 'icoonEl', 'bgBezig', 'plaatjeBezig'].forEach((k) => delete c[k]);
        return c;
      }),
    }));
  }

  #staatSleutel(st) {
    return JSON.stringify(st, (k, v) => {
      if (k === 'uitsnede' && v) return '#' + objectNummer(v);
      if (v && typeof v === 'object' && !Array.isArray(v) && Object.getPrototypeOf(v) !== Object.prototype) return '#' + objectNummer(v);
      return v;
    });
  }

  /** Na een wijziging: even wachten tot de klant stilzit, dan de stand vastleggen. */
  #planStap() {
    if (this.stapHerstellen || !this.sides) return;
    clearTimeout(this.stapKlok);
    this.stapKlok = setTimeout(() => this.#bewaarStap(), 450);
  }

  #bewaarStap() {
    if (this.stapHerstellen) return;
    // Midden in een gebaar, of een afbeelding die nog binnenkomt: straks opnieuw.
    const onderweg = this.sides.some((s) => (s.lagen || []).some((l) => l.type === 'eigen' && !l.plaatje && !l.plaatjeStuk));
    if (this.#bezigMetGebaar() || onderweg) {
      this.#planStap();
      return;
    }
    const st = this.#staat();
    const sleutel = this.#staatSleutel(st);
    const g = this.geschiedenis || (this.geschiedenis = []);
    if (g.length && g[this.stapPlek].sleutel === sleutel) return;
    g.splice(this.stapPlek + 1);
    g.push({ st: st, sleutel: sleutel });
    if (g.length > 40) g.shift();
    this.stapPlek = g.length - 1;
    this.#toonStapKnoppen();
    this.#planConcept();
  }

  /* ---------- Concept bewaren bij herladen ---------------------------------------
   * Klantreis-audit 1 nov 2026 (M01): wie de pagina herlaadt, of wiens telefoon het
   * tabblad op de achtergrond opnieuw laadt, was zijn ontwerp kwijt. Daarom wordt
   * het ontwerp na elke wijziging (rustig, na 1,5 s stilte) als concept per product
   * in de browser bewaard, met dezelfde opslag als "Ontwerp aanpassen" uit de
   * winkelmand (IndexedDB, inclusief geuploade bestanden). Bij openen komt het
   * terug, met een knop om opnieuw te beginnen. Staat het ontwerp in de winkelmand,
   * dan wordt het concept opgeruimd: dan is het niet meer "onderweg".
   */
  #conceptSleutel() {
    const form = this.closest('form');
    const product = form && form.querySelector('[name="product-id"]');
    return 'concept:' + ((product && product.value) || location.pathname.replace(/\/$/, ''));
  }

  #heeftOntwerp() {
    return !!this.sides && this.sides.some((s) => s.img || this.#heeftLagen(s));
  }

  #planConcept() {
    // Pas na de controle bij het openen (anders wist de lege beginstand het concept),
    // en niet als een regel uit de winkelmand wordt aangepast: die heeft zijn eigen opslag.
    if (!this.conceptAan || this.bewerk) return;
    clearTimeout(this.conceptKlok);
    this.conceptKlok = setTimeout(() => this.#bewaarConcept(), 1500);
  }

  async #bewaarConcept() {
    const sleutel = this.#conceptSleutel();
    if (!this.#heeftOntwerp()) {
      const o = await this.#opslag();
      if (o && o.verwijder) o.verwijder(sleutel);
      return;
    }
    // Net toegevoegd en sindsdien niets veranderd: niet opnieuw als concept bewaren.
    if (this.conceptToegevoegd && this.conceptToegevoegd === this.#vingerafdruk()) return;
    this.conceptToegevoegd = null;
    await this.#momentopname(sleutel, { zonderVoorbeeld: true });
  }

  async #herstelConcept() {
    try {
      const o = await this.#opslag();
      const concept = o ? await o.haal(this.#conceptSleutel()) : null;
      const vers = concept && concept.bewaard && Date.now() - concept.bewaard < 14 * 864e5;
      const inhoud = concept && (concept.zijden || []).some((z) => z && (z.vlagCode || z.bestand || (z.lagen || []).length));
      if (vers && inhoud && !this.#heeftOntwerp()) {
        await this.#herstelOntwerp(concept);
        this.#toonConceptMelding();
      }
    } catch (err) {
      /* een concept terugzetten is een extra; zonder begint de klant gewoon leeg */
    }
    this.conceptAan = true;
  }

  /** "Je vorige ontwerp is teruggezet" met een knop om leeg te beginnen. */
  #toonConceptMelding() {
    const tekst = this.dataset.tConceptTerug;
    if (!tekst) return;
    this.#toonBewerkMelding(tekst, false);
    const el = this.querySelector('[data-flagcfg-bewerkmelding]');
    if (!el || !this.dataset.tConceptWis) return;
    const knop = document.createElement('button');
    knop.type = 'button';
    knop.textContent = this.dataset.tConceptWis;
    knop.style.cssText = 'font:inherit;color:inherit;background:none;border:0;padding:.5rem .25rem;margin-left:.25rem;text-decoration:underline;cursor:pointer;min-height:44px';
    knop.addEventListener('click', async () => {
      this.conceptAan = false;
      clearTimeout(this.conceptKlok);
      const o = await this.#opslag();
      if (o && o.verwijder) await o.verwijder(this.#conceptSleutel());
      location.reload();
    });
    el.append(' ', knop);
  }

  /** Opnieuw beginnen (na het terugzetten van een ontwerp uit de winkelmand). */
  #wisGeschiedenis() {
    clearTimeout(this.stapKlok);
    this.geschiedenis = [];
    this.stapPlek = 0;
    this.#bewaarStap();
    this.#toonStapKnoppen();
  }

  #ongedaan() {
    // Een wijziging die nog niet is vastgelegd (de klant drukt meteen na het
    // slepen op terug): eerst vastleggen, anders sprong hij er één te ver terug.
    clearTimeout(this.stapKlok);
    this.#bewaarStap();
    if (!this.geschiedenis || this.stapPlek <= 0) return;
    this.stapPlek--;
    this.#zetStaat(this.geschiedenis[this.stapPlek].st);
  }

  #opnieuw() {
    clearTimeout(this.stapKlok);
    this.#bewaarStap();
    if (!this.geschiedenis || this.stapPlek >= this.geschiedenis.length - 1) return;
    this.stapPlek++;
    this.#zetStaat(this.geschiedenis[this.stapPlek].st);
  }

  #toonStapKnoppen() {
    const terug = !!this.geschiedenis && this.stapPlek > 0;
    const vooruit = !!this.geschiedenis && this.stapPlek < this.geschiedenis.length - 1;
    this.querySelectorAll('[data-stap]').forEach((b) => {
      const uit = b.dataset.stap === 'terug' ? !terug : !vooruit;
      if (b.disabled !== uit) b.disabled = uit;
    });
  }

  /** Staat dit beeld, deze uitsnede of deze link nog in een eerdere stap? Dan niet vrijgeven. */
  #inGeschiedenis(v) {
    if (!v || !this.geschiedenis) return false;
    return this.geschiedenis.some(({ st }) =>
      st.some((z) =>
        z.img === v || z.origBeeld === v || z.uitsnede === v || z.objectUrl === v ||
        z.lagen.some((l) => l.plaatje === v || l.origBeeld === v || l.uitsnede === v || l.objectUrl === v)));
  }

  #zetStaat(st) {
    this.stapHerstellen = true;
    try {
      this.sides.forEach((s, i) => {
        const z = st[i];
        if (!z) return;
        const anderBeeld = s.img !== z.img;
        Object.assign(s, {
          bg: z.bg, zoom: z.zoom, cx: z.cx, cy: z.cy, stretch: z.stretch, img: z.img, vector: z.vector,
          vlagCode: z.vlagCode, bronBestand: z.bronBestand, fileName: z.fileName, objectUrl: z.objectUrl,
          origBeeld: z.origBeeld, uitsnede: z.uitsnede, zonderAchtergrond: z.zonderAchtergrond, bgBezig: null,
        });
        [s.bgInput, s.bgInput2].forEach((el) => {
          if (el) el.value = s.bg;
        });
        if (s.zoomInput) s.zoomInput.value = String(Math.round((s.zoom || 1) * 100));
        if (anderBeeld) this.#zetHoofdbeeld(s, z);
        s.lagen = z.lagen.map((c) => Object.assign({}, c));
        s.gekozenLaag = null;
        s.sleepLaag = null;
        s.hulplijnen = null;
      });
      // Kopieën op schermformaat van elementen die er niet meer zijn opruimen.
      if (this.elementKopieen) {
        [...this.elementKopieen.keys()].forEach((l) => {
          if (!this.sides.some((s) => s.lagen.includes(l))) this.#elementKopieWeg(l);
        });
      }
      this.sides.forEach((s) => {
        this.#toonBgWis(s);
        this.#tekenLagenLijst(s);
      });
      this.#render();
    } finally {
      this.stapHerstellen = false;
    }
    this.#toonStapKnoppen();
  }

  /** De hoofdafbeelding van een zijde terugzetten, met alles wat er op de pagina bij hoort. */
  #zetHoofdbeeld(s, z) {
    s.uploadBeurt = (s.uploadBeurt || 0) + 1;
    s.vooraf = z.vooraf;
    s.drukKenmerk = null;
    s.drukPixels = null;
    if (s.libGrid) s.libGrid.querySelectorAll('.is-chosen').forEach((e) => e.classList.remove('is-chosen'));
    s.nameEl.textContent = z.naam;
    s.libValue.value = z.lib;
    s.libValue.disabled = z.libUit;
    const urlUit = () => {
      if (s.urlInput) {
        s.urlInput.value = '';
        s.urlInput.disabled = true;
      }
    };
    if (!s.img) {
      s.fileInput.value = '';
      s.fileInput.disabled = false;
      urlUit();
      s.controls.hidden = true;
      s.noteEl.hidden = true;
      if (s.qualityEl) s.qualityEl.hidden = true;
      s.posInput.value = '';
      s.posInput.disabled = true;
      if (s.drukInput) {
        s.drukInput.value = '';
        s.drukInput.disabled = true;
      }
      return;
    }
    s.controls.hidden = false;
    s.noteEl.hidden = true;
    if (s.vlagCode) {
      const knop = s.libGrid && s.libGrid.querySelector('[data-code="' + s.vlagCode + '"]');
      if (knop) knop.classList.add('is-chosen');
      s.fileInput.value = '';
      s.fileInput.disabled = false;
      urlUit();
    } else if (s.vooraf) {
      this.#schakelBestandsveld(s);
    } else if (s.bronBestand) {
      urlUit();
      s.fileInput.disabled = false;
      try {
        const dt = new DataTransfer();
        dt.items.add(s.bronBestand);
        s.fileInput.files = dt.files;
      } catch (err) {
        /* oudere browser: de upload hieronder is de weg */
      }
      this.#alvastUploaden(s, s.bronBestand);
    }
  }

  /* ---------- Dupliceren ----------------------------------------------------- */

  /**
   * Een kopie van een element, vóór het origineel (dus erbovenop), en meteen
   * gekozen. `verschuif`: iets opzij, zodat je ziet dat er twee zijn. `stil`:
   * lijst en tekening niet bijwerken (midden in een gebaar; dat doet het loslaten).
   */
  #dupliceerLaag(side, l, verschuif, stil) {
    if (!l || !side.lagen.includes(l)) return null;
    const kopie = Object.assign({}, l, { id: this.#nieuwId() });
    ['stipEl', 'stipNaNaam', 'icoonEl', 'bgBezig'].forEach((k) => delete kopie[k]);
    if (verschuif) this.#verschuifKopie(l, kopie);
    side.lagen.splice(side.lagen.indexOf(l), 0, kopie);
    side.gekozenLaag = kopie;
    if (!stil) {
      this.#tekenLagenLijst(side);
      this.#render();
    }
    return kopie;
  }

  /** Vier procent schuin naar rechtsonder; past dat niet meer op de vlag, dan naar linksboven. */
  #verschuifKopie(l, kopie) {
    const stap = 4;
    kopie.cx = l.cx + stap <= 98 ? l.cx + stap : Math.max(2, l.cx - stap);
    kopie.cy = l.cy + stap <= 98 ? l.cy + stap : Math.max(2, l.cy - stap);
  }

  /**
   * Het dupliceerknopje op het doek, naast het draaiknopje onder (of boven) het
   * gekozen element. Tikken geeft een kopie iets opzij; vasthouden en slepen
   * trekt de kopie er meteen uit.
   */
  #dupliceerknop(side, l) {
    const k = this.#draaiknop(side, l);
    if (!k) return null;
    const s = this.#viewPerPx();
    const hoek = ((l.hoek || 0) * Math.PI) / 180;
    return { x: k.x + 34 * s * Math.cos(hoek), y: k.y + 34 * s * Math.sin(hoek), richting: k.richting };
  }

  #dupliceerknopOpPunt(side, l, p) {
    if (!this.classList.contains('is-vol') || !side) return false;
    const k = this.#dupliceerknop(side, l);
    if (!k) return false;
    return Math.hypot(p.x - k.x, p.y - k.y) <= 15 * this.#viewPerPx();
  }

  #zijdeMetDupliceerknop(p) {
    if (!this.classList.contains('is-vol') || !this.tekstToegestaan) return null;
    const { view } = this.layout;
    return this.sides.find((s) => s.rect.x < view.w && this.#dupliceerknopOpPunt(s, s.gekozenLaag, p)) || null;
  }

  /** Tekent het dupliceerknopje: wit rondje met paarse rand en twee kaartjes erin. */
  #tekenDupliceerknop(ctx, side, l) {
    const k = this.#dupliceerknop(side, l);
    if (!k) return;
    const s = this.#viewPerPx();
    const r = 11 * s;
    ctx.save();
    ctx.beginPath();
    ctx.arc(k.x, k.y, r + 1.5 * s, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.22)';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(k.x, k.y, r, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.lineWidth = 2 * s;
    ctx.strokeStyle = SELECTIE_KLEUR;
    ctx.stroke();
    const z = 6 * s, o = 2 * s;
    ctx.lineWidth = 1.6 * s;
    ctx.lineJoin = 'round';
    const kaart = (x, y) => {
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(x, y, z, z, 1.5 * s);
      else ctx.rect(x, y, z, z);
    };
    kaart(k.x - z / 2 - o, k.y - z / 2 - o);
    ctx.stroke();
    kaart(k.x - z / 2 + o, k.y - z / 2 + o);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  /* ---------- Bijsnijden ----------------------------------------------------- */

  #openBijsnijden(side, l) {
    if (!l || !l.plaatje || this.uitsnedeVenster) return;
    const zwevend = this.klaarZwevend;
    if (zwevend) zwevend.style.visibility = 'hidden';
    const d = this.dataset;
    const oud = l.snB ? { x: l.snX, y: l.snY, b: l.snB, h: l.snH } : { x: 0, y: 0, b: 1, h: 1 };
    this.uitsnedeVenster = new BijsnijdVenster({
      beeld: l.plaatje,
      snede: l.snB ? oud : null,
      t: {
        bijsnijden: d.tBijsnijden || 'Bijsnijden',
        klaar: d.tUitKlaar || 'Klaar',
        bsUitleg: d.tBsUitleg || 'Sleep de hoeken of de randen. Het kader zelf kun je verschuiven.',
        bsVrij: d.tBsVrij || 'Vrij',
        bsVierkant: d.tBsVierkant || 'Vierkant',
        bsLiggend: d.tBsLiggend || 'Liggend',
        bsStaand: d.tBsStaand || 'Staand',
        bsHeel: d.tBsHeel || 'Hele afbeelding',
      },
      bijKlaar: (sn) => {
        this.uitsnedeVenster = null;
        if (zwevend) zwevend.style.visibility = '';
        if (!side.lagen.includes(l)) return;
        const nieuw = sn || { x: 0, y: 0, b: 1, h: 1 };
        const p = l.plaatje;
        const iw = p instanceof HTMLImageElement ? p.naturalWidth : p.width;
        const ih = p instanceof HTMLImageElement ? p.naturalHeight : p.height;
        // Wat zichtbaar blijft, blijft op dezelfde plek en even groot: de hele
        // afbeelding houdt dezelfde schaal, alleen de rand valt weg.
        const r = side.rect;
        const volHoog = l.grootte / oud.h; // hoogte van de hele afbeelding in % van de vlag
        const volHoogPx = (volHoog / 100) * r.h;
        const volBreedPx = volHoogPx * (iw / Math.max(1, ih));
        const dx = (nieuw.x + nieuw.b / 2 - (oud.x + oud.b / 2)) * volBreedPx;
        const dy = (nieuw.y + nieuw.h / 2 - (oud.y + oud.h / 2)) * volHoogPx;
        const a = ((l.hoek || 0) * Math.PI) / 180;
        l.cx += ((dx * Math.cos(a) - dy * Math.sin(a)) / r.w) * 100;
        l.cy += ((dx * Math.sin(a) + dy * Math.cos(a)) / r.h) * 100;
        l.grootte = Math.max(4, Math.min(300, Math.round(volHoog * nieuw.h * 10) / 10));
        if (sn) Object.assign(l, { snX: sn.x, snY: sn.y, snB: sn.b, snH: sn.h });
        else ['snX', 'snY', 'snB', 'snH'].forEach((k) => delete l[k]);
        l.verhouding = (iw * (l.snB || 1)) / Math.max(1, ih * (l.snH || 1));
        this.#tekenLagenLijst(side);
        this.#render();
      },
    });
  }

  /**
   * Achtergrond verwijderen bij een geüploade afbeelding: de hoofdafbeelding
   * (l leeg) of een eigen afbeelding als element (l). Niet bij vectoren en
   * landvlaggen: die hebben geen achtergrond om weg te halen.
   */
  #kanUitsnede(side, l) {
    const vector = l ? l.vector || l.vlagCode : side.vector || side.vlagCode;
    return !!(l ? l.plaatje : side.img) && !vector;
  }

  #bgSleutelVan(side, l) {
    return l ? 'l' + l.id : 'z' + side.index;
  }

  /** De teksten (data-attributen uit de snippet, Nederlands als reserve). */
  #bgTeksten() {
    const d = this.dataset;
    return {
      weg: d.tBgWeg || 'Achtergrond verwijderen',
      bezig: d.tBgBezig || 'Verwerking bezig…',
      verbeter: d.tBgVerbeter || 'Uitsnede verbeteren',
      origineel: d.tBgTerug || 'Origineel herstellen',
      gelukt: d.tBgKlaar || 'Achtergrond verwijderd.',
      mislukt: d.tBgFout || 'Verwerking mislukt.',
      opnieuw: d.tBgOpnieuw || 'Opnieuw proberen',
      al: d.tBgAl || 'Deze afbeelding heeft al een doorzichtige achtergrond.',
      geen: d.tBgGeen || 'Hier vonden we geen onderwerp om uit te knippen.',
      herstel: d.tUitHerstel || 'Herstelpenseel',
      wis: d.tUitWis || 'Verwijderpenseel',
      maat: d.tUitMaat || 'Penseelgrootte',
      terug: d.tUitTerug || 'Correctie ongedaan maken',
      vooruit: d.tUitVooruit || 'Correctie opnieuw uitvoeren',
      vergelijk: d.tUitVergelijk || 'Houd ingedrukt om het origineel te zien',
      wisAlles: d.tUitWisalles || 'Alle correcties wissen',
      klaar: d.tUitKlaar || 'Klaar',
      uitleg: d.tUitUitleg || 'Schilder over wat terug moet of weg moet. Zoomen: knijpen of scrollen.',
    };
  }

  /** Waar de worker het model en ONNX Runtime vandaan haalt. */
  #bgCfg() {
    const model = this.dataset.bgModel ? new URL(this.dataset.bgModel, location.href).href : '';
    return { ort: BG_ORT, model: model, gpu: false };
  }

  /**
   * Knoppen in de rij van de afbeelding in de ontwerptool: eerst "Achtergrond
   * verwijderen", daarna "Uitsnede verbeteren" met "Origineel herstellen".
   */
  #achtergrondKnop(side, l) {
    if (!this.#kanUitsnede(side, l) || !this.dataset.bgModel) return null;
    const doel = l || side;
    const t = this.#bgTeksten();
    const bezig = !!doel.bgBezig;
    const klaar = !!doel.uitsnede && !bezig;
    const knop = document.createElement('button');
    knop.type = 'button';
    knop.className = 'flagcfg-tekst__bgweg';
    knop.dataset.bgdoel = this.#bgSleutelVan(side, l);
    knop.innerHTML = (bezig ? BG_DRAAIER : BG_STAF) + '<span></span>';
    knop.querySelector('span').textContent = bezig ? t.bezig : klaar ? t.verbeter : t.weg;
    knop.disabled = bezig;
    if (bezig) knop.setAttribute('aria-busy', 'true');
    knop.addEventListener('click', () => (doel.uitsnede ? this.#openUitsnede(side, l) : this.#verwijderAchtergrond(side, l)));
    if (!klaar) return [knop];
    const terug = document.createElement('button');
    terug.type = 'button';
    terug.className = 'flagcfg-tekst__midden';
    terug.textContent = t.origineel;
    terug.addEventListener('click', () => this.#herstelOrigineel(side, l));
    return [knop, terug];
  }

  /**
   * De AI (of het weghalen op kleur) in de worker laten rekenen. De ontwerptool
   * blijft intussen gewoon bruikbaar; is de afbeelding inmiddels weg of
   * vervangen, dan wordt het resultaat genegeerd.
   */
  async #verwijderAchtergrond(side, l) {
    const doel = l || side;
    const veld = l ? 'plaatje' : 'img';
    const bron = doel[veld];
    if (!bron || doel.bgBezig || doel.uitsnede) return;
    const beurt = {};
    doel.bgBezig = beurt;
    if (this.bgMeldingEl) this.bgMeldingEl.remove();
    this.#tekenLagenLijst(side);
    let uit = null;
    try {
      const inv = bgInvoer(bron, BG_INVOER_MAAT);
      uit = await bgVraag({ w: inv.w, h: inv.h, px: inv.px, cfg: this.#bgCfg() }, [inv.px.buffer]);
    } catch (err) {
      uit = { reden: 'fout' };
    }
    if (doel.bgBezig !== beurt) return;
    doel.bgBezig = null;
    const nogDaar = doel[veld] === bron && (!l || (side.lagen || []).includes(l));
    if (!nogDaar) {
      this.#tekenLagenLijst(side);
      return;
    }
    const t = this.#bgTeksten();
    if (!uit || !uit.masker) {
      const reden = (uit && uit.reden) || 'fout';
      this.#tekenLagenLijst(side);
      this.#bgMelding(side, l, reden === 'al' ? t.al : reden === 'geen' ? t.geen : t.mislukt, reden === 'al' ? 'info' : 'fout', reden === 'fout');
      return;
    }
    const u = {
      methode: uit.methode,
      w: uit.w,
      h: uit.h,
      streken: [],
      terug: [],
      basis: bgUitPixels(uit.w, uit.h, uit.masker),
      basisKleur: bgUitPixels(uit.w, uit.h, uit.kleur),
    };
    u.masker = u.basis;
    u.kleur = u.basisKleur;
    doel.origBeeld = bron;
    doel.uitsnede = u;
    doel.zonderAchtergrond = true;
    doel[veld] = bgSamenstellen(bron, u);
    // De PNG's voor het bewaren van het ontwerp alvast maken, na het drukke moment.
    setTimeout(() => bgBlobs(u), 1500);
    this.#tekenLagenLijst(side);
    this.#render();
    this.#bgMelding(side, l, t.gelukt, 'klaar');
  }

  /** Terug naar de afbeelding zoals hij geüpload is. */
  #herstelOrigineel(side, l) {
    const doel = l || side;
    const veld = l ? 'plaatje' : 'img';
    if (!doel.uitsnede && !doel.zonderAchtergrond) return;
    const orig = doel.origBeeld || doel[veld];
    this.#vergeetAchtergrond(doel, veld);
    doel[veld] = orig;
    this.#tekenLagenLijst(side);
    this.#render();
  }

  /** Venster "Uitsnede verbeteren": penselen op het masker. */
  #openUitsnede(side, l) {
    const doel = l || side;
    const veld = l ? 'plaatje' : 'img';
    if (!doel.uitsnede || !doel.origBeeld || this.uitsnedeVenster) return;
    // Gedeeld met de andere zijde? Dan eerst een eigen kopie, anders verandert
    // het masker daar mee zonder dat die uitsnede opnieuw wordt samengesteld.
    // Altijd op een eigen kopie werken: de vorige stand kan nog in "Ongedaan
    // maken" zitten, of door de andere zijde gedeeld worden. Pas bij Klaar met
    // een wijziging komt de kopie in de plaats van het origineel.
    const vorige = doel.uitsnede;
    const u = bgKopie(vorige);
    if (this.bgMeldingEl) this.bgMeldingEl.remove();
    // De zwevende "Klaar" van de ontwerptool staat op dezelfde plek als die van
    // dit venster. Wie daar tikt, wil het venster sluiten, niet de hele
    // ontwerptool (Jos, 11 okt 2026). Zolang het venster open is: verbergen.
    const zwevend = this.klaarZwevend;
    if (zwevend) zwevend.style.visibility = 'hidden';
    const terugZwevend = () => {
      if (zwevend) zwevend.style.visibility = '';
    };
    this.uitsnedeVenster = new UitsnedeVenster({
      orig: doel.origBeeld,
      u: u,
      t: this.#bgTeksten(),
      bijKlaar: (gewijzigd) => {
        this.uitsnedeVenster = null;
        terugZwevend();
        if (gewijzigd && doel.uitsnede === vorige && doel.origBeeld) {
          const oud = doel[veld];
          doel.uitsnede = u;
          doel[veld] = bgSamenstellen(doel.origBeeld, u);
          if (oud && oud !== doel.origBeeld && !this.#beeldElders(oud, doel) && !this.#inGeschiedenis(oud)) bgVrij(oud);
        } else {
          bgVrijUitsnede(u);
        }
        this.#tekenLagenLijst(side);
        this.#render();
      },
      bijOrigineel: () => {
        this.uitsnedeVenster = null;
        terugZwevend();
        bgVrijUitsnede(u);
        this.#herstelOrigineel(side, l);
      },
    });
  }

  /**
   * Korte melding vlak onder de knop. Verdwijnt na een paar seconden vanzelf;
   * bij een fout met een knop "Opnieuw proberen".
   */
  #bgMelding(side, l, tekst, soort, opnieuw) {
    if (this.bgMeldingEl) this.bgMeldingEl.remove();
    clearTimeout(this.bgMeldingKlok);
    const knoppen = [...this.querySelectorAll('[data-bgdoel="' + this.#bgSleutelVan(side, l) + '"]')];
    const knop = knoppen.find((k) => k.offsetParent) || knoppen[0];
    if (!knop) return;
    const anker = knop.closest('.flagcfg-tekst__knoppen, .flagcfg__btns') || knop;
    const kleuren = {
      fout: ['#fdf2f2', '#8a1f1f', '#f1c9c9'],
      klaar: ['#eef7f0', '#1f5130', '#cfe6d6'],
      info: ['#eef3fb', '#22314d', '#d3dcee'],
    }[soort] || ['#eef3fb', '#22314d', '#d3dcee'];
    const el = document.createElement('p');
    el.setAttribute('role', 'status');
    // Rechtstreeks opgemaakt, net als de bewerkmelding: de gebundelde opmaak
    // ververst niet altijd tegelijk met dit script.
    el.style.cssText = [
      'flex:1 0 100%', 'grid-column:1/-1', 'margin:.35rem 0 0', 'padding:.5rem .7rem', 'border-radius:8px',
      'font-size:.8rem', 'line-height:1.4', 'background:' + kleuren[0], 'color:' + kleuren[1], 'border:1px solid ' + kleuren[2],
    ].join(';');
    el.textContent = tekst;
    if (opnieuw) {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = this.#bgTeksten().opnieuw;
      b.style.cssText = 'background:none;border:0;padding:0;margin-left:.4rem;font:inherit;color:inherit;text-decoration:underline;cursor:pointer';
      b.addEventListener('click', () => {
        el.remove();
        this.#verwijderAchtergrond(side, l);
      });
      el.append(b);
    }
    anker.after(el);
    this.bgMeldingEl = el;
    this.bgMeldingKlok = setTimeout(() => {
      el.remove();
      if (this.bgMeldingEl === el) this.bgMeldingEl = null;
    }, opnieuw ? 15000 : 7000);
  }

  /**
   * Wordt dit beeld nog door een andere zijde of een ander element gebruikt?
   * "Zelfde ontwerp op de andere kant" deelt de afbeelding; dan mag het
   * bewerkte beeld niet vrijgegeven worden zolang de andere kant het toont.
   */
  #beeldElders(beeld, doel) {
    return (this.sides || []).some((s) =>
      (s !== doel && (s.img === beeld || s.origBeeld === beeld)) ||
      (s.lagen || []).some((l) => l !== doel && (l.plaatje === beeld || l.origBeeld === beeld)));
  }

  /** Deelt een andere zijde of een ander element dezelfde uitsnede (masker)? */
  #uitsnedeElders(u, doel) {
    return (this.sides || []).some((s) =>
      (s !== doel && s.uitsnede === u) || (s.lagen || []).some((l) => l !== doel && l.uitsnede === u));
  }

  /** De knop in de gewone weergave (productpagina): tekst en stand bijwerken. */
  #toonBgKnop(side) {
    const knop = side.bgWegKnop;
    if (!knop) return;
    const kan = this.#kanUitsnede(side, null) && !!this.dataset.bgModel;
    knop.hidden = !kan;
    if (!kan) return;
    const t = this.#bgTeksten();
    const tekst = side.bgBezig ? t.bezig : side.uitsnede ? t.verbeter : t.weg;
    if (knop.textContent !== tekst) knop.textContent = tekst;
    knop.disabled = !!side.bgBezig;
    knop.dataset.bgdoel = this.#bgSleutelVan(side, null);
  }

  /** Bij een nieuwe afbeelding: de vorige uitsnede vergeten en vrijgeven. */
  #vergeetAchtergrond(doel, veld) {
    doel.bgBezig = null;
    if (doel.zonderAchtergrond && doel[veld] && doel[veld] !== doel.origBeeld && !this.#beeldElders(doel[veld], doel) &&
        !this.#inGeschiedenis(doel[veld])) {
      bgVrij(doel[veld]);
    }
    if (doel.uitsnede && !this.#uitsnedeElders(doel.uitsnede, doel) && !this.#inGeschiedenis(doel.uitsnede)) {
      bgVrijUitsnede(doel.uitsnede);
    }
    doel.uitsnede = null;
    doel.origBeeld = null;
    doel.zonderAchtergrond = false;
  }

  /** De uitsnede in de vorm waarin hij in de browser bewaard wordt (PNG's en de streken). */
  async #bgOpslagVorm(u) {
    if (!u) return null;
    const [basis, kleur] = await bgBlobs(u);
    if (!basis || !kleur) return null;
    return { methode: u.methode, basis: basis, kleur: kleur, streken: u.streken.map((st) => ({ m: st.m, r: st.r, p: st.p.slice() })) };
  }

  /**
   * Rij voor de afbeelding die op de productpagina is geupload. Die is geen
   * laag maar de achtergrond van het doek, en de knop om hem weg te halen
   * zit in het blok dat binnen de ontwerptool verborgen is. Zonder deze rij
   * zit je er dus aan vast zodra je de tool opent.
   */
  #achtergrondRij(side) {
    const rij = document.createElement('div');
    rij.className = 'flagcfg-tekst flagcfg-tekst--achtergrond';

    const merk = document.createElement('span');
    merk.className = 'flagcfg-tekst__greep flagcfg-tekst__greep--vast';
    merk.innerHTML = '<span class="flagcfg-tekst__nummer">◻</span>';
    merk.title = this.dataset.tAchtergrondUitleg || 'Achtergrond';

    const naam = document.createElement('span');
    naam.className = 'flagcfg-tekst__vormnaam flagcfg-tekst__bestand';
    naam.textContent = side.fileName || this.dataset.tGeupload || 'Afbeelding';

    const schuif = document.createElement('label');
    schuif.className = 'flagcfg-tekst__schuif';
    const sp = document.createElement('span');
    sp.textContent = this.dataset.tTekstGrootte || 'Grootte';
    const inp = document.createElement('input');
    inp.type = 'range';
    inp.min = 20;
    inp.max = 300;
    inp.step = 1;
    inp.value = String(Math.round(side.zoom * 100));
    inp.addEventListener('input', () => {
      side.zoom = Number(inp.value) / 100;
      side.stretch = false;
      this.#render();
    });
    schuif.append(sp, inp);

    const knoppen = document.createElement('div');
    knoppen.className = 'flagcfg-tekst__knoppen';

    const vullen = document.createElement('button');
    vullen.type = 'button';
    vullen.className = 'flagcfg-tekst__midden';
    vullen.textContent = this.dataset.tFill || 'Vlag vullen';
    vullen.addEventListener('click', () => this.#action(side, 'fill'));

    const midden = document.createElement('button');
    midden.type = 'button';
    midden.className = 'flagcfg-tekst__midden';
    midden.textContent = this.dataset.tCentreren || 'Centreren';
    midden.addEventListener('click', () => {
      side.cx = 50;
      side.cy = 50;
      this.#render();
    });

    const weg = document.createElement('button');
    weg.type = 'button';
    weg.className = 'flagcfg-tekst__weg';
    weg.textContent = this.dataset.tTekstWeg || 'Verwijderen';
    weg.addEventListener('click', () => {
      this.#wisOntwerp(side);
      this.#tekenLagenLijst(side);
    });

    const zonderBg = this.#achtergrondKnop(side, null);
    if (zonderBg) knoppen.append(...zonderBg);
    knoppen.append(vullen, midden, weg);
    rij.append(merk, naam, schuif, knoppen);
    return rij;
  }

  #tekenLagenLijst(side) {
    this.#toonBgKnop(side);
    // De sjabloonbalk hoort weg zodra er iets op de vlag staat, en terug zodra
    // alles weer weg is. Deze methode draait bij elke wijziging in de lagen.
    this.#toonSjablonen(side);
    this.#toonWisAlles(side);

    this.#toonVlagPaneel(side);
    const lijst = side.tekstLijst;
    if (!lijst) return;
    lijst.textContent = '';

    side.lagen.forEach((l, nr) => {
      // Vlaggenlijn, elk vlaggetje anders: alleen de foto van het gekozen vlaggetje in de lijst.
      if (l.vlaggetje && this.#vlagApart(side) && l.vlaggetje !== (this.vlagNr || 1)) return;
      const rij = document.createElement('div');
      rij.className = 'flagcfg-tekst';
      rij.dataset.id = l.id;

      // Greep om de volgorde te veranderen. Wat bovenaan staat ligt op de
      // vlag bovenop; sleep je iets omhoog, dan komt het voor de rest.
      const greep = document.createElement('button');
      greep.type = 'button';
      greep.className = 'flagcfg-tekst__greep';
      greep.setAttribute('aria-label', this.dataset.tVolgorde || 'Volgorde');
      greep.innerHTML =
        '<span class="flagcfg-tekst__nummer">' + (nr + 1) + '</span>' +
        '<svg viewBox="0 0 24 24" aria-hidden="true">' +
        '<circle cx="9" cy="6" r="1.7"/><circle cx="15" cy="6" r="1.7"/>' +
        '<circle cx="9" cy="12" r="1.7"/><circle cx="15" cy="12" r="1.7"/>' +
        '<circle cx="9" cy="18" r="1.7"/><circle cx="15" cy="18" r="1.7"/></svg>';
      this.#bindSleepVolgorde(side, rij, greep, l);
      rij.append(greep);

      const schuif = (label, min, max, waarde, bij) => {
        const wrap = document.createElement('label');
        wrap.className = 'flagcfg-tekst__schuif';
        const sp = document.createElement('span');
        sp.textContent = label;
        const inp = document.createElement('input');
        inp.type = 'range';
        inp.min = min;
        inp.max = max;
        inp.step = 1;
        inp.value = waarde;
        inp.addEventListener('input', () => {
          bij(Number(inp.value));
          this.#render();
        });
        wrap.append(sp, inp);
        return wrap;
      };

      if (l.type === 'tekst') {
        const invoer = document.createElement('input');
        invoer.type = 'text';
        invoer.className = 'flagcfg-tekst__invoer';
        invoer.value = l.tekst;
        invoer.maxLength = 60;
        invoer.setAttribute('aria-label', this.dataset.tTekstLabel || 'Tekst');
        invoer.addEventListener('input', () => {
          l.tekst = invoer.value;
          this.#render();
        });

        const font = document.createElement('select');
        font.className = 'flagcfg-tekst__font';
        font.setAttribute('aria-label', this.dataset.tTekstFont || 'Lettertype');
        // Met kopjes per soort: met ruim vijftig lettertypen is één lange lijst niet meer te overzien.
        const taalF = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
        const groepen = {};
        LETTERTYPEN.forEach((f) => {
          const soort = f.soort || 'standaard';
          if (!groepen[soort]) {
            groepen[soort] = document.createElement('optgroup');
            const n = LETTER_SOORTEN[soort] || {};
            groepen[soort].label = n[taalF] || n.nl || soort;
            font.appendChild(groepen[soort]);
          }
          const o = document.createElement('option');
          o.value = f.css;
          o.textContent = f.naam;
          o.style.fontFamily = f.css;
          o.style.fontWeight = String(letterGewicht(f.css));
          groepen[soort].appendChild(o);
        });
        font.value = l.font;
        font.addEventListener('change', () => {
          l.font = font.value;
          this.#render();
        });

        rij.append(invoer, font);
      } else {
        const naam = document.createElement('span');
        naam.className = 'flagcfg-tekst__vormnaam';
        if (l.type === 'eigen') {
          naam.textContent = l.naam || this.dataset.tAfbeelding || 'afbeelding';
          naam.classList.add('flagcfg-tekst__bestand');
          // Stipje met de scherpte van dit ene element. Alleen bij bitmaps:
          // een SVG of een vlag uit de databank is een tekening en altijd
          // scherp. De verwijzing blijft bewaard zodat het slepen aan de
          // Grootte-schuif hem live kan bijwerken zonder de lijst opnieuw op
          // te bouwen — dat laatste zou op een telefoon gaan haperen.
          //
          // Bewust NAAST de naam en niet erin: een foto uit een fotorol heet
          // "EA7291CC-42B5-4B74-8145-A…" en die naam wordt afgekapt. Zat het
          // stipje daarbinnen, dan werd het mee afgekapt en zag je juist het
          // getal niet meer waar het om ging.
          if (!l.vector) {
            const stip = document.createElement('span');
            stip.className = 'flagcfg-tekst__scherpte';
            stip.hidden = true;
            l.stipEl = stip;
            l.stipNaNaam = true;
          } else {
            l.stipEl = null;
          }
        } else {
          const taal = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
          if (l.type === 'vorm' && l.tekening) {
            // Getekend element: het icoontje uit de tekening zelf, in de kleur
            // van de laag. De verwijzing blijft bewaard zodat een andere kleur
            // ook het icoontje bijwerkt, zonder de hele lijst opnieuw te bouwen.
            const beeld = document.createElement('span');
            beeld.className = 'flagcfg-tekst__vormicoon';
            beeld.appendChild(elementBeeld(l.tekening, l.kleur));
            l.icoonEl = beeld;
            rij.append(beeld);
            naam.textContent = l.elementNaam || l.teken || this.dataset.tVorm || 'vorm';
          } else if (l.type === 'vorm') {
            // De naam wordt hier opgezocht en niet uit de laag gelezen: een vorm
            // die uit een sjabloon komt heeft er geen bij zich, en dan zag je in
            // de lijst alleen een kleurvakje zonder te weten wat het was.
            const bron = ((window.FLAGCFG_KLEUR || {}).vormen || [])
              .find((v) => v.vorm === l.vorm);
            // Het vormpje er als klein icoontje bij, zodat in één oogopslag te
            // zien is of een regel een vorm of een tekst is.
            if (bron && bron.s) {
              const beeld = document.createElement('span');
              beeld.className = 'flagcfg-tekst__vormicoon';
              beeld.innerHTML = bron.s;
              const svg = beeld.querySelector('svg');
              if (svg) {
                svg.setAttribute('width', '100%');
                svg.setAttribute('height', '100%');
                svg.setAttribute('color', l.kleur || 'currentColor');
              }
              rij.append(beeld);
            }
            naam.textContent = bron
              ? (bron.namen[taal] || bron.namen.nl)
              : (l.teken || this.dataset.tVorm || 'vorm');
          } else {
            naam.textContent = l.teken;
          }
          naam.classList.add('flagcfg-tekst__teken');
        }
        rij.append(naam);
        if (l.stipNaNaam && l.stipEl) rij.append(l.stipEl);
      }

      // Tekst en vormen hebben een kleur om te kiezen. Een kleursymbool en een
      // eigen afbeelding brengen hun eigen kleuren mee; een kleurkiezer die
      // niets doet is erger dan geen kleurkiezer.
      // Een getekend element met vaste kleuren (een taart, een molen) krijgt er
      // om dezelfde reden geen.
      const vasteKleuren = l.type === 'vorm' && l.tekening && !leesElement(l.tekening).kleurbaar;
      if ((l.type === 'tekst' || l.type === 'vorm') && !vasteKleuren) {
        const kleur = document.createElement('input');
        kleur.type = 'color';
        kleur.className = 'flagcfg-tekst__kleur';
        kleur.value = l.kleur;
        kleur.setAttribute('aria-label', this.dataset.tTekstKleur || 'Kleur');
        kleur.addEventListener('input', () => {
          l.kleur = kleur.value;
          this.#render();
        });
        if (l.tekening) {
          // Het icoontje pas bijwerken na het kiezen, niet bij elke stap van de
          // kleurkiezer: een nieuw plaatje maken is meer werk dan opnieuw tekenen.
          kleur.addEventListener('change', () => {
            if (l.icoonEl) {
              l.icoonEl.textContent = '';
              l.icoonEl.appendChild(elementBeeld(l.tekening, l.kleur));
            }
          });
        }
        rij.append(kleur);
      }

      // Een afbeelding mag verder dan 100: om een liggende vlag te vullen met
      // een vierkant logo is meer dan de vlaghoogte nodig, en anders zou de
      // knop "Vlag vullen" een waarde zetten die de schuif niet kan tonen.
      const maxMaat = l.type === 'tekst' ? 40 : (l.type === 'eigen' ? 300 : 100);
      rij.append(schuif(this.dataset.tTekstGrootte || 'Grootte', 4, maxMaat, l.grootte, (v) => (l.grootte = v)));
      rij.append(schuif(this.dataset.tTekstHoek || 'Draaien', -180, 180, l.hoek, (v) => (l.hoek = v)));

      const knoppen = document.createElement('div');
      knoppen.className = 'flagcfg-tekst__knoppen';

      // Pijltjes naast het slepen. Slepen is prettig als het werkt, maar het
      // hangt af van browser en invoerapparaat; een knop doet het altijd.
      const schuifLaag = (richting) => {
        const i = side.lagen.indexOf(l);
        const j = i + richting;
        if (i < 0 || j < 0 || j >= side.lagen.length) return;
        side.lagen.splice(i, 1);
        side.lagen.splice(j, 0, l);
        this.#tekenLagenLijst(side);
        this.#render();
      };

      // Met tekst erbij (Jos, 11 okt 2026): een pijltje alleen zei een nieuwe
      // klant niets. De uitleg (voorgrond/achtergrond) staat in de tooltip.
      const pijl = (richting, kort, label, pad) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'flagcfg-tekst__pijl flagcfg-tekst__pijl--tekst';
        b.setAttribute('aria-label', label);
        b.title = label;
        b.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' + pad + '"/></svg><span></span>';
        b.querySelector('span').textContent = kort;
        const eerste = side.lagen.indexOf(l) === 0;
        const laatste = side.lagen.indexOf(l) === side.lagen.length - 1;
        b.disabled = richting < 0 ? eerste : laatste;
        b.addEventListener('click', () => schuifLaag(richting));
        return b;
      };

      knoppen.append(
        pijl(-1, this.dataset.tVorenKort || 'Naar voren', this.dataset.tNaarVoren || 'Naar voren', 'M12 6l6 7h-4v5h-4v-5H6l6-7Z'),
        pijl(1, this.dataset.tAchterenKort || 'Naar achteren', this.dataset.tNaarAchteren || 'Naar achteren', 'M12 18l-6-7h4V6h4v5h4l-6 7Z')
      );

      // Iets terugzetten naar het midden is met slepen lastig precies te
      // krijgen, zeker op een telefoon.
      const midden = document.createElement('button');
      midden.type = 'button';
      midden.className = 'flagcfg-tekst__midden';
      midden.textContent = this.dataset.tCentreren || 'Centreren';
      midden.addEventListener('click', () => {
        l.cx = 50;
        l.cy = 50;
        this.#render();
      });

      // Alleen bij een afbeelding of landvlag. Een landvlag die je uit de
      // databank kiest komt binnen als klein element; wie er een landvlag van
      // wil maken moest hem tot nu toe met de hand groot slepen.
      let vullen = null;
      if (l.type === 'eigen') {
        vullen = document.createElement('button');
        vullen.type = 'button';
        vullen.className = 'flagcfg-tekst__midden';
        vullen.textContent = this.dataset.tFill || 'Vlag vullen';
        vullen.addEventListener('click', () => {
          // Zo groot maken dat hij het doek helemaal bedekt, zonder de
          // verhouding te vervormen — een uitgerekt logo is erger dan een
          // logo dat niet helemaal past.
          const r = side.rect;
          const verh = l.verhouding || 1;
          const nodig = (r.w / r.h) / verh * 100;
          l.grootte = Math.round(Math.max(100, nodig));
          l.cx = 50;
          l.cy = 50;
          l.hoek = 0;
          this.#tekenLagenLijst(side);
          this.#render();
        });
      }

      const weg = document.createElement('button');
      weg.type = 'button';
      weg.className = 'flagcfg-tekst__weg';
      weg.textContent = this.dataset.tTekstWeg || 'Verwijderen';
      weg.addEventListener('click', () => this.#verwijderLaag(side, l.id));

      // Dupliceren: een kopie iets opzij, bovenop het origineel (Jos, 11 okt 2026).
      const dubbel = document.createElement('button');
      dubbel.type = 'button';
      dubbel.className = 'flagcfg-tekst__midden';
      dubbel.textContent = this.dataset.tDupliceren || 'Dupliceren';
      dubbel.addEventListener('click', () => this.#dupliceerLaag(side, l, true));

      // Bijsnijden: alleen bij een eigen afbeelding, niet bij een landvlag.
      let snij = null;
      if (l.type === 'eigen' && !l.vlagCode && l.plaatje) {
        snij = document.createElement('button');
        snij.type = 'button';
        snij.className = 'flagcfg-tekst__bgweg';
        snij.innerHTML = BG_BIJSNIJD + '<span></span>';
        snij.querySelector('span').textContent = this.dataset.tBijsnijden || 'Bijsnijden';
        snij.addEventListener('click', () => this.#openBijsnijden(side, l));
      }

      // Bewerkingen van de afbeelding zelf vooraan, dan de rest.
      const zonderBg = l.type === 'eigen' ? this.#achtergrondKnop(side, l) : null;
      if (zonderBg) knoppen.append(zonderBg[0]);
      if (snij) knoppen.append(snij);
      if (zonderBg && zonderBg[1]) knoppen.append(zonderBg[1]);
      if (vullen) knoppen.append(vullen);
      // Tekststijl (Jos, 13 okt 2026): één knop die een paneel openklapt, zodat
      // de rij niet voller wordt dan nodig.
      let stijlPaneel = null;
      if (l.type === 'tekst') {
        this.stijlOpen = this.stijlOpen || new Set();
        const T = stijlTeksten();
        const stijl = document.createElement('button');
        stijl.type = 'button';
        stijl.className = 'flagcfg-tekst__midden';
        stijl.textContent = T.stijl;
        stijl.setAttribute('aria-expanded', this.stijlOpen.has(l.id) ? 'true' : 'false');
        stijl.addEventListener('click', () => {
          if (this.stijlOpen.has(l.id)) this.stijlOpen.delete(l.id);
          else this.stijlOpen.add(l.id);
          this.#tekenLagenLijst(side);
        });
        knoppen.append(stijl);
        if (this.stijlOpen.has(l.id)) stijlPaneel = this.#stijlPaneel(side, l);
      }
      if (this.#vlagApart(side) && !l.verdeel) {
        const TV = vlagTeksten();
        const waar = document.createElement('button');
        waar.type = 'button';
        waar.className = 'flagcfg-tekst__midden';
        waar.textContent = l.vlaggetje ? TV.naarAlle : TV.alleenHier + ' ' + (this.vlagNr || 1);
        waar.addEventListener('click', () => {
          if (l.vlaggetje) delete l.vlaggetje;
          else l.vlaggetje = this.vlagNr || 1;
          this.#tekenLagenLijst(side);
          this.#render();
        });
        knoppen.append(waar);
      }
      knoppen.append(dubbel, midden, weg);
      rij.append(knoppen);
      if (stijlPaneel) rij.append(stijlPaneel);

      lijst.appendChild(rij);
    });

    // Onderaan, want de achtergrond ligt ook onder alle elementen.
    if (side.img) lijst.appendChild(this.#achtergrondRij(side));
  }

  /**
   * Het stijlpaneel van een tekstlaag: uitlijnen op de vlag, letterafstand,
   * boog, rand, een effect en doorzichtigheid. Alles werkt meteen op het doek;
   * de lijst wordt alleen opnieuw opgebouwd bij "Stijl wissen".
   */
  #stijlPaneel(side, l) {
    stijlCss();
    const T = stijlTeksten();
    const p = document.createElement('div');
    p.className = 'flagcfg-stijl';
    const rijMet = (label, ...inhoud) => {
      const rij = document.createElement('div');
      rij.className = 'flagcfg-stijl__rij';
      const sp = document.createElement('span');
      sp.className = 'flagcfg-stijl__label';
      sp.textContent = label;
      rij.append(sp, ...inhoud);
      return rij;
    };
    const schuif = (min, max, waarde, bij, label) => {
      const inp = document.createElement('input');
      inp.type = 'range';
      inp.min = min;
      inp.max = max;
      inp.step = 1;
      inp.value = waarde;
      inp.setAttribute('aria-label', label);
      inp.addEventListener('input', () => { bij(Number(inp.value)); this.#render(); });
      return inp;
    };
    const kleurVeld = (waarde, bij, label) => {
      const k = document.createElement('input');
      k.type = 'color';
      k.className = 'flagcfg-stijl__kleur';
      k.value = waarde;
      k.setAttribute('aria-label', label);
      k.addEventListener('input', () => { bij(k.value); this.#render(); });
      return k;
    };

    // Uitlijnen op de vlag: tegen de linker-, rechter-, boven- of onderrand,
    // met een vaste marge, of in het midden.
    const MARGE = 5;
    const zet = (fn) => () => {
      const r = side.rect;
      if (!r || !r.w) return;
      const k = this.#laagKader(side, l);
      const mv = MARGE * (r.w / r.h);
      fn(k, mv);
      this.#render();
    };
    const knop = (label, pad, doe) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'flagcfg-stijl__knop';
      b.title = label;
      b.setAttribute('aria-label', T.uitlijnen + ': ' + label);
      b.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' + pad + '"/></svg>';
      b.addEventListener('click', doe);
      return b;
    };
    const groep = (...knoppen) => {
      const g = document.createElement('span');
      g.className = 'flagcfg-stijl__groep';
      g.append(...knoppen);
      return g;
    };
    p.append(rijMet(T.uitlijnen,
      groep(
        knop(T.links, 'M4 4v16M8 8h11M8 16h7', zet((k) => { l.cx = MARGE + k.halfB; })),
        knop(T.midden, 'M12 4v16M6 8h12M8 16h8', zet(() => { l.cx = 50; })),
        knop(T.rechts, 'M20 4v16M5 8h11M9 16h7', zet((k) => { l.cx = 100 - MARGE - k.halfB; })),
      ),
      groep(
        knop(T.boven, 'M4 4h16M8 8v11M16 8v7', zet((k, mv) => { l.cy = mv + k.halfH; })),
        knop(T.midden, 'M4 12h16M8 6v12M16 8v8', zet(() => { l.cy = 50; })),
        knop(T.onder, 'M4 20h16M8 5v11M16 9v7', zet((k, mv) => { l.cy = 100 - mv - k.halfH; })),
      )));

    p.append(rijMet(T.afstand, schuif(-10, 60, l.spatie || 0, (v) => { l.spatie = v; }, T.afstand)));
    p.append(rijMet(T.boog, schuif(-100, 100, l.boog || 0, (v) => { l.boog = v; }, T.boog)));

    // Rand: standaard in een kleur die afsteekt tegen de letters.
    const licht = (hex) => {
      const n = parseInt(String(hex || '#000000').slice(1), 16);
      return ((n >> 16) * 299 + ((n >> 8) & 255) * 587 + (n & 255) * 114) / 1000 > 150;
    };
    const randStart = l.randKleur || (licht(l.kleur) ? '#000000' : '#ffffff');
    p.append(rijMet(T.rand,
      kleurVeld(randStart, (v) => { l.randKleur = v; }, T.rand),
      schuif(0, 20, l.rand || 0, (v) => { l.rand = v; if (v && !l.randKleur) l.randKleur = randStart; }, T.rand)));

    // Effect met een eigen kleur; bij "geen" en "holle letters" is die er niet.
    const STANDAARD = { schaduw: '#000000', hard: '#000000', gloed: '#ffd166', echo: '#ff6b6b', vlak: '#ffffff' };
    const keuze = document.createElement('select');
    keuze.setAttribute('aria-label', T.effect);
    ['', 'schaduw', 'hard', 'gloed', 'echo', 'hol', 'vlak'].forEach((e) => {
      const o = document.createElement('option');
      o.value = e;
      o.textContent = T[e || 'geen'];
      keuze.appendChild(o);
    });
    keuze.value = l.effect || '';
    const effKleur = kleurVeld(l.effectKleur || STANDAARD[l.effect] || '#000000', (v) => { l.effectKleur = v; }, T.effect);
    const toonKleur = () => { effKleur.hidden = !keuze.value || keuze.value === 'hol'; };
    keuze.addEventListener('change', () => {
      l.effect = keuze.value;
      // Elk effect begint in zijn eigen kleur: een zwart vlak achter zwarte
      // letters zou de tekst onleesbaar maken.
      l.effectKleur = STANDAARD[keuze.value] || '';
      if (l.effectKleur) effKleur.value = l.effectKleur;
      toonKleur();
      this.#render();
    });
    toonKleur();
    p.append(rijMet(T.effect, keuze, effKleur));

    p.append(rijMet(T.dekking, schuif(10, 100, l.dekking == null || l.dekking === '' ? 100 : l.dekking, (v) => { l.dekking = v; }, T.dekking)));

    const wis = document.createElement('button');
    wis.type = 'button';
    wis.className = 'flagcfg-stijl__wis';
    wis.textContent = T.wissen;
    wis.addEventListener('click', () => {
      ['spatie', 'boog', 'rand', 'randKleur', 'effect', 'effectKleur', 'dekking'].forEach((k) => delete l[k]);
      this.#tekenLagenLijst(side);
      this.#render();
    });
    p.append(wis);
    return p;
  }

  /**
   * Rekent uit waar een laag staat en hoe groot hij is, binnen een gegeven
   * doek. Wordt gedeeld door de preview en het drukbestand: dezelfde som op
   * een andere schaal, zodat wat de klant ziet ook gedrukt wordt.
   */
  /* ---------- Vlaggenlijn: elk vlaggetje anders (17 okt 2026) ----------
   * Een tekstlaag met `verdeel` heeft de hele naam als tekst; op vlaggetje n staat één letter, met de
   * naam in het midden van de lijn (spaties zijn lege vlaggetjes). Een laag met `vlaggetje: n` staat
   * alleen op vlaggetje n (de foto's). Beide zijn gewone getallen en ja/nee, dus #laagGegevens, het
   * kenmerk, ongedaan maken en de winkelmand nemen ze vanzelf mee.
   */
  /** Vlaggenlijn, elk vlaggetje anders: een nieuwe laag komt op het gekozen vlaggetje, tenzij "Alle vlaggetjes". */
  #opVlaggetje(side, l) {
    if (this.#vlagApart(side) && !side.vlagAlles) l.vlaggetje = this.vlagNr || 1;
  }

  /**
   * Een letter van de verdeelde naam moet binnen het vlaggetje passen; een wimpel wordt naar onderen smaller
   * (Jos, 18 okt 2026: "De M past niet"). Maakt de letter zo nodig kleiner.
   */
  #pasLetter(ctx, rect, l) {
    if (!ctx || !rect || !l.tekst) return l;
    const wimpel = String(this.vorm || '').indexOf('wimpel') >= 0;
    let px = (l.grootte / 100) * rect.h;
    for (let i = 0; i < 6; i++) {
      const m = tekstOpmaak(ctx, l, px);
      const onder = (l.cy / 100) + (m.hoog / rect.h) / 2;
      const ruimte = rect.w * (wimpel ? Math.max(0.12, (1 - onder) * 0.8) : 0.8);
      if (m.breed <= ruimte) break;
      px *= ruimte / m.breed;
    }
    return Object.assign({}, l, { grootte: (px / rect.h) * 100 });
  }

  #vlagApart(side) {
    if (!this.probo || !this.probo.vel || !side) return false;
    return !!(side.vlagApart || side.lagen.some((l) => l.verdeel || l.vlaggetje));
  }

  #aantalVlaggetjes() {
    return this.probo && this.probo.vel ? this.probo.vel.plekken.length : 0;
  }

  #letterVoor(l, nr) {
    const N = this.#aantalVlaggetjes() || 1;
    const tekst = String(l.tekst || '');
    const letters = (typeof Intl !== 'undefined' && Intl.Segmenter
      ? [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(tekst)].map((x) => x.segment)
      : Array.from(tekst)).slice(0, N);
    const start = Math.floor((N - letters.length) / 2);
    const t = letters[nr - 1 - start] || '';
    return t.trim();
  }

  #lagenVoor(side, nr, ctx, rect) {
    if (nr == null || !this.#vlagApart(side)) return side.lagen;
    return side.lagen
      .filter((l) => !l.vlaggetje || l.vlaggetje === nr)
      .map((l) => (l.verdeel ? this.#pasLetter(ctx, rect, Object.assign({}, l, { verdeel: false, tekst: this.#letterVoor(l, nr) })) : l));
  }

  /** Een laag die op het gekozen vlaggetje niet te zien is, kan ook niet gekozen worden. */
  #onzichtbaar(side, l) {
    if (!this.#vlagApart(side)) return false;
    const nr = this.vlagNr || 1;
    if (l.vlaggetje && l.vlaggetje !== nr) return true;
    return !!(l.verdeel && !this.#letterVoor(l, nr));
  }

  /** Het paneel boven de lagenlijst, alleen bij een vlaggenlijn. */
  #toonVlagPaneel(side) {
    const lijst = side.tekstLijst;
    if (!lijst) return;
    if (!this.probo || !this.probo.vel) {
      if (side.vlagPaneel) side.vlagPaneel.hidden = true;
      if (side.vlagTitel) side.vlagTitel.hidden = true;
      return;
    }
    vlagCss();
    const T = vlagTeksten();
    const N = this.#aantalVlaggetjes();
    side.vlagN = N;
    if (!this.vlagNr || this.vlagNr > N) this.vlagNr = 1;
    let p = side.vlagPaneel;
    if (!p) {
      p = document.createElement('div');
      p.className = 'flagcfg-vlaggetjes';
      // Boven de knoppen Uploaden, Tekst, Symbolen en Vlaggen (Jos, 18 okt 2026): eerst kiezen welk
      // vlaggetje, dan toevoegen.
      const werk = side.root.querySelector('[data-werkbalk]') || lijst;
      werk.parentElement.insertBefore(p, werk);
      side.vlagPaneel = p;
    }
    p.hidden = false;
    p.textContent = '';
    const apart = this.#vlagApart(side);
    // Kopje boven de knoppen Uploaden, Tekst, Symbolen en Vlaggen: welk vlaggetje je nu bewerkt, zoals
    // "Linkerzijde" bij een dubbelzijdige vlag.
    const werkbalk = side.root.querySelector('[data-werkbalk]');
    if (werkbalk) {
      let titel = side.vlagTitel;
      if (!titel) {
        titel = document.createElement('p');
        titel.className = 'flagcfg__sidetitle flagcfg-vlaggetjes__titel';
        werkbalk.parentElement.insertBefore(titel, werkbalk);
        side.vlagTitel = titel;
      }
      titel.hidden = !apart;
      titel.textContent = side.vlagAlles ? T.alle : T.nr + ' ' + (this.vlagNr || 1);
    }

    const aan = document.createElement('label');
    aan.className = 'flagcfg-vlaggetjes__aan';
    const vink = document.createElement('input');
    vink.type = 'checkbox';
    vink.checked = apart;
    vink.addEventListener('change', () => {
      side.vlagApart = vink.checked;
      if (vink.checked) {
        // Foto's die al op de vlag stonden gaan naar vlaggetje 1, 2, ... (Jos, 18 okt 2026: zijn foto
        // stond na het aanvinken nog op alle vlaggetjes). Landvlaggen, tekst en symbolen blijven op alle.
        let nr = 0;
        side.lagen.forEach((l) => {
          if (l.type !== 'eigen' || l.vlagCode || l.vlaggetje || nr >= N) return;
          nr += 1;
          l.vlaggetje = nr;
          if (side.rect && l.verhouding) {
            l.grootte = Math.round(Math.max(100, (side.rect.w / side.rect.h) / l.verhouding * 100));
            l.cx = 50;
            l.cy = 50;
            l.hoek = 0;
          }
        });
        this.vlagNr = 1;
      } else {
        // Terug naar één ontwerp voor alle vlaggetjes: de foto's per vlaggetje en de verdeelde naam weg.
        side.lagen = side.lagen.filter((l) => !l.vlaggetje);
        // De naam wordt weer een gewone tekst op elk vlaggetje; zo klein dat hij erin past (Jos, 18 okt 2026:
        // anders stond 'FIJNE KERST' veel te breed op elk vlaggetje).
        side.lagen.forEach((l) => {
          if (!l.verdeel) return;
          delete l.verdeel;
          if (String(this.vorm || '').indexOf('wimpel') >= 0) l.cy = Math.min(l.cy, 24);
          if (this.ctx && side.rect) l.grootte = Math.max(2, Math.round(this.#pasLetter(this.ctx, side.rect, l).grootte * 10) / 10);
        });
      }
      this.#tekenLagenLijst(side);
      this.#render();
    });
    const tekst = document.createElement('span');
    tekst.textContent = T.aan;
    aan.append(vink, tekst);
    p.append(aan);
    if (!apart) return;

    // Strook met alle vlaggetjes in de volgorde van de band.
    const strook = document.createElement('div');
    strook.className = 'flagcfg-vlaggetjes__strook';
    side.vlagDoekjes = [];
    for (let n = 1; n <= N; n++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-pressed', n === this.vlagNr ? 'true' : 'false');
      b.setAttribute('aria-label', T.nr + ' ' + n);
      const c = document.createElement('canvas');
      const nummer = document.createElement('span');
      nummer.textContent = String(n);
      b.append(c, nummer);
      b.addEventListener('click', () => {
        this.vlagNr = n;
        side.gekozenLaag = null;
        this.#tekenLagenLijst(side);
        this.#render();
      });
      strook.append(b);
      side.vlagDoekjes.push(c);
    }
    // Teller en pijltjes: op een telefoon past maar een deel van de strook in beeld (Jos, 18 okt 2026).
    const kop = document.createElement('div');
    kop.className = 'flagcfg-vlaggetjes__kop';
    kop.textContent = T.nr + ' ' + this.vlagNr + ' ' + T.van + ' ' + N;
    const rand = document.createElement('div');
    rand.className = 'flagcfg-vlaggetjes__rand';
    const pijl = (richting) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'flagcfg-vlaggetjes__pijl';
      b.setAttribute('aria-label', richting < 0 ? '‹' : '›');
      b.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' + (richting < 0 ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7') + '"/></svg>';
      b.addEventListener('click', () => {
        strook.scrollLeft += richting * Math.max(80, strook.clientWidth * 0.8);
        setTimeout(toonPijlen, 450);
      });
      return b;
    };
    const links = pijl(-1);
    const rechts = pijl(1);
    const toonPijlen = () => {
      const meer = strook.scrollWidth > strook.clientWidth + 4;
      links.hidden = !meer || strook.scrollLeft < 4;
      rechts.hidden = !meer || strook.scrollLeft + strook.clientWidth > strook.scrollWidth - 4;
    };
    strook.addEventListener('scroll', toonPijlen, { passive: true });
    rand.append(links, strook, rechts);
    p.append(kop, rand);
    requestAnimationFrame(() => {
      // Het gekozen vlaggetje in beeld houden.
      const gekozen = strook.children[this.vlagNr - 1];
      if (gekozen) strook.scrollLeft = Math.max(0, gekozen.offsetLeft - strook.clientWidth / 2 + gekozen.offsetWidth / 2);
      toonPijlen();
    });

    // Waar komt wat je toevoegt? Standaard op het gekozen vlaggetje.
    const waar = document.createElement('div');
    waar.className = 'flagcfg-vlaggetjes__waar';
    const waarTekst = document.createElement('span');
    waarTekst.textContent = T.komtOp;
    const groep = document.createElement('span');
    groep.className = 'flagcfg-vlaggetjes__keuze';
    [[false, T.dit], [true, T.alle]].forEach(([alles, label]) => {
      const k = document.createElement('button');
      k.type = 'button';
      k.textContent = label;
      k.setAttribute('aria-pressed', !!side.vlagAlles === alles ? 'true' : 'false');
      k.addEventListener('click', () => {
        side.vlagAlles = alles;
        this.#tekenLagenLijst(side);
      });
      groep.append(k);
    });
    waar.append(waarTekst, groep);
    p.append(waar);

    // De naam: één verdeelde tekstlaag.
    const rij = document.createElement('div');
    rij.className = 'flagcfg-vlaggetjes__rij';
    const veld = document.createElement('input');
    veld.type = 'text';
    veld.maxLength = N;
    veld.placeholder = T.naam;
    veld.setAttribute('aria-label', T.naam);
    const bestaand = side.lagen.find((l) => l.verdeel);
    veld.value = bestaand ? bestaand.tekst || '' : '';
    veld.addEventListener('input', () => {
      let l = side.lagen.find((x) => x.verdeel);
      if (!l) {
        l = {
          id: this.#nieuwId(), type: 'tekst', tekst: '', verdeel: true,
          font: '"Bowlby One SC", Impact, sans-serif', kleur: this.#leesbareKleur(side),
          grootte: 34, hoek: 0, cx: 50, cy: String(this.vorm || '').indexOf('wimpel') >= 0 ? 30 : 45,
        };
        side.lagen.unshift(l);
        this.#tekenLagenLijst(side);
        veld.focus();
      }
      l.tekst = veld.value;
      this.#lijstVeldBij(side, l);
      this.#render();
    });

    // Lettertype en kleur van de letters (Jos, 18 okt 2026).
    const naamLaag = () => {
      let l = side.lagen.find((x) => x.verdeel);
      if (!l) {
        l = {
          id: this.#nieuwId(), type: 'tekst', tekst: veld.value || '', verdeel: true,
          font: '"Bowlby One SC", Impact, sans-serif', kleur: this.#leesbareKleur(side),
          grootte: 34, hoek: 0, cx: 50, cy: String(this.vorm || '').indexOf('wimpel') >= 0 ? 30 : 45,
        };
        side.lagen.unshift(l);
      }
      return l;
    };
    const huidig = side.lagen.find((x) => x.verdeel);
    const font = document.createElement('select');
    font.className = 'flagcfg-vlaggetjes__font';
    font.setAttribute('aria-label', T.lettertype);
    const taalF = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
    const groepen = {};
    LETTERTYPEN.forEach((lt) => {
      const soort = lt.soort || 'standaard';
      if (!groepen[soort]) {
        groepen[soort] = document.createElement('optgroup');
        const nm = LETTER_SOORTEN[soort] || {};
        groepen[soort].label = nm[taalF] || nm.nl || soort;
        font.appendChild(groepen[soort]);
      }
      const o = document.createElement('option');
      o.value = lt.css;
      o.textContent = lt.naam;
      groepen[soort].appendChild(o);
    });
    font.value = huidig ? huidig.font : '"Bowlby One SC", Impact, sans-serif';
    font.addEventListener('change', () => {
      naamLaag().font = font.value;
      this.#tekenLagenLijst(side);
      this.#render();
    });
    const kleur = document.createElement('input');
    kleur.type = 'color';
    kleur.className = 'flagcfg-vlaggetjes__kleur';
    kleur.setAttribute('aria-label', T.kleur);
    kleur.value = huidig && /^#[0-9a-f]{6}$/i.test(huidig.kleur) ? huidig.kleur : this.#leesbareKleur(side);
    kleur.addEventListener('input', () => {
      naamLaag().kleur = kleur.value;
      this.#render();
    });
    kleur.addEventListener('change', () => this.#tekenLagenLijst(side));
    rij.append(veld, font, kleur);
    p.append(rij);

    const uitleg = document.createElement('ul');
    uitleg.className = 'flagcfg-vlaggetjes__uitleg';
    const regels = String(this.vorm || '').indexOf('dubbel') >= 0 ? T.uitleg.concat(T.dubbel) : T.uitleg;
    regels.forEach((regel) => {
      const li = document.createElement('li');
      li.textContent = regel;
      uitleg.append(li);
    });
    p.append(uitleg);
    this.#planVlaggetjes(side);
  }

  /** De voorbeeldjes in de strook bijwerken, nooit tijdens een gebaar. */
  #planVlaggetjes(side) {
    if (!side.vlagDoekjes || !side.vlagDoekjes.length) return;
    clearTimeout(this.vlagKlok);
    this.vlagKlok = setTimeout(() => {
      if (this.#bezigMetGebaar()) { this.#planVlaggetjes(side); return; }
      this.#tekenVlaggetjes(side);
    }, 300);
  }

  #tekenVlaggetjes(side) {
    const r = side.rect;
    if (!r || !side.vlagDoekjes) return;
    const b = 76;
    const h = Math.round((b * r.h) / r.w);
    side.vlagDoekjes.forEach((c, i) => {
      if (!c.isConnected) return;
      c.width = b;
      c.height = h;
      const d = c.getContext('2d');
      const pad = vlagPad(this.vorm, { x: 0, y: 0, w: b, h: h });
      d.beginPath();
      if (pad && pad.length) {
        d.moveTo(pad[0][0], pad[0][1]);
        for (let k = 1; k < pad.length; k++) d.lineTo(pad[k][0], pad[k][1]);
        d.closePath();
      } else {
        d.rect(0, 0, b, h);
      }
      d.save();
      d.clip();
      d.fillStyle = side.bg || '#ffffff';
      d.fillRect(0, 0, b, h);
      try {
        if (side.img) {
          const g = this.#geometry(side);
          const k = b / r.w;
          d.drawImage(side.img, (g.x - r.x) * k, (g.y - r.y) * k, g.w * k, g.h * k);
        }
        this.#tekenLagen(d, side, { x: 0, y: 0, w: b, h: h }, i + 1);
      } catch (err) {
        /* een voorbeeldje mag mislukken */
      }
      d.restore();
      d.strokeStyle = 'rgba(0,0,0,.25)';
      d.lineWidth = 1;
      d.stroke();
    });
  }

  #laagVlak(ctx, rect, l) {
    if (l.verdeel) l = this.#pasLetter(ctx, rect, Object.assign({}, l, { verdeel: false, tekst: this.#letterVoor(l, this.vlagNr || 1) }));
    const x = rect.x + (l.cx / 100) * rect.w;
    const y = rect.y + (l.cy / 100) * rect.h;
    if (l.type !== 'tekst') {
      const hoog = (l.grootte / 100) * rect.h;
      // Een kleursymbool is vierkant; een eigen afbeelding houdt zijn eigen
      // verhouding, anders wordt een logo uitgerekt.
      const breed = (l.type === 'eigen' || l.type === 'vorm') ? hoog * (l.verhouding || 1) : hoog;
      return { x: x, y: y, breed: breed, hoog: hoog };
    }
    const px = (l.grootte / 100) * rect.h;
    const m = tekstOpmaak(ctx, l, px);
    return { x: x, y: y, px: px, breed: m.breed, hoog: m.hoog };
  }

  /* ---------- hulplijnen bij het uitlijnen ---------- */

  /**
   * Hoe dicht een element bij een uitlijnpunt moet komen voordat het vastklikt,
   * uitgedrukt in canvaspixels. Bewust klein: te ruim en de klant kan niets meer
   * bewust net naast het midden zetten.
   */
  static get SNAP() {
    return 8;
  }

  /**
   * De omhullende rechthoek van een laag, in percentages van het vlagvlak.
   *
   * Bij een gedraaide laag wordt de rechthoek om de gedraaide vorm heen
   * genomen, niet de oorspronkelijke breedte en hoogte. Anders lijkt een schuin
   * gezet symbool uitgelijnd terwijl het dat zichtbaar niet is.
   */
  #laagKader(side, l) {
    const rect = side.rect;
    const v = this.#laagVlak(this.ctx, rect, l);
    let breed = v.breed;
    let hoog = v.hoog;
    if (l.hoek) {
      const r = (l.hoek * Math.PI) / 180;
      const c = Math.abs(Math.cos(r));
      const s = Math.abs(Math.sin(r));
      const b = breed * c + hoog * s;
      const h = breed * s + hoog * c;
      breed = b;
      hoog = h;
    }
    const halfB = (breed / rect.w) * 50;
    const halfH = (hoog / rect.h) * 50;
    return {
      links: l.cx - halfB,
      midX: l.cx,
      rechts: l.cx + halfB,
      boven: l.cy - halfH,
      midY: l.cy,
      onder: l.cy + halfH,
      halfB: halfB,
      halfH: halfH,
    };
  }

  /**
   * Verzamelt waar een element op uit kan lijnen, in percentages.
   *
   * De prioriteit bepaalt welke lijn wint als er meerdere binnen bereik liggen:
   * 1 het midden van de vlag, 2 het midden van een ander element, 3 een rand.
   * Zonder die volgorde zou een element bij het midden van de vlag ineens aan de
   * rand van iets anders blijven hangen.
   */
  #uitlijnDoelen(side, negeer) {
    const x = [{ waarde: 50, prio: 1 }];
    const y = [{ waarde: 50, prio: 1 }];
    (side.lagen || []).forEach((l) => {
      if (l === negeer) return;
      const k = this.#laagKader(side, l);
      x.push({ waarde: k.midX, prio: 2 });
      y.push({ waarde: k.midY, prio: 2 });
      x.push({ waarde: k.links, prio: 3 }, { waarde: k.rechts, prio: 3 });
      y.push({ waarde: k.boven, prio: 3 }, { waarde: k.onder, prio: 3 });
    });
    return { x: x, y: y };
  }

  /**
   * Past de positie aan als er iets binnen bereik ligt, en zegt welke lijnen er
   * getekend moeten worden.
   *
   * Het element lijnt met drie punten tegelijk uit: zijn eigen middelpunt en
   * zijn twee randen. Zo klikt hij zowel vast op "midden onder midden" als op
   * "linkerrand gelijk", wat je nodig hebt om een rijtje symbolen netjes onder
   * elkaar te krijgen.
   */
  #uitlijnen(side, laag, cx, cy, doelen) {
    const rect = side.rect;
    // De andere elementen staan stil zolang er gesleept wordt, dus hun
    // uitlijnpunten worden eenmalig bij het oppakken berekend en meegegeven.
    // Ze elke vingerbeweging opnieuw uitrekenen kost een tekstmeting per laag,
    // en dat telt op zodra er veel op de vlag staat.
    if (!doelen) doelen = this.#uitlijnDoelen(side, laag);
    const k = this.#laagKader(side, Object.assign({}, laag, { cx: cx, cy: cy }));
    const tolX = (FlagConfigurator.SNAP / rect.w) * 100;
    const tolY = (FlagConfigurator.SNAP / rect.h) * 100;

    // Per as de beste kandidaat zoeken: eerst op prioriteit, dan op afstand.
    const zoek = (kanten, lijst, tol) => {
      let best = null;
      kanten.forEach((kant) => {
        lijst.forEach((d) => {
          const afstand = Math.abs(kant.positie - d.waarde);
          if (afstand > tol) return;
          if (!best || d.prio < best.prio || (d.prio === best.prio && afstand < best.afstand)) {
            best = { prio: d.prio, afstand: afstand, lijn: d.waarde, verschuif: d.waarde - kant.positie };
          }
        });
      });
      return best;
    };

    const bX = zoek(
      [{ positie: k.midX }, { positie: k.links }, { positie: k.rechts }],
      doelen.x, tolX
    );
    const bY = zoek(
      [{ positie: k.midY }, { positie: k.boven }, { positie: k.onder }],
      doelen.y, tolY
    );

    const lijnen = [];
    if (bX) { cx += bX.verschuif; lijnen.push({ richting: 'x', pos: bX.lijn }); }
    if (bY) { cy += bY.verschuif; lijnen.push({ richting: 'y', pos: bY.lijn }); }
    return { cx: cx, cy: cy, lijnen: lijnen };
  }

  /**
   * Tekent de hulplijnen over de vlag heen. Alleen tijdens het slepen; ze staan
   * los van de lagen en komen dus nooit op het drukbestand terecht.
   */
  #tekenHulplijnen(ctx, side, rect) {
    const lijnen = side.hulplijnen;
    if (!lijnen || !lijnen.length) return;
    ctx.save();
    ctx.strokeStyle = '#e5484d';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 5]);
    lijnen.forEach((l) => {
      ctx.beginPath();
      if (l.richting === 'x') {
        const x = rect.x + (l.pos / 100) * rect.w;
        ctx.moveTo(x, rect.y);
        ctx.lineTo(x, rect.y + rect.h);
      } else {
        const y = rect.y + (l.pos / 100) * rect.h;
        ctx.moveTo(rect.x, y);
        ctx.lineTo(rect.x + rect.w, y);
      }
      ctx.stroke();
    });
    ctx.restore();
  }

  /**
   * Tekent een kader om het element dat op dit moment versleept wordt.
   *
   * Waarom: op een telefoon bedekt je vinger juist het element dat je
   * verplaatst, en bij een vlag vol tekst en symbolen zie je niet welk
   * onderdeel meebeweegt. Het kader beantwoordt die ene vraag: dit heb je vast.
   *
   * Staat bewust hier en niet in #tekenLagen. Die methode wordt namelijk ook
   * gebruikt om het drukbestand te maken, en dan zou dit kader meegedrukt
   * worden. Dit volgt hetzelfde patroon als #tekenHulplijnen: alleen in het
   * scherm, nooit in de export.
   */
  /** Hoeveel canvaseenheden er in een schermpixel gaan. */
  #viewPerPx() {
    // Tijdens het slepen de maat van het oppakken (zie naarVlak in #bindGestures).
    const r = this.gebaarRect || (this.canvas ? this.canvas.getBoundingClientRect() : null);
    const breed = r ? r.width : 0;
    return breed ? this.layout.view.w / breed : 1;
  }

  /** Middelpunt en de vier hoekjes van het kader om een laag, in canvaseenheden. */
  #hoekpunten(side, l) {
    const v = this.#laagVlak(this.ctx, side.rect, l);
    const marge = 6 * this.#viewPerPx();
    const b = v.breed / 2 + marge;
    const h = v.hoog / 2 + marge;
    const hoek = ((l.hoek || 0) * Math.PI) / 180;
    const cos = Math.cos(hoek);
    const sin = Math.sin(hoek);
    return {
      midden: { x: v.x, y: v.y },
      vlak: v,
      punten: [[-b, -h], [b, -h], [-b, h], [b, h]].map(([dx, dy]) => ({
        x: v.x + dx * cos - dy * sin,
        y: v.y + dx * sin + dy * cos,
      })),
    };
  }

  /**
   * Welk hoekje van het gekozen element ligt onder dit punt? -1 als geen.
   *
   * Het aanraakgebied is 22 schermpixels rond het hoekje, dus 44 breed: groot
   * genoeg voor een vinger, ook al is het hoekje zelf kleiner getekend. Bij een
   * klein element zou dat gebied het hele element bedekken en kon je het niet
   * meer verschuiven. Daarom telt een aanraking binnen het element zelf alleen
   * als hoekje als hij echt vlak bij de hoek is.
   */
  #hoekjeOpPunt(side, l, p) {
    if (!l || !side.lagen || !side.lagen.includes(l)) return -1;
    if (l.type === 'tekst' && !l.tekst) return -1;
    const s = this.#viewPerPx();
    const bereik = 22 * s;
    const { punten, vlak, midden } = this.#hoekpunten(side, l);
    const hoek = (-(l.hoek || 0) * Math.PI) / 180;
    const dx = p.x - midden.x;
    const dy = p.y - midden.y;
    const lx = dx * Math.cos(hoek) - dy * Math.sin(hoek);
    const ly = dx * Math.sin(hoek) + dy * Math.cos(hoek);
    const binnen = Math.abs(lx) <= vlak.breed / 2 && Math.abs(ly) <= vlak.hoog / 2;
    let beste = -1;
    let dichtst = Infinity;
    punten.forEach((q, i) => {
      const d = Math.hypot(p.x - q.x, p.y - q.y);
      if (d > bereik || d >= dichtst) return;
      if (binnen && d > 10 * s) return;
      beste = i;
      dichtst = d;
    });
    return beste;
  }

  /** De zichtbare zijde waarvan een hoekje van het gekozen element onder dit punt ligt. */
  #zijdeMetHoekje(p) {
    if (!this.classList.contains('is-vol') || !this.tekstToegestaan) return null;
    const { view } = this.layout;
    return this.sides.find((s) => s.rect.x < view.w && this.#hoekjeOpPunt(s, s.gekozenLaag, p) >= 0) || null;
  }

  /**
   * Het draaiknopje onder het gekozen element (Jos, 7 okt 2026, zoals Canva).
   * Het hangt 30 schermpixels onder het kader en draait mee met het element.
   * Valt het daar buiten beeld, dan komt het boven het kader.
   * Geeft het midden van het knopje in canvaseenheden en de richting (1 onder,
   * -1 boven), of null als er niets gekozen is.
   */
  #draaiknop(side, l) {
    if (!l || !side.lagen || !side.lagen.includes(l)) return null;
    if (l.type === 'tekst' && !l.tekst) return null;
    const s = this.#viewPerPx();
    const { vlak } = this.#hoekpunten(side, l);
    const afstand = vlak.hoog / 2 + 6 * s + 30 * s;
    const hoek = ((l.hoek || 0) * Math.PI) / 180;
    const plek = (richting) => ({
      x: vlak.x - richting * afstand * Math.sin(hoek),
      y: vlak.y + richting * afstand * Math.cos(hoek),
      richting,
    });
    const onder = plek(1);
    const { view } = this.layout;
    if (onder.y > view.h - 14 * s || onder.y < 14 * s) {
      const boven = plek(-1);
      if (boven.y >= 14 * s && boven.y <= view.h - 14 * s) return boven;
    }
    return onder;
  }

  /** Ligt dit punt op het draaiknopje van het gekozen element? 22 schermpixels rond het midden. */
  #draaiknopOpPunt(side, l, p) {
    if (!this.classList.contains('is-vol') || !side) return false;
    const k = this.#draaiknop(side, l);
    if (!k) return false;
    return Math.hypot(p.x - k.x, p.y - k.y) <= 22 * this.#viewPerPx();
  }

  /** De zichtbare zijde waarvan het draaiknopje onder dit punt ligt. */
  #zijdeMetDraaiknop(p) {
    if (!this.classList.contains('is-vol') || !this.tekstToegestaan) return null;
    const { view } = this.layout;
    return this.sides.find((s) => s.rect.x < view.w && this.#draaiknopOpPunt(s, s.gekozenLaag, p)) || null;
  }

  #tekenSelectie(ctx, side, rect) {
    // Tijdens het slepen het element dat je vasthebt; daarbuiten, in de
    // ontwerptool, het gekozen element (met hoekjes om te schalen). Nooit in het
    // controlebeeld: zie #makePreview.
    if (this.zonderSelectie) return;
    const l = side.sleepLaag || (this.classList.contains('is-vol') ? side.gekozenLaag : null);
    // includes(): een sjabloon of "alles wissen" vervangt de lagen, en dan mag
    // er geen kader blijven staan om een element dat er niet meer is.
    if (!l || !side.lagen || !side.lagen.includes(l)) return;
    if (l.type === 'tekst' && !l.tekst) return;

    const v = this.#laagVlak(ctx, rect, l);
    // Iets ruimer dan het element zelf, zodat de lijn eromheen ligt en niet
    // dwars door de letters of het logo.
    // Maten in schermpixels, omgerekend naar canvaseenheden: het doek staat op
    // een telefoon veel kleiner in beeld dan op een computer, en een kader of
    // hoekje hoort er op elk scherm even groot uit te zien.
    const s = this.#viewPerPx();
    const marge = 6 * s;
    const b = v.breed + marge * 2;
    const h = v.hoog + marge * 2;

    ctx.save();
    ctx.translate(v.x, v.y);
    // Meedraaien met het element; #laagVlak geeft de maten zonder rotatie.
    if (l.hoek) ctx.rotate((l.hoek * Math.PI) / 180);

    // Eerst een donkere schaduwlijn, dan de paarse erover. Zonder die onderlaag
    // valt paars weg op een donkerpaarse of zwarte vlag.
    ctx.lineWidth = 3.5 * s;
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.28)';
    ctx.strokeRect(-b / 2, -h / 2, b, h);

    ctx.lineWidth = 2 * s;
    ctx.strokeStyle = SELECTIE_KLEUR;
    ctx.strokeRect(-b / 2, -h / 2, b, h);

    // Hoekpunten: wit gevuld met een paarse rand, zodat ze op elke
    // achtergrondkleur zichtbaar blijven.
    // Rond, wit met een paarse rand en een zachte schaduw, zodat ze op elke
    // kleur zichtbaar zijn. Het aanraakgebied is groter (#hoekjeOpPunt).
    const straal = 7 * s;
    [[-b / 2, -h / 2], [b / 2, -h / 2], [-b / 2, h / 2], [b / 2, h / 2]].forEach(([hx, hy]) => {
      ctx.beginPath();
      ctx.arc(hx, hy, straal + 1.5 * s, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.22)';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(hx, hy, straal, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.lineWidth = 2 * s;
      ctx.strokeStyle = SELECTIE_KLEUR;
      ctx.stroke();
    });

    // Het draaiknopje. Weg zolang het element zelf versleept of geschaald wordt,
    // zodat een vinger het onderweg niet kan raken; wel tijdens het draaien.
    const knop = (!side.sleepLaag || side.draaien) ? this.#draaiknop(side, l) : null;
    if (knop) {
      const r = knop.richting;
      const kStraal = 11 * s;
      const kAfstand = h / 2 + 30 * s;
      ctx.beginPath();
      ctx.moveTo(0, r * (h / 2));
      ctx.lineTo(0, r * (kAfstand - kStraal));
      ctx.lineWidth = 2 * s;
      ctx.strokeStyle = SELECTIE_KLEUR;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, r * kAfstand, kStraal + 1.5 * s, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.22)';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(0, r * kAfstand, kStraal, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.lineWidth = 2 * s;
      ctx.strokeStyle = SELECTIE_KLEUR;
      ctx.stroke();
      // draaipijltje: een boog met een punt
      const py = r * kAfstand;
      const ps = 5.5 * s;
      ctx.beginPath();
      ctx.arc(0, py, ps, -Math.PI * 0.35, Math.PI * 1.25);
      ctx.lineWidth = 2 * s;
      ctx.lineCap = 'round';
      ctx.stroke();
      const eindX = ps * Math.cos(-Math.PI * 0.35);
      const eindY = py + ps * Math.sin(-Math.PI * 0.35);
      ctx.beginPath();
      ctx.moveTo(eindX - 3.2 * s, eindY - 2.6 * s);
      ctx.lineTo(eindX, eindY);
      ctx.lineTo(eindX - 3.8 * s, eindY + 2.2 * s);
      ctx.lineJoin = 'round';
      ctx.stroke();
      ctx.lineCap = 'butt';
    }

    ctx.restore();

    // Het dupliceerknopje naast het draaiknopje; niet tijdens het draaien zelf.
    if (knop && !side.draaien) this.#tekenDupliceerknop(ctx, side, l);

    // Tijdens het draaien het aantal graden naast het knopje, rechtop.
    if (knop && side.draaien) {
      const tekst = Math.round(l.hoek || 0) + '°';
      ctx.save();
      ctx.font = '700 ' + 13 * s + 'px Arial, Helvetica, sans-serif';
      const tb = ctx.measureText(tekst).width + 16 * s;
      const th = 24 * s;
      const tx = knop.x + 26 * s;
      const ty = knop.y - th / 2;
      ctx.fillStyle = 'rgba(31, 36, 48, 0.9)';
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(tx, ty, tb, th, th / 2);
      else ctx.rect(tx, ty, tb, th);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(tekst, tx + tb / 2, ty + th / 2 + 0.5 * s);
      ctx.restore();
    }
  }

  /** Tekent alle lagen van een zijde binnen het opgegeven doek. */
  #tekenLagen(ctx, side, rect, nr) {
    if (!this.tekstToegestaan || !side.lagen.length) return;
    // Alleen op het scherm een kopie op schermformaat (#schermKopie). Het
    // drukbestand en de vergroting geven een eigen tekenvlak mee en tekenen dus
    // altijd het origineel, op volle scherpte.
    const scherm = ctx === this.ctx;
    const snel = scherm && this.#bezigMetGebaar();
    const beeld = (l, v) => (scherm && this.#schermKopie(l.plaatje, v.breed, v.hoog, snel)) || l.plaatje;
    // Van achteren naar voren: de laag bovenaan de lijst (index 0) wordt als
    // laatste getekend en ligt dus bovenop, zoals je verwacht.
    // Vlaggenlijn met elk vlaggetje anders: alleen de lagen van dit vlaggetje, en van een verdeelde naam
    // alleen de letter die hier hoort. Op het scherm is dat het gekozen vlaggetje.
    const lagen = this.#lagenVoor(side, nr === undefined ? (scherm ? this.vlagNr || 1 : null) : nr, ctx, rect);
    [...lagen].reverse().forEach((l) => {
      if (l.type === 'tekst' && !l.tekst) return;
      const v = this.#laagVlak(ctx, rect, l);
      ctx.save();
      try {
        ctx.translate(v.x, v.y);
        if (l.hoek) ctx.rotate((l.hoek * Math.PI) / 180);
        ctx.fillStyle = l.kleur;
        if (l.type === 'eigen') {
          if (!l.plaatje) return;
          if (l.snB) {
            // Bijgesneden: alleen het gekozen stuk. De schermkopie op de maat van
            // de hele afbeelding, zodat het zichtbare stuk scherp blijft.
            const b = (scherm && this.#schermKopie(l.plaatje, v.breed / l.snB, v.hoog / l.snH, snel)) || l.plaatje;
            const iw = b instanceof HTMLImageElement ? b.naturalWidth : b.width;
            const ih = b instanceof HTMLImageElement ? b.naturalHeight : b.height;
            ctx.drawImage(b, l.snX * iw, l.snY * ih, l.snB * iw, l.snH * ih, -v.breed / 2, -v.hoog / 2, v.breed, v.hoog);
            return;
          }
          ctx.drawImage(beeld(l, v), -v.breed / 2, -v.hoog / 2, v.breed, v.hoog);
        } else if (l.type === 'vorm') {
          this.#tekenVorm(ctx, l, v.breed, v.hoog);
        } else if (l.type === 'kleur') {
          // Nog niet binnen? Dan alvast beginnen met laden en deze ronde
          // overslaan; zodra het plaatje er is wordt er opnieuw getekend.
          if (!l.plaatje) {
            this.#laadPlaatje(l);
            return;
          }
          ctx.drawImage(beeld(l, v), -v.breed / 2, -v.hoog / 2, v.breed, v.hoog);
        } else {
          tekenTekst(ctx, l, v.px, l.kleur);
        }
      } catch (err) {
        // Op het scherm een kapot element overslaan, zodat de rest zichtbaar
        // blijft. Voor het drukbestand juist niet: daar liever geen bestand dan
        // een bestand waar stilletjes een element uit mist.
        if (!scherm) throw err;
      } finally {
        ctx.restore();
      }
    });
  }

  /**
   * Tekent een vorm rond het nulpunt, in de kleur van de laag.
   *
   * Getekend en niet als plaatje geladen: zo kan de klant er een eigen kleur
   * aan geven, en blijft de rand haarscherp — ook op een vlag van vier meter,
   * waar een plaatje van 128 bij 128 zichtbaar zou vervagen.
   */
  #tekenVorm(ctx, l, breed, hoog) {
    if (l.tekening || String(l.vorm || '').indexOf('el-') === 0) {
      this.#tekenElement(ctx, l, breed, hoog);
      return;
    }
    const b = breed / 2;
    const h = hoog / 2;
    const dik = Math.max(2, Math.min(breed, hoog) * 0.14);
    ctx.fillStyle = l.kleur;
    ctx.strokeStyle = l.kleur;
    ctx.lineWidth = dik;
    ctx.lineJoin = 'round';
    ctx.beginPath();

    switch (l.vorm) {
      case 'cirkel':
        ctx.ellipse(0, 0, b, h, 0, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'ring':
        ctx.ellipse(0, 0, b - dik / 2, h - dik / 2, 0, 0, Math.PI * 2);
        ctx.stroke();
        break;
      case 'vierkant':
        ctx.rect(-b, -h, breed, hoog);
        ctx.fill();
        break;
      case 'kader':
        ctx.rect(-b + dik / 2, -h + dik / 2, breed - dik, hoog - dik);
        ctx.stroke();
        break;
      case 'balk':
      case 'streep':
        ctx.rect(-b, -h, breed, hoog);
        ctx.fill();
        break;
      case 'driehoek':
        ctx.moveTo(0, -h);
        ctx.lineTo(b, h);
        ctx.lineTo(-b, h);
        ctx.closePath();
        ctx.fill();
        break;
      case 'ruit':
        ctx.moveTo(0, -h);
        ctx.lineTo(b, 0);
        ctx.lineTo(0, h);
        ctx.lineTo(-b, 0);
        ctx.closePath();
        ctx.fill();
        break;
      case 'ster': {
        // vijfpuntige ster: afwisselend een punt op de buiten- en binnenring
        const punten = 5;
        for (let i = 0; i < punten * 2; i += 1) {
          const straal = i % 2 === 0 ? 1 : 0.42;
          const hoek = (Math.PI / punten) * i - Math.PI / 2;
          const x = Math.cos(hoek) * b * straal;
          const y = Math.sin(hoek) * h * straal;
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.fill();
        break;
      }
      case 'hart': {
        // twee bogen bovenin die samenkomen in een punt onderaan
        const t = -h * 0.35;
        ctx.moveTo(0, h);
        ctx.bezierCurveTo(-b * 1.35, -h * 0.1, -b * 0.55, -h * 1.25, 0, t);
        ctx.bezierCurveTo(b * 0.55, -h * 1.25, b * 1.35, -h * 0.1, 0, h);
        ctx.closePath();
        ctx.fill();
        break;
      }
      case 'pijl': {
        const schacht = h * 0.42;
        const kop = b * 0.42;
        ctx.moveTo(-b, -schacht);
        ctx.lineTo(b - kop, -schacht);
        ctx.lineTo(b - kop, -h);
        ctx.lineTo(b, 0);
        ctx.lineTo(b - kop, h);
        ctx.lineTo(b - kop, schacht);
        ctx.lineTo(-b, schacht);
        ctx.closePath();
        ctx.fill();
        break;
      }
      case 'vlag':
        ctx.moveTo(-b, -h);
        ctx.lineTo(b, -h);
        ctx.lineTo(b * 0.42, 0);
        ctx.lineTo(b, h);
        ctx.lineTo(-b, h);
        ctx.closePath();
        ctx.fill();
        break;
      default:
        ctx.rect(-b, -h, breed, hoog);
        ctx.fill();
    }
  }

  /**
   * Tekent een getekend element (zie leesElement). Een sjabloon heeft de
   * tekening niet bij zich, alleen de naam; die staat dan in
   * FLAGCFG_SJABLOON_TEKENINGEN (assets/flag-sjablonen.js).
   *
   * Op het scherm met een kant-en-klare kopie op schermformaat, zodra die er
   * is: een ballonnentros of vuurwerk is honderd vlakjes, en die bij elke
   * sleepbeweging opnieuw vullen kan een telefoon laten haperen. Het
   * drukbestand en de vergroting tekenen altijd de echte paden.
   */
  #tekenElement(ctx, l, breed, hoog) {
    const bron = l.tekening || ((window.FLAGCFG_SJABLOON_TEKENINGEN || {})[l.vorm] || {}).t;
    if (!bron) return;
    const t = leesElement(bron);
    if (ctx === this.ctx && l.tekening) {
      const kopie = this.#elementKopie(l, breed, hoog);
      if (kopie) {
        ctx.drawImage(kopie, -breed / 2, -hoog / 2, breed, hoog);
        return;
      }
    }
    tekenElementPaden(ctx, t, l.kleur, breed, hoog);
  }

  /**
   * Dezelfde aanpak als #schermKopie: tijdens een gebaar mag de kopie net een
   * andere maat hebben (bij knijpen verandert die elke beweging), daarbuiten
   * alleen een kopie die precies past. Anders nu de paden, en de kopie wordt
   * even later gemaakt, nooit midden in een gebaar.
   */
  #elementKopie(l, breed, hoog) {
    const pxB = Math.round(breed * DPR);
    const pxH = Math.round(hoog * DPR);
    if (pxB < 2 || pxH < 2 || pxB > 2400 || pxH > 2400) return null;
    const sleutel = l.vorm + '|' + l.kleur;
    const oud = this.elementKopieen && this.elementKopieen.get(l);
    const snel = this.#bezigMetGebaar();
    if (oud && oud.sleutel === sleutel && (snel || (Math.abs(oud.b - pxB) <= 1 && Math.abs(oud.h - pxH) <= 1))) return oud.doek;
    if (!snel) {
      if (!this.elementWacht) this.elementWacht = new Map();
      this.elementWacht.set(l, [pxB, pxH]);
      clearTimeout(this.elementTimer);
      this.elementTimer = setTimeout(() => this.#maakElementKopieen(), 180);
    }
    return null;
  }

  #maakElementKopieen() {
    if (this.#bezigMetGebaar()) {
      this.elementTimer = setTimeout(() => this.#maakElementKopieen(), 250);
      return;
    }
    const lijst = this.elementWacht || new Map();
    this.elementWacht = new Map();
    if (!this.elementKopieen) this.elementKopieen = new WeakMap();
    lijst.forEach(([pxB, pxH], l) => {
      if (!l.tekening) return;
      try {
        const doek = document.createElement('canvas');
        doek.width = pxB;
        doek.height = pxH;
        const k = doek.getContext('2d');
        k.translate(pxB / 2, pxH / 2);
        tekenElementPaden(k, leesElement(l.tekening), l.kleur, pxB, pxH);
        // De vorige kopie meteen vrijgeven: een iPhone ruimt canvasgeheugen
        // pas laat op (zie CLAUDE.md, "Lege vlag op de iPhone").
        this.#elementKopieWeg(l);
        this.elementKopieen.set(l, { sleutel: l.vorm + '|' + l.kleur, b: pxB, h: pxH, doek });
      } catch (err) {
        // Zonder kopie tekent hij gewoon de paden; dat is alleen iets zwaarder.
      }
    });
  }

  /**
   * Zet het doek opnieuw neer en vergeet alle bewaarde kopieën (elementen en
   * afbeeldingen op schermformaat). Jos (17 okt 2026, iPhone): na het openen
   * van een sjabloon werd het doek even wit en kwam het terug zonder de
   * elementen. Safari gooit bij geheugendruk (het drukbestand van 8000 px) het
   * beeld van canvassen weg; het doek werd hersteld, maar de lege kopieën
   * bleven in gebruik. Daarom na elk drukbestand en bij elk herstel opnieuw.
   */
  #herstelDoek() {
    if (!this.isConnected || !this.layout || !this.canvas) return;
    if (this.#bezigMetGebaar()) { setTimeout(() => this.#herstelDoek(), 400); return; }
    this.elementKopieen = new WeakMap();
    this.schermKopieen = new WeakMap();
    this.#zetCanvas();
    this.#render(true);
  }

  #elementKopieWeg(l) {
    const oud = this.elementKopieen && this.elementKopieen.get(l);
    if (oud && oud.doek) {
      oud.doek.width = 0;
      oud.doek.height = 0;
    }
    if (this.elementKopieen) this.elementKopieen.delete(l);
  }

  /** Welke laag ligt er onder dit punt? Bovenste eerst. */
  #laagOpPunt(side, p) {
    if (!this.tekstToegestaan) return null;
    const ctx = this.ctx;
    const r = side.rect;
    // Bovenste laag eerst: die ligt er ook visueel bovenop.
    for (let i = 0; i < side.lagen.length; i++) {
      const l = side.lagen[i];
      if (l.type === 'tekst' && !l.tekst) continue;
      if (this.#onzichtbaar(side, l)) continue;
      const v = this.#laagVlak(ctx, r, l);
      // terugdraaien naar het assenstelsel van de laag, dan simpel rechthoek toetsen
      const dx = p.x - v.x;
      const dy = p.y - v.y;
      const hoek = (-l.hoek * Math.PI) / 180;
      const lx = dx * Math.cos(hoek) - dy * Math.sin(hoek);
      const ly = dx * Math.sin(hoek) + dy * Math.cos(hoek);
      // Raakmarge meegroeien met de laag: op een telefoon is het doek maar een
      // paar honderd beeldpunten breed, dus een vaste marge is onraakbaar.
      const marge = Math.max(10, Math.min(v.breed, v.hoog) * 0.2);
      if (Math.abs(lx) <= v.breed / 2 + marge && Math.abs(ly) <= v.hoog / 2 + marge) return l;
    }
    return null;
  }

  /** Korte samenvatting van de lagen, voor de instructie aan de drukker. */
  #laagOmschrijving(side) {
    if (!this.tekstToegestaan || !side.lagen.length) return '';
    const fonts = Object.fromEntries(LETTERTYPEN.map((f) => [f.css, f.naam]));
    const delen = side.lagen
      .filter((l) => l.type !== 'tekst' || l.tekst)
      .map((l) => {
        const draai = l.hoek ? ', ' + Math.round(l.hoek) + ' graden gedraaid' : '';
        if (l.type === 'eigen') {
          return 'eigen afbeelding "' + (l.naam || '?') + '" (' +
            Math.round(l.grootte) + '% van de hoogte' + draai +
            (l.zonderAchtergrond ? ', achtergrond weggehaald' : '') +
            (l.bronUrl ? ', bestand: ' + l.bronUrl : '') + ')';
        }
        if (l.type === 'kleur') {
          return 'kleursymbool ' + l.teken + ' (' +
            Math.round(l.grootte) + '% van de hoogte' + draai + ')';
        }
        if (l.type === 'vorm' && (l.tekening || String(l.vorm || '').indexOf('el-') === 0)) {
          const kleurbaar = l.tekening && leesElement(l.tekening).kleurbaar;
          return 'element ' + ((l.namen && l.namen.nl) || l.teken || l.vorm) + ' (' + (kleurbaar ? l.kleur + ', ' : '') +
            Math.round(l.grootte) + '% van de hoogte' + draai + ')';
        }
        if (l.type === 'vorm') {
          return 'vorm ' + ((l.namen && l.namen.nl) || l.vorm) + ' (' + l.kleur + ', ' +
            Math.round(l.grootte) + '% van de hoogte' + draai + ')';
        }
        return '"' + l.tekst + '" (' + (fonts[l.font] || 'Arial') + ', ' + l.kleur + ', ' +
          Math.round(l.grootte) + '% van de hoogte' + draai + ')';
      });
    return delen.length ? ' — ontwerptool: ' + delen.join(' + ') : '';
  }

  // ---------- vlaggendatabank ----------

  /** Zet aria-expanded op beide knoppen die de databank openen. */
  #libAria(side, open) {
    [side.libToggle, side.libToggleWb].forEach((k) => {
      if (k) k.setAttribute('aria-expanded', String(open));
    });
  }

  #bindLibrary(side) {
    if (!side.libToggle && !side.libToggleWb) return;

    // Onthouden waar het paneel thuishoort, zodat het na de ontwerptool
    // weer onder de rode knop komt te staan.
    if (side.libBlok) {
      side.libThuis = { ouder: side.libBlok.parentNode, na: side.libBlok.previousElementSibling };
    }

    const klap = async () => {
      const open = side.libPanel.hidden;
      side.libPanel.hidden = !open;
      this.#libAria(side, open);
      // Maar één paneel tegelijk: gaan de vlaggen open, dan gaat Symbolen dicht.
      if (open) {
        const symboolBlok = side.root.querySelector('[data-symboolblok]');
        const symboolKnop = side.root.querySelector('[data-symboolopen]');
        if (symboolBlok && !symboolBlok.hidden) {
          symboolBlok.hidden = true;
          if (symboolKnop) symboolKnop.setAttribute('aria-expanded', 'false');
        }
      }
      if (open && !side.libBuilt) {
        side.libBuilt = true;
        await this.#buildGrid(side);
      }
      if (open) side.libSearch.focus();
    };

    [side.libToggle, side.libToggleWb].forEach((k) => {
      if (k) k.addEventListener('click', klap);
    });

    side.libSearch.addEventListener('input', () => this.#filterGrid(side));
  }

  /**
   * Verhuist het paneel van de vlaggendatabank naar de werkbalk en terug.
   * Zonder dit klap je onderin een tegel open en verschijnt de lijst boven
   * in beeld, buiten het zicht.
   */
  #libVerhuis(side, naarWerkbalk) {
    if (!side.libBlok || !side.werkbalk || !side.libThuis) return;
    if (naarWerkbalk) {
      side.werkbalk.after(side.libBlok);
    } else if (side.libThuis.na) {
      side.libThuis.na.after(side.libBlok);
    } else {
      side.libThuis.ouder.prepend(side.libBlok);
    }
  }

  async #buildGrid(side) {
    const flags = await this.#flags();
    const frag = document.createDocumentFragment();
    const koppen = {
      thema: this.dataset.tGrpThema || 'Thema’s',
      provincie: this.dataset.tGrpProvincie || 'Provincies',
      gemeente: this.dataset.tGrpGemeente || 'Gemeenten',
      belgie: this.dataset.tGrpBelgie || 'België',
      land: this.dataset.tGrpLand || 'Landen',
    };
    // Bij vierhonderd vlaggen telt elke besparing per toetsaanslag. Daarom
    // onthouden we hier alvast waar het zoeken naar kijkt, in plaats van dat
    // straks vierhonderd keer uit de data-attributen te lezen.
    side.libItems = [];
    side.libKoppen = [];
    let vorigeGroep = null;
    flags.forEach((f) => {
      if (f.groep !== vorigeGroep) {
        vorigeGroep = f.groep;
        const kop = document.createElement('p');
        kop.className = 'flagcfg__libkop';
        kop.textContent = koppen[f.groep] || '';
        frag.appendChild(kop);
        side.libKoppen.push({ el: kop, groep: f.groep, verborgen: false });
      }
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'flagcfg__libitem';
      btn.title = f.label;
      const img = document.createElement('img');
      img.src = this.flagBase + 'flag-' + f.code + '.svg';
      img.alt = '';
      img.loading = 'lazy';
      img.width = 40;
      img.height = 30;
      const naam = document.createElement('span');
      naam.textContent = f.label;
      btn.append(img, naam);
      btn.addEventListener('click', () => this.#pickFlag(side, f));
      frag.appendChild(btn);
      side.libItems.push({
        el: btn,
        groep: f.groep,
        zoek: (f.label + ' ' + f.code + ' ' + (f.zoek || '')).toLowerCase(),
        verborgen: false,
      });
    });
    side.libGrid.appendChild(frag);
  }

  #filterGrid(side) {
    const q = side.libSearch.value.trim().toLowerCase();
    const items = side.libItems || [];
    const gevuld = {};
    let zichtbaar = 0;
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      const hit = !q || it.zoek.indexOf(q) >= 0;
      const moetWeg = !hit;
      // Alleen schrijven als het echt verandert. Zonder deze regel rekent de
      // browser bij elke toetsaanslag de opmaak van vierhonderd elementen
      // opnieuw uit, ook als er niets verandert.
      if (it.verborgen !== moetWeg) {
        it.el.hidden = moetWeg;
        it.verborgen = moetWeg;
      }
      if (hit) { zichtbaar += 1; gevuld[it.groep] = true; }
    }
    // Een kopje zonder vlaggen eronder verdwijnt mee, anders blijft er een
    // losse kop "Provincies" staan boven niets.
    (side.libKoppen || []).forEach((k) => {
      const weg = !gevuld[k.groep];
      if (k.verborgen !== weg) { k.el.hidden = weg; k.verborgen = weg; }
    });
    side.libEmpty.hidden = zichtbaar > 0;
  }

  /**
   * Zet vlaggen uit de databank meteen op het doek, voor wie van een vlagpagina
   * komt. Precies hetzelfde als zelf op die vlag tikken in de lijst. Een code
   * die niet in de databank staat doet niets.
   */
  async #startMetVlag(codes) {
    const lijst = await this.#flags();
    let gekozen = false;
    // Komt van een vlagpagina (?vlag=..): dan nodigt de knop uit om tekst toe te voegen.
    this.vanVlagpagina = true;
    codes.forEach((code, i) => {
      const side = this.sides && this.sides[i];
      const c = String(code || '').toLowerCase();
      const flag = c && lijst.find((f) => f.code === c);
      if (side && flag && side.libGrid && side.libValue) {
        this.#pickFlag(side, flag);
        gekozen = true;
      }
    });
    // Meteen laten zien: de rij "Jouw ontwerp" open en in beeld. Pas na het
    // laden van de pagina, anders duwen de productfoto's de rij nog omlaag. Is
    // de klant zelf al aan het scrollen, dan niet meer verspringen.
    if (!gekozen) {
      this.vanVlagpagina = false;
      this.#toonOntwerpStatus();
    }
    const rij = this.ontwerpRij;
    if (!gekozen || !rij) return;
    this.#klapRij(rij, true);
    const inBeeld = () => {
      if (window.scrollY > 80) return;
      const zacht = !matchMedia('(prefers-reduced-motion: reduce)').matches;
      rij.scrollIntoView({ behavior: zacht ? 'smooth' : 'auto', block: 'start' });
    };
    if (document.readyState === 'complete') setTimeout(inBeeld, 150);
    else window.addEventListener('load', () => setTimeout(inBeeld, 150), { once: true });
  }

  /**
   * Laadt een los scriptbestand één keer per pagina. Lukt het niet, dan wordt
   * de belofte afgewezen en werkt de rest van de tool gewoon door.
   */
  static laadScript(url) {
    FlagConfigurator.scripts = FlagConfigurator.scripts || {};
    if (!FlagConfigurator.scripts[url]) {
      FlagConfigurator.scripts[url] = new Promise((ok, fout) => {
        const el = document.createElement('script');
        el.src = url;
        el.onload = ok;
        el.onerror = fout;
        document.head.appendChild(el);
      });
    }
    return FlagConfigurator.scripts[url];
  }

  /**
   * Opent de tool met een sjabloon erop, vanaf een themapagina
   * (/pages/vlaggen/geslaagd-vlag). Daarna werkt alles zoals na een klik op dat
   * sjabloon: de klant past de naam aan, kiest een maat en bestelt.
   */
  async #startMetSjabloon(id) {
    const side = this.sides && this.sides[0];
    if (!side) return;
    try {
      if (!window.FLAGCFG_SJABLONEN) {
        if (!FlagConfigurator.sjabloonBelofte && this.dataset.sjabloonUrl) {
          FlagConfigurator.sjabloonBelofte = FlagConfigurator.laadScript(this.dataset.sjabloonUrl);
        }
        await FlagConfigurator.sjabloonBelofte;
      }
      // De symbolenlijst is nodig voor sjablonen met een symbool, en de vormen
      // voor de namen in de lagenlijst (zie #laadSjablonen).
      if (!window.FLAGCFG_KLEUR_INDEX && this.dataset.kleurIndexUrl) {
        if (!FlagConfigurator.symbolenBelofte) {
          FlagConfigurator.symbolenBelofte = FlagConfigurator.laadScript(this.dataset.kleurIndexUrl);
        }
        await FlagConfigurator.symbolenBelofte;
      }
    } catch (err) {
      return;                                   // niet geladen: de tool werkt gewoon zonder
    }
    const sj = (window.FLAGCFG_SJABLONEN || []).find((x) => x.id === id);
    if (!sj) return;
    const vormcat = (window.FLAGCFG_KLEUR_INDEX || []).find((c) => c.id === 'vormen');
    if (vormcat) this.#laadKleurCategorie(vormcat, () => this.#tekenLagenLijst(side));
    this.vanVlagpagina = true;
    this.#pasSjabloonToe(side, sj);
    const rij = this.ontwerpRij;
    if (!rij) return;
    this.#klapRij(rij, true);
    const inBeeld = () => {
      if (window.scrollY > 80) return;
      const zacht = !matchMedia('(prefers-reduced-motion: reduce)').matches;
      rij.scrollIntoView({ behavior: zacht ? 'smooth' : 'auto', block: 'start' });
    };
    if (document.readyState === 'complete') setTimeout(inBeeld, 150);
    else window.addEventListener('load', () => setTimeout(inBeeld, 150), { once: true });
  }

  #pickFlag(side, flag) {
    // Binnen de ontwerptool wordt een landvlag een element, net als een eigen
    // afbeelding: je kunt hem verslepen, schalen en combineren met tekst.
    // Buiten de tool blijft het zoals het was — daar vult de vlag het doek,
    // want dat is wat iemand wil die alleen een landvlag bestelt.
    if (this.classList.contains('is-vol')) {
      this.#voegVlagToe(side, flag);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (side.objectUrl) URL.revokeObjectURL(side.objectUrl);
      side.objectUrl = null;
      side.drukKenmerk = null;
      side.drukPixels = null;
      if (side.drukInput) {
        side.drukInput.value = '';
        side.drukInput.disabled = true;
      }
      this.#vergeetAchtergrond(side, 'img');
      side.img = img;
      side.vector = true; // vlaggen uit de databank zijn vectoren: altijd scherp
      side.fileName = flag.label;
      // Om het ontwerp later te kunnen herstellen: welke vlag het was, en dat
      // er geen eigen bestand bij hoort.
      side.vlagCode = flag.code;
      side.bronBestand = null;
      side.cx = 50;
      side.cy = 50;
      side.zoom = 1;
      side.zoomInput.value = '100';
      // landvlag: exact op vlagformaat, zoals vlaggenfabrikanten doen
      side.stretch = true;
      side.controls.hidden = false;
      side.noteEl.hidden = true;
      this.#tekenLagenLijst(side);
      side.libValue.value = flag.label + ' (' + flag.code.toUpperCase() + ')';
      side.libValue.disabled = false;
      side.nameEl.textContent = this.chosenText + ': ' + flag.label;
      side.fileInput.value = '';
      side.libPanel.hidden = true;
      this.#libAria(side, false);
      side.libGrid.querySelectorAll('.is-chosen').forEach((e) => e.classList.remove('is-chosen'));
      const btn = side.libGrid.querySelector('[data-code="' + flag.code + '"]');
      if (btn) btn.classList.add('is-chosen');
      this.#render();
    };
    img.onerror = () => {
      side.noteEl.textContent = this.noPreviewText;
      side.noteEl.hidden = false;
    };
    img.src = this.flagBase + 'flag-' + flag.code + '.svg';
  }

  // ---------- eigen upload ----------

  #onFile(side) {
    const file = side.fileInput.files && side.fileInput.files[0];
    if (side.objectUrl && !this.#beeldGedeeld(side) && !this.#inGeschiedenis(side.objectUrl)) URL.revokeObjectURL(side.objectUrl);
    this.#vergeetAchtergrond(side, 'img');
    side.img = null;
    side.objectUrl = null;

    if (side.libValue) {
      // zichtbare eigenschap: alleen de naam, niet de downloadlink
      side.libValue.value = file ? file.name : '';
      side.libValue.disabled = !file;
    }
    side.vooraf = null;
    side.fileInput.disabled = false;
    if (side.urlInput) {
      side.urlInput.value = '';
      side.urlInput.disabled = true;
    }
    if (side.libGrid) side.libGrid.querySelectorAll('.is-chosen').forEach((e) => e.classList.remove('is-chosen'));

    if (!file) {
      side.fileName = '';
      side.nameEl.textContent = '';
      side.controls.hidden = true;
      side.noteEl.hidden = true;
      this.#render();
      return;
    }

    side.stretch = false;
    side.vector = file.type === 'image/svg+xml';
    side.fileName = file.name;
    side.vlagCode = null;
    side.bronBestand = file;
    side.nameEl.textContent = file.name;
    this.#alvastUploaden(side, file);

    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      side.img = img;
      side.objectUrl = url;
      side.cx = 50;
      side.cy = 50;
      side.zoom = 1;
      side.zoomInput.value = '100';
      side.controls.hidden = false;
      side.noteEl.hidden = true;
      this.#tekenLagenLijst(side);
      this.#render();
    };
    img.onerror = async () => {
      URL.revokeObjectURL(url);
      // Een PDF kan de browser niet als <img> laden, maar met pdf.js wel
      // tekenen. Dat is de moeite waard: een vector-PDF is juist het beste
      // drukbestand dat een klant kan aanleveren, en zonder voorbeeld denkt
      // hij dat de configurator stuk is.
      if (/\.pdf$/i.test(file.name) || file.type === 'application/pdf') {
        const bitmap = await this.#pdfNaarBeeld(file, side);
        if (bitmap) {
          side.img = bitmap;
          side.vector = true;
          side.cx = 50;
          side.cy = 50;
          side.zoom = 1;
          side.zoomInput.value = '100';
          side.controls.hidden = false;
          side.noteEl.hidden = true;
          this.#tekenLagenLijst(side);
          this.#render();
          return;
        }
      }
      // AI / EPS en mislukte PDF's: geen voorbeeld, bestand gaat wel mee
      side.controls.hidden = true;
      side.noteEl.textContent = this.noPreviewText;
      side.noteEl.hidden = false;
      this.#render();
    };
    // Een PDF altijd via pdf.js (21 okt 2026, #1050). Safari laadt een PDF wél als <img>, met de
    // paginamaat in punten als naturalWidth, maar maakt er intern hoogstens een beeld van ~16,7
    // miljoen beeldpunten van. Op het drukbestand van 225 x 150 cm bleef daardoor rechts 48 cm en
    // onder 32 cm wit, terwijl het kleine controlebeeld wel vol was.
    if (/\.pdf$/i.test(file.name) || file.type === 'application/pdf') {
      img.onerror();
      return;
    }
    img.src = url;
  }

  /**
   * Tekent de eerste pagina van een PDF naar een canvas, zodat de klant zijn
   * ontwerp gewoon ziet en kan plaatsen. pdf.js wordt pas opgehaald zodra er
   * echt een PDF langskomt — bezoekers die een PNG uploaden laden niets extra.
   * Mislukt het (geen internet, kapotte PDF), dan valt alles terug op de oude
   * melding en gaat het bestand nog steeds gewoon mee naar de drukker.
   */
  async #pdfNaarBeeld(file, side) {
    try {
      side.noteEl.textContent = this.pdfBezigText || this.noPreviewText;
      side.noteEl.hidden = false;
      const klaar = (belofte) =>
        Promise.race([belofte, new Promise((_, af) => setTimeout(() => af(new Error('te traag')), 12000))]);
      const pdfjs = await klaar(FlagConfigurator.#pdfBibliotheek());
      const buffer = await klaar(file.arrayBuffer());
      const doc = await klaar(pdfjs.getDocument({ data: buffer }).promise);
      const page = await klaar(doc.getPage(1));
      // Ruim renderen: de vlag is groot en dit beeld wordt ook het drukbestand.
      const basis = page.getViewport({ scale: 1 });
      // Bovengrens (21 okt 2026): een PDF met een pagina op ware grootte (225 x 150 cm = 6378 x 4252
      // punten) werd op 2x getekend, 108 miljoen beeldpunten per zijde. Meer dan 8000 px heeft geen
      // zin (groter wordt geen drukbestand, zie #drukMaat), en een iPhone kan per tekenvlak zo'n 16
      // miljoen beeldpunten aan.
      const ios = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const schaal = Math.min(
        8,
        Math.max(2, 2400 / Math.max(basis.width, basis.height)),
        8000 / Math.max(basis.width, basis.height),
        ios ? Math.sqrt(16e6 / (basis.width * basis.height)) : Infinity,
      );
      const viewport = page.getViewport({ scale: schaal });
      const breed = Math.round(viewport.width);
      const hoog = Math.round(viewport.height);
      const canvas = document.createElement('canvas');
      canvas.width = breed;
      canvas.height = hoog;

      // pdf.js drijft zijn tekenlus met requestAnimationFrame aan, en die staat
      // stil zodra het tabblad niet zichtbaar is. Wisselt de klant tijdens het
      // uploaden even van tabblad, dan blijft hij anders naar "wordt ingelezen"
      // kijken. Daarom tijdens het tekenen terugvallen op setTimeout.
      const origRaf = window.requestAnimationFrame;
      window.requestAnimationFrame = (cb) =>
        document.hidden ? setTimeout(() => cb(performance.now()), 16) : origRaf.call(window, cb);
      try {
        await klaar(page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise);
      } finally {
        window.requestAnimationFrame = origRaf;
      }

      // De rest van de configurator verwacht iets met naturalWidth/naturalHeight.
      canvas.naturalWidth = breed;
      canvas.naturalHeight = hoog;
      return canvas;
    } catch (err) {
      window.__pdfFout = String(err && err.message ? err.message : err);
      return null;
    }
  }

  /**
   * pdf.js eenmalig laden en onthouden. Let op: een Worker mag niet
   * rechtstreeks vanaf een ander domein starten, dus we maken hem uit een
   * blob die de CDN-module importeert. Zonder die omweg blijft getDocument
   * eindeloos wachten en ziet de klant alleen "wordt ingelezen".
   */
  static #pdfBelofte = null;
  static #pdfBibliotheek() {
    if (FlagConfigurator.#pdfBelofte) return FlagConfigurator.#pdfBelofte;
    const basis = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.6.82/build/';
    FlagConfigurator.#pdfBelofte = import(basis + 'pdf.min.mjs').then((mod) => {
      const shim = new Blob([`import '${basis}pdf.worker.min.mjs';`], { type: 'text/javascript' });
      mod.GlobalWorkerOptions.workerPort = new Worker(URL.createObjectURL(shim), { type: 'module' });
      return mod;
    });
    return FlagConfigurator.#pdfBelofte;
  }

  /**
   * Zet het bestand alvast weg op ons eigen uploadpunt, terwijl de klant nog
   * aan het ontwerpen is. Lukt dat, dan hoeft bij het bestellen alleen de URL
   * mee en is de winkelwagen direct klaar. Mislukt het, dan gaat het bestand
   * gewoon op de oude manier mee — de klant merkt er niets van.
   */
  async #alvastUploaden(side, file) {
    side.vooraf = null;
    // Mislukt (27 okt 2026): groter dan 24 MB weigert het uploadpunt (413). Dan niet eindeloos wachten
    // op het origineel; het drukbestand wordt toch gemaakt en is waar de drukker mee werkt.
    side.voorafMislukt = false;
    if (!this.uploadUrl || !file) return;

    const beurt = (side.uploadBeurt = (side.uploadBeurt || 0) + 1);
    try {
      const body = new FormData();
      body.append('file', file, file.name);
      const res = await fetch(this.uploadUrl, { method: 'POST', body });
      if (!res.ok) {
        if (beurt === side.uploadBeurt) side.voorafMislukt = true;
        return;
      }
      const data = await res.json();
      // klant koos intussen een ander bestand? dan dit resultaat negeren
      if (beurt !== side.uploadBeurt || !data.url) return;
      side.vooraf = data.url;
      this.#schakelBestandsveld(side);
    } catch (err) {
      if (beurt === side.uploadBeurt) side.voorafMislukt = true;
    }
  }

  /** Bestand is al geupload: stuur alleen de URL mee i.p.v. het bestand zelf. */
  #schakelBestandsveld(side) {
    if (!side.vooraf) return;
    side.fileInput.disabled = true;
    if (!side.urlInput) {
      side.urlInput = document.createElement('input');
      side.urlInput.type = 'hidden';
      side.urlInput.name = side.fileInput.name;
      side.fileInput.parentNode.appendChild(side.urlInput);
    }
    side.urlInput.value = side.vooraf;
    side.urlInput.disabled = false;
  }

  // ---------- plaatsing ----------

  #action(side, act) {
    if (!side.img) return;
    side.stretch = false;
    if (act === 'clear') {
      this.#wisOntwerp(side);
      return;
    }
    if (act === 'center') {
      side.cx = 50;
      side.cy = 50;
    } else {
      const base = this.#containScale(side);
      const cover = Math.max(side.rect.w / side.img.naturalWidth, side.rect.h / side.img.naturalHeight);
      side.zoom = act === 'fill' ? cover / base : 1;
      side.zoomInput.value = String(Math.round(side.zoom * 100));
    }
    this.#render();
  }

  /**
   * Ontwerp weghalen en terug naar het lege voorbeeld.
   *
   * Zonder dit zat een klant vast aan zijn eerste keuze: een ander bestand
   * kiezen kon wel, maar helemaal terug naar blanco niet.
   */
  #wisOntwerp(side) {
    // De lijst toont een rij voor de achtergrond; die moet mee verdwijnen.
    setTimeout(() => this.#tekenLagenLijst(side), 0);
    if (side.objectUrl && !this.#beeldGedeeld(side) && !this.#inGeschiedenis(side.objectUrl)) URL.revokeObjectURL(side.objectUrl);
    side.objectUrl = null;
    this.#vergeetAchtergrond(side, 'img');
    side.img = null;
    side.vector = false;
    side.vlagCode = null;
    side.bronBestand = null;
    side.fileName = '';
    side.ppi = 0;
    side.cx = 50;
    side.cy = 50;
    side.zoom = 1;
    side.stretch = false;
    side.bg = '#ffffff';

    if (side.zoomInput) side.zoomInput.value = '100';
    if (side.bgInput) side.bgInput.value = '#ffffff';
    side.fileInput.value = '';
    side.fileInput.disabled = false;
    side.controls.hidden = true;
    side.noteEl.hidden = true;
    if (side.qualityEl) side.qualityEl.hidden = true;
    // Leeg, niet "Upload je ontwerp": die tekst staat nu in de knop zelf.
    side.nameEl.textContent = '';

    // alles wat naar de bestelling zou gaan weer loslaten
    side.vooraf = null;
    side.uploadBeurt = (side.uploadBeurt || 0) + 1;   // lopende upload negeren
    if (side.urlInput) {
      side.urlInput.value = '';
      side.urlInput.disabled = true;
    }
    side.libValue.value = '';
    side.libValue.disabled = true;
    side.libGrid.querySelectorAll('.is-chosen').forEach((e) => e.classList.remove('is-chosen'));
    side.posInput.value = '';
    side.posInput.disabled = true;
    side.drukKenmerk = null;
    side.drukPixels = null;
    if (side.drukInput) {
      side.drukInput.value = '';
      side.drukInput.disabled = true;
    }
    if (this.previewInput && !this.sides.some((z) => z !== side && z.img)) {
      this.previewInput.value = '';
      this.previewInput.disabled = true;
    }

    this.#render();
  }

  #containScale(side) {
    return Math.min(side.rect.w / side.img.naturalWidth, side.rect.h / side.img.naturalHeight);
  }

  #geometry(side) {
    const r = side.rect;
    if (side.stretch) return { x: r.x, y: r.y, w: r.w, h: r.h };
    const base = this.#containScale(side);
    const w = side.img.naturalWidth * base * side.zoom;
    const h = side.img.naturalHeight * base * side.zoom;
    return { x: r.x + (side.cx / 100) * r.w - w / 2, y: r.y + (side.cy / 100) * r.h - h / 2, w, h };
  }

  /** Kleiner dan de vlag valt er nooit buiten; groter dekt de vlag altijd volledig. */
  #clamp(side) {
    if (side.stretch) return;
    const r = side.rect;
    const base = this.#containScale(side);
    const w = side.img.naturalWidth * base * side.zoom;
    const h = side.img.naturalHeight * base * side.zoom;
    const hw = (w / 2 / r.w) * 100;
    const hh = (h / 2 / r.h) * 100;
    side.cx = Math.min(Math.max(hw, 100 - hw), Math.max(Math.min(hw, 100 - hw), side.cx));
    side.cy = Math.min(Math.max(hh, 100 - hh), Math.max(Math.min(hh, 100 - hh), side.cy));
  }

  /**
   * Klikken op de vlag opent de bestandskiezer voor precies die zijde.
   *
   * Bij een dubbelzijdige vlag staan links en rechts naast elkaar; klik je op
   * de linkerhelft dan gaat het over de linkerzijde. Zonder dit moest de klant
   * de juiste uploadknop onder de preview opzoeken, en dat is bij twee zijden
   * niet meteen duidelijk.
   *
   * Alleen op een lege zijde: staat er al een ontwerp, dan wil de klant slepen
   * en schalen, niet opnieuw uploaden.
   */
  #bindKlikOpDoek() {
    // Lang drukken op het doek: geen menu van de browser (opslaan, kopiëren).
    // Dat hoort bij een afbeelding op een website, niet bij een tekenvlak.
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    this.canvas.addEventListener('click', (e) => {
      const r = this.canvas.getBoundingClientRect();
      const { view } = this.layout;
      const x = ((e.clientX - r.left) / r.width) * view.w;
      const y = ((e.clientY - r.top) / r.height) * view.h;

      let gekozen = null;
      this.sides.forEach((side) => {
        const v = side.rect;
        if (x >= v.x && x <= v.x + v.w && y >= v.y && y <= v.y + v.h) gekozen = side;
      });
      // naast het doek geklikt: bij één zijde is dat toch die ene
      if (!gekozen && this.sides.length === 1) gekozen = this.sides[0];

      // In de ontwerptool (Jos, 7 okt 2026, naar het voorbeeld van Canva): de
      // eerste tik op een tekst kiest hem alleen (dat deed pointerdown al). Pas
      // een tweede tik op dezelfde, al gekozen tekst opent het typen op de vlag
      // zelf. Zo springt het beeld niet meer naar het tekstveld onder de vlag,
      // en kan slepen nooit per ongeluk in typen eindigen. Niet na slepen, en
      // niet op een hoekje, want dat is om groter of kleiner te maken.
      if (this.classList.contains('is-vol')) {
        if (!gekozen || this.netGesleept) return;
        const punt = { x, y };
        if (this.#hoekjeOpPunt(gekozen, gekozen.gekozenLaag, punt) >= 0) return;
        if (this.#draaiknopOpPunt(gekozen, gekozen.gekozenLaag, punt)) return;
        if (this.#dupliceerknopOpPunt(gekozen, gekozen.gekozenLaag, punt)) return;
        const laag = this.#laagOpPunt(gekozen, punt);
        if (laag && laag.type === 'tekst' && laag === this.vorigeKeuze) this.#typOpVlag(gekozen, laag, false);
        return;
      }

      if (!gekozen || gekozen.img) return;

      // In de ontwerptool nooit. Daar is de klant elementen aan het schikken en
      // klikt hij voortdurend naast iets; dan is een bestandskiezer die
      // openspringt puur in de weg. Uploaden kan daar via de knop Uploaden.
      if (this.classList.contains('is-vol')) return;

      // Staat er al iets op deze kant — tekst, een symbool, een afbeelding —
      // dan is een klik bedoeld om dat te pakken, niet om te uploaden. De
      // kiezer hoort alleen open te gaan op een nog lege vlag, als uitnodiging
      // om te beginnen.
      if (gekozen.lagen && gekozen.lagen.length) return;

      // Net een tekst versleept? Dan eindigt dat gebaar ook in een klik, en
      // die mag de bestandskiezer niet openen.
      if (this.netGesleept) return;

      // Op een tekst geklikt: de klant wil die verplaatsen of aanpassen,
      // geen bestand kiezen.
      if (this.#laagOpPunt(gekozen, { x, y })) return;

      gekozen.fileInput.click();
    });
    this.#doekAanwijzer();
  }

  /**
   * Typen op de vlag (Jos, 7 okt 2026). Over de tekst op het doek komt een
   * onzichtbaar invoerveld met hetzelfde lettertype, dezelfde maat en draaiing.
   * Het veld laat alleen de cursor zien; de letters zelf tekent het doek, net
   * als altijd, zodat wat de klant ziet precies is wat er gedrukt wordt.
   *
   * Het tekstveld in de lijst onder de vlag blijft bestaan en loopt gelijk op
   * (Jos: "laat die functie ook bestaan").
   *
   * Moet binnen de klik zelf beginnen: anders opent een iPhone het toetsenbord
   * niet. preventScroll houdt de pagina stil, zodat het beeld niet verspringt.
   */
  #typOpVlag(side, l, allesKiezen) {
    this.#stopTypen();
    // Een verdeelde naam typ je in het vlaggetjes-paneel, niet letter voor letter op de vlag.
    if (l.verdeel) return;
    const ouder = this.canvas && this.canvas.parentElement;
    if (!ouder || !side.rect) { this.#bewerkTekst(side, l); return; }
    if (getComputedStyle(ouder).position === 'static') ouder.style.position = 'relative';

    const veld = document.createElement('input');
    veld.type = 'text';
    veld.value = l.tekst || '';
    veld.maxLength = 60;
    veld.autocomplete = 'off';
    veld.setAttribute('enterkeyhint', 'done');
    veld.setAttribute('aria-label', this.dataset.tTekstLabel || 'Tekst');
    // Inline en niet in de stylesheet: die bundel ververst niet altijd tegelijk
    // met dit script (zie de Klaar-knop).
    Object.assign(veld.style, {
      position: 'absolute', zIndex: '6', margin: '0', padding: '0', border: '0', outline: 'none',
      background: 'transparent', color: 'transparent', webkitTextFillColor: 'transparent',
      textAlign: 'center', fontWeight: '700', boxSizing: 'content-box', transformOrigin: '50% 50%',
      whiteSpace: 'pre', letterSpacing: '0', textShadow: 'none', borderRadius: '0', appearance: 'none',
      // Het vlak eromheen is niet selecteerbaar (iPhone-menu bij lang drukken);
      // zonder dit kun je op een iPhone soms niet typen in een veld daarbinnen.
      webkitUserSelect: 'text', userSelect: 'text', webkitTouchCallout: 'default',
    });
    ouder.appendChild(veld);

    const opnieuw = () => this.#plaatsTypveld();
    this.typen = { side, l, veld, opnieuw };
    window.addEventListener('resize', opnieuw);
    this.#plaatsTypveld();

    veld.addEventListener('input', () => {
      l.tekst = veld.value;
      this.#lijstVeldBij(side, l);
      this.#plaatsTypveld();
      this.#render();
    });
    veld.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        veld.blur();
      }
    });
    veld.addEventListener('blur', () => this.#stopTypen());

    try {
      veld.focus({ preventScroll: true });
    } catch (err) {
      veld.focus();
    }
    try {
      if (allesKiezen) veld.select();
      else veld.setSelectionRange(veld.value.length, veld.value.length);
    } catch (err) {
      /* niet elk toestel ondersteunt dit */
    }
    this.#render();
  }

  /** Legt het typveld precies over de tekst op het doek. */
  #plaatsTypveld() {
    const t = this.typen;
    if (!t) return;
    const { side, l, veld } = t;
    if (!side.lagen.includes(l) || !side.rect || !this.layout) { veld.blur(); return; }
    const r = this.canvas.getBoundingClientRect();
    const schaal = r.width / Math.max(1, this.layout.view.w);
    const v = this.#laagVlak(this.ctx, side.rect, l);
    const px = Math.max(1, v.px * schaal);
    // Een iPhone zoomt de hele pagina in bij een invoerveld met letters kleiner
    // dan 16 px. Daarom minstens 16 px, en dan met scale() terug naar de maat
    // van de tekst op het doek.
    const fs = Math.max(16, px);
    const k = px / fs;
    const breed = (v.breed * schaal + px * 1.4) / k;
    const hoog = fs * 1.3;
    Object.assign(veld.style, {
      left: (this.canvas.offsetLeft + v.x * schaal) + 'px',
      top: (this.canvas.offsetTop + v.y * schaal) + 'px',
      width: Math.max(24, breed) + 'px',
      height: hoog + 'px',
      lineHeight: hoog + 'px',
      fontSize: fs + 'px',
      fontFamily: l.font,
      fontWeight: String(letterGewicht(l.font)),
      letterSpacing: ((Number(l.spatie) || 0) / 100) * fs + 'px',
      caretColor: l.kleur || '#000000',
      transform: 'translate(-50%, -50%) rotate(' + (l.hoek || 0) + 'deg) scale(' + k + ')',
    });
  }

  /** Stopt het typen op de vlag: het veld weg, alles opnieuw tekenen. */
  #stopTypen() {
    const t = this.typen;
    if (!t) return;
    this.typen = null;
    window.removeEventListener('resize', t.opnieuw);
    t.veld.remove();
    this.#lijstVeldBij(t.side, t.l);
    this.#render();
  }

  /** Houdt het tekstveld in de lijst onder de vlag gelijk met wat er getypt is. */
  #lijstVeldBij(side, l) {
    const lijst = side.tekstLijst;
    if (!lijst) return;
    const rij = [...lijst.querySelectorAll('.flagcfg-tekst')].find((el) => el.dataset.id === String(l.id));
    const invoer = rij && rij.querySelector('.flagcfg-tekst__invoer');
    if (invoer && invoer !== document.activeElement && invoer.value !== l.tekst) invoer.value = l.tekst;
  }

  /**
   * Zet de cursor in het tekstveld van deze tekst, achteraan. Moet binnen de
   * klik zelf gebeuren: anders opent een iPhone het toetsenbord niet.
   */
  #bewerkTekst(side, l) {
    const lijst = side.tekstLijst;
    if (!lijst) return;
    const rij = [...lijst.querySelectorAll('.flagcfg-tekst')].find((el) => el.dataset.id === String(l.id));
    const invoer = rij && rij.querySelector('.flagcfg-tekst__invoer');
    if (!invoer) return;
    invoer.focus();
    const eind = invoer.value.length;
    try {
      invoer.setSelectionRange(eind, eind);
    } catch (err) {
      /* niet elk invoerveld ondersteunt dit */
    }
  }

  /**
   * Haalt het doek terug als de browser het beeld heeft weggegooid.
   *
   * Safari op een iPhone gooit bij geheugendruk, of na even een andere app, het
   * beeld van een canvas weg. Dan bleef de vlag leeg tot er toevallig opnieuw
   * getekend werd (Jos, 12 sep). Bij terugkeren naar de pagina en na het
   * herstellen van het tekenvlak daarom een vers tekenvlak en opnieuw tekenen.
   */
  #bindHerstelDoek() {
    const herstel = () => this.#herstelDoek();
    this.canvas.addEventListener('contextrestored', herstel);

    const zichtbaar = () => {
      if (!this.isConnected) {
        document.removeEventListener('visibilitychange', zichtbaar);
        return;
      }
      if (!document.hidden) herstel();
    };
    document.addEventListener('visibilitychange', zichtbaar);

    const terug = (e) => {
      if (!this.isConnected) {
        window.removeEventListener('pageshow', terug);
        return;
      }
      if (e.persisted) herstel();
    };
    window.addEventListener('pageshow', terug);
  }

  /**
   * De muisaanwijzer hoort te kloppen met wat een klik doet. Een handje boven
   * het doek belooft "klik hier om te uploaden"; zodra dat niet meer gebeurt —
   * in de ontwerptool, of als er al iets op de vlag staat — is een gewone
   * aanwijzer eerlijker.
   */
  #doekAanwijzer() {
    if (!this.canvas) return;
    const nodigt = !this.classList.contains('is-vol')
      && this.sides.some((s) => !s.img && !(s.lagen && s.lagen.length));
    const aanwijzer = nodigt ? 'pointer' : 'default';
    if (this.canvas.style.cursor !== aanwijzer) this.canvas.style.cursor = aanwijzer;
  }

  #bindGestures() {
    const punten = new Map();          // actieve vingers/muis
    let modus = null;                  // 'sleep' of 'knijp'
    let zijde = null;
    let start = {};

    const naarVlak = (e) => {
      // Tijdens het slepen de maat van het doek van het moment van oppakken.
      // Elke beweging opnieuw opvragen dwong de browser de hele pagina opnieuw op
      // te meten; dat was de hoofdoorzaak van het haperen (gemeten 18 ms per
      // beweging op een computer, op een telefoon een veelvoud).
      const r = this.gebaarRect || this.canvas.getBoundingClientRect();
      const { view } = this.layout;
      return { x: ((e.clientX - r.left) / r.width) * view.w, y: ((e.clientY - r.top) / r.height) * view.h };
    };

    // Een zijde met alleen tekst telt ook: je moet die tekst kunnen verslepen
    // zonder dat er een ontwerp onder ligt.
    const zoek = (p) =>
      this.sides.find(
        (s) =>
          (s.img || (s.lagen && s.lagen.length)) &&
          p.x >= s.rect.x && p.x <= s.rect.x + s.rect.w && p.y >= s.rect.y && p.y <= s.rect.y + s.rect.h
      );

    let sleepTekst = null;
    let sleepDoelen = null;   // uitlijnpunten, eenmalig bij het oppakken berekend
    let knijpLaag = null;     // element dat met twee vingers geschaald wordt
    let maatGewijzigd = false;

    const afstand = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    const midden = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
    const begrensZoom = (z) => Math.min(3, Math.max(0.2, z));
    // Zelfde grenzen als de schuifregelaar Grootte in de elementenlijst.
    const maatGrens = (l, waarde) => {
      const max = l.type === 'tekst' ? 40 : (l.type === 'eigen' ? 300 : 100);
      return Math.min(max, Math.max(4, Math.round(waarde * 2) / 2));
    };

    const zetZoom = (s, z) => {
      s.zoom = begrensZoom(z);
      s.zoomInput.value = String(Math.round(s.zoom * 100));
    };

    this.canvas.addEventListener('pointerdown', (e) => {
      // Aan het typen op de vlag en naast het tekstveld het doek aangeraakt:
      // typen stoppen. Een iPhone haalt het toetsenbord daar niet vanzelf weg.
      if (this.typen) this.typen.veld.blur();
      const p = naarVlak(e);
      if (punten.size === 0) {
        // Een hoekje van het gekozen element mag net buiten de vlag liggen;
        // eerst daarop letten, dan pas op de zijde zelf.
        zijde = this.#zijdeMetHoekje(p) || this.#zijdeMetDupliceerknop(p) || this.#zijdeMetDraaiknop(p) || zoek(p);
        if (!zijde) return;
      }
      punten.set(e.pointerId, p);
      if (punten.size === 1) {
        // Het slepen begint. Tot het loslaten: de maat van het doek vasthouden,
        // niets aan de pagina veranderen, en afbeeldingen op schermformaat
        // klaar hebben staan.
        this.gebaar = true;
        this.gebaarTijd = performance.now();
        this.gebaarRect = this.canvas.getBoundingClientRect();
        this.#warmSchermKopieen();
      }
      try {
        this.canvas.setPointerCapture(e.pointerId);
      } catch (err) {
        /* niets */
      }

      if (punten.size === 1) {
        modus = 'sleep';
        // Wat er gekozen was vóór deze aanraking. Tikt de klant op een tekst die
        // al gekozen was, dan opent de klik daarna het typen op de vlag.
        this.vorigeKeuze = zijde.gekozenLaag;
        // Ligt er een tekstlaag onder de vinger, dan versleep je die en niet
        // het ontwerp eronder. Dat is wat je verwacht als je een woord aanraakt.
        sleepTekst = this.#laagOpPunt(zijde, p);
        sleepDoelen = sleepTekst ? this.#uitlijnDoelen(zijde, sleepTekst) : null;
        // Voor het kader om het element. Alleen bij een laag: sleep je de
        // vullende achtergrondafbeelding, dan zou een kader om de hele vlag
        // niets toevoegen.
        zijde.sleepLaag = sleepTekst;
        start = sleepTekst
          ? { x: p.x, y: p.y, cx: sleepTekst.cx, cy: sleepTekst.cy }
          : { x: p.x, y: p.y, cx: zijde.cx, cy: zijde.cy };

        // Een hoekje van het gekozen element vastgepakt? Dan wordt het groter of
        // kleiner vanuit zijn midden in plaats van verschoven. Alleen de grootte
        // verandert, nooit de verhouding: er wordt niets uitgerekt.
        const hoekje = this.classList.contains('is-vol') ? this.#hoekjeOpPunt(zijde, zijde.gekozenLaag, p) : -1;
        if (this.#dupliceerknopOpPunt(zijde, zijde.gekozenLaag, p)) {
          // Het dupliceerknopje: een kopie maken en die meteen vastpakken.
          // Tikken zet hem bij het loslaten iets opzij; slepen trekt hem eruit.
          const kopie = this.#dupliceerLaag(zijde, zijde.gekozenLaag, false, true);
          this.dupliceerGebaar = { kopie: kopie, bron: this.vorigeKeuze, zijde: zijde };
          sleepTekst = kopie;
          sleepDoelen = this.#uitlijnDoelen(zijde, kopie);
          zijde.sleepLaag = kopie;
          start = { x: p.x, y: p.y, cx: kopie.cx, cy: kopie.cy };
        } else if (this.#draaiknopOpPunt(zijde, zijde.gekozenLaag, p)) {
          // Het draaiknopje: draaien rond het midden. Alleen hiermee; het
          // element zelf slepen verplaatst het alleen (Jos: geen ongelukjes).
          modus = 'draai';
          sleepTekst = zijde.gekozenLaag;
          sleepDoelen = null;
          zijde.sleepLaag = sleepTekst;
          zijde.draaien = true;
          const m = this.#hoekpunten(zijde, sleepTekst).midden;
          start = { m, x: p.x, y: p.y, hoek: sleepTekst.hoek || 0, a: Math.atan2(p.y - m.y, p.x - m.x), begonnen: false };
        } else if (hoekje >= 0) {
          modus = 'hoek';
          sleepTekst = zijde.gekozenLaag;
          sleepDoelen = null;
          zijde.sleepLaag = sleepTekst;
          const m = this.#hoekpunten(zijde, sleepTekst).midden;
          start = { m, d: Math.max(1, Math.hypot(p.x - m.x, p.y - m.y)), grootte: sleepTekst.grootte };
        } else if (this.classList.contains('is-vol')) {
          // Aanraken kiest het element; naast een element wist het de keuze.
          zijde.gekozenLaag = sleepTekst;
        }
      } else if (punten.size === 2 && zijde) {
        // Lag de eerste vinger op een element, dan maakt knijpen dat element
        // groter of kleiner. Anders, zoals altijd, het ontwerp eronder.
        knijpLaag = sleepTekst && zijde.lagen.includes(sleepTekst) ? sleepTekst : null;
        sleepTekst = null;
        zijde.sleepLaag = knijpLaag;
        const [a, b] = [...punten.values()];
        modus = 'knijp';
        start = {
          d: afstand(a, b) || 1, m: midden(a, b), zoom: zijde.zoom, cx: zijde.cx, cy: zijde.cy,
          grootte: knijpLaag ? knijpLaag.grootte : 0,
        };
      }
      // Alleen het ontwerp verliest zijn "vul de vlag"-stand door slepen; een
      // tekst verschuiven of een element schalen mag daar niets aan veranderen.
      if (zijde && !sleepTekst && !knijpLaag) zijde.stretch = false;
      this.canvas.classList.add('is-dragging');
    });

    this.canvas.addEventListener('pointermove', (e) => {
      if (!punten.has(e.pointerId) || !zijde) return;
      punten.set(e.pointerId, naarVlak(e));

      if (modus === 'sleep' && punten.size === 1) {
        const p = punten.get(e.pointerId);
        // Vanaf een paar eenheden beweging noemen we het slepen. De klik die
        // daarna volgt is dan geen bedoelde klik meer.
        if (Math.hypot(p.x - start.x, p.y - start.y) > 4) this.netGesleept = true;
        const doel = sleepTekst || zijde;
        doel.cx = start.cx + ((p.x - start.x) / zijde.rect.w) * 100;
        doel.cy = start.cy + ((p.y - start.y) / zijde.rect.h) * 100;
        if (sleepTekst) {
          // tekst mag tot de rand, maar niet erbuiten verdwijnen
          doel.cx = Math.min(100, Math.max(0, doel.cx));
          doel.cy = Math.min(100, Math.max(0, doel.cy));

          // Uitlijnen op de vlag en op de andere elementen. Alleen voor lagen:
          // het geuploade ontwerp eronder vult meestal de hele vlag, en daar
          // valt niets zinnigs mee uit te lijnen.
          const uit = this.#uitlijnen(zijde, sleepTekst, doel.cx, doel.cy, sleepDoelen);
          doel.cx = uit.cx;
          doel.cy = uit.cy;
          zijde.hulplijnen = uit.lijnen;
        }
        this.#tekenInFrame();
      } else if (modus === 'draai' && punten.size === 1) {
        const p = punten.get(e.pointerId);
        // Eerst een klein stukje bewegen: een tikje op het knopje doet niets.
        if (!start.begonnen) {
          if (Math.hypot(p.x - start.x, p.y - start.y) < 6 * this.#viewPerPx()) return;
          start.begonnen = true;
        }
        let hoek = start.hoek + ((Math.atan2(p.y - start.m.y, p.x - start.m.x) - start.a) * 180) / Math.PI;
        hoek = ((((hoek + 180) % 360) + 360) % 360) - 180;
        // Bijna recht? Dan klikt hij recht: 0, 90, 180 of 270 graden.
        const recht = Math.round(hoek / 90) * 90;
        if (Math.abs(hoek - recht) < 4) hoek = recht === -180 ? 180 : recht;
        sleepTekst.hoek = Math.round(hoek);
        maatGewijzigd = true;
        this.netGesleept = true;
        this.#tekenInFrame();
      } else if (modus === 'hoek' && punten.size === 1) {
        const p = punten.get(e.pointerId);
        const d = Math.hypot(p.x - start.m.x, p.y - start.m.y);
        sleepTekst.grootte = maatGrens(sleepTekst, start.grootte * (d / start.d));
        maatGewijzigd = true;
        this.netGesleept = true;
        this.#tekenInFrame();
      } else if (modus === 'knijp' && punten.size >= 2 && knijpLaag) {
        const [a, b] = [...punten.values()];
        knijpLaag.grootte = maatGrens(knijpLaag, start.grootte * (afstand(a, b) / start.d));
        maatGewijzigd = true;
        this.netGesleept = true;
        this.#tekenInFrame();
      } else if (modus === 'knijp' && punten.size >= 2) {
        const [a, b] = [...punten.values()];
        const m = midden(a, b);
        zetZoom(zijde, start.zoom * (afstand(a, b) / start.d));
        // schuif mee met het midden tussen de vingers
        zijde.cx = start.cx + ((m.x - start.m.x) / zijde.rect.w) * 100;
        zijde.cy = start.cy + ((m.y - start.m.y) / zijde.rect.h) * 100;
        this.#tekenInFrame();
      }
    });

    const stop = (e) => {
      if (!punten.has(e.pointerId)) return;
      punten.delete(e.pointerId);
      try {
        this.canvas.releasePointerCapture(e.pointerId);
      } catch (err) {
        /* niets */
      }

      if (punten.size === 0) {
        modus = null;
        // De hulplijnen en het selectiekader horen alleen bij het slepen zelf;
        // loslaten wist ze allebei.
        this.sides.forEach((s) => { s.hulplijnen = null; s.sleepLaag = null; s.draaien = false; });
        // Groter of kleiner gemaakt met een hoekje of met knijpen: de
        // schuifregelaar in de lijst moet de nieuwe waarde laten zien.
        if (maatGewijzigd && zijde) this.#tekenLagenLijst(zijde);
        maatGewijzigd = false;
        if (this.dupliceerGebaar) {
          const { kopie, bron, zijde: z } = this.dupliceerGebaar;
          this.dupliceerGebaar = null;
          // Alleen getikt: de kopie iets opzij, anders zie je niet dat er twee zijn.
          if (!this.netGesleept && bron) this.#verschuifKopie(bron, kopie);
          this.#tekenLagenLijst(z);
        }
        knijpLaag = null;
        zijde = null;
        sleepTekst = null;
        sleepDoelen = null;
        this.canvas.classList.remove('is-dragging');
        // De klik komt vlak na het loslaten; daarna mag klikken weer gewoon.
        if (this.netGesleept) setTimeout(() => (this.netGesleept = false), 0);
        // Slepen voorbij. Een nog wachtende tekenbeurt vervalt: nu komt de
        // volledige, met scherpte, knoppen en de regels voor de bestelling.
        if (this.frameVraag) cancelAnimationFrame(this.frameVraag);
        this.frameVraag = 0;
        this.gebaar = false;
        this.gebaarRect = null;
        this.#render();
      } else if (punten.size === 1 && zijde && knijpLaag) {
        // Een element geschaald met twee vingers en er een losgelaten: niets
        // meer doen tot ook de tweede los is, anders schiet het element weg.
        modus = null;
      } else if (punten.size === 1 && zijde) {
        // van knijpen terug naar slepen met de overgebleven vinger
        const p = [...punten.values()][0];
        modus = 'sleep';
        sleepTekst = null;
        zijde.sleepLaag = null;
        start = { x: p.x, y: p.y, cx: zijde.cx, cy: zijde.cy };
      }
    };
    this.canvas.addEventListener('pointerup', stop);
    this.canvas.addEventListener('pointercancel', stop);

    // Met een muis een ander pijltje boven een hoekje, zodat je ziet dat je daar
    // kunt schalen.
    this.canvas.addEventListener('pointermove', (e) => {
      if (punten.size || e.pointerType !== 'mouse' || !this.classList.contains('is-vol')) return;
      const p = naarVlak(e);
      const s = this.#zijdeMetHoekje(p);
      const i = s ? this.#hoekjeOpPunt(s, s.gekozenLaag, p) : -1;
      const dubbel = i < 0 && !!this.#zijdeMetDupliceerknop(p);
      const draai = i < 0 && !dubbel && !!this.#zijdeMetDraaiknop(p);
      const aanwijzer = dubbel ? 'copy' : draai ? 'grab' : i < 0 ? 'default' : (i === 0 || i === 3 ? 'nwse-resize' : 'nesw-resize');
      // Alleen schrijven als hij verandert: elke schrijfactie laat de browser de
      // pagina opnieuw opmaken.
      if (this.canvas.style.cursor !== aanwijzer) this.canvas.style.cursor = aanwijzer;
    });

    // desktop: ctrl/cmd + scrollwiel schaalt (zonder ctrl blijft de pagina gewoon scrollen)
    this.canvas.addEventListener(
      'wheel',
      (e) => {
        if (!e.ctrlKey && !e.metaKey) return;
        const s = zoek(naarVlak(e));
        if (!s) return;
        e.preventDefault();
        s.stretch = false;
        zetZoom(s, s.zoom * (e.deltaY < 0 ? 1.1 : 1 / 1.1));
        this.#render();
      },
      { passive: false }
    );
  }

  // ---------- tekenen ----------

  /**
   * Tekent tijdens het slepen hooguit één keer per beeldverversing.
   *
   * Een muis of vinger geeft vaak meer bewegingen door dan het scherm ververst.
   * Elke beweging meteen tekenen stapelt werk op dat niemand ziet, en dan loopt
   * het beeld achter de vinger aan.
   */
  #tekenInFrame() {
    this.gebaarTijd = performance.now();
    if (this.frameVraag) return;
    this.frameVraag = requestAnimationFrame(() => {
      this.frameVraag = 0;
      this.#render(true);
    });
  }

  /** Sleept er nu een vinger of muis op het doek? */
  #bezigMetGebaar() {
    if (!this.gebaar) return false;
    // Vangnet: komt het loslaten nooit binnen, dan niet eeuwig stil blijven.
    if (performance.now() - (this.gebaarTijd || 0) > 15000) {
      this.gebaar = false;
      this.gebaarRect = null;
      return false;
    }
    return true;
  }

  /**
   * Zwaar werk (controlebeeld, drukbestand van duizenden pixels) nooit midden in
   * een sleepbeweging. Pakt de klant vlak na het loslaten het volgende element,
   * dan wacht dit tot die beweging klaar is. Gemeten: het drukbestand alleen al
   * was een blokkade van 444 ms op een computer.
   */
  #naGebaar(sleutel, taak) {
    if (this.#bezigMetGebaar()) {
      clearTimeout(this[sleutel]);
      this[sleutel] = setTimeout(() => this.#naGebaar(sleutel, taak), 250);
      return;
    }
    taak();
  }

  /**
   * Een kopie van een afbeelding op de maat waarop hij nu in beeld staat.
   *
   * Een foto van 5000 pixels of een getekende landvlag (SVG) bij elke beweging
   * opnieuw verkleinen is op een telefoon zwaar; een kant-en-klare kopie op
   * schermformaat tekenen niet. Alleen voor het scherm: het drukbestand en de
   * vergroting tekenen altijd het origineel (zie #tekenLagen).
   *
   * Tijdens het slepen mag een kopie van net een andere maat gebruikt worden;
   * bij knijpen of een hoekje verandert de maat elke beweging. Daarbuiten alleen
   * een kopie die precies past. Past hij niet, dan nu het origineel, en wordt de
   * kopie even later op de achtergrond gemaakt.
   */
  #schermKopie(bron, breed, hoog, snel) {
    if (!bron || !(bron.naturalWidth || bron.width)) return null;
    const pxB = Math.round(breed * DPR);
    const pxH = Math.round(hoog * DPR);
    if (pxB < 1 || pxH < 1 || pxB > 4096 || pxH > 4096) return null;
    const oud = this.schermKopieen && this.schermKopieen.get(bron);
    // Een kopie die al groter is (zelfde verhouding), is ook goed: dan maken twee
    // duplicaten van hetzelfde beeld in een andere maat niet steeds elkaars kopie opnieuw.
    if (oud && (snel || (Math.abs(oud.b - pxB) <= 1 && Math.abs(oud.h - pxH) <= 1) ||
        (oud.b >= pxB && oud.h >= pxH && Math.abs(oud.b / oud.h - pxB / pxH) < 0.02))) return oud.doek;
    if (!snel) this.#kopieLater(bron, pxB, pxH);
    return null;
  }

  #kopieLater(bron, pxB, pxH) {
    if (!this.kopieWacht) this.kopieWacht = new Map();
    this.kopieWacht.set(bron, [pxB, pxH]);
    clearTimeout(this.kopieTimer);
    this.kopieTimer = setTimeout(() => this.#maakWachtendeKopieen(), 180);
  }

  #maakWachtendeKopieen() {
    if (this.#bezigMetGebaar()) {
      this.kopieTimer = setTimeout(() => this.#maakWachtendeKopieen(), 250);
      return;
    }
    const lijst = this.kopieWacht || new Map();
    this.kopieWacht = new Map();
    lijst.forEach(([pxB, pxH], bron) => this.#maakKopie(bron, pxB, pxH));
  }

  #maakKopie(bron, pxB, pxH) {
    try {
      const doek = document.createElement('canvas');
      doek.width = pxB;
      doek.height = pxH;
      const k = doek.getContext('2d');
      k.imageSmoothingQuality = 'high';
      k.drawImage(bron, 0, 0, pxB, pxH);
      if (!this.schermKopieen) this.schermKopieen = new WeakMap();
      // De vorige kopie van deze afbeelding meteen vrijgeven. Een iPhone ruimt
      // canvasgeheugen pas laat op, en loopt dan tegen zijn grens aan.
      const vorige = this.schermKopieen.get(bron);
      if (vorige && vorige.doek) {
        vorige.doek.width = 0;
        vorige.doek.height = 0;
      }
      this.schermKopieen.set(bron, { b: pxB, h: pxH, doek });
      return doek;
    } catch (err) {
      return null;
    }
  }

  /**
   * Bij het oppakken: ontbrekende schermkopieën meteen maken, zodat al het
   * slepen daarna licht blijft. Meestal staan ze er al (#kopieLater).
   */
  #warmSchermKopieen() {
    const { view } = this.layout;
    const zorg = (bron, breed, hoog) => {
      if (!bron || !(bron.naturalWidth || bron.width)) return;
      const pxB = Math.round(breed * DPR);
      const pxH = Math.round(hoog * DPR);
      if (pxB < 1 || pxH < 1 || pxB > 4096 || pxH > 4096) return;
      const oud = this.schermKopieen && this.schermKopieen.get(bron);
      if (oud && Math.abs(oud.b - pxB) <= 1 && Math.abs(oud.h - pxH) <= 1) return;
      this.#maakKopie(bron, pxB, pxH);
    };
    this.sides.forEach((side) => {
      if (!side.rect || side.rect.x >= view.w) return;
      if (side.img && side.img.naturalWidth) {
        const g = this.#geometry(side);
        zorg(side.img, g.w, g.h);
      }
      if (!this.tekstToegestaan || !side.lagen) return;
      side.lagen.forEach((l) => {
        if ((l.type !== 'eigen' && l.type !== 'kleur') || !l.plaatje) return;
        const v = this.#laagVlak(this.ctx, side.rect, l);
        zorg(l.plaatje, v.breed, v.hoog);
      });
    });
  }

  #render(skipOutput) {
    const ctx = this.ctx;
    const { view, mast, foot, ticks } = this.layout;
    // Altijd vanaf de basisstand beginnen. Liep een eerdere tekenbeurt halverwege
    // op een fout, dan bleef er anders een verschuiving of draaiing staan en viel
    // de hele vlag voortaan buiten beeld.
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    ctx.clearRect(0, 0, view.w, view.h);
    // Tijdens het slepen alleen tekenen en niets in de pagina schrijven; het
    // loslaten tekent daarna alles bij (scherpte, knoppen, bestelregels).
    const stil = this.#bezigMetGebaar();

    // Hier meeliften: bij elke hertekening kan de stand veranderd zijn — een
    // laag erbij, een upload, of de ontwerptool die opengaat.
    if (!stil) this.#doekAanwijzer();
    if (!stil) this.sides.forEach((z) => {
      // Vlaggenlijn: het paneel meteen tonen, en opnieuw opbouwen als een andere variant een ander aantal vlaggetjes heeft.
      if (this.probo && this.probo.vel && (!z.vlagPaneel || z.vlagN !== this.#aantalVlaggetjes())) this.#toonVlagPaneel(z);
      else if (z.vlagPaneel && !(this.probo && this.probo.vel) && !z.vlagPaneel.hidden) z.vlagPaneel.hidden = true;
      if (this.#vlagApart(z)) this.#planVlaggetjes(z);
    });

    this.sides.forEach((side) => {
      const r = side.rect;

      // In de focusstand staat de andere zijde buiten beeld. Niet tekenen, maar
      // de scherpte en de vergrootknop wel bijhouden.
      if (r.x >= view.w) {
        if (!stil) {
          this.#toonScherpte(side);
          this.#toonPreviewKnop();
        }
        return;
      }

      const pad = () => {
        ctx.beginPath();
        if (this.isBeach || this.proboVlag) {
          const p = vlagPad(this.vorm, r);
          ctx.moveTo(p[0][0], p[0][1]);
          for (let i = 1; i < p.length; i++) ctx.lineTo(p[i][0], p[i][1]);
          ctx.closePath();
        } else {
          ctx.rect(r.x, r.y, r.w, r.h);
        }
      };

      pad();
      // Ook zonder geuploade afbeelding telt de gekozen achtergrondkleur:
      // met de ontwerptool is een egaal gekleurde vlag met tekst of een
      // symbool een gewone bestelling. Stond hier eerder wit afgedwongen,
      // terwijl het drukbestand de kleur wel gebruikte — dan drukt de
      // drukker iets anders dan de klant op zijn scherm zag.
      ctx.fillStyle = side.bg || '#ffffff';
      ctx.fill();

      if (side.img) {
        this.#clamp(side);
        const g = this.#geometry(side);
        ctx.save();
        try {
          pad();
          ctx.clip();
          ctx.drawImage(this.#schermKopie(side.img, g.w, g.h, stil) || side.img, g.x, g.y, g.w, g.h);
        } catch (err) {
          /* afbeelding (nog) niet te tekenen; de rest van de vlag wel */
        } finally {
          ctx.restore();
        }
      }

      // Tekst ligt bovenop het ontwerp, en wordt net als het ontwerp
      // afgesneden op de vlagvorm.
      if (side.lagen && side.lagen.length) {
        ctx.save();
        try {
          pad();
          ctx.clip();
          this.#tekenLagen(ctx, side, r);
          // Binnen dezelfde afsnijding, zodat een hulplijn bij een beachflag of
          // een gepunte vlag niet buiten het doek uitsteekt.
          this.#tekenHulplijnen(ctx, side, r);
          this.#tekenSelectie(ctx, side, r);
        } finally {
          ctx.restore();
        }
      }

      pad();
      ctx.strokeStyle = '#dcdde0';
      ctx.lineWidth = 2;
      ctx.stroke();

      this.#tekenMarge(ctx, r);
      if (this.probo) this.#tekenProbo(ctx, r, pad);

      if (!stil) {
        this.#toonScherpte(side);
        this.#toonPreviewKnop();
      }
    });

    if (this.layout.spandoek) {
      this.#tekenRingen(ctx, this.layout.flags[0]);
      if (!stil) this.#toonLeegMelding();
      if (!skipOutput) this.#output();
      return;
    }

    ctx.fillStyle = '#6f7276';
    if (mast) this.#roundRect(ctx, mast.x, mast.y, mast.w, mast.h, mast.w / 2);
    if (foot) this.#roundRect(ctx, foot.x, foot.y, foot.w, foot.h, foot.h / 2);
    // Schuine stok van de kioskvlag, door de tunnel langs de bovenrand.
    (this.layout.mastLijnen || []).forEach((m) => {
      ctx.beginPath();
      ctx.moveTo(m.x1, m.y1);
      ctx.lineTo(m.x2, m.y2);
      ctx.lineWidth = m.w;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#6f7276';
      ctx.stroke();
    });

    ctx.strokeStyle = '#b9bbbf';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    const dubbel = this.sides.length > 1;
    const focus = this.layout.focus;
    ticks.forEach((y) => {
      ctx.beginPath();
      if (focus === 0) {
        // mast rechts van de linkerzijde: streepjes naar de vlag toe
        ctx.moveTo(mast.x - 10, y);
        ctx.lineTo(mast.x, y);
      } else if (focus === 1) {
        ctx.moveTo(mast.x + mast.w, y);
        ctx.lineTo(mast.x + mast.w + 10, y);
      } else if (dubbel) {
        ctx.moveTo(mast.x - 18, y);
        ctx.lineTo(mast.x, y);
        ctx.moveTo(mast.x + mast.w, y);
        ctx.lineTo(mast.x + mast.w + 18, y);
      } else {
        ctx.moveTo(mast.x + mast.w, y);
        ctx.lineTo(mast.x + mast.w + 18, y);
      }
      ctx.stroke();
    });

    if (!stil) {
      this.#toonLeegMelding();
      this.#toonOntwerpStatus();
    }
    if (!skipOutput) this.#output();
  }

  #toonLeegMelding() {
    if (!this.emptyEl) return;
    const verberg = this.sides.some((s) => s.img || this.#heeftLagen(s));
    if (this.emptyEl.hidden !== verberg) this.emptyEl.hidden = verberg;
  }

  /**
   * Rekent uit hoeveel beeldpunten er per gedrukte centimeter overblijven en
   * zegt er iets over in gewone taal. Een logo van 800 px op een vlag van
   * 150 cm is nu eenmaal 14 ppi — dat wil je weten vóór je bestelt, niet erna.
   */
  #toonScherpte(side) {
    const el = side.qualityEl;
    if (!el) return;

    // De vullende afbeelding van de productpagina.
    if (!side.img || side.vector || !side.img.naturalWidth) {
      side.ppi = 0;
    } else {
      const r = side.rect;
      const g = this.#geometry(side);
      const cmBreed = this.flagCm.w * (g.w / r.w);
      side.ppi = Math.round(side.img.naturalWidth / (cmBreed / 2.54));
    }

    // En de eigen afbeeldingen die in de ontwerptool geplaatst zijn. Daar
    // sleept iemand een logo groter tot het te grof wordt, dus die moet deze
    // regel net zo goed volgen. De slechtste van de twee wordt getoond.
    // De stipjes per element tonen de wérkelijke scherpte van dat element,
    // ongewogen: de klant moet kunnen zien wat hij heeft staan. De regel
    // eronder vat samen wat dat voor de vlag als geheel betekent.
    this.#toonStippen(side);

    const laag = this.#laagScherpte(side);
    const uitLaag = laag.ppi && (!side.ppi || laag.ppi < side.ppi);
    const ppi = uitLaag ? laag.ppi : side.ppi;

    if (!ppi) {
      if (!el.hidden) el.hidden = true;
      // ook hierlangs: wie zijn te grove ontwerp weghaalt of vervangt door een
      // vectorbestand moet de rem meteen kwijt zijn.
      this.#bewaakScherpte();
      return;
    }

    const band = this.#scherpteBand(ppi);
    const tekst = {
      best: this.dataset.tQBest,
      good: this.dataset.tQGood,
      fair: this.dataset.tQFair,
      poor: this.dataset.tQPoor,
    }[band];

    // Alleen schrijven wat anders is: dit loopt bij elke tekenbeurt langs.
    const klasse = 'flagcfg__quality is-' + band;
    const regel = (tekst || '') + ' (' + ppi + ' ppi)';
    if (el.className !== klasse) el.className = klasse;
    if (el.textContent !== regel) el.textContent = regel;
    if (el.hidden) el.hidden = false;
    this.#bewaakScherpte();
  }

  /**
   * Houdt bestellingen tegen waar de drukker niets mee kan.
   *
   * Aanleiding: waarschuwen alleen bleek niet genoeg. Twee vlaggen van
   * 150 x 100 cm zijn de deur uit gegaan op 26 en 30 ppi — allebei een door
   * AI gemaakte afbeelding van pakweg 1500 pixels, uitgerekt over anderhalve
   * meter. De klant zag de waarschuwing en bestelde toch.
   *
   *   onder 18 ppi  : stevige waarschuwing, klant moet uitdrukkelijk bevestigen
   *   18 tot 36 ppi : mildere waarschuwing, ook met bevestiging
   *   vanaf 36 ppi  : niets aan de hand
   *
   * Jos wil bewust niet hard blokkeren: de klant houdt zijn eigen keuze. De
   * bevestiging wordt daarom in de bestelling vastgelegd (zie #zetPlaatsing),
   * zodat achteraf vaststaat dat het een bewuste keuze was en niet een fout
   * die de winkel heeft laten passeren.
   *
   * De slechtste zijde bepaalt. Een zijde zonder eigen afbeelding telt niet
   * mee: tekst, vormen en symbolen zijn vectortekeningen en op elk formaat
   * scherp.
   */
  /**
   * De scherpte van een eigen afbeelding die ín de ontwerptool is geplaatst.
   * De vullende afbeelding van de productpagina wordt elders gemeten; hier
   * gaat het om de losse elementen, die de klant zelf groter kan slepen.
   *
   * Tekst, vormen, symbolen en vlaggen uit de databank tellen niet mee: dat
   * zijn tekeningen en die blijven op elk formaat scherp.
   */
  /** De band waar een ppi in valt; één plek, zodat stip en regel niet uiteenlopen. */
  #scherpteBand(ppi) {
    if (ppi >= 72) return 'best';
    if (ppi >= 36) return 'good';
    if (ppi >= 18) return 'fair';
    return 'poor';
  }

  /**
   * Werkt de stipjes in de elementenlijst bij. Raakt alleen aan wat verandert,
   * want dit loopt bij elke tekenbeurt langs — ook tijdens het slepen.
   */
  #toonStippen(side) {
    if (!side.lagen) return;
    const metingen = this.#laagMetingen(side);
    const perLaag = new Map(metingen.map((m) => [m.laag, m]));

    for (const l of side.lagen) {
      const stip = l.stipEl;
      if (!stip) continue;
      const m = perLaag.get(l);
      if (!m) {
        if (!stip.hidden) stip.hidden = true;
        continue;
      }
      const band = this.#scherpteBand(m.ppi);
      const tekst = m.ppi + ' ppi';
      if (stip.dataset.band !== band) {
        stip.dataset.band = band;
        stip.className = 'flagcfg-tekst__scherpte is-' + band;
      }
      if (stip.textContent !== tekst) stip.textContent = tekst;
      if (stip.hidden) stip.hidden = false;
    }
  }

  #laagMetingen(side) {
    const uit = [];
    if (!this.tekstToegestaan || !side.lagen || !side.lagen.length) return uit;
    const r = side.rect;
    if (!r || !r.w || !r.h || !this.flagCm || !this.flagCm.w) return uit;

    for (const l of side.lagen) {
      if (l.type !== 'eigen' || l.vector) continue;
      const p = l.plaatje;
      if (!p || !p.naturalWidth) continue;
      const hoog = (l.grootte / 100) * r.h;
      const breed = hoog * (l.verhouding || 1);
      const cmBreed = this.flagCm.w * (breed / r.w);
      if (cmBreed <= 0) continue;
      uit.push({
        laag: l,
        naam: l.naam || '',
        ppi: Math.round((p.naturalWidth * (l.snB || 1)) / (cmBreed / 2.54)),
        // hoeveel van het doek dit element beslaat
        deel: (breed * hoog) / (r.w * r.h),
      });
    }
    return uit;
  }

  /**
   * Het oordeel over de hele vlag, gewogen naar hoe groot een element erop
   * staat. Een wazig accent van drie procent is iets anders dan een vervaagde
   * foto die het hele doek vult; zonder die weging krijgt de klant bij elk
   * klein icoontje een rode melding en gaat hij ze negeren.
   *
   * Onder KLEIN_DEEL komt een element niet verder dan "krap" — het valt op de
   * vlag simpelweg niet genoeg op om hem af te keuren.
   */
  #laagScherpte(side) {
    const KLEIN_DEEL = 0.05;
    const metingen = this.#laagMetingen(side);
    let laagste = 0;
    let naam = '';
    for (const m of metingen) {
      const gewogen = m.deel < KLEIN_DEEL ? Math.max(m.ppi, 18) : m.ppi;
      if (!laagste || gewogen < laagste) {
        laagste = gewogen;
        naam = m.naam;
      }
    }
    return { ppi: laagste, naam: naam, aantal: metingen.length };
  }

  #bewaakScherpte() {
    const rij = this.querySelector('[data-scherpte]');
    if (!rij) return;
    const tekstEl = rij.querySelector('[data-scherptetekst]');
    const akkoordRij = rij.querySelector('[data-scherpteakkoordrij]');
    const vinkje = rij.querySelector('[data-scherpteakkoord]');

    // De slechtste van alles wat er ligt: de vullende afbeelding én de eigen
    // afbeeldingen die in de ontwerptool geplaatst zijn.
    const gemeten = [];
    this.sides.forEach((s) => {
      if (s.ppi > 0) gemeten.push(s.ppi);
      const laag = this.#laagScherpte(s);
      if (laag.ppi > 0) gemeten.push(laag.ppi);
    });
    const laagste = gemeten.length ? Math.min(...gemeten) : 0;

    let stand = 'vrij';
    if (laagste > 0 && laagste < 18) stand = 'blok';
    else if (laagste > 0 && laagste < 36) stand = 'akkoord';

    // Deze methode loopt bij elke tekenbeurt langs, ook tijdens het slepen.
    // Verandert er niets, dan het scherm met rust laten — anders kost het
    // onnodig werk op een telefoon.
    if (stand === this.scherpteStand) {
      this.#zetBestelknop();
      return;
    }
    if (vinkje) vinkje.checked = false;
    this.scherpteStand = stand;

    if (stand === 'vrij') {
      rij.hidden = true;
    } else {
      rij.hidden = false;
      const laag = stand === 'blok';
      rij.classList.toggle('is-blok', laag);
      if (tekstEl) {
        tekstEl.textContent = laag
          ? (this.dataset.tResBlok || '')
          : (this.dataset.tResKrap || '');
      }
      // Ook bij de laagste stand mag de klant zelf beslissen; alleen de toon
      // is scherper, zodat het geen achteloos vinkje wordt.
      const jaTekst = rij.querySelector('[data-scherpteakkoordtekst]');
      if (jaTekst) {
        jaTekst.textContent = laag
          ? (this.dataset.tResJaLaag || '')
          : (this.dataset.tResJa || '');
      }
      if (akkoordRij) akkoordRij.hidden = false;
    }

    this.#zetBestelknop();
  }

  /**
   * Eén plek die bepaalt of de bestelknop aan of uit staat. Het uploaden zet
   * hem ook uit; zonder deze gedeelde plek zette de een hem weer aan terwijl
   * de ander hem net had uitgezet.
   */
  #zetBestelknop() {
    const form = this.closest('form');
    const knop = form && form.querySelector('button[type="submit"]');
    if (!knop) return;

    if (this.bezigMetUploaden) {
      knop.disabled = true;
      return;
    }
    const vinkje = this.querySelector('[data-scherpteakkoord]');
    const moetBevestigen = this.scherpteStand === 'blok' || this.scherpteStand === 'akkoord';
    knop.disabled = !!(moetBevestigen && vinkje && !vinkje.checked);
  }

  /**
   * De regel in de bestelling die vastlegt dát de klant een krappe resolutie
   * bewust heeft geaccepteerd. Zonder die regel is achteraf niet te zien of
   * het een bewuste keuze was of iets dat is doorgeglipt — en dat is precies
   * wat de drukker wil weten.
   *
   * Geldt ook voor een ontwerp dat alleen uit tool-elementen bestaat: daar is
   * geen vullende afbeelding, maar een te groot gesleept logo telt net zo goed.
   */
  #akkoordNotitie(side) {
    const laag = this.#laagScherpte(side);
    const ergste = (laag.ppi && (!side.ppi || laag.ppi < side.ppi)) ? laag.ppi : side.ppi;
    if (!ergste || ergste >= 36) return '';
    const vinkje = this.querySelector('[data-scherpteakkoord]');
    if (!vinkje || !vinkje.checked) return '';

    // Niet één getal maar de onderdelen zelf: dan ziet de drukker om welk
    // element het gaat en hoeft hij niet te raden waar de korrel vandaan komt.
    const zwak = this.#laagMetingen(side)
      .filter((m) => m.ppi < 36)
      .sort((a, b) => a.ppi - b.ppi)
      .map((m) => {
        const pct = m.deel * 100;
        const deel = pct < 1 ? 'minder dan 1' : String(Math.round(pct));
        return (m.naam || 'afbeelding') + ' ' + m.ppi + ' ppi (' + deel + '% van de vlag)';
      });
    if (side.ppi && side.ppi < 36) {
      zwak.unshift('vullende afbeelding ' + side.ppi + ' ppi');
    }
    const details = zwak.length ? ' [' + zwak.join('; ') + ']' : '';

    return ergste < 18
      ? ' — LAGE RESOLUTIE, KLANT UITDRUKKELIJK AKKOORD' + details
      : ' — krappe resolutie, klant akkoord' + details;
  }

  #bindScherpteRem() {
    const vinkje = this.querySelector('[data-scherpteakkoord]');
    if (!vinkje) return;
    vinkje.addEventListener('change', () => {
      this.#zetBestelknop();
      // Ook de omschrijving voor de drukker opnieuw opbouwen: daar hoort in te
      // staan dát de klant akkoord ging. Zonder deze regel bleef die tekst
      // achter bij het vinkje.
      this.#output();
    });
  }

  /**
   * Veilige marge (productveld, bijvoorbeeld bij Probo): een stippellijn
   * waarbinnen tekst en logo's moeten blijven. Alleen op het scherm. Dus niet
   * in #tekenLagen, want dan komt hij in het drukbestand, en niet in het
   * controlebeeld (zonderSelectie). Canvas tekenen schrijft niets in de pagina,
   * dus dit mag ook tijdens het slepen.
   */
  #tekenMarge(ctx, r) {
    if (!(this.margeCm > 0) || this.isBeach || this.probo || this.zonderSelectie) return;
    const mx = (this.margeCm / this.flagCm.w) * r.w;
    const my = (this.margeCm / this.flagCm.h) * r.h;
    if (mx * 2 >= r.w || my * 2 >= r.h) return;
    const s = this.#viewPerPx();
    if (!Number.isFinite(s) || s <= 0) return;
    ctx.save();
    ctx.setLineDash([6 * s, 4 * s]);
    ctx.lineWidth = 1.5 * s;
    ctx.strokeStyle = 'rgba(8, 145, 178, 0.9)';
    ctx.strokeRect(r.x + mx, r.y + my, r.w - mx * 2, r.h - my * 2);
    ctx.restore();
  }

  /**
   * Een andere variant gekozen bij een beachflag: vorm en bandkleur volgen.
   * Bij Probo verschilt de maat per vorm, dus dan wordt het tekenvlak
   * herbouwd; het ontwerp blijft op dezelfde plek in procenten.
   */
  #volgBeachVariant(id) {
    // Ook de boot-, golf- en kioskvlag van Probo: andere maat of kant is een
    // andere vorm uit de werktekening.
    const vorm = this.proboVlag ? this.proboVormen[String(id)] : this.vormen[String(id)];
    const band = this.banden[String(id)];
    const andereBand = !!band && band !== this.band;
    if (andereBand) this.band = band;
    if (vorm && vorm !== this.vorm) {
      const v = proboVorm(vorm);
      // Een vlag van Probo zonder geladen vorm houdt de vorige; anders zou hij
      // ineens rechthoekig worden.
      if (this.proboVlag && !v) return;
      this.vorm = vorm;
      this.probo = v;
      if (v) {
        this.flagCm = { w: v.doek.b, h: v.doek.h };
        if (this.proboVlag && v.afloop > 0) this.afloopCm = v.afloop;
        this.layout = this.#nieuweLayout();
        this.#zetCanvas();
        this.sides.forEach((side, i) => {
          side.rect = this.layout.flags[i];
          if (side.sjabloonKlaar && window.FLAGCFG_SJABLONEN) this.#bouwSjablonen(side);
        });
      }
      this.#render();
      return;
    }
    if (andereBand) this.#render();
  }

  /**
   * Beachflag van Probo, alleen op het scherm (niet in #tekenLagen, dus niet in
   * het drukbestand):
   * - de elastische band aan de mastzijde, in de gekozen kleur. Wat daaronder
   *   staat, ziet niemand, en dat moet de klant zien.
   * - de veilige marge als stippellijn. Die blijft uit het controlebeeld.
   */
  #tekenProbo(ctx, r, pad) {
    const v = this.probo;
    if (!v) return;
    const s = this.#viewPerPx();
    const px = Number.isFinite(s) && s > 0 ? s : 1;
    const lijn = (punten, dicht) => {
      ctx.beginPath();
      ctx.moveTo(punten[0][0], punten[0][1]);
      for (let i = 1; i < punten.length; i++) ctx.lineTo(punten[i][0], punten[i][1]);
      if (dicht) ctx.closePath();
    };
    ctx.save();
    try {
      pad();
      ctx.clip();
      (v.band || []).forEach((b) => {
        const p = proboPunten(b, v, r);
        if (p.length < 3) return;
        lijn(p, true);
        ctx.fillStyle = this.band === 'zwart' ? '#1f1f22' : '#f3f3f1';
        ctx.fill();
        ctx.lineWidth = px;
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.28)';
        ctx.stroke();
      });
      // Boot-, golf- en kioskvlag: wat om de stok of het buisje gaat (band,
      // tunnel, omslag) grijs over het ontwerp. Niet in het controlebeeld:
      // daar zou de drukker het voor een deel van de print aanzien.
      if (!this.zonderSelectie) {
        // Baniervlag (3 nov 2026): de smalle strook aan de mastzijde is de band
        // met haken, die wordt in de gekozen kleur (wit of zwart) getekend. De
        // tunnel bovenaan blijft grijs.
        const banier = String(this.vorm || '').startsWith('banier');
        (v.plooi || []).forEach((b) => {
          const p = proboPunten(b, v, r);
          if (p.length < 3) return;
          lijn(p, true);
          const xs = b.filter((s) => s.length >= 3).map((s) => s[1]);
          if (banier && xs.length && Math.max(...xs) <= 5) {
            ctx.fillStyle = this.band === 'zwart' ? '#1f1f22' : '#f3f3f1';
            ctx.fill();
            ctx.lineWidth = px;
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.28)';
            ctx.stroke();
            return;
          }
          ctx.fillStyle = 'rgba(38, 40, 44, 0.34)';
          ctx.fill();
        });
      }
    } finally {
      ctx.restore();
    }
    if (this.zonderSelectie) return;
    ctx.save();
    ctx.setLineDash([6 * px, 4 * px]);
    ctx.lineWidth = 1.5 * px;
    ctx.strokeStyle = 'rgba(8, 145, 178, 0.9)';
    (v.marge || []).forEach((m) => {
      const p = proboPunten(m, v, r);
      if (p.length < 2) return;
      lijn(p, m.length > 0 && m[m.length - 1][0] === 'Z');
      ctx.stroke();
    });
    ctx.restore();
  }

  /** Afloop in beeldpunten van het drukbestand; 0 als het product geen afloop heeft. */
  #afloopPx(breed, afloopCm = this.afloopCm, maatBreed = this.flagCm.w) {
    return afloopCm > 0 ? Math.max(1, Math.round((afloopCm / maatBreed) * breed)) : 0;
  }

  /**
   * Vult de afloop rondom het eindformaat door de buitenste rij en kolom
   * beeldpunten naar buiten door te trekken. Het ontwerp zelf blijft op
   * precies dezelfde plek, en schuift het snijden een paar millimeter, dan
   * komt er geen witte rand. Het doek wordt op zichzelf getekend: dat mag, de
   * browser kopieert de bron eerst.
   */
  #vulAfloop(d, doek, breed, hoog, rand) {
    const hoogte = hoog + rand * 2;
    d.setTransform(1, 0, 0, 1, 0, 0);
    d.drawImage(doek, rand, rand, breed, 1, rand, 0, breed, rand);
    d.drawImage(doek, rand, rand + hoog - 1, breed, 1, rand, rand + hoog, breed, rand);
    d.drawImage(doek, rand, 0, 1, hoogte, 0, 0, rand, hoogte);
    d.drawImage(doek, rand + breed - 1, 0, 1, hoogte, rand + breed, 0, rand, hoogte);
  }

  /**
   * Voor de drukker, bij de achterkant van de dubbelzijdige kioskvlag: die kant
   * ligt na het vouwen gespiegeld ten opzichte van de werktekening. Bij alle
   * andere vlaggen niets.
   */
  #spiegelNotitie(side) {
    return side.rect && side.rect.spiegel ? 'achterkant, gespiegeld ten opzichte van de werktekening - ' : '';
  }

  /** Voor de drukker: ", plus 10 mm afloop rondom", of niets. */
  #afloopNotitie() {
    return this.afloopCm > 0 ? ', plus ' + Math.round(this.afloopCm * 10) + ' mm afloop rondom' : '';
  }

  /** Productsoort in de regel voor de drukker: "Vlag", "Spandoek", "Bouwhekdoek". */
  #soortNaam() {
    // Boot-, golf- en kioskvlag: de naam uit de werktekening, met maat en kant.
    // Daarachter volgt de snijmaat; die is groter dan de vlag zelf (tunnel,
    // omslag, golftube).
    if (this.proboVlag && this.probo) return this.probo.naam + ', snijmaat';
    if (this.probo) return 'Beachflag ' + (this.dataset.proboVormNaam || 'Straight') + ' (mast links)';
    return this.isSpandoek ? (this.dataset.soortNaam || 'Spandoek') : 'Vlag';
  }

  /**
   * Zeilringen om de vijftig centimeter, zoals de drukkerij ze standaard zet.
   * Ze staan bewust ín het doek getekend: dan ziet de klant meteen hoeveel
   * ruimte hij aan de rand moet vrijhouden.
   */
  #tekenRingen(ctx, r) {
    const inzet = 13;
    const perRij = Math.max(2, Math.round(this.flagCm.w / 50) + 1);
    const perKolom = Math.max(2, Math.round(this.flagCm.h / 50) + 1);

    const punten = [];
    for (let i = 0; i < perRij; i++) {
      const x = r.x + inzet + ((r.w - inzet * 2) * i) / (perRij - 1);
      punten.push([x, r.y + inzet], [x, r.y + r.h - inzet]);
    }
    for (let j = 1; j < perKolom - 1; j++) {
      const y = r.y + inzet + ((r.h - inzet * 2) * j) / (perKolom - 1);
      punten.push([r.x + inzet, y], [r.x + r.w - inzet, y]);
    }

    punten.forEach(([x, y]) => {
      ctx.beginPath();
      ctx.arc(x, y, 6.5, 0, Math.PI * 2);
      ctx.fillStyle = '#f4f4f5';
      ctx.fill();
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = '#9a9da2';
      ctx.stroke();
    });
  }

  #roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(x, y, w, h, r);
    else ctx.rect(x, y, w, h);
    ctx.fill();
  }

  // ---------- doorgeven aan de bestelling ----------

  #output() {
    // Ook een bestand zonder voorbeeld (bijvoorbeeld .ai) komt hier langs, en
    // daarvoor wordt niet getekend.
    if (!this.#bezigMetGebaar()) {
      this.#toonOntwerpStatus();
      this.#planStap();
    }

    // Laatste controle vlak voor er iets naar de bestelling gaat: wat in het
    // formulier staat is wat de klant koopt. Wijkt de preview daarvan af, dan
    // eerst gelijktrekken — #zetMaat tekent opnieuw en komt hier meteen terug.
    if (!this.isBeach && !this.proboVlag && this.variantVeld) {
      const cm = this.#maatVan(this.variantVeld.value);
      if (cm && (cm.w !== this.flagCm.w || cm.h !== this.flagCm.h)) {
        this.#zetMaat(cm);
        return;
      }
    }

    this.sides.forEach((side) => {
      const tekstUitleg = this.#laagOmschrijving(side);
      if (!side.img) {
        // Alleen tekst, geen ontwerp: dan is er nog steeds iets te drukken.
        if (tekstUitleg) {
          const soortT = this.#soortNaam();
          side.posInput.value =
            soortT + ' ' + this.flagCm.w + 'x' + this.flagCm.h + ' cm - ' + this.#spiegelNotitie(side) +
            'egale achtergrond ' + side.bg + tekstUitleg +
            this.#akkoordNotitie(side) +
            (side.drukPixels ? ' — DRUKKLAAR BESTAND MEEGELEVERD (' + side.drukPixels + this.#afloopNotitie() + ')' : '');
          side.posInput.disabled = false;
          return;
        }
        side.posInput.value = '';
        side.posInput.disabled = true;
        return;
      }
      const r = side.rect;
      const g = this.#geometry(side);
      // leesbare instructie voor de drukker (en nog steeds exact)
      const pct = (v) => v.toFixed(1).replace('.', ',') + '%';
      const vult =
        Math.abs(g.w - r.w) < 0.5 && Math.abs(g.h - r.h) < 0.5 && Math.abs(g.x - r.x) < 0.5 && Math.abs(g.y - r.y) < 0.5;
      const namen = { veer: 'veervorm', druppel: 'druppelvorm', recht: 'rechthoekig' };
      const soort = this.#soortNaam();
      const maat = (this.isBeach && !this.probo
        ? 'Beachflag ' + (namen[this.vorm] || this.vorm) + ' - '
        : soort + ' ' + this.flagCm.w + 'x' + this.flagCm.h + ' cm - ') + this.#spiegelNotitie(side);
      const scherpte = (side.zonderAchtergrond ? ', achtergrond van het ontwerp weggehaald' : '')
        + (side.ppi ? ', ontwerp ca. ' + side.ppi + ' ppi op ware grootte' : '')
        + this.#akkoordNotitie(side);
      // Staat er een drukklaar bestand klaar, dan hoeft de drukker niets meer
      // in te passen; de percentages blijven erbij staan als controle.
      const drukklaar = side.drukPixels
        ? ' — DRUKKLAAR BESTAND MEEGELEVERD (' + side.drukPixels + ', exact op ' +
          this.flagCm.w + ' x ' + this.flagCm.h + ' cm' + this.#afloopNotitie() + ')'
        : '';
      // "het hele doek" en niet "de hele vlag": deze regel gaat sinds de
      // ontwerptool ook op het spandoek mee naar de drukker.
      side.posInput.value =
        (vult
          ? maat + 'ontwerp vult het hele doek' + scherpte + drukklaar
          : maat +
            'ontwerp ' +
            pct((g.w / r.w) * 100) +
            ' breed x ' +
            pct((g.h / r.h) * 100) +
            ' hoog, ' +
            pct(((g.x - r.x) / r.w) * 100) +
            ' vanaf links, ' +
            pct(((g.y - r.y) / r.h) * 100) +
            ' vanaf boven, achtergrond ' +
            side.bg +
            scherpte +
            drukklaar) + tekstUitleg;
      side.posInput.disabled = false;
    });

    // Controlebeeld en drukbestand wel meteen maken (350/900 ms, zoals altijd), maar pas
    // uploaden als de klant even stilzit (RUST_MS, zie #uploadMoment). Elke upload is een
    // Cloudflare-schrijfactie: na elke wijziging uploaden haalde op 10 okt 2026 966 van de
    // 1.000 gratis per dag. Op 11 okt wachtte ook het máken op die rust, en dan moest een
    // telefoon bij bestellen eerst nog seconden tekenen (Tim, 14 okt: "extreme vertraging").
    // Nu ligt het bestand klaar en gaat bij bestellen alleen de upload nog weg.
    this.uploadWacht = true;
    clearTimeout(this.uploadTimer);
    this.uploadTimer = setTimeout(() => this.#openUpload(), FlagConfigurator.RUST_MS);

    // Niets aan het ontwerp veranderd (bijvoorbeeld alleen de statusregel bijgewerkt
    // nadat het drukbestand binnen was)? Dan ook geen nieuw controlebeeld: dat kostte
    // bij bestellen ruim een seconde extra wachten op een identiek plaatje.
    if (!(this.previewSleutel && this.previewSleutel === this.#previewSleutelNu() && !this.previewWacht &&
          (this.previewBeurt || 0) === (this.previewKlaarBeurt || 0) &&
          this.previewInput && this.previewInput.value && !this.previewInput.disabled)) {
      clearTimeout(this.previewTimer);
      this.previewWacht = true;
      this.previewTimer = setTimeout(() => this.#naGebaar('previewTimer', () => {
        this.previewWacht = false;
        this.#makePreview();
      }), 350);
    }

    clearTimeout(this.drukTimer);
    this.drukWacht = true;
    this.drukTimer = setTimeout(() => this.#naGebaar('drukTimer', () => {
      this.drukWacht = false;
      this.#maakDrukbestanden();
    }), 900);
  }

  static RUST_MS = 4000;

  /** Wat er op het controlebeeld staat: het ontwerp (zie #vingerafdruk) plus de focusstand. */
  #previewSleutelNu() {
    if (!this.sides || !this.sides.some((s) => s.img || this.#heeftLagen(s))) return '';
    return this.#vingerafdruk() + '|' + (this.layout ? this.layout.focus : '');
  }

  /** Wacht tot er geüpload mag worden: de klant zit stil, of hij bestelt (#nuBestanden). */
  #uploadMoment() {
    if (!this.uploadWacht) return Promise.resolve();
    return new Promise((res) => (this.uploadRij = this.uploadRij || []).push(res));
  }

  #openUpload() {
    clearTimeout(this.uploadTimer);
    this.uploadWacht = false;
    const rij = this.uploadRij || [];
    this.uploadRij = [];
    rij.forEach((res) => res());
  }

  /** Staat er nog een controlebeeld of drukbestand in de wacht, maak het dan nu. */
  #nuBestanden() {
    this.#openUpload();
    if (this.previewWacht) {
      clearTimeout(this.previewTimer);
      this.previewWacht = false;
      this.#makePreview();
    }
    if (this.drukWacht) {
      clearTimeout(this.drukTimer);
      this.drukWacht = false;
      this.#maakDrukbestanden();
    }
  }

  /**
   * Maakt een compact controlebeeld (JPEG, ~900px breed) dat bij elke bestelling meegaat.
   * Bewust klein: het is een referentie voor de drukker, niet het drukbestand zelf.
   */
  #makePreview() {
    // Op hetzelfde rustmoment als het controlebeeld de ontwerp-ID bijwerken:
    // dan staat hij klaar voordat de klant op "in winkelwagen" drukt.
    this.#zetOntwerpId();
    if (!this.previewInput || !this.canvas.toBlob) return;
    // Het controlebeeld is een foto van het doek. In de focusstand staat daar
    // maar een zijde op, en dat hoort niet bij de bestelling. Bij het verlaten
    // van de focusstand wordt opnieuw getekend en komt het controlebeeld alsnog.
    if (this.layout && (this.layout.focus === 0 || this.layout.focus === 1)) return;
    if (!this.sides.some((s) => s.img || this.#heeftLagen(s))) return;
    // Welk controlebeeld is het nieuwste? Zie #wachtOpBestanden.
    const beurt = (this.previewBeurt = (this.previewBeurt || 0) + 1);
    const sleutel = this.#previewSleutelNu();
    try {
      const { view } = this.layout;
      const breedte = Math.min(900, view.w);
      const klein = document.createElement('canvas');
      klein.width = Math.round(breedte);
      klein.height = Math.round((view.h / view.w) * breedte);

      const k = klein.getContext('2d');
      k.fillStyle = '#ffffff'; // JPEG kent geen transparantie
      k.fillRect(0, 0, klein.width, klein.height);
      // Het kader om een gekozen element hoort niet in het controlebeeld.
      this.zonderSelectie = true;
      this.#render(true);
      k.drawImage(this.canvas, 0, 0, klein.width, klein.height);
      this.zonderSelectie = false;
      this.#render(true);

      klein.toBlob(
        async (blob) => {
          klein.width = 0;
          klein.height = 0;
          const mislukt = () => {
            // Geen oud controlebeeld bij een nieuw ontwerp, en het bestellen niet laten wachten.
            if (beurt !== this.previewBeurt) return;
            this.previewInput.value = '';
            this.previewInput.disabled = true;
            this.previewSleutel = '';
            this.previewKlaarBeurt = beurt;
          };
          if (!blob || !this.uploadUrl) return mislukt();
          // Vooraf wegzetten en alleen de URL meesturen. Het bestelverzoek
          // blijft daardoor klein, zodat de winkelwagen meteen bijwerkt.
          try {
            // Zelfde beeld als het vorige (alleen opnieuw getekend, niets veranderd)? Dan niet
            // nog eens uploaden: elke upload is een schrijfactie bij Cloudflare.
            let vinger = '';
            if (crypto.subtle) {
              const h = await crypto.subtle.digest('SHA-1', await blob.arrayBuffer());
              vinger = [...new Uint8Array(h)].map((x) => x.toString(16).padStart(2, '0')).join('');
            }
            if (beurt !== this.previewBeurt) return;
            if (vinger && vinger === this.previewVinger && this.previewInput.value && !this.previewInput.disabled) {
              this.previewKlaarBeurt = beurt;
              this.previewSleutel = sleutel;
              return;
            }
            await this.#uploadMoment();
            if (beurt !== this.previewBeurt) return; // intussen een nieuwer controlebeeld
            const body = new FormData();
            body.append('file', blob, 'controlebeeld.jpg');
            const res = await fetch(this.uploadUrl, { method: 'POST', body });
            if (!res.ok) return mislukt();
            const data = await res.json();
            if (!data.url) return mislukt();
            // Intussen al een nieuwer controlebeeld onderweg: dit oudere niet meer invullen.
            if (beurt !== this.previewBeurt) return;
            this.previewInput.value = data.url;
            this.previewInput.disabled = false;
            this.previewVinger = vinger;
            this.previewSleutel = sleutel;
            this.previewKlaarBeurt = beurt;
          } catch (err) {
            /* vangnet: de plaatsingsregel en het drukbestand blijven de bron */
            mislukt();
          }
        },
        'image/jpeg',
        0.82
      );
    } catch (err) {
      /* niets */
    }
  }

  /**
   * Maakt per zijde een drukklaar bestand: alleen het vlagvlak, op precies de
   * verhouding van het gekozen formaat, met het ontwerp er al in geplaatst.
   *
   * Zonder dit krijgt de drukker het losse ontwerp plus een zin met percentages
   * en moet hij het zelf inpassen. Dat kost studiotijd en ging bij bestelling
   * #1009 mis. Nu komt er een bestand binnen dat zo op de pers kan.
   *
   * De resolutie wordt niet hoger gezet dan het aangeleverde ontwerp aankan —
   * opblazen voegt niets toe en maakt het bestand alleen zwaarder.
   */
  #maakDrukbestanden() {
    if (!this.uploadUrl) return;
    // Beachflags nog niet: de printmaat in deze code (72 x 260 cm) is een
    // schatting voor de scherpteberekening, geen template van de fabriek. Een
    // "drukklaar" bestand op een geraden maat is erger dan geen bestand.
    // Probo-beachflags wel: daar is de snijmaat uit de werktekening bekend.
    if (this.isBeach && !this.probo) return;
    this.sides.forEach((side) => this.#maakDrukbestand(side));
  }

  /**
   * Eén drukbestand tegelijk op het tekenvlak. De zijden starten nog wel samen
   * (lettertypes laden, uploaden), maar het grote canvas maakt er maar één
   * tegelijk. Geeft een functie terug die het slot weer vrijgeeft.
   */
  async #wachtOpDrukSlot() {
    const vorige = this.drukSlot || Promise.resolve();
    let vrij;
    this.drukSlot = new Promise((res) => {
      vrij = res;
    });
    await vorige;
    return vrij;
  }

  /**
   * Is het ontwerp een echt vectorbestand? Dan is er geen bronresolutie die de
   * bovengrens bepaalt en mag het drukbestand naar de volle 150 ppi.
   *
   * Let op: een PDF is hier al door pdf.js tot een bitmap van vaste maat
   * gemaakt. Die telt dus gewoon mee als bitmap, ook al staat side.vector op
   * true.
   */
  #schaalbaar(side) {
    if (!side.vector) return false;
    if (side.vlagCode) return true;                   // vlag uit de databank is .svg
    const type = (side.bronBestand && side.bronBestand.type) || '';
    return type === 'image/svg+xml' || /\.svg$/i.test(side.fileName || '');
  }

  #drukMaat(side) {
    const uit = this.#drukMaatRuw(side);
    // Nooit groter dan een vlag van 300 x 200 op 8000 px breed (ruim 42
    // miljoen beeldpunten). Een staande baniervlag van 100 x 400 cm zou anders
    // 5906 x 23622 worden: meer dan een half gigabyte geheugen (5 okt 2026).
    // Een wimpel (25 x 200 op 150 ppi = 1476 x 11811) zit net boven wat Safari op een iPhone in één
    // canvas tekent (16,7 miljoen); dan bleef het drukbestand leeg. Daar een lagere grens.
    const MAX = this.isWimpel ? 16000000 : 8000 * 5334;
    if (uit.breed * uit.hoog <= MAX) return uit;
    const k = Math.sqrt(MAX / (uit.breed * uit.hoog));
    return { breed: Math.floor(uit.breed * k), hoog: Math.floor(uit.hoog * k) };
  }

  #drukMaatRuw(side) {
    const r = side.rect;
    const bovengrens = Math.round((this.flagCm.w / 2.54) * 150);
    // Alleen tekst, geen ontwerp: er is geen bronbestand dat de bovengrens
    // bepaalt, dus gaan we naar de volle 150 ppi. Tekst is scherp op elk
    // formaat, dus dat kost niets aan kwaliteit.
    if (!side.img) {
      const breed = Math.max(1200, Math.min(8000, bovengrens));
      return { breed, hoog: Math.max(1, Math.round((breed * r.h) / r.w)) };
    }
    const g = this.#geometry(side);
    const deel = Math.max(g.w / r.w, 0.05);           // hoeveel van de breedte het ontwerp beslaat
    // Een vector heeft geen bronresolutie: die mag naar de bovengrens toe.
    // Zonder deze regel leverde een SVG-logo op een vlag van 300 cm een
    // drukbestand van 10 ppi op, terwijl de scherpterem terecht niets zei.
    const uitOntwerp = this.#schaalbaar(side)
      ? Infinity
      : Math.round(side.img.naturalWidth / deel);
    const breed = Math.max(1200, Math.min(8000, bovengrens, uitOntwerp));
    return { breed, hoog: Math.max(1, Math.round((breed * r.h) / r.w)) };
  }

  /**
   * Vlaggenlijn (Jos, 5 oktober 2026): het drukvel volgens de werktekening van
   * Probo, met op elke plek het vlaggetje dat de klant ontwierp.
   *
   * Op het vel liggen wimpels om en om met de punt naar beneden en naar boven,
   * zodat ze in elkaar passen. Een vlaggetje met de punt naar boven ligt op zijn
   * kop (draai 180); dat moet het ontwerp ook. Elke plek wordt op de snijlijn
   * plus 0,5 cm afloop geknipt, anders zou een ontwerp over het buurvlaggetje
   * heen vallen.
   *
   * Het vel is ruim anderhalve meter breed. Op een iPhone mag een tekenvlak
   * niet groter zijn dan zo'n 16 miljoen beeldpunten; daarom hoogstens 14
   * miljoen, en nooit meer dan 100 ppi. Voor vlaggetjes van 15 cm is dat ruim.
   */
  #maakVlaggenlijnVel(doek, v, breed, hoog, rand, bg, nummers) {
    const vel = v.vel;
    const pxPerCm = Math.min(100 / 2.54, Math.sqrt(14e6 / (vel.b * vel.h)));
    const c = document.createElement('canvas');
    c.width = Math.round(vel.b * pxPerCm);
    c.height = Math.round(vel.h * pxPerCm);
    const d = c.getContext('2d');
    d.fillStyle = bg || '#ffffff';
    d.fillRect(0, 0, c.width, c.height);
    d.imageSmoothingQuality = 'high';
    vel.plekken.forEach((p, j) => {
      // Elk vlaggetje anders: `doek` is dan een functie die het vlaggetje met dat nummer tekent.
      const bron = typeof doek === 'function' ? doek(nummers[j]) : doek;
      d.save();
      d.beginPath();
      p.clip.forEach((q, i) => {
        const x = (p.x + q[0]) * pxPerCm;
        const y = (p.y + q[1]) * pxPerCm;
        if (i) d.lineTo(x, y); else d.moveTo(x, y);
      });
      d.closePath();
      d.clip();
      d.translate((p.x + p.b / 2) * pxPerCm, (p.y + p.h / 2) * pxPerCm);
      if (p.draai) d.rotate((p.draai * Math.PI) / 180);
      d.scale((p.b * pxPerCm) / breed, (p.h * pxPerCm) / hoog);
      d.drawImage(bron, -(rand + breed / 2), -(rand + hoog / 2));
      d.restore();
    });
    return c;
  }

  /**
   * Verborgen veld voor het drukvel van de achterzijde (vlaggenlijn, dubbelzijdig, elk vlaggetje anders).
   * De Probo-koppeling herkent "achterzijde" als de achterkant.
   */
  #achterInput(side, maken) {
    if (side.drukAchterInput || !maken) return side.drukAchterInput || null;
    if (!side.drukInput) return null;
    const i = document.createElement('input');
    i.type = 'hidden';
    i.name = 'properties[_Drukbestand achterzijde]';
    i.disabled = true;
    side.drukInput.after(i);
    side.drukAchterInput = i;
    return i;
  }

  async #maakDrukbestand(side) {
    if (!side.drukInput) return;
    const heeftLagen = this.tekstToegestaan && this.#heeftLagen(side);
    if (!side.img && !heeftLagen) return;

    const r = side.rect;
    const g = side.img ? this.#geometry(side) : { x: r.x, y: r.y, w: 0, h: 0 };
    const { breed, hoog } = this.#drukMaat(side);
    // Maat en afloop nu vastleggen. Hieronder wordt gewacht (letters, symbolen,
    // het drukslot); kiest de klant intussen een andere maat, dan kreeg het
    // bestand anders de afloop en de naam van die nieuwe maat.
    const maatCm = { w: this.flagCm.w, h: this.flagCm.h };
    const afloopCm = this.afloopCm;
    // Vlaggenlijn: de klant ontwerpt één vlaggetje, de drukker wil een vel met
    // alle vlaggetjes (zie #maakVlaggenlijnVel). Ook dit nu vastleggen.
    const velVorm = this.probo && this.probo.vel ? this.probo : null;

    // niet opnieuw maken als er niets veranderd is. De lagen horen hier ook
    // in: verandert de klant een woord of een kleur, dan moet er een nieuw
    // drukbestand komen — anders drukt de drukker de vorige versie.
    const laagKenmerk = side.lagen
      .map((l) => [l.type, l.tekst, l.font, l.teken, l.naam, l.vorm, l.verhouding,
                   l.kleur, l.grootte, l.hoek, Math.round(l.cx), Math.round(l.cy), l.zonderAchtergrond ? 'za' + bgSleutel(l.uitsnede) : '',
                   l.snB ? [l.snX, l.snY, l.snB, l.snH].map((x) => x.toFixed(4)).join('/') : '',
                   // Tekststijl (13 okt 2026): een andere rand of boog is een andere vlag.
                   l.spatie, l.boog, l.rand, l.randKleur, l.effect, l.effectKleur, l.dekking,
                   // Vlaggenlijn: verdeelde naam en foto per vlaggetje (17 okt 2026).
                   l.verdeel ? 'v' : '', l.vlaggetje || ''].join(','))
      .join(';');
    const kenmerk = [side.fileName, this.flagCm.w + 'x' + this.flagCm.h, 'afloop' + this.afloopCm,
                     this.isBeach || this.proboVlag ? this.vorm : '', breed, hoog, side.bg, side.stretch ? 1 : 0,
                     // Achtergrond weggehaald: ander beeld, dus een nieuw drukbestand.
                     side.zonderAchtergrond ? 'za' + bgSleutel(side.uitsnede) : '',
                     // In verhouding tot het vlagvlak, niet in beeldpunten: het doek
                     // wordt op verschillende groottes getoond (gewoon of in de
                     // focusstand), en dat is geen reden voor een nieuw drukbestand.
                     Math.round(((g.x - r.x) / r.w) * 10000), Math.round(((g.y - r.y) / r.h) * 10000),
                     Math.round((g.w / r.w) * 10000), Math.round((g.h / r.h) * 10000), laagKenmerk].join('|');
    if (side.drukKenmerk === kenmerk) return;
    side.drukKenmerk = kenmerk;

    // Meteen loslaten: tot het nieuwe bestand klaar is mag er geen oude URL
    // meegaan. Anders krijgt de drukker bij een maatwijziging het bestand van
    // het vorige formaat — precies de fout die bestelling #1009 opleverde.
    side.drukPixels = null;
    side.drukInput.value = '';
    side.drukInput.disabled = true;
    const achterInput = this.#achterInput(side);
    if (achterInput) {
      achterInput.value = '';
      achterInput.disabled = true;
    }

    // Webletters moeten binnen zijn voordat er getekend wordt. Anders valt de
    // browser stilletjes terug op Arial en krijgt de klant een andere vlag
    // gedrukt dan hij op zijn scherm zag.
    if (document.fonts && document.fonts.load) {
      const nodig = side.lagen
        .filter((l) => l.type === 'tekst' && l.tekst)
        .map((l) => letterGewicht(l.font) + ' 40px ' + l.font.split(',')[0].trim());
      try {
        await letterbladKlaar();
        await Promise.all([...new Set(nodig)].map((f) => document.fonts.load(f)));
      } catch (err) {
        /* lukt het laden niet, dan tekenen we met wat er is */
      }
    }

    // Kleurensymbolen worden als afbeelding getekend en die laden met
    // vertraging. Eerst wachten tot ze er allemaal zijn — anders staat het
    // symbool wel in de preview maar niet op het drukbestand, en dat merkt
    // niemand tot het pakket bij de klant ligt.
    const wachtend = side.lagen.filter(
      (l) => (l.type === 'kleur' || l.type === 'eigen') && !l.plaatje && !l.plaatjeStuk
    );
    if (wachtend.length) {
      wachtend.forEach((l) => this.#laadPlaatje(l));
      const klaar = await new Promise((res) => {
        const start = Date.now();
        const kijk = () => {
          if (wachtend.every((l) => l.plaatje || l.plaatjeStuk)) return res(true);
          if (Date.now() - start > 8000) return res(false);
          setTimeout(kijk, 120);
        };
        kijk();
      });
      // Niet gelukt, of de klant heeft intussen iets anders gedaan: dit
      // drukbestand niet maken. Het kenmerk vrijgeven zodat een volgende
      // poging het opnieuw probeert.
      if (!klaar || side.drukKenmerk !== kenmerk) {
        if (side.drukKenmerk === kenmerk) side.drukKenmerk = null;
        return;
      }
    }

    // Het tekenen hieronder gebeurt op drukformaat en houdt de browser even
    // bezig. Sleept de klant intussen weer, dan eerst die beweging afwachten.
    while (this.#bezigMetGebaar()) await new Promise((res) => setTimeout(res, 200));
    if (side.drukKenmerk !== kenmerk) return;   // intussen alweer iets veranderd

    // Om de beurt: twee drukbestanden van 8000 px tegelijk kostten samen ruim
    // 340 MB, en daar liep een iPhone op vast (#wachtOpDrukSlot).
    const vrij = await this.#wachtOpDrukSlot();
    let blob;
    let blobAchter = null;
    let doek = null;
    // Vlaggenlijn met elk vlaggetje anders; bij dubbelzijdig ook een vel voor de achterzijde (de
    // volgorde is daar anders, zie vlagNummers).
    const apart = !!velVorm && this.#vlagApart(side);
    const dubbel = apart && String(this.vorm).indexOf('dubbel') >= 0;
    // Afloop rondom, alleen bij producten die dat vragen (Probo). Bij de
    // andere producten is dit 0 en blijft het drukbestand exact de eindmaat.
    const rand = this.#afloopPx(breed, afloopCm, maatCm.w);
    try {
      if (side.drukKenmerk !== kenmerk) return;   // tijdens het wachten veranderd
      doek = document.createElement('canvas');
      doek.width = breed + rand * 2;
      doek.height = hoog + rand * 2;
      const d = doek.getContext('2d');
      d.fillStyle = side.bg || '#ffffff';
      d.fillRect(0, 0, doek.width, doek.height);
      // Het eindformaat begint na de afloop; alles hieronder tekent daarop.
      d.translate(rand, rand);
      const schaal = breed / r.w;
      d.imageSmoothingQuality = 'high';
      if (side.img) {
        d.drawImage(side.img, (g.x - r.x) * schaal, (g.y - r.y) * schaal, g.w * schaal, g.h * schaal);
      }
      // Dezelfde tekstsom als in de preview, alleen op drukformaat. Het doek
      // begint hier linksboven op 0,0, dus geven we een rechthoek mee die
      // daarop staat — de percentages doen de rest.
      this.#tekenLagen(d, side, { x: 0, y: 0, w: breed, h: hoog });
      if (rand > 0) this.#vulAfloop(d, doek, breed, hoog, rand);
      if (velVorm && apart) {
        // Elk vlaggetje anders: per plek het vlaggetje met het nummer van die plek tekenen.
        const tekenNr = (nr) => {
          d.setTransform(1, 0, 0, 1, 0, 0);
          d.fillStyle = side.bg || '#ffffff';
          d.fillRect(0, 0, doek.width, doek.height);
          d.translate(rand, rand);
          if (side.img) d.drawImage(side.img, (g.x - r.x) * schaal, (g.y - r.y) * schaal, g.w * schaal, g.h * schaal);
          this.#tekenLagen(d, side, { x: 0, y: 0, w: breed, h: hoog }, nr);
          if (rand > 0) this.#vulAfloop(d, doek, breed, hoog, rand);
          return doek;
        };
        let vel = this.#maakVlaggenlijnVel(tekenNr, velVorm, breed, hoog, rand, side.bg, vlagNummers(this.vorm, velVorm.vel, false));
        blob = await new Promise((res) => vel.toBlob(res, 'image/jpeg', 0.92));
        vel.width = 0;
        vel.height = 0;
        if (dubbel) {
          // Achterkant: van achteren gezien draait de volgorde om, dus vlaggetje n krijgt daar de inhoud van
          // vlaggetje N+1-n. Zo zie je van beide kanten dezelfde slinger en lees je de naam van twee kanten
          // goed (Jos, 19 okt 2026).
          const N = velVorm.vel.plekken.length;
          vel = this.#maakVlaggenlijnVel((nr) => tekenNr(N + 1 - nr), velVorm, breed, hoog, rand, side.bg, vlagNummers(this.vorm, velVorm.vel, true));
          blobAchter = await new Promise((res) => vel.toBlob(res, 'image/jpeg', 0.92));
          vel.width = 0;
          vel.height = 0;
        }
      } else if (velVorm) {
        const vel = this.#maakVlaggenlijnVel(doek, velVorm, breed, hoog, rand, side.bg);
        // Het losse vlaggetje is niet meer nodig; meteen vrijgeven.
        doek.width = 0;
        doek.height = 0;
        doek = vel;
      }
      if (!blob) blob = await new Promise((res) => doek.toBlob(res, 'image/jpeg', 0.92));
    } catch (err) {
      return;                                   // tekenen mislukt: gewoon overslaan
    } finally {
      // Meteen vrijgeven. Zonder dit bleef elk drukbestand van 170 MB in het
      // geheugen hangen tot de browser zelf ging opruimen; op een iPhone kwam
      // het doek dan leeg in beeld.
      if (doek) {
        doek.width = 0;
        doek.height = 0;
      }
      vrij();
      // Het grote tekenvlak kan op een iPhone het beeld van de andere canvassen
      // hebben gekost; zie #herstelDoek.
      if (doek) this.#herstelDoek();
    }
    if (!blob) {
      // Mislukt, bijvoorbeeld door te weinig geheugen. Het kenmerk vrijgeven,
      // zodat de volgende wijziging het opnieuw probeert in plaats van nooit.
      if (side.drukKenmerk === kenmerk) side.drukKenmerk = null;
      return;
    }

    const naam = velVorm
      ? 'drukklaar-vlaggenlijn-' + velVorm.vel.plekken.length + 'x-' + maatCm.w + 'x' + maatCm.h + 'cm-vel-' +
        velVorm.vel.b + 'x' + velVorm.vel.h + 'cm' + (apart ? '-elk-anders' : '') + (dubbel ? '-voorzijde' : '') + '.jpg'
      : 'drukklaar-' + maatCm.w + 'x' + maatCm.h + 'cm-' +
        (rand ? Math.round(afloopCm * 10) + 'mm-afloop-' : '') + (breed + rand * 2) + 'x' + (hoog + rand * 2) + '.jpg';
    // Klaar, maar pas uploaden bij rust of bestellen; is het intussen alweer veranderd,
    // dan dit bestand niet meer versturen (scheelt een schrijfactie).
    await this.#uploadMoment();
    if (side.drukKenmerk !== kenmerk) return;
    try {
      const body = new FormData();
      body.append('file', blob, naam);
      const res = await fetch(this.uploadUrl, { method: 'POST', body });
      if (!res.ok) return;
      const data = await res.json();
      if (!data.url || side.drukKenmerk !== kenmerk) return;   // intussen weer gewijzigd
      if (blobAchter) {
        // Eerst ook de achterzijde binnen, dan pas allebei invullen: nooit een bestelling met alleen
        // een voorzijde.
        const b2 = new FormData();
        b2.append('file', blobAchter, naam.replace('-voorzijde', '-achterzijde'));
        const res2 = await fetch(this.uploadUrl, { method: 'POST', body: b2 });
        if (!res2.ok) return;
        const data2 = await res2.json();
        if (!data2.url || side.drukKenmerk !== kenmerk) return;
        const achter = this.#achterInput(side, true);
        achter.value = data2.url;
        achter.disabled = false;
      }
      side.drukInput.value = data.url;
      side.drukInput.disabled = false;
      side.drukPixels = (breed + rand * 2) + ' x ' + (hoog + rand * 2) + ' px';
      this.#output();
    } catch (err) {
      /* vangnet: de drukker heeft nog het originele bestand en de plaatsingsregel */
    }
  }

  /**
   * Terugkoppeling tijdens het uploaden. Zonder dit lijkt er niets te gebeuren
   * bij grote ontwerpbestanden, en klikken klanten nog eens (dubbele bestelling).
   */
  #bindBusyState() {
    const form = this.closest('form');
    const melding = this.querySelector('[data-busy]');
    if (!form || !melding) return;

    const knop = () => form.querySelector('button[type="submit"]');

    const bezig = () => {
      // vermeld de omvang: dan snapt de klant waarom een groot ontwerp langer duurt
      let mb = 0;
      this.sides.forEach((s) => {
        const f = s.fileInput.files && s.fileInput.files[0];
        if (f) mb += f.size;
      });
      mb = mb / 1048576;
      const basis = this.dataset.tBusy || '';
      melding.textContent = mb >= 1 ? basis + ' (' + mb.toFixed(1).replace('.', ',') + ' MB)' : basis;
      melding.hidden = false;
      this.bezigMetUploaden = true;
      const b = knop();
      if (b) b.setAttribute('aria-busy', 'true');
      this.#zetBestelknop();
    };

    const klaar = () => {
      melding.hidden = true;
      this.bezigMetUploaden = false;
      const b = knop();
      if (b) b.removeAttribute('aria-busy');
      // niet blind weer aanzetten: als het ontwerp te grof is moet de knop uit
      // blijven staan.
      this.#zetBestelknop();
    };

    form.addEventListener('submit', () => {
      if (!this.sides.some((s) => s.fileInput.files && s.fileInput.files.length)) return;
      bezig();
      // veiligheidsnet: nooit langer dan een halve minuut blokkeren
      clearTimeout(this.busyTimer);
      this.busyTimer = setTimeout(klaar, 30000);
    });

    ['cart:update', 'cart:add', 'cart:error'].forEach((n) =>
      document.addEventListener(n, () => {
        clearTimeout(this.busyTimer);
        klaar();
      })
    );
  }

  // ---------- ontwerp bewaren en terughalen vanuit de winkelmand ----------
  //
  // Een klant die drie vlaggen in zijn winkelmand heeft, moet kunnen zien welke
  // welke is en er een opnieuw kunnen openen. Het ontwerp zelf (lagen, kleuren,
  // posities, geuploade bestanden) bewaren we in de browser met
  // FlagOntwerpOpslag (assets/flag-ontwerpopslag.js). In de bestelling komt
  // alleen een korte sleutel: _Ontwerp-id.

  /**
   * Wacht tot de opslagmodule er is.
   *
   * Die module staat onderaan de pagina en wordt, net als dit script, met defer
   * geladen. Dit script staat hoger en draait dus eerder. "Nog niet gevonden" is
   * daarom geen "niet bewaard": er wordt gewacht tot hij er is. Zonder dat
   * wachten zag Jos bij "Ontwerp aanpassen" de melding dat het ontwerp niet
   * bewaard was, terwijl het er gewoon stond. In de test viel het niet op,
   * omdat beide scripts daar al in het geheugen stonden en vlak na elkaar liepen.
   */
  #opslag() {
    return new Promise((klaar) => {
      if (window.FlagOntwerpOpslag) return klaar(window.FlagOntwerpOpslag);
      let gemeld = false;
      const meld = (waarde) => {
        if (gemeld) return;
        gemeld = true;
        clearInterval(peiling);
        clearTimeout(opgeven);
        document.removeEventListener('flagcfg:opslag-klaar', bijSeintje);
        klaar(waarde);
      };
      const bijSeintje = () => meld(window.FlagOntwerpOpslag || null);
      document.addEventListener('flagcfg:opslag-klaar', bijSeintje);
      // Vangnet als het seintje er al was voordat we luisterden.
      const peiling = setInterval(() => {
        if (window.FlagOntwerpOpslag) meld(window.FlagOntwerpOpslag);
      }, 50);
      // Laadt de module helemaal niet (geblokkeerd, netwerkfout), dan na een
      // ruime tijd opgeven.
      const opgeven = setTimeout(() => meld(window.FlagOntwerpOpslag || null), 10000);
    });
  }

  #winkelWortel() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /** De gegevens van een laag die het ontwerp bepalen: geen beelden, geen paginaonderdelen. */
  #laagGegevens(l) {
    const overslaan = ['objectUrl', 'bronBestand', 'plaatje', 'plaatjeBezig', 'plaatjeStuk', 'stipEl', 'stipNaNaam', 'origBeeld', 'uitsnede', 'bgBezig', 'bgMethode'];
    const uit = {};
    Object.keys(l).forEach((k) => {
      if (overslaan.includes(k)) return;
      const v = l[k];
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') uit[k] = v;
    });
    return uit;
  }

  /**
   * Een vaste vingerafdruk van het ontwerp zoals het nu is. Hetzelfde ontwerp
   * geeft dezelfde ID, zodat twee keer toevoegen zoals altijd een hoger aantal
   * oplevert en geen tweede regel. Een ander ontwerp krijgt een andere ID.
   */
  #vingerafdruk() {
    const sleutel = (f) => (f ? [f.name || '', f.size || 0, f.lastModified || 0].join('|') : '');
    const afwerking = this.querySelector('[data-finishkeuze]');
    const gegevens = {
      v: this.variantVeld ? this.variantVeld.value : '',
      a: afwerking && !afwerking.disabled ? afwerking.value : '',
      z: this.sides.map((s) => ({
        bg: s.bg || '#ffffff',
        img: s.img ? 1 : 0,
        vlag: s.img ? s.vlagCode || '' : '',
        bestand: s.img && !s.vlagCode ? sleutel(s.bronBestand) : '',
        zoom: Math.round((s.zoom || 1) * 1000),
        cx: Math.round((s.cx || 0) * 10),
        cy: Math.round((s.cy || 0) * 10),
        rek: s.stretch ? 1 : 0,
        za: s.img && s.zonderAchtergrond ? bgSleutel(s.uitsnede) : 0,
        lagen: (s.lagen || []).map((l) => {
          const g = this.#laagGegevens(l);
          delete g.id;
          delete g.svg;
          // De tekening hoort bij de naam van het element; die naam (vorm) telt al mee.
          delete g.tekening;
          if (l.bronBestand) g.bestand = sleutel(l.bronBestand);
          if (l.zonderAchtergrond) g.za = bgSleutel(l.uitsnede);
          return g;
        }),
      })),
    };
    const tekst = JSON.stringify(gegevens);
    // FNV-1a, twee keer met een ander begin: kort, en ruim uniek genoeg voor
    // de paar regels in een winkelmand.
    const fnv = (begin) => {
      let h = begin >>> 0;
      for (let i = 0; i < tekst.length; i++) {
        h ^= tekst.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
      }
      return h.toString(36);
    };
    return 'o' + fnv(2166136261) + fnv(3339675911);
  }

  /** Zet de sleutel in het verborgen veld _Ontwerp-id. */
  #zetOntwerpId() {
    const veld = this.querySelector('[data-ontwerpid]');
    if (!veld) return;
    if (!this.sides || !this.sides.some((s) => s.img || this.#heeftLagen(s))) {
      veld.value = '';
      veld.disabled = true;
      return;
    }
    const afdruk = this.#vingerafdruk();
    // In de bewerkstand houdt de regel zijn eigen sleutel zolang er niets
    // veranderd is. Anders valt een gedupliceerde regel bij opslaan zonder
    // wijziging samen met het origineel.
    veld.value = this.bewerk && this.bewerk.startAfdruk === afdruk ? this.bewerk.id : afdruk;
    veld.disabled = false;
  }

  /**
   * Legt het hele ontwerp vast in de browser, onder de gegeven sleutel.
   * zonderVoorbeeld: alleen het ontwerp, geen vergrote voorbeeldplaatjes (voor het
   * tussentijds bewaren van een concept; dat gebeurt vaak en moet licht blijven).
   */
  async #momentopname(id, { zonderVoorbeeld = false } = {}) {
    const o = await this.#opslag();
    if (!o || !id) return;
    try {
      const bestandsvelden = (f, reserve) => ({
        bestand: f,
        bestandNaam: (f && f.name) || reserve || '',
        bestandType: (f && f.type) || '',
        bestandTijd: (f && f.lastModified) || 0,
      });
      const zijden = await Promise.all(
        this.sides.map(async (s) => {
          const titel = s.root.querySelector('.flagcfg__sidetitle');
          const zijde = {
            label: titel ? titel.textContent.trim() : '',
            bg: s.bg || '#ffffff',
            zoom: s.zoom,
            cx: s.cx,
            cy: s.cy,
            stretch: !!s.stretch,
            vector: !!s.vector,
            zonderAchtergrond: !!(s.img && s.uitsnede),
            uitsnede: s.img && s.uitsnede ? await this.#bgOpslagVorm(s.uitsnede) : null,
            fileName: s.fileName || '',
            vlagCode: s.img ? s.vlagCode || '' : '',
            vooraf: s.vooraf || '',
            libLabel: s.libValue && !s.libValue.disabled ? s.libValue.value : '',
            bestand: null,
            lagen: await Promise.all((s.lagen || []).map(async (l) => {
              const g = this.#laagGegevens(l);
              if (l.bronBestand) Object.assign(g, bestandsvelden(l.bronBestand, l.naam));
              if (l.uitsnede) g.uitsnede = await this.#bgOpslagVorm(l.uitsnede);
              else g.zonderAchtergrond = false;
              return g;
            })),
            voorbeeld: null,
          };
          if (s.img && !s.vlagCode && s.bronBestand) Object.assign(zijde, bestandsvelden(s.bronBestand, s.fileName));
          return zijde;
        })
      );
      const afwerking = this.querySelector('[data-finishkeuze]');
      const ontwerp = {
        id,
        v: 1,
        pad: location.pathname,
        variantId: this.variantVeld ? this.variantVeld.value : '',
        afwerking: afwerking && !afwerking.disabled ? afwerking.value : '',
        zijden,
      };
      // Eerst het ontwerp zelf wegschrijven, zonder voorbeeldplaatjes. Dat gaat
      // snel, zodat het ook bewaard is als de klant meteen doorklikt naar de
      // winkelmand: een onderbroken schrijfactie laat anders niets achter.
      await o.bewaar(ontwerp);
      if (zonderVoorbeeld) return;

      // Daarna per zijde een nette vergroting voor "Bekijk ontwerp" in de
      // winkelmand. Het controlebeeld is daarvoor te klein als er op een
      // telefoon ontworpen is. Mislukt dit, dan blijft het controlebeeld de
      // terugval.
      let metVoorbeeld = false;
      for (let i = 0; i < this.sides.length; i++) {
        const s = this.sides[i];
        if ((this.isBeach && !this.probo) || !(s.img || this.#heeftLagen(s))) continue;
        zijden[i].voorbeeld = await new Promise((klaar) => {
          try {
            const doekje = this.#previewDoek(s, 1600);
            doekje.toBlob((b) => {
              doekje.width = 0;
              doekje.height = 0;
              klaar(b);
            }, 'image/jpeg', 0.86);
          } catch (err) {
            klaar(null);
          }
        });
        if (zijden[i].voorbeeld) metVoorbeeld = true;
      }
      if (metVoorbeeld) await o.bewaar(ontwerp);
    } catch (err) {
      /* bewaren is een extra; het bestellen zelf gaat gewoon door */
    }
  }

  /** Zet een bewaard ontwerp terug op het doek: afbeeldingen, lagen, kleuren en posities. */
  async #herstelOntwerp(ontwerp) {
    const afwerking = this.querySelector('[data-finishkeuze]');
    if (afwerking && ontwerp.afwerking && [...afwerking.options].some((o) => o.value === ontwerp.afwerking)) {
      afwerking.value = ontwerp.afwerking;
      afwerking.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const laadBeeld = (src, cors) =>
      new Promise((klaar) => {
        const img = new Image();
        if (cors) img.crossOrigin = 'anonymous';
        img.onload = () => klaar(img);
        img.onerror = () => klaar(null);
        img.src = src;
      });
    const alsBestand = (blob, naam, type, tijd) => {
      if (!blob) return null;
      try {
        return new File([blob], naam || 'ontwerp', { type: type || blob.type || '', lastModified: tijd || Date.now() });
      } catch (err) {
        return blob;
      }
    };

    const zijden = ontwerp.zijden || [];
    await Promise.all(
      this.sides.map(async (side, i) => {
        const z = zijden[i];
        if (!z) return;

        side.bg = z.bg || '#ffffff';
        [side.bgInput, side.bgInput2].forEach((el) => {
          if (el) el.value = side.bg;
        });

        // De afbeelding die het doek vult: een landvlag, of een eigen bestand.
        if (z.vlagCode) {
          const img = await laadBeeld(this.flagBase + 'flag-' + z.vlagCode + '.svg', true);
          if (img) {
            side.img = img;
            side.vector = true;
            side.vlagCode = z.vlagCode;
            side.bronBestand = null;
            side.fileName = z.fileName || '';
            side.libValue.value = z.libLabel || side.fileName;
            side.libValue.disabled = !side.libValue.value;
            side.nameEl.textContent = this.chosenText + ': ' + side.fileName;
            const knop = side.libGrid && side.libGrid.querySelector('[data-code="' + z.vlagCode + '"]');
            if (knop) knop.classList.add('is-chosen');
          }
        } else if (z.bestand) {
          const bestand = alsBestand(z.bestand, z.bestandNaam, z.bestandType, z.bestandTijd);
          const pdf = /\.pdf$/i.test(z.bestandNaam || '') || (z.bestandType || '') === 'application/pdf';
          let img = null;
          if (pdf) {
            img = await this.#pdfNaarBeeld(bestand, side);
          } else {
            const url = URL.createObjectURL(bestand);
            img = await laadBeeld(url, false);
            if (img) side.objectUrl = url;
            else URL.revokeObjectURL(url);
          }
          if (img) {
            side.img = img;
            side.zonderAchtergrond = false;
            side.origBeeld = null;
            side.uitsnede = null;
            // Het masker komt uit de opslag; de AI hoeft niet opnieuw te rekenen.
            if (z.uitsnede && !z.vector) {
              const u = await bgUitOpslag(z.uitsnede);
              if (u) {
                side.origBeeld = img;
                side.uitsnede = u;
                side.img = bgSamenstellen(img, u);
                side.zonderAchtergrond = true;
              }
            }
            side.vector = !!z.vector;
            side.vlagCode = null;
            side.bronBestand = bestand;
            side.fileName = z.fileName || z.bestandNaam || '';
            side.nameEl.textContent = side.fileName;
            side.libValue.value = side.fileName;
            side.libValue.disabled = !side.fileName;
            // Het origineel staat al bij de uploadserver: dezelfde URL gaat
            // mee naar de drukker, er hoeft niets opnieuw geupload te worden.
            if (z.vooraf) {
              side.vooraf = z.vooraf;
              this.#schakelBestandsveld(side);
            } else if (bestand instanceof File) {
              this.#alvastUploaden(side, bestand);
            }
          }
        }
        if (side.img) {
          side.zoom = typeof z.zoom === 'number' ? z.zoom : 1;
          side.cx = typeof z.cx === 'number' ? z.cx : 50;
          side.cy = typeof z.cy === 'number' ? z.cy : 50;
          side.stretch = !!z.stretch;
          if (side.zoomInput) side.zoomInput.value = String(Math.round(side.zoom * 100));
          side.controls.hidden = false;
          side.noteEl.hidden = true;
        }

        // De elementen: tekst, vormen, symbolen en eigen afbeeldingen.
        side.lagen = (z.lagen || []).map((g) => {
          const l = Object.assign({}, g, { id: this.#nieuwId() });
          const bewaardeUitsnede = g.uitsnede;
          delete l.uitsnede;
          delete l.bestand;
          delete l.bestandNaam;
          delete l.bestandType;
          delete l.bestandTijd;
          if (l.type === 'kleur' && l.svg) this.#laadPlaatje(l);
          if (l.type === 'eigen') {
            if (g.vlagCode) {
              laadBeeld(this.flagBase + 'flag-' + g.vlagCode + '.svg', true).then((img) => {
                if (!img) {
                  l.plaatjeStuk = true;
                  return;
                }
                l.plaatje = img;
                this.#render();
              });
            } else if (g.bestand) {
              const bestand = alsBestand(g.bestand, g.bestandNaam, g.bestandType, g.bestandTijd);
              l.bronBestand = bestand;
              const url = URL.createObjectURL(bestand);
              l.objectUrl = url;
              laadBeeld(url, false).then((img) => {
                if (!img) {
                  l.plaatjeStuk = true;
                  URL.revokeObjectURL(url);
                  return;
                }
                l.plaatje = img;
                if (!l.zonderAchtergrond || !bewaardeUitsnede) {
                  l.zonderAchtergrond = false;
                  this.#tekenLagenLijst(side);
                  this.#render();
                  return;
                }
                return bgUitOpslag(bewaardeUitsnede).then((u) => {
                  if (u && l.plaatje === img) {
                    l.origBeeld = img;
                    l.uitsnede = u;
                    l.plaatje = bgSamenstellen(img, u);
                  } else {
                    bgVrijUitsnede(u);
                    l.zonderAchtergrond = false;
                  }
                  this.#tekenLagenLijst(side);
                  this.#render();
                });
              });
            } else {
              l.plaatjeStuk = true;
            }
          }
          return l;
        });
      })
    );

    this.sides.forEach((side) => {
      this.#toonBgWis(side);
      this.#tekenLagenLijst(side);
    });
    this.#render();
    // Het ontwerp uit de winkelmand is het beginpunt: niet terug naar een lege vlag.
    setTimeout(() => this.#wisGeschiedenis(), 600);
  }

  /** Korte melding boven het doek. */
  #toonBewerkMelding(tekst, fout) {
    if (!tekst) return;
    let el = this.querySelector('[data-flagcfg-bewerkmelding]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-flagcfg-bewerkmelding', '');
      el.setAttribute('role', 'status');
      const stage = this.querySelector('.flagcfg__stage');
      if (stage && stage.parentNode) stage.parentNode.insertBefore(el, stage);
      else this.prepend(el);
    }
    // Rechtstreeks opgemaakt, net als de Klaar-knop: de gebundelde opmaak
    // ververst niet altijd tegelijk met dit script.
    el.style.cssText = [
      'margin:0 0 .8rem', 'padding:.7rem .9rem', 'border-radius:8px', 'font-size:.88rem', 'line-height:1.4',
      'background:' + (fout ? '#fdf2f2' : '#eef3fb'),
      'color:' + (fout ? '#8a1f1f' : '#22314d'),
      'border:1px solid ' + (fout ? '#f1c9c9' : '#d3dcee'),
    ].join(';');
    el.textContent = tekst;
  }

  /** Zet de pagina in de stand "je past een ontwerp uit je winkelmand aan". */
  #zetBewerkModus() {
    const form = this.closest('form');
    if (!form) return;
    const knop = form.querySelector('button[type="submit"]');
    if (knop) {
      // Alleen de tekst vervangen, niet de opbouw van de knop: het thema
      // hangt er zelf onderdelen aan op.
      const doel =
        [...knop.querySelectorAll('*')].find((el) => !el.children.length && el.textContent.trim()) || knop;
      if (this.dataset.tBewerkOpslaan) doel.textContent = this.dataset.tBewerkOpslaan;
    }
    // "Direct kopen" zou een nieuwe regel maken en meteen afrekenen; in deze
    // stand is dat verwarrend.
    const blok = form.closest('product-form-component') || form.parentElement;
    if (blok) {
      blok.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout, [data-shopify="payment-button"]').forEach((el) => {
        el.style.display = 'none';
      });
    }
    const aantal = form.querySelector('input[name="quantity"]');
    if (aantal && this.bewerk) {
      aantal.value = String(this.bewerk.aantal);
      aantal.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.#toonBewerkMelding(this.dataset.tBewerkBanner || '', false);
  }

  /** Kwam de klant via "Ontwerp aanpassen"? Dan het ontwerp terughalen en de tool openen. */
  async #startBewerken(id, regel) {
    const o = await this.#opslag();
    const ontwerp = o ? await o.haal(id) : null;
    if (!ontwerp) {
      this.#toonBewerkMelding(this.dataset.tBewerkKwijt || '', true);
      return;
    }

    let winkelregel = null;
    try {
      const winkelmand = await fetch(this.#winkelWortel() + 'cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());
      // Eerst op de sleutel uit de link, anders op het ontwerp-ID. De sleutel
      // van een regel kan veranderen (bijvoorbeeld als het aantal intussen is
      // aangepast); het ontwerp-ID verandert niet.
      winkelregel =
        winkelmand.items.find((i) => i.key === regel) ||
        winkelmand.items.find((i) => i.properties && i.properties['_Ontwerp-id'] === id) ||
        null;
    } catch (err) {
      /* dan gewoon als nieuw ontwerp behandelen */
    }

    await this.#herstelOntwerp(ontwerp);

    // Staat de regel niet meer in de winkelmand (intussen verwijderd), dan
    // is er niets om bij te werken: het wordt dan een gewone nieuwe bestelling.
    if (winkelregel) {
      this.bewerk = {
        id,
        regel: winkelregel.key,
        aantal: winkelregel.quantity,
        variantId: winkelregel.variant_id,
        startAfdruk: this.#vingerafdruk(),
      };
      this.#zetBewerkModus();
    }
    this.#zetOntwerpId();
    if (this.tekstToegestaan) this.#volScherm(true);
  }

  /**
   * Wacht tot het drukbestand, het controlebeeld en een eventueel geupload
   * origineel van het nieuwe ontwerp klaar zijn. Anders gaat bij het bijwerken
   * de vorige versie mee naar de drukker.
   */
  #bestandenKlaar() {
    if (this.previewWacht || this.drukWacht) return false;
    const druk =
      (this.isBeach && !this.probo) ||
      this.sides.every((s) => {
        if (!(s.img || this.#heeftLagen(s)) || !s.drukInput) return true;
        return !!s.drukInput.value && !s.drukInput.disabled;
      });
    const origineel = this.sides.every((s) => !(s.img && s.bronBestand && !s.vlagCode) || !!s.vooraf || !!s.voorafMislukt);
    // Zonder ontwerp (of in de focusstand) komt er geen controlebeeld; daar niet op wachten.
    const beeld =
      !this.previewInput ||
      !this.sides.some((s) => s.img || this.#heeftLagen(s)) ||
      (this.previewBeurt || 0) === (this.previewKlaarBeurt || 0);
    return druk && origineel && beeld;
  }

  /**
   * Zet de bestanden op de winkelmandregel zodra ze klaar zijn (27 okt 2026). De vlag staat er dan al in;
   * de klant zag eerst tot 20 s een draaiend rondje. De regel wordt gevonden op _Ontwerp-id. Alle
   * eigenschappen gaan opnieuw mee (cart/change vervangt ze in hun geheel). Daarna ververst de lade.
   */
  #vulRegelAan(form, id) {
    const pauze = (ms) => new Promise((r) => setTimeout(r, ms));
    const wortel = this.#winkelWortel();
    const werk = (async () => {
      await this.#wachtOpBestanden();
      // #wachtOpBestanden geeft na 20 s op; een grote upload op 4G mag langer duren.
      for (let n = 0; n < 240 && !this.#bestandenKlaar(); n++) await pauze(250);
      const eigenschappen = {};
      for (const [naam, waarde] of new FormData(form).entries()) {
        const m = /^properties\[(.+)\]$/.exec(naam);
        if (m && typeof waarde === 'string' && waarde !== '') eigenschappen[m[1]] = waarde;
      }
      eigenschappen['_Ontwerp-id'] = id;
      for (let poging = 0; poging < 8; poging++) {
        try {
          const mand = await fetch(wortel + 'cart.js', {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          }).then((r) => r.json());
          const regel = mand.items.find((i) => i.properties && i.properties['_Ontwerp-id'] === id);
          if (!regel) {
            await pauze(750); // het thema is nog bezig met toevoegen
            continue;
          }
          const anders = Object.keys(eigenschappen).some((k) => regel.properties[k] !== eigenschappen[k]);
          if (!anders) return;
          const r = await fetch(wortel + 'cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: regel.key, quantity: regel.quantity, properties: eigenschappen }),
          });
          if (!r.ok) throw new Error('cart/change ' + r.status);
          const nieuw = await r.json();
          // Lade en winkelmand halen zichzelf opnieuw op (geen sections mee = opnieuw renderen).
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: { resource: nieuw, sourceId: 'flagcfg', data: { source: 'flagcfg-ontwerp', itemCount: nieuw.item_count } },
            })
          );
          return;
        } catch (err) {
          await pauze(1000);
        }
      }
    })();
    FlagConfigurator.openstaand.add(werk);
    werk.finally(() => FlagConfigurator.openstaand.delete(werk));
    FlagConfigurator.#bewaakAfrekenen(this.dataset.tOpslaan || this.dataset.tBusy || '');
  }

  static openstaand = new Set();

  /**
   * Afrekenen of naar de winkelmand terwijl het ontwerp nog wordt opgeslagen: eerst afmaken, dan
   * pas verder. Anders kan er een bestelling zonder drukbestand ontstaan. Eén keer per pagina.
   */
  static #bewaakAfrekenen(tekst) {
    if (FlagConfigurator.bewaakt) return;
    FlagConfigurator.bewaakt = true;
    const doelen = 'button[name="checkout"], a[href*="/checkout"], a[href$="/cart"], a[href*="/cart?"]';
    let melding = null;
    const toon = (aan) => {
      if (!melding) {
        melding = document.createElement('div');
        melding.setAttribute('role', 'status');
        melding.style.cssText =
          'position:fixed;left:50%;bottom:calc(1rem + env(safe-area-inset-bottom,0px));transform:translateX(-50%);' +
          'z-index:10000;max-width:calc(100% - 2rem);padding:.75rem 1rem;border-radius:12px;background:#111;color:#fff;' +
          'font:500 .9rem/1.35 system-ui,sans-serif;box-shadow:0 6px 24px rgb(0 0 0 / .25)';
        melding.textContent = tekst || 'Je ontwerp wordt opgeslagen…';
        document.body.appendChild(melding);
      }
      melding.hidden = !aan;
    };
    document.addEventListener(
      'click',
      async (e) => {
        if (!FlagConfigurator.openstaand.size) return;
        const el = e.target instanceof Element && e.target.closest(doelen);
        if (!el) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        toon(true);
        await Promise.allSettled([...FlagConfigurator.openstaand]);
        toon(false);
        if (el instanceof HTMLAnchorElement) location.href = el.href;
        else el.click();
      },
      true
    );
    window.addEventListener('beforeunload', (e) => {
      if (!FlagConfigurator.openstaand.size) return;
      e.preventDefault();
      e.returnValue = '';
    });
  }

  #wachtOpBestanden() {
    this.#nuBestanden();
    return new Promise((klaar) => {
      const begin = Date.now();
      const kijk = () => {
        // Komt er tijdens het wachten nog een tekenbeurt bij (het thema bouwt na een
        // maatwissel de pagina bij), dan ook die meteen maken in plaats van 4 s te wachten.
        this.#nuBestanden();
        if (this.#bestandenKlaar() || Date.now() - begin > 20000) return klaar();
        setTimeout(kijk, 250);
      };
      setTimeout(kijk, 100);
    });
  }

  /**
   * Bestellen terwijl het nieuwste drukbestand of controlebeeld nog niet klaar is: eerst
   * afmaken, dan alsnog versturen. Voorheen ging dan stilletjes het vorige bestand mee
   * (of geen), als de klant binnen een seconde na zijn laatste wijziging bestelde.
   */
  async #wachtEnVerstuur(form, knop) {
    if (this.bezigMetVersturen) return; // tweede tik tijdens het wachten: niet dubbel bestellen
    this.bezigMetVersturen = true;
    const b = knop || form.querySelector('button[type="submit"]');
    if (b) {
      FlagConfigurator.#wachtStijl();
      b.disabled = true;
      b.setAttribute('aria-busy', 'true');
      b.setAttribute('data-flagcfg-wacht', '');
    }
    try {
      await this.#wachtOpBestanden();
    } finally {
      this.bezigMetVersturen = false;
      if (b) {
        b.disabled = false;
        b.removeAttribute('aria-busy');
        b.removeAttribute('data-flagcfg-wacht');
      }
    }
    this.versturenNaWachten = true;
    try {
      if (form.requestSubmit) form.requestSubmit(b || undefined);
      else form.submit();
    } finally {
      this.versturenNaWachten = false;
    }
  }

  /**
   * Draaiend rondje op de bestelknop zolang de bestanden nog weg moeten. Zonder dit zag
   * de klant niets gebeuren (of al "Toegevoegd") terwijl de winkelmand nog leeg bleef.
   */
  static #wachtStijl() {
    if (document.getElementById('flagcfg-wacht-stijl')) return;
    const st = document.createElement('style');
    st.id = 'flagcfg-wacht-stijl';
    st.textContent =
      'button[data-flagcfg-wacht]{position:relative;opacity:1!important;cursor:progress}' +
      'button[data-flagcfg-wacht]>*{visibility:hidden}' +
      'button[data-flagcfg-wacht]::after{content:"";position:absolute;inset:0;margin:auto;width:20px;height:20px;' +
      'border-radius:50%;border:2px solid rgba(255,255,255,.45);border-top-color:#fff;animation:flagcfg-draai .7s linear infinite}' +
      '@keyframes flagcfg-draai{to{transform:rotate(360deg)}}' +
      '@media (prefers-reduced-motion:reduce){button[data-flagcfg-wacht]::after{animation-duration:2s}}';
    document.head.appendChild(st);
  }

  /** Vervangt de winkelmandregel door het aangepaste ontwerp, in plaats van een nieuwe toe te voegen. */
  async #werkRegelBij(form, id) {
    const knop = form.querySelector('button[type="submit"]');
    if (knop) {
      knop.disabled = true;
      knop.setAttribute('aria-busy', 'true');
    }
    const wortel = this.#winkelWortel();
    try {
      await this.#wachtOpBestanden();
      this.#zetOntwerpId();
      const veld = this.querySelector('[data-ontwerpid]');
      const nieuwId = (veld && !veld.disabled && veld.value) || id;

      // Niets veranderd: dan valt er ook niets bij te werken.
      if (nieuwId === this.bewerk.id) {
        location.href = wortel + 'cart';
        return;
      }

      await this.#momentopname(nieuwId);

      const gegevens = new FormData(form);
      const eigenschappen = {};
      for (const [naam, waarde] of gegevens.entries()) {
        const m = /^properties\[(.+)\]$/.exec(naam);
        if (m && typeof waarde === 'string' && waarde !== '') eigenschappen[m[1]] = waarde;
      }
      eigenschappen['_Ontwerp-id'] = nieuwId;
      // De regel vlak voor het bijwerken opnieuw opzoeken: sleutel en aantal
      // kunnen veranderd zijn sinds de pagina openging. Is hij intussen uit de
      // winkelmand gehaald, dan wordt het aangepaste ontwerp een nieuwe regel.
      let sleutel = this.bewerk.regel;
      let aantal = this.bewerk.aantal || 1;
      let oudeVariant = Number(this.bewerk.variantId);
      let bestaatNog = true;
      try {
        const nu = await fetch(wortel + 'cart.js', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then((r) => r.json());
        const regel =
          nu.items.find((i) => i.properties && i.properties['_Ontwerp-id'] === this.bewerk.id) ||
          nu.items.find((i) => i.key === this.bewerk.regel);
        if (regel) {
          sleutel = regel.key;
          aantal = regel.quantity;
          oudeVariant = Number(regel.variant_id);
        } else {
          bestaatNog = false;
        }
      } catch (err) {
        /* dan met wat we bij het openen wisten */
      }
      const variant = Number(gegevens.get('id')) || oudeVariant;

      const stuur = (pad, body) =>
        fetch(wortel + pad, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        }).then((r) => {
          if (!r.ok) throw new Error(pad + ' ' + r.status);
          return r.json();
        });

      if (!bestaatNog) {
        await stuur('cart/add.js', { items: [{ id: variant, quantity: aantal, properties: eigenschappen }] });
      } else if (variant === oudeVariant) {
        await stuur('cart/change.js', { id: sleutel, quantity: aantal, properties: eigenschappen });
      } else {
        // Een andere maat is een andere variant, en die kan Shopify niet op een
        // bestaande regel wisselen. Eerst de nieuwe toevoegen en dan pas de
        // oude weghalen: mislukt het toevoegen, dan staat de oude er nog.
        await stuur('cart/add.js', { items: [{ id: variant, quantity: aantal, properties: eigenschappen }] });
        // Na het toevoegen heeft de oude regel een andere sleutel gekregen:
        // Shopify rekent de hele winkelmand opnieuw door. Daarom opnieuw
        // opzoeken op het oude ontwerp-ID en de oude maat, en pas dan weghalen.
        // Getest: met de sleutel van voor het toevoegen bleef de oude regel staan.
        const naToevoegen = await fetch(wortel + 'cart.js', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then((r) => r.json());
        const oud =
          naToevoegen.items.find(
            (i) =>
              i.properties &&
              i.properties['_Ontwerp-id'] === this.bewerk.id &&
              Number(i.variant_id) === oudeVariant
          ) || naToevoegen.items.find((i) => i.key === sleutel);
        if (oud) await stuur('cart/change.js', { id: oud.key, quantity: 0 });
      }
      location.href = wortel + 'cart';
    } catch (err) {
      this.#toonBewerkMelding(this.dataset.tBewerkMislukt || '', true);
      if (knop) {
        knop.disabled = false;
        knop.removeAttribute('aria-busy');
      }
    }
  }

  #bindOntwerpOpslag() {
    const form = this.closest('form');
    if (!form) return;

    // Op window en in de vangfase: dan gebeurt dit voordat het thema het
    // formulier uitleest. Het thema luistert zelf op document, ook in de
    // vangfase, en een luisteraar op het formulier komt dus altijd te laat.
    window.addEventListener(
      'submit',
      (e) => {
        if (e.target !== form) return;
        // Zonder ontwerp niet bestellen: de rij gaat open (Jos, 15 sep 2026).
        if (this.#moetOntwerpVragen()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.#vraagOmOntwerp();
          return;
        }
        // Direct toevoegen (27 okt 2026). Zijn drukbestand, upload of controlebeeld nog niet klaar,
        // dan gaat de vlag tóch meteen de winkelmand in; #vulRegelAan maakt de bestanden op de
        // achtergrond af en zet ze daarna op de regel. Afrekenen wacht daarop (#bewaakAfrekenen).
        const aanvullen = !this.bewerk && !this.versturenNaWachten && !this.#bestandenKlaar();
        if (aanvullen) {
          // Het ruwe bestand niet via de winkelmand meesturen: bij een grote foto bleef dat hangen
          // (klant, 27 okt 2026). Het thema leest het formulier direct na deze luisteraar; daarna weer aan.
          const uit = this.sides.map((sd) => sd.fileInput).filter((fi) => fi && !fi.disabled && fi.files && fi.files.length);
          uit.forEach((fi) => { fi.disabled = true; });
          setTimeout(() => uit.forEach((fi) => {
            const sd = this.sides.find((x) => x.fileInput === fi);
            if (!(sd && sd.vooraf)) fi.disabled = false; // al geupload: het URL-veld neemt het over
          }), 0);
        }
        this.#zetOntwerpId();
        const veld = this.querySelector('[data-ontwerpid]');
        const id = veld && !veld.disabled ? veld.value : '';
        if (aanvullen && id) this.#vulRegelAan(form, id);

        if (this.bewerk) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.#werkRegelBij(form, id);
          return;
        }
        if (id) this.#momentopname(id);
      },
      true
    );

    // De bestelknop van het thema speelt bij de tik zelf al een animatie af,
    // vóór het versturen. Daarom hier ook de tik al tegenhouden, anders vliegt
    // er een vlag naar de winkelmand die er nooit aankomt.
    window.addEventListener(
      'click',
      (e) => {
        const knop = e.target instanceof Element && e.target.closest('button[type="submit"]');
        if (!knop || knop.form !== form) return;
        if (this.#moetOntwerpVragen()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.#vraagOmOntwerp();
          return;
        }
        // Bestanden nog niet weg: sinds 27 okt 2026 niet meer tegenhouden. De vlag gaat direct de
        // winkelmand in en de bestanden volgen (zie de submit-luisteraar hierboven).
      },
      true
    );

    const params = new URLSearchParams(location.search);
    const id = params.get('ontwerp');
    const regel = params.get('regel');
    if (id && regel) this.#startBewerken(id, regel);
    // Vanaf een vlagpagina (/pages/vlaggen/...): ?vlag=gh zet die vlag meteen
    // op het doek, ?vlag2=nl op de tweede kant (Jos, 7 okt 2026).
    const vlag = params.get('vlag');
    if (vlag && !(id && regel)) this.#startMetVlag([vlag, params.get('vlag2')]);
    // Vanaf een themapagina (geslaagd, geboorte, Abraham ...): ?sjabloon=geslaagd-tas
    // zet dat sjabloon meteen op de eerste kant (Jos, 13 okt 2026).
    const sjabloon = params.get('sjabloon');
    if (sjabloon && !vlag && !(id && regel)) this.#startMetSjabloon(sjabloon);
    // Geen opdracht uit de link: een bewaard concept terugzetten (M01). Met een
    // opdracht kiest de klant bewust iets nieuws; dat wordt dan het concept.
    if (!(id && regel) && !vlag && !sjabloon) this.#herstelConcept();
    else this.conceptAan = true;
    // In de winkelmand: het concept is geen concept meer. Het thema meldt een
    // toevoeging als 'cart:update' met het productformulier als bron.
    document.addEventListener('cart:update', (e) => {
      const d = (e.detail && e.detail.data) || {};
      if (d.source !== 'product-form-component' || d.didError) return;
      if (this.bewerk || !this.#heeftOntwerp()) return;
      clearTimeout(this.conceptKlok);
      this.conceptToegevoegd = this.#vingerafdruk();
      this.#opslag().then((o) => o && o.verwijder && o.verwijder(this.#conceptSleutel()));
    });
  }

  /** Voorkomt dat velden van andere apps (Customily) dubbel meegaan. */
  #disableForeignProperties() {
    const form = this.closest('form');
    if (!form) return;
    form.querySelectorAll('[name^="properties["]').forEach((el) => {
      if (!this.contains(el)) el.disabled = true;
    });
  }
}

console.info('[ontwerptool] versie ' + FLAGCFG_VERSIE);

if (!customElements.get('flag-configurator')) {
  customElements.define('flag-configurator', FlagConfigurator);
}
